/* tslint:disable */
/* eslint-disable */
/**
 * Levittown API
 * Levittown API Documentation
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction, replaceWithSerializableTypeIfNeeded } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface AccountTrackingList {
    'accounts': Array<AccountTrackingReturn>;
    'total': number;
}
/**
 * Whether we pull transactions for one account, and how far we have got.
 */
export interface AccountTrackingReturn {
    'account_number': string;
    /**
     * Earliest day we believe is complete. Absent until the first pull has run, which is also what tells the sweep to fetch 90 days.
     */
    'backfilled_from'?: string | null;
    'bank_connection_id': string;
    'disabled_at'?: string | null;
    'enabled': boolean;
    'enabled_at'?: string | null;
    /**
     * The assignment, when there is one. Absent for a company account nobody has given to an association, and for one whose assignment has been retired.
     */
    'hoa_bank_account_id'?: string | null;
    'hoa_id'?: string | null;
    'id': string;
    /**
     * **Absent means never synced**, which is a fact about us rather than about the account — an empty transaction list under this means \"we have not looked yet\", not \"there is nothing\".
     */
    'last_synced_at'?: string | null;
    /**
     * Latest day we believe is complete.
     */
    'synced_through'?: string | null;
    'transaction_count': number;
}
/**
 * Turn the daily transaction pull on or off for one account.
 */
export interface AccountTrackingUpdate {
    'enabled': boolean;
}
/**
 * Two of these, not one enum with a `modified_accrual` variant: \"modified accrual\" is accrual revenue plus cash expenses, and is not defined identically in every state.
 */

export const AccountingBasis = {
    ACCRUAL: 'accrual',
    CASH: 'cash'
} as const;

export type AccountingBasis = typeof AccountingBasis[keyof typeof AccountingBasis];


export interface AccountingPeriodList {
    'periods': Array<AccountingPeriodReturn>;
}
export interface AccountingPeriodReturn {
    'closed_at'?: number | null;
    'closed_by'?: string | null;
    'end_date': string;
    'fiscal_year': number;
    'hoa_id': string;
    'id': string;
    /**
     * The single period holding brought-forward balances. Reporting before it must fail rather than return zeros.
     */
    'is_opening': boolean;
    /**
     * 0 for the opening period, then 1..12.
     */
    'period_number': number;
    'start_date': string;
    'status': string;
}
/**
 * Whether a homeowner can pay this home by ACH, and what is on file.
 */
export interface AchAvailabilityReturn {
    /**
     * The debit authorisation to show before an account is saved.
     */
    'authorization_text': string;
    'authorization_version': string;
    'bank_account'?: CounterpartyBankAccountReturn | null;
    /**
     * The earliest effective date a payment may carry (the bank\'s day).
     */
    'earliest_effective_date': string;
    'enabled': boolean;
    /**
     * Why not, when `enabled` is false.
     */
    'reason'?: string | null;
    /**
     * The association\'s receiving account, masked, when enabled.
     */
    'receiving_account_last4'?: string | null;
    /**
     * `PPD` | `WEB`
     */
    'sec_code': string;
}
/**
 * Pay the association from the account on file. `token` then `add`; the association\'s staff settle the day\'s batch.
 */
export interface AchPaymentCreate {
    /**
     * Decimal string, e.g. `\"250.00\"`. May exceed the balance.
     */
    'amount': string;
    /**
     * Today or later in the bank\'s calendar.
     */
    'effective_date': string;
}
/**
 * Open charge amounts bucketed by how overdue they are.
 */
export interface AgingBuckets {
    /**
     * Not yet due, or due today.
     */
    'current': string;
    'days_1_30': string;
    'days_31_60': string;
    'days_61_90': string;
    'days_over_90': string;
}
/**
 * Delinquency across an association, as of the association\'s today.  The buckets sum to the balance for any home that owes money. They do not for a home carrying a **credit** balance: buckets count open charges, and there are none, while the balance is negative. That is the honest reading of both numbers rather than a reconciliation error.
 */
export interface AgingReport {
    'as_of': string;
    'hoa_id': string;
    'homes': Array<AgingRow>;
    'page': number;
    'per_page': number;
    'total': number;
    'totals': AgingTotals;
}
/**
 * One home\'s line on the delinquency report.
 */
export interface AgingRow {
    'aging': AgingBuckets;
    /**
     * Signed, like every other balance. Negative only where the HOA allows credit balances.
     */
    'balance': string;
    'case_id'?: string | null;
    'case_status'?: CaseStatus | null;
    'days_past_due': number;
    /**
     * What the payer file\'s HOLD column would say today — the collections case, unless the tenure overrides it.
     */
    'hold': boolean;
    'home_id': string;
    'home_label'?: string | null;
    'oldest_unpaid_due_date'?: string | null;
    'ownership_tenure_id'?: string | null;
    'payer_name'?: string | null;
}


/**
 * What an aging run did.
 */
export interface AgingRunReport {
    'as_of': string;
    'cases_advanced': number;
    'cases_opened': number;
    'cases_resolved': number;
    'hoa_id': string;
    'homes_checked': number;
}
/**
 * How the delinquency report is ordered.
 */

export const AgingSort = {
    BALANCE: 'balance',
    DAYS_PAST_DUE: 'days_past_due'
} as const;

export type AgingSort = typeof AgingSort[keyof typeof AgingSort];


/**
 * The report\'s footer: the same figures, summed over every row that matched the filters — **not** just the page.
 */
export interface AgingTotals {
    'balance': string;
    'current': string;
    'days_1_30': string;
    'days_31_60': string;
    'days_61_90': string;
    'days_over_90': string;
    /**
     * Homes counted in these totals.
     */
    'homes': number;
}
/**
 * How a household is given a way in (phase 3).
 */

export const AmenityAccessCodeMode = {
    NONE: 'none',
    STATIC: 'static',
    PER_BOOKING: 'per_booking'
} as const;

export type AmenityAccessCodeMode = typeof AmenityAccessCodeMode[keyof typeof AmenityAccessCodeMode];


/**
 * Publish a new agreement version. Earlier versions are never edited.
 */
export interface AmenityAgreementCreate {
    'body': string;
    'title': string;
}
export interface AmenityAgreementList {
    'agreements': Array<AmenityAgreementReturn>;
    'total': number;
}
export interface AmenityAgreementReturn {
    'amenity_id': string;
    'body': string;
    'id': string;
    'published_at': number;
    'published_by'?: string | null;
    'title': string;
    'version': number;
}
/**
 * Whether a homeowner\'s request is confirmed on the spot or waits for a manager.
 */

export const AmenityApprovalMode = {
    AUTO: 'auto',
    MANUAL: 'manual'
} as const;

export type AmenityApprovalMode = typeof AmenityApprovalMode[keyof typeof AmenityApprovalMode];


/**
 * Archive an amenity. With future bookings it answers 409 unless `cancel_future` is set, which cancels them (fees reversed).
 */
export interface AmenityArchiveQuery {
    'cancel_future'?: boolean | null;
    'reason'?: string | null;
}
export interface AmenityAvailabilityReturn {
    'amenity_id': string;
    'bookable': boolean;
    'booking_mode': AmenityBookingMode;
    /**
     * Shared capacity: the headcount a slot holds.
     */
    'capacity'?: number | null;
    'closures': Array<AvailabilityClosure>;
    'date': string;
    /**
     * Present when asked for a home.
     */
    'home_usage'?: ReservationHomeUsage | null;
    'hours_configured': boolean;
    'max_duration_minutes': number;
    'min_duration_minutes': number;
    /**
     * `overnight` amenities: one entry per night from `date` through `to`. Empty for the other modes, which use `starts`.
     */
    'nights': Array<AvailabilityNight>;
    /**
     * The opening windows that apply to this date. Empty with `hours_configured` false means open round the clock.
     */
    'open_windows': Array<AmenityHoursWindow>;
    /**
     * The date is outside every season and there is no off-season schedule: closed all day.
     */
    'out_of_season': boolean;
    /**
     * The season `date` falls in, if the amenity keeps seasons.
     */
    'season_name'?: string | null;
    'slot_increment_minutes': number;
    'starts': Array<AvailabilityStart>;
    'timezone': string;
}


/**
 * How an amenity is booked.  - `exclusive`: one household at a time (the clubhouse). - `info_only`: listed with hours and rules, never booked. - `shared_capacity`: many households per slot up to `capacity`   people (a pool session). - `overnight`: check-in on one day, check-out on a later one (a   guest suite, a guest parking spot).
 */

export const AmenityBookingMode = {
    EXCLUSIVE: 'exclusive',
    INFO_ONLY: 'info_only',
    SHARED_CAPACITY: 'shared_capacity',
    OVERNIGHT: 'overnight'
} as const;

export type AmenityBookingMode = typeof AmenityBookingMode[keyof typeof AmenityBookingMode];


export interface AmenityClosureCreate {
    /**
     * Cancel any pending or approved booking the closure overlaps, reversing its fee. Without it an overlap answers 409 with the list of bookings in the way.
     */
    'cancel_conflicting'?: boolean | null;
    /**
     * Required with `cancel_conflicting`; sent to the households.
     */
    'cancel_reason'?: string | null;
    'ends_local': string;
    /**
     * Shown to homeowners.
     */
    'reason': string;
    'starts_local': string;
}
export interface AmenityClosureList {
    'closures': Array<AmenityClosureReturn>;
    'total': number;
}
export interface AmenityClosureReturn {
    'amenity_id': string;
    'created_by_user_id'?: string | null;
    'date_created'?: number | null;
    'ends_local': string;
    'id': string;
    'reason': string;
    'starts_local': string;
}
/**
 * Create an amenity. Everything but `hoa_id` and `name` has a default.
 */
export interface AmenityCreate {
    /**
     * Default `none`.
     */
    'access_code_mode'?: AmenityAccessCodeMode | null;
    /**
     * How to get in. Shown to the booking household inside the reveal window, never before.
     */
    'access_instructions'?: string | null;
    /**
     * Hours before the start the household is shown the code and the instructions. Default 24.
     */
    'access_reveal_hours'?: number | null;
    'approval_mode'?: AmenityApprovalMode | null;
    'booking_mode'?: AmenityBookingMode | null;
    /**
     * Cleanup time after each booking. Default 0.
     */
    'buffer_minutes'?: number | null;
    'cancel_deadline_hours'?: number | null;
    /**
     * `shared_capacity`: the headcount one slot holds, across households. Required for that mode.
     */
    'capacity'?: number | null;
    /**
     * `overnight`: minutes from local midnight a stay begins on the arrival day and ends on the departure day. Required for that mode.
     */
    'check_in_minute'?: number | null;
    'check_out_minute'?: number | null;
    /**
     * Phase 3. A refundable deposit, decimal string. Held from approval until the booking is settled.
     */
    'deposit_amount'?: string | null;
    'description'?: string | null;
    'eligibility'?: AmenityEligibility | null;
    /**
     * Decimal string. Required unless `fee_type` is `none`.
     */
    'fee_amount'?: string | null;
    'fee_type'?: AmenityFeeType | null;
    'guest_fee_per_person'?: string | null;
    'hoa_id': string;
    /**
     * The weekly schedule, if known at creation. Default-season windows only: seasons are added after the amenity exists.
     */
    'hours'?: Array<AmenityHoursWindow> | null;
    /**
     * Per-guest pricing: people the fee includes, and the decimal-string fee for each beyond them. Both or neither.
     */
    'included_guests'?: number | null;
    /**
     * The items a manager checks before and after a booking.
     */
    'inspection_checklist'?: Array<string> | null;
    /**
     * Decimal string: dollars for `flat`, 0–100 for `percent`.
     */
    'late_cancel_amount'?: string | null;
    /**
     * Default `keep_full`.
     */
    'late_cancel_policy'?: AmenityLateCancelPolicy | null;
    'location'?: string | null;
    /**
     * Default 240.
     */
    'max_duration_minutes'?: number | null;
    'max_lead_days'?: number | null;
    'max_nights'?: number | null;
    'max_party_size'?: number | null;
    /**
     * Default 60.
     */
    'min_duration_minutes'?: number | null;
    'min_nights'?: number | null;
    'min_notice_hours'?: number | null;
    'name': string;
    'per_home_active_limit'?: number | null;
    'per_home_daily_limit'?: number | null;
    'per_home_monthly_hours_limit'?: string | null;
    'per_home_monthly_limit'?: number | null;
    /**
     * Decimal hours a household may hold in a week / month. Not applied to `overnight`.
     */
    'per_home_weekly_hours_limit'?: string | null;
    'per_home_weekly_limit'?: number | null;
    'requires_agreement'?: boolean | null;
    'requires_good_standing'?: boolean | null;
    /**
     * A guest parking spot: the booking must name the vehicle.
     */
    'requires_vehicle'?: boolean | null;
    /**
     * Shown to homeowners before booking. Conduct and supervision rules — never an age ban.
     */
    'rules_text'?: string | null;
    /**
     * Default 60.
     */
    'slot_increment_minutes'?: number | null;
    'sort_order'?: number | null;
    /**
     * Required with `static`.
     */
    'static_access_code'?: string | null;
}


/**
 * What a required document is.
 */

export const AmenityDocumentKind = {
    COI: 'coi',
    WAIVER: 'waiver',
    OTHER: 'other'
} as const;

export type AmenityDocumentKind = typeof AmenityDocumentKind[keyof typeof AmenityDocumentKind];


export interface AmenityDocumentRequirementCreate {
    'description'?: string | null;
    /**
     * Default 7.
     */
    'due_days_before_start'?: number | null;
    /**
     * Default true. False retires it: bookings stop needing it.
     */
    'is_active'?: boolean | null;
    'kind': AmenityDocumentKind;
    /**
     * The document must carry an expiry date covering the booking.
     */
    'requires_expiry'?: boolean | null;
    'title': string;
}


export interface AmenityDocumentRequirementList {
    'requirements': Array<AmenityDocumentRequirementReturn>;
    'total': number;
}
export interface AmenityDocumentRequirementReturn {
    'amenity_id': string;
    'description'?: string | null;
    'due_days_before_start': number;
    'id': string;
    'is_active': boolean;
    'kind': AmenityDocumentKind;
    'requires_expiry': boolean;
    'title': string;
}


/**
 * Who in a household may book.
 */

export const AmenityEligibility = {
    ALL_RESIDENTS: 'all_residents',
    OWNERS_ONLY: 'owners_only'
} as const;

export type AmenityEligibility = typeof AmenityEligibility[keyof typeof AmenityEligibility];


/**
 * How the rental fee is computed.
 */

export const AmenityFeeType = {
    NONE: 'none',
    FLAT: 'flat',
    HOURLY: 'hourly',
    PER_NIGHT: 'per_night'
} as const;

export type AmenityFeeType = typeof AmenityFeeType[keyof typeof AmenityFeeType];


export interface AmenityGroupAvailabilityReturn {
    'date': string;
    'group_id': string;
    'starts': Array<AmenityGroupAvailabilityStart>;
    'timezone': string;
}
export interface AmenityGroupAvailabilityStart {
    /**
     * The amenities free from here, in booking order.
     */
    'available_amenity_ids': Array<string>;
    /**
     * The fee for the minimum duration at the first free amenity.
     */
    'fee_for_min'?: string | null;
    'start_local': string;
    /**
     * `available` when any amenity of the group is free at the minimum duration from here; otherwise the first amenity\'s reason.
     */
    'status': AvailabilitySlotStatus;
}


/**
 * Create a group of identical amenities, or (`AmenityGroupUpdate`) replace one. `amenity_ids` is the whole membership: an amenity named here leaves any other group; one left out leaves this one.
 */
export interface AmenityGroupCreate {
    'amenity_ids': Array<string>;
    'description'?: string | null;
    'hoa_id': string;
    'name': string;
    'per_home_active_limit'?: number | null;
    'per_home_daily_limit'?: number | null;
    'per_home_monthly_limit'?: number | null;
    'per_home_weekly_limit'?: number | null;
}
export interface AmenityGroupList {
    'groups': Array<AmenityGroupReturn>;
    'total': number;
}
/**
 * One amenity in a group, with what a booking page needs to offer lengths and show a price without loading every amenity.
 */
export interface AmenityGroupMember {
    /**
     * Decimal string; null when the amenity holds no deposit.
     */
    'deposit_amount'?: string | null;
    /**
     * Decimal string.
     */
    'fee_amount'?: string | null;
    'fee_type': AmenityFeeType;
    'id': string;
    'is_active': boolean;
    'max_duration_minutes': number;
    'max_party_size'?: number | null;
    'min_duration_minutes': number;
    'name': string;
    'requires_agreement': boolean;
    'requires_vehicle': boolean;
    'slot_increment_minutes': number;
    'sort_order': number;
    /**
     * The association\'s timezone; every `*_local` time is in it.
     */
    'timezone': string;
}


export interface AmenityGroupReturn {
    /**
     * In booking order: \"any court\" takes the first free one.
     */
    'amenities': Array<AmenityGroupMember>;
    'description'?: string | null;
    'hoa_id': string;
    'id': string;
    'name': string;
    'per_home_active_limit'?: number | null;
    'per_home_daily_limit'?: number | null;
    'per_home_monthly_limit'?: number | null;
    'per_home_weekly_limit'?: number | null;
}
export interface AmenityGroupUpdate {
    'amenity_ids': Array<string>;
    'description'?: string | null;
    'name': string;
    'per_home_active_limit'?: number | null;
    'per_home_daily_limit'?: number | null;
    'per_home_monthly_limit'?: number | null;
    'per_home_weekly_limit'?: number | null;
}
/**
 * Replace an amenity\'s whole weekly schedule. An empty list means \"hours not configured\": bookable round the clock.
 */
export interface AmenityHoursReplace {
    'windows': Array<AmenityHoursWindow>;
}
/**
 * One opening window. Minutes from local midnight; `close_minute` of 1440 means \"until midnight\".
 */
export interface AmenityHoursWindow {
    'close_minute': number;
    'open_minute': number;
    /**
     * The season this window applies in. Absent for the default schedule, which applies outside every season.
     */
    'season_id'?: string | null;
    /**
     * 0 = Sunday … 6 = Saturday.
     */
    'weekday': number;
}
/**
 * What cancelling after the deadline costs a household.
 */

export const AmenityLateCancelPolicy = {
    KEEP_FULL: 'keep_full',
    FLAT: 'flat',
    PERCENT: 'percent'
} as const;

export type AmenityLateCancelPolicy = typeof AmenityLateCancelPolicy[keyof typeof AmenityLateCancelPolicy];


export interface AmenityList {
    'amenities': Array<AmenityReturn>;
    'total': number;
}
/**
 * Add or replace a pricing rule. `weekdays` lists the local weekdays it applies on, 0 = Sunday … 6 = Saturday.
 */
export interface AmenityPriceRuleCreate {
    /**
     * Decimal string, more than zero.
     */
    'amount': string;
    /**
     * Exclusive; null or absent: until midnight.
     */
    'end_minute'?: number | null;
    'fee_type': AmenityPriceRuleFeeType;
    'name': string;
    /**
     * Higher wins when two rules match. Default 0.
     */
    'priority'?: number | null;
    /**
     * Minutes from local midnight. Default 0.
     */
    'start_minute'?: number | null;
    'weekdays': Array<number>;
}


/**
 * How a pricing rule\'s amount is charged.
 */

export const AmenityPriceRuleFeeType = {
    FLAT: 'flat',
    HOURLY: 'hourly',
    PER_NIGHT: 'per_night'
} as const;

export type AmenityPriceRuleFeeType = typeof AmenityPriceRuleFeeType[keyof typeof AmenityPriceRuleFeeType];


export interface AmenityPriceRuleList {
    'price_rules': Array<AmenityPriceRuleReturn>;
    'total': number;
}
export interface AmenityPriceRuleReturn {
    'amenity_id': string;
    'amount': string;
    'end_minute'?: number | null;
    'fee_type': AmenityPriceRuleFeeType;
    'id': string;
    'name': string;
    'priority': number;
    'start_minute': number;
    'weekdays': Array<number>;
}


export interface AmenityReturn {
    'access_code_mode': AmenityAccessCodeMode;
    /**
     * Staff reads only; null on a homeowner\'s amenity read (they get it on their booking, inside the reveal window).
     */
    'access_instructions'?: string | null;
    'access_reveal_hours': number;
    'approval_mode': AmenityApprovalMode;
    /**
     * Seconds since the epoch; set once archived.
     */
    'archived_at'?: number | null;
    /**
     * Member-portal reads only: why `can_book` is false — `AMENITY_NOT_BOOKABLE`, `HOME_SUSPENDED`, `ROLE_NOT_ELIGIBLE` or `NOT_IN_GOOD_STANDING`. Null when `can_book` is true, and on staff reads.
     */
    'booking_block'?: ReservationRuleCode | null;
    /**
     * With `HOME_SUSPENDED`: the last day of the suspension (local).
     */
    'booking_block_until'?: string | null;
    'booking_mode': AmenityBookingMode;
    'buffer_minutes': number;
    /**
     * Member-portal reads only (null on staff reads): whether the selected home can book this amenity at all, whatever time it picks. A slot can still be refused; this answers the questions that do not depend on the slot.
     */
    'can_book'?: boolean | null;
    'cancel_deadline_hours'?: number | null;
    'capacity'?: number | null;
    'check_in_minute'?: number | null;
    'check_out_minute'?: number | null;
    'current_agreement'?: AmenityAgreementReturn | null;
    'date_created'?: number | null;
    'date_updated'?: number | null;
    /**
     * Decimal string, or null for no deposit.
     */
    'deposit_amount'?: string | null;
    'description'?: string | null;
    /**
     * Active requirements only.
     */
    'document_requirements': Array<AmenityDocumentRequirementReturn>;
    'eligibility': AmenityEligibility;
    /**
     * Decimal string, or null when there is no fee.
     */
    'fee_amount'?: string | null;
    'fee_type': AmenityFeeType;
    'group_id'?: string | null;
    'group_name'?: string | null;
    /**
     * Decimal string.
     */
    'guest_fee_per_person'?: string | null;
    'hoa_id': string;
    'hours': Array<AmenityHoursWindow>;
    'id': string;
    'included_guests'?: number | null;
    'inspection_checklist': Array<string>;
    'is_active': boolean;
    /**
     * Decimal string, or null for `keep_full`.
     */
    'late_cancel_amount'?: string | null;
    'late_cancel_policy': AmenityLateCancelPolicy;
    'location'?: string | null;
    'max_duration_minutes': number;
    'max_lead_days'?: number | null;
    'max_nights'?: number | null;
    'max_party_size'?: number | null;
    'min_duration_minutes': number;
    'min_nights'?: number | null;
    'min_notice_hours'?: number | null;
    'name': string;
    'per_home_active_limit'?: number | null;
    'per_home_daily_limit'?: number | null;
    'per_home_monthly_hours_limit'?: string | null;
    'per_home_monthly_limit'?: number | null;
    /**
     * Decimal strings, or null for unlimited.
     */
    'per_home_weekly_hours_limit'?: string | null;
    'per_home_weekly_limit'?: number | null;
    'photos': Array<AttachmentReturn>;
    'price_rules': Array<AmenityPriceRuleReturn>;
    'requires_agreement': boolean;
    'requires_good_standing': boolean;
    'requires_vehicle': boolean;
    'rules_text'?: string | null;
    /**
     * Seasons with their own hours. Empty: one schedule all year.
     */
    'seasons': Array<AmenitySeasonReturn>;
    'slot_increment_minutes': number;
    'sort_order': number;
    /**
     * Staff reads only. Never on a homeowner\'s amenity read.
     */
    'static_access_code'?: string | null;
    /**
     * The association\'s IANA timezone. Every `*_local` time on this amenity and its bookings is wall-clock time there.
     */
    'timezone': string;
    /**
     * Closures that have not ended yet. Filled on single-amenity reads; empty in lists.
     */
    'upcoming_closures': Array<AmenityClosureReturn>;
}


/**
 * A named span of the year with its own hours. Months and days, so it recurs; an end before the start wraps the new year (Dec 1 – Mar 31). An amenity\'s seasons may not overlap.
 */
export interface AmenitySeasonCreate {
    'end_day': number;
    'end_month': number;
    'name': string;
    'start_day': number;
    'start_month': number;
}
export interface AmenitySeasonList {
    'seasons': Array<AmenitySeasonReturn>;
    'total': number;
}
export interface AmenitySeasonReturn {
    'amenity_id': string;
    'end_day': number;
    'end_month': number;
    'id': string;
    'name': string;
    'start_day': number;
    'start_month': number;
}
/**
 * Bar a household from booking — one amenity, or every amenity when `amenity_id` is absent — from `starts_on` (default today) through `ends_on`.
 */
export interface AmenitySuspensionCreate {
    'amenity_id'?: string | null;
    'ends_on': string;
    'hoa_id': string;
    'home_id': string;
    /**
     * Required. Sent to the household.
     */
    'reason': string;
    'starts_on'?: string | null;
}
export interface AmenitySuspensionLift {
    /**
     * Required. Recorded.
     */
    'reason': string;
}
export interface AmenitySuspensionList {
    'suspensions': Array<AmenitySuspensionReturn>;
    'total': number;
}
export interface AmenitySuspensionReturn {
    /**
     * Not lifted, and today (local) is not past `ends_on`.
     */
    'active': boolean;
    /**
     * Null: every amenity.
     */
    'amenity_id'?: string | null;
    'amenity_name'?: string | null;
    'created_by_user_id'?: string | null;
    'ends_on': string;
    'hoa_id': string;
    'home_address': string;
    'home_id': string;
    'id': string;
    'lift_reason'?: string | null;
    'lifted_at'?: number | null;
    'lifted_by_user_id'?: string | null;
    'reason': string;
    'source': AmenitySuspensionSource;
    'starts_on': string;
}


/**
 * Where a suspension came from.
 */

export const AmenitySuspensionSource = {
    AUTOMATIC: 'automatic',
    MANUAL: 'manual'
} as const;

export type AmenitySuspensionSource = typeof AmenitySuspensionSource[keyof typeof AmenitySuspensionSource];


/**
 * Save an amenity\'s configuration.  **A full replacement**, like saving a form: an omitted limit becomes unlimited and an omitted rule its default. Only `is_active` is \"leave unchanged when absent\".
 */
export interface AmenityUpdate {
    'access_code_mode'?: AmenityAccessCodeMode | null;
    'access_instructions'?: string | null;
    'access_reveal_hours'?: number | null;
    'approval_mode'?: AmenityApprovalMode | null;
    'booking_mode'?: AmenityBookingMode | null;
    'buffer_minutes'?: number | null;
    'cancel_deadline_hours'?: number | null;
    /**
     * `shared_capacity`: the headcount one slot holds, across households. Required for that mode.
     */
    'capacity'?: number | null;
    /**
     * `overnight`: minutes from local midnight a stay begins on the arrival day and ends on the departure day. Required for that mode.
     */
    'check_in_minute'?: number | null;
    'check_out_minute'?: number | null;
    /**
     * **Phase-3 settings: absent leaves the stored value unchanged**, unlike the fields above, so a client saving the older fields cannot wipe these. Null clears a nullable one.  A refundable deposit, decimal string; null for none.
     */
    'deposit_amount'?: string | null;
    'description'?: string | null;
    'eligibility'?: AmenityEligibility | null;
    /**
     * The `date_updated` (seconds since the epoch) of the amenity as the editor loaded it. When sent and the stored amenity has changed since, the save is refused with 409 rather than silently overwriting someone else\'s edit — this route replaces the whole configuration, so two managers saving different tabs would otherwise each undo the other. Optional: omitted, the save always wins, as before.
     */
    'expected_date_updated'?: number | null;
    'fee_amount'?: string | null;
    'fee_type'?: AmenityFeeType | null;
    'guest_fee_per_person'?: string | null;
    'included_guests'?: number | null;
    'inspection_checklist'?: Array<string> | null;
    'is_active'?: boolean | null;
    /**
     * Decimal string: dollars for `flat`, 0–100 for `percent`.
     */
    'late_cancel_amount'?: string | null;
    /**
     * Default `keep_full`.
     */
    'late_cancel_policy'?: AmenityLateCancelPolicy | null;
    'location'?: string | null;
    'max_duration_minutes'?: number | null;
    'max_lead_days'?: number | null;
    'max_nights'?: number | null;
    'max_party_size'?: number | null;
    'min_duration_minutes'?: number | null;
    'min_nights'?: number | null;
    'min_notice_hours'?: number | null;
    'name': string;
    'per_home_active_limit'?: number | null;
    'per_home_daily_limit'?: number | null;
    'per_home_monthly_hours_limit'?: string | null;
    'per_home_monthly_limit'?: number | null;
    /**
     * Decimal hours a household may hold in a week / month. Not applied to `overnight`.
     */
    'per_home_weekly_hours_limit'?: string | null;
    'per_home_weekly_limit'?: number | null;
    'requires_agreement'?: boolean | null;
    'requires_good_standing'?: boolean | null;
    /**
     * A guest parking spot: the booking must name the vehicle.
     */
    'requires_vehicle'?: boolean | null;
    'rules_text'?: string | null;
    'slot_increment_minutes'?: number | null;
    'sort_order'?: number | null;
    'static_access_code'?: string | null;
}


export interface ApplicationReturn {
    'amount': string;
    'applied_at'?: number | null;
    'charge_entry_id': string;
    'id': string;
    /**
     * Set when a returned cheque unwound this application. The row is kept so the record of what the payment had settled survives.
     */
    'reversed_at'?: number | null;
}
/**
 * What kind of thing a file is attached to.
 */

export const AttachmentEntityType = {
    VIOLATION: 'violation',
    COMMENT: 'comment',
    AMENITY: 'amenity',
    RESERVATION_DOCUMENT: 'reservation_document',
    RESERVATION_INSPECTION: 'reservation_inspection'
} as const;

export type AttachmentEntityType = typeof AttachmentEntityType[keyof typeof AttachmentEntityType];


/**
 * One attached file, as a client sees it.
 */
export interface AttachmentReturn {
    'file_extension': string;
    'file_id': string;
    'file_name': string;
    'file_size': number;
    'id': string;
    /**
     * Whether the inline endpoint will serve this for display.  `false` does not mean broken — a `heic` from an iPhone is stored faithfully and downloads fine, it is simply not something most browsers can render, so the UI shows a download link rather than an `<img>` that will not paint.
     */
    'renderable': boolean;
    'sort_order': number;
}
export interface AvailabilityClosure {
    'ends_local': string;
    'reason': string;
    'starts_local': string;
}
/**
 * One night of an `overnight` amenity: whether a stay could include it.
 */
export interface AvailabilityNight {
    /**
     * The night of this date: check-in that day, check-out the next.
     */
    'date': string;
    /**
     * One night\'s fee.
     */
    'fee': string;
    'status': AvailabilitySlotStatus;
}


/**
 * How a candidate start time looks on the availability grid.
 */

export const AvailabilitySlotStatus = {
    AVAILABLE: 'available',
    BOOKED: 'booked',
    CLOSED: 'closed',
    PAST: 'past',
    TOO_SOON: 'too_soon',
    TOO_FAR: 'too_far',
    FULL: 'full',
    OUT_OF_SEASON: 'out_of_season'
} as const;

export type AvailabilitySlotStatus = typeof AvailabilitySlotStatus[keyof typeof AvailabilitySlotStatus];


export interface AvailabilityStart {
    /**
     * The fee for the minimum duration from here.
     */
    'fee_for_min': string;
    /**
     * The latest end a booking starting here could have. Null unless `available`.
     */
    'max_end_local'?: string | null;
    /**
     * Shared capacity: the people still bookable at the minimum duration from here. Null for other modes.
     */
    'remaining_capacity'?: number | null;
    'start_local': string;
    'status': AvailabilitySlotStatus;
}


/**
 * One home whose cached balance disagrees with its entries.
 */
export interface BalanceDrift {
    'cached_balance': string;
    'computed_balance': string;
    'difference': string;
    'home_id': string;
}
export interface BalanceReturn {
    'aging': AgingBuckets;
    /**
     * Signed decimal string. Negative only where the HOA allows credit balances.
     */
    'balance': string;
    /**
     * What every schedule in force charges this home for the current period, summed.  **`null` means no schedule is in force** — the association bills nothing recurring, which is not the same as billing zero, and a screen that shows `$0.00` for both is telling a manager the association is up to date when it is in fact unconfigured.
     */
    'current_period_amount'?: string | null;
    'days_past_due': number;
    'hoa_id': string;
    'home_id': string;
    'oldest_unpaid_due_date'?: string | null;
    'ownership_tenure_id'?: string | null;
    'updated_at': number;
}
export interface BalanceSheet {
    'as_of': string;
    'assets': Array<BalanceSheetLine>;
    /**
     * Assets equal liabilities plus equity. True by construction while every journal entry balances, and asserted rather than assumed.
     */
    'balanced': boolean;
    'equity': Array<BalanceSheetLine>;
    'hoa_id': string;
    'liabilities': Array<BalanceSheetLine>;
    /**
     * Income less expenses for the fiscal year containing `as_of`.  A computed line, never an account. It is the reason the sheet balances before a year is closed: income and expense accounts are still carrying the year\'s activity, and this is that activity taking its place in equity. Deliberately not seeded in the chart — the incumbent\'s chart lists a \"Net Income\" account and nothing can post to one.
     */
    'net_income': string;
    'total_assets': string;
    'total_equity': string;
    'total_liabilities': string;
    'total_liabilities_and_equity': string;
}
export interface BalanceSheetLine {
    'account_name': string;
    'account_number': string;
    /**
     * Stated the way a reader expects: assets positive when the association holds them, liabilities and equity positive when it owes them.
     */
    'amount': string;
}
/**
 * Result of recomputing an HOA\'s balances from its entries.  Drift is reported, never silently repaired — a mismatch means something wrote the ledger without updating the cache, and that is a bug worth seeing (`docs/DUES_DESIGN.md` §2.2).
 */
export interface BalanceVerifyReport {
    'drift_count': number;
    'drifted': Array<BalanceDrift>;
    'hoa_id': string;
    'homes_checked': number;
    /**
     * True when `repair=true` was passed and the cache was rewritten.
     */
    'repaired': boolean;
}
/**
 * What an assigned account is used for.
 */

export const BankAccountPurpose = {
    OPERATING: 'operating',
    RESERVE: 'reserve',
    OTHER: 'other'
} as const;

export type BankAccountPurpose = typeof BankAccountPurpose[keyof typeof BankAccountPurpose];


/**
 * One account as returned live from the bank, annotated with which connection it came from.
 */
export interface BankAccountReturn {
    'account_name'?: string | null;
    'account_number': string;
    'account_type'?: string | null;
    /**
     * The ACH originator ids the bank assigned to the account. Empty means the account cannot originate or receive a payable.
     */
    'ach_company_ids'?: Array<string>;
    'balance_as_of'?: string | null;
    'closed_date'?: string | null;
    'connection_id': string;
    /**
     * The connection\'s human-readable label (if set), so callers can identify which connection an account came from without exposing only the opaque `connection_id`.
     */
    'connection_label'?: string | null;
    'current_balance'?: number | null;
    'is_open'?: boolean | null;
    'open_date'?: string | null;
    'provider_key': string;
    'status'?: string | null;
}
/**
 * Request to store (or rotate) a company\'s bank credentials.  `provider_key`, `base_url`, and `api_version` default to Columbia, so a minimal request only needs `company_id`, `client_id`, and `client_secret`.
 */
export interface BankConnectionCreate {
    'api_version'?: string;
    'base_url'?: string;
    'client_id': string;
    /**
     * Stored encrypted at rest and never returned by the API.
     */
    'client_secret': string;
    'company_id': string;
    'label'?: string | null;
    /**
     * The Column-A Lockbox Management CO ID for payer files (bank- assigned; distinct from `client_id`). Optional — can be added later via update once the bank provides it.
     */
    'lockbox_company_id'?: string | null;
    'provider_key'?: string;
}
/**
 * A stored bank connection. Deliberately omits the client secret.
 */
export interface BankConnectionReturn {
    'bank_id': string;
    'client_id': string;
    'company_id': string;
    'created_by'?: string | null;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'id': string;
    'is_active': boolean;
    'label'?: string | null;
    'lockbox_company_id'?: string | null;
    'provider_key': string;
}
/**
 * Partial update to a stored connection. Every field is optional; absent fields are left unchanged. Supplying `client_id` and/or `client_secret` re-verifies the resulting credential pair against the bank before persisting. `client_secret`, when present, rotates the stored secret (and is never returned).
 */
export interface BankConnectionUpdate {
    'client_id'?: string | null;
    'client_secret'?: string | null;
    'is_active'?: boolean | null;
    'label'?: string | null;
    /**
     * Set/replace the Lockbox Management CO ID (Column A for payer files). Absent leaves it unchanged.
     */
    'lockbox_company_id'?: string | null;
}
/**
 * One `FileInfoData` entry as returned to the client.
 */
export interface BankFileInfoReturn {
    'code'?: string | null;
    'name'?: string | null;
    'value'?: string | null;
}
export interface BankFileList {
    'files': Array<BankFileReturn>;
    'total': number;
}
/**
 * A file status record as returned live from the bank.
 */
export interface BankFileReturn {
    'complete_date'?: string | null;
    'error_code'?: string | null;
    'error_message'?: string | null;
    'file_hash'?: string | null;
    'file_id'?: string | null;
    'file_info'?: Array<BankFileInfoReturn>;
    'file_name'?: string | null;
    'file_service'?: string | null;
    'file_status'?: string | null;
    'file_type'?: string | null;
    'file_uid'?: string | null;
    'reference_id'?: string | null;
    'reference_type'?: string | null;
    'submit_date'?: string | null;
}
/**
 * What kind of thing the institution originated.  A closed set on purpose. These are the movements the bank is the *only* record of; anything else on a statement either settles a posting we already made — in which case it is matched, not posted — or needs a person to decide what it is, which is what a manual journal entry is for.
 */

export const BankItemKind = {
    INTEREST: 'interest',
    RESERVE_INTEREST: 'reserve_interest',
    SERVICE_CHARGE: 'service_charge'
} as const;

export type BankItemKind = typeof BankItemKind[keyof typeof BankItemKind];


/**
 * Post an unexplained bank item the institution originated.
 */
export interface BankItemPost {
    /**
     * The unexplained bank transaction to post and clear.
     */
    'bank_transaction_id': string;
    /**
     * The fund the income or expense belongs to. Not derivable from the account — fund lives on the line, and an account may be hit in more than one. Same reason `RevaluationRequest` asks.
     */
    'fund_id': string;
    /**
     * What it is. A closed set: interest, reserve interest, or a service charge. Anything else on a statement either settles a posting we already made, and is matched rather than posted, or needs a person, and is a manual journal entry.
     */
    'kind': BankItemKind;
}


export interface BillingPolicyReturn {
    'allow_credit_balances': boolean;
    'days_to_demand'?: number | null;
    'days_to_late_notice'?: number | null;
    'estoppel_fee'?: string | null;
    'estoppel_fee_rush'?: string | null;
    'estoppel_good_through_days': number;
    'grace_period_days': number;
    'hold_threshold_amount'?: string | null;
    'hold_threshold_days'?: number | null;
    'id': string;
    'interest_accrual': InterestAccrual;
    'interest_annual_rate'?: string | null;
    'interest_basis': FeeBasis;
    'interest_day_count': DayCount;
    'interest_enabled': boolean;
    'interest_method': InterestMethod;
    'late_fee_amount'?: string | null;
    'late_fee_basis': FeeBasis;
    'late_fee_cap_total'?: string | null;
    'late_fee_compounds': boolean;
    'late_fee_max_per_period'?: string | null;
    'late_fee_recurs': boolean;
    'late_fee_type': LateFeeType;
    'nsf_fee'?: string | null;
    'payment_application_order': Array<LedgerEntryType>;
    'proration_method': ProrationMethod;
    'rounding_mode': RoundingMode;
    'scope_id'?: string | null;
    'scope_type': string;
    'turnover_balance_policy': TurnoverBalancePolicy;
    'variance_threshold_percent': number;
}


/**
 * Every field optional — omitted fields are left as they are.
 */
export interface BillingPolicyUpdate {
    'allow_credit_balances'?: boolean | null;
    'days_to_demand'?: number | null;
    'days_to_late_notice'?: number | null;
    'estoppel_fee'?: string | null;
    'estoppel_fee_rush'?: string | null;
    'estoppel_good_through_days'?: number | null;
    'grace_period_days'?: number | null;
    'hold_threshold_amount'?: string | null;
    'hold_threshold_days'?: number | null;
    'interest_accrual'?: InterestAccrual | null;
    'interest_annual_rate'?: string | null;
    'interest_basis'?: FeeBasis | null;
    'interest_day_count'?: DayCount | null;
    'interest_enabled'?: boolean | null;
    'interest_method'?: InterestMethod | null;
    'late_fee_amount'?: string | null;
    'late_fee_basis'?: FeeBasis | null;
    'late_fee_cap_total'?: string | null;
    'late_fee_compounds'?: boolean | null;
    'late_fee_max_per_period'?: string | null;
    'late_fee_recurs'?: boolean | null;
    'late_fee_type'?: LateFeeType | null;
    'nsf_fee'?: string | null;
    /**
     * Ordered list of entry types, most-preferred first. Empty or omitted means oldest charge first regardless of type.
     */
    'payment_application_order'?: Array<LedgerEntryType> | null;
    'proration_method'?: ProrationMethod | null;
    /**
     * Applied once, when a fee or interest entry is written.
     */
    'rounding_mode'?: RoundingMode | null;
    'turnover_balance_policy'?: TurnoverBalancePolicy | null;
    'variance_threshold_percent'?: number | null;
}


export interface BillingStatementLine {
    /**
     * Signed decimal string — positive increases what is owed.
     */
    'amount': string;
    'created_by'?: string;
    'description': string;
    'due_date'?: string;
    'effective_date': string;
    'entry_type': string;
    'hoa_id': string;
    'home_id': string;
    'id': string;
    'ownership_tenure_id'?: string;
    'posted_at': number;
    'reverses_entry_id'?: string;
    'source_id'?: string;
    'source_type'?: string;
    'running_balance': string;
}
/**
 * A home\'s account over a period.
 */
export interface BillingStatementReturn {
    'aging': AgingBuckets;
    'closing_balance': string;
    /**
     * Today\'s figure, which is not the same as `closing_balance` when the statement is backdated.
     */
    'current_balance': string;
    'days_past_due': number;
    'from': string;
    'hoa_id': string;
    'hoa_name': string;
    'home_id': string;
    'lines': Array<BillingStatementLine>;
    /**
     * Everything before `from`, so the period reads as a statement rather than as a filtered list.
     */
    'opening_balance': string;
    'to': string;
}
/**
 * The body of `PATCH /hoa/{id}/board-impersonation`.  One field, and explicit rather than a bare boolean, so the JSON reads as a decision rather than as a value: `{\"enabled\": true}`.
 */
export interface BoardImpersonationUpdate {
    'enabled': boolean;
}
export interface BudgetLineReturn {
    'account_id': string;
    'account_name': string;
    'account_number': string;
    /**
     * Summed from `periods`. There is no stored annual figure to disagree with them.
     */
    'annual': string;
    'fund_code': string;
    'fund_id': string;
    'periods': Array<BudgetPeriodAmount>;
}
/**
 * One account-and-fund\'s figures for a fiscal year.
 */
export interface BudgetLineSet {
    'account_id': string;
    /**
     * An annual figure to spread evenly, as a convenience. The rounding remainder lands in the last period so the twelve sum to it exactly.  A seasonal line cannot be expressed this way, which is the whole reason budgets are stored per period — use `periods` for those.
     */
    'annual'?: string | null;
    'fund_id': string;
    /**
     * Period number to figure, e.g. `{\"1\": \"1333.33\", \"2\": \"1333.33\"}`.  Periods left out are set to nothing. This **replaces** the year\'s figures for this account and fund rather than merging — a budget line is a complete statement about a year, and merging would make \"no snow removal after March\" impossible to express.  Supply this **or** `annual`, not both.
     */
    'periods'?: { [key: string]: string; };
}
export interface BudgetPeriodAmount {
    'amount': string;
    'period_number': number;
}
export interface BudgetYearReturn {
    'fiscal_year': number;
    'hoa_id': string;
    'lines': Array<BudgetLineReturn>;
}
export interface BudgetYearSet {
    'lines': Array<BudgetLineSet>;
}
/**
 * Request to send a message to multiple members
 */
export interface BulkMemberMessageRequest {
    'hoa_id': string;
    'member_ids': Array<string>;
    'message_body': string;
    'send_email'?: boolean;
    'sender_name': string;
    'subject': string;
}
/**
 * Response for bulk message operations
 */
export interface BulkMessageResponse {
    'errors'?: Array<string>;
    'failed_count': number;
    'failed_member_ids'?: Array<string>;
    'notifications': Array<NotificationResponse>;
    'successful_count': number;
    'total_requested': number;
}
/**
 * Response for bulk read status update
 */
export interface BulkReadStatusResponse {
    'failed_ids'?: Array<string>;
    'updated_count': number;
    'updated_notifications': Array<NotificationResponse>;
}
/**
 * Request to bulk update read status
 */
export interface BulkReadStatusUpdate {
    'is_read': boolean;
    'notification_ids': Array<string>;
}
/**
 * Move a case forward, or resolve it.
 */
export interface CaseAdvanceRequest {
    'reason': string;
    'status': CaseStatus;
}


/**
 * One page of a case\'s audit trail, newest first.
 */
export interface CaseEventList {
    'events': Array<CaseEventReturn>;
    'page': number;
    'per_page': number;
    'total': number;
}
export interface CaseEventReturn {
    'case_id': string;
    'created_by'?: string | null;
    'description': string;
    'event_type': CollectionsEventType;
    'id': string;
    'occurred_on': string;
    'status_from'?: CaseStatus | null;
    'status_to'?: CaseStatus | null;
}


/**
 * One page of delinquency cases.
 */
export interface CaseList {
    'cases': Array<CaseReturn>;
    'page': number;
    'per_page': number;
    'total': number;
}
/**
 * Add a note to a case\'s trail.
 */
export interface CaseNoteRequest {
    'note': string;
}
/**
 * Open a collections case on a home by hand.  No status field: a case always opens at `reminder`, the same place the aging job starts one. Opening is an observation; escalating is a decision, and it goes through `advance`.
 */
export interface CaseOpenRequest {
    'home_id': string;
    /**
     * Why, recorded as the first note on the trail.
     */
    'reason'?: string | null;
}
export interface CaseReturn {
    'closed_on'?: string | null;
    'hoa_id': string;
    'home_id': string;
    /**
     * The home\'s street address, resolved at read time.
     */
    'home_label'?: string | null;
    'id': string;
    'opened_on': string;
    'ownership_tenure_id'?: string | null;
    /**
     * Who is on the account: the tenure\'s billing payer. This is the person a collections conversation is actually about.
     */
    'payer_name'?: string | null;
    'status': CaseStatus;
    'trigger_balance'?: string | null;
    'trigger_days_past_due'?: number | null;
}


/**
 * How far along a delinquency is.  Ordered: the aging job compares against the current status and only ever moves a case **forward**, and never past `late_notice` — demand, attorney, lien and foreclosure have legal consequences and a person decides them.
 */

export const CaseStatus = {
    REMINDER: 'reminder',
    LATE_NOTICE: 'late_notice',
    DEMAND: 'demand',
    ATTORNEY: 'attorney',
    LIEN: 'lien',
    FORECLOSURE: 'foreclosure',
    RESOLVED: 'resolved'
} as const;

export type CaseStatus = typeof CaseStatus[keyof typeof CaseStatus];


/**
 * Post a charge by hand. `amount` is a positive decimal string (e.g. `\"125.00\"`) — never a float. The sign is applied from the type, so a caller never has to know the ledger\'s convention.
 */
export interface ChargeCreate {
    /**
     * Positive decimal string, e.g. `\"125.00\"`.
     */
    'amount': string;
    'description': string;
    /**
     * When it becomes overdue. Drives aging.
     */
    'due_date'?: string | null;
    /**
     * Accounting date. Defaults to today when omitted.
     */
    'effective_date'?: string | null;
    /**
     * One of the manually-chargeable types (see `LedgerEntryType`).
     */
    'entry_type': string;
    /**
     * Optional client key. Replaying the same key is a no-op that returns the original entry rather than posting a second one.
     */
    'idempotency_key'?: string | null;
}
/**
 * What one connection has had taken back.
 */
export interface ChargebackList {
    'bank_connection_id': string;
    'chargebacks': Array<ChargebackReturn>;
    /**
     * **Absent means never pulled.** An empty list under this means \"we have not looked\", not \"nothing bounced\" — opposite facts, and only one of them is reassuring.
     */
    'synced_through'?: string | null;
    'total': number;
}
/**
 * Attach a held chargeback to the payment it reverses, by hand.  Validated in the service, not here — the bank module returns `BankError::Config` for a bad request rather than using the `validator` derive the billing module does.
 */
export interface ChargebackResolveRequest {
    /**
     * The payment this chargeback reverses.
     */
    'payment_id': string;
}
/**
 * One chargeback, as a manager sees it.  **`deposit_account_number` is the bank\'s account, never the payer\'s.** A chargeback carries no payer account number at all — there is no detail string on this resource — so anything on this screen that looks like \"whose money is this\" comes from the payment we matched it to, not from the chargeback.
 */
export interface ChargebackReturn {
    /**
     * `pending` | `reversed` | `held` | `ignored`.
     */
    'action_status': string;
    /**
     * Decimal string. Absent when the bank\'s literal would not convert, in which case `amount_raw` carries the original characters.
     */
    'amount'?: string | null;
    /**
     * Non-null here is a defect, not an unusual chargeback.
     */
    'amount_raw'?: string | null;
    'bank_connection_id': string;
    'chargeback_date'?: string | null;
    /**
     * `Check` or `ACH`.
     */
    'chargeback_type'?: string | null;
    /**
     * The instrument number on a returned cheque.
     */
    'check_number'?: string | null;
    /**
     * The **depository** account. Not the payer\'s.
     */
    'deposit_account_number'?: string | null;
    'deposit_date'?: string | null;
    /**
     * What **Columbia** charged the association. Not the HOA\'s configured NSF fee, which is a policy number and usually different — showing them side by side is the point.
     */
    'fee'?: string | null;
    'hoa_id'?: string | null;
    /**
     * Why it has not reversed.  **The most useful field on the page.** `ach_matching_not_supported` means we have no identifier to look with and nobody should go hunting; `unmatched` means we looked and found nothing, which is worth a person\'s time. Flattening the two would waste it.
     */
    'hold_reason'?: string | null;
    'home_id'?: string | null;
    'id': string;
    /**
     * `resolved` | `ambiguous` | `unmatched`.
     */
    'match_status': string;
    /**
     * **Set when this row came from a mock, not the bank.** Absent on every real one. A reversal is real money off a homeowner\'s ledger, so \"did this come from Columbia\" has to be answerable.
     */
    'mock_source_url'?: string | null;
    /**
     * The payment this reverses, once matched.
     */
    'payment_id'?: string | null;
    /**
     * The bank\'s own id. What a person quotes back to Columbia.
     */
    'provider_chargeback_id': string;
    /**
     * Free text from the bank. **Display only** — it is inconsistent across sources and must never be branched on.
     */
    'return_reason'?: string | null;
    /**
     * `R01`, `R03`, … The structured reason, and the only one safe to act on. Absent on cheques entirely.
     */
    'return_reason_code'?: string | null;
    'reversed_at'?: string | null;
    /**
     * A user id, or `system` when the job did it unattended.
     */
    'reversed_by'?: string | null;
}
/**
 * Request to record an issued check.  `amount` is a decimal string (e.g. `\"125.00\"`) — never a float. `expense_gl_account_id` and `fund_id` drive the journal entry; leave the account out and the check is registered with the bank but posts nothing (the row says why).
 */
export interface CheckCreate {
    'amount': string;
    /**
     * Digits as printed on the check. Leading zeros are kept.
     */
    'check_number': string;
    /**
     * The expense account the check pays. Optional: without it the check is registered with the bank but not posted.
     */
    'expense_gl_account_id'?: string | null;
    /**
     * The fund both legs post in. Defaults to the operating fund.
     */
    'fund_id'?: string | null;
    /**
     * `hoa_bank_accounts` id the check is drawn on.
     */
    'hoa_bank_account_id': string;
    'issue_date': string;
    'memo'?: string | null;
    'payee_additional'?: string | null;
    /**
     * Who the check is to. Snapshotted; a vendor link is optional.
     */
    'payee_name': string;
    'vendor_id'?: string | null;
}
export interface CheckList {
    'checks': Array<CheckReturn>;
    'total': number;
}
/**
 * One register row: the check in our books and where its Positive Pay submission stands with the bank.
 */
export interface CheckReturn {
    'account_number': string;
    'amount': string;
    'check_number': string;
    'created_by'?: string | null;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    /**
     * True when the bank accepted the issue entry more than a day ago and has still not downloaded it. The bank imports within minutes, so this means something changed on their side.
     */
    'download_stale': boolean;
    'expense_gl_account_id'?: string | null;
    'fund_id'?: string | null;
    /**
     * Set when the check posted nothing: `no_period`, `no_cash_account` or `no_expense_account`.
     */
    'gl_skipped_reason'?: string | null;
    'hoa_bank_account_id': string;
    'hoa_id': string;
    'id': string;
    'issue_date': string;
    'journal_entry_id'?: string | null;
    'memo'?: string | null;
    'payee_additional'?: string | null;
    'payee_name': string;
    'pp_aba_number'?: string | null;
    'pp_bank_id'?: string | null;
    'pp_company_id'?: string | null;
    'pp_downloaded_at'?: string | null;
    /**
     * The bank\'s own error, verbatim. Code `51` means the module is off at the bank.
     */
    'pp_error_code'?: string | null;
    'pp_error_message'?: string | null;
    'pp_service'?: string | null;
    /**
     * `pending` | `sending` | `sent` | `downloaded` | `failed`.
     */
    'pp_status': string;
    'pp_submitted_at'?: string | null;
    'pp_void_bank_id'?: string | null;
    'pp_void_error_code'?: string | null;
    'pp_void_error_message'?: string | null;
    /**
     * The void entry\'s own lifecycle; null until voided, and null on a void that never reached the bank because the issue never did.
     */
    'pp_void_status'?: string | null;
    'reversal_entry_id'?: string | null;
    /**
     * `issued` | `void`.
     */
    'status': string;
    'vendor_id'?: string | null;
    'void_reason'?: string | null;
    'voided_at'?: string | null;
    'voided_by'?: string | null;
}
/**
 * What a manual \"refresh from bank\" found, plus the register as it stands afterwards. Same read the daily job makes; a person asked for it now rather than waiting for the schedule.
 */
export interface CheckSyncReturn {
    /**
     * Accounts that had an entry awaiting download and were asked about.
     */
    'accounts_checked': number;
    /**
     * Bank entries that were awaiting download when the refresh ran.
     */
    'awaiting': number;
    'checks': CheckList;
    /**
     * Entries the bank has now pulled into its Positive Pay service.
     */
    'downloaded': number;
    /**
     * Entries at the bank with no register row.
     */
    'orphans': number;
    /**
     * Entries accepted more than a day ago and still not imported.
     */
    'stale': number;
}
/**
 * Request to void a recorded check.
 */
export interface CheckVoidRequest {
    'reason': string;
}
/**
 * The management realm\'s JWT payload.  **`user_type`, `company_id` and `hoa_id` are not authoritative.** They are carried so the manager portal can gate its own navigation without a round trip; the server re-reads all three from the user\'s row on every request in `auth::authenticate`. Do not reintroduce a server-side decision based on them — a token minted before a role change would still be asserting the old role.  Deliberately carries no name or email: it never has, and code that assumed otherwise silently rendered blanks for months. See `knowledge/notes/gotcha-identity-is-not-in-the-token.md`.
 */
export interface Claims {
    /**
     * Which realm this token is for. Management tokens are minted with and validated against [`MANAGEMENT_AUDIENCE`]; the member portal uses its own. Both realms sign with the same secret, so without this the only thing stopping a member token being replayed here is that `MemberClaims` happens to lack the fields this struct requires — separation by accident of shape rather than by check.
     */
    'aud': string;
    /**
     * When this *session* began, as opposed to `iat`, which is when this token was minted. Preserved across refreshes so a session can be capped in absolute terms; without it, refreshing weekly renewed a session forever.
     */
    'auth_time': number;
    /**
     * Company ID - Option<String> to support SuperAdmin (None) and regular users (Some)
     */
    'company_id'?: string | null;
    'exp': number;
    /**
     * HOA ID for BoardMembers - which HOA they are assigned to ✅ CHANGED: From Option<i32> to Option<String>
     */
    'hoa_id'?: string | null;
    'iat': number;
    /**
     * This refresh token\'s own id, and the thing rotation turns over. The family accepts exactly one, so presenting a superseded one after the grace window is what reuse detection sees.  **Refresh tokens only.** An access token is not rotated and carries `None`; nothing may key anything on a `jti` from one.
     */
    'jti'?: string | null;
    /**
     * The refresh-token **family** this token belongs to — our session id, not WorkOS\'s.  Carried on access tokens as well as refresh tokens, so that revoking a family (by signing out, or by detecting reuse) stops requests on the next one rather than at the end of the access token\'s hour. Same reasoning as every other value `authenticate` re-reads.  **Not `workos_session_id`, and the two must not be conflated.** That one is WorkOS\'s, absent on the break-glass password path, and unchanged by our rotation. This one is ours, always present on a session opened after 2026-09-19, and the key to `refresh_sessions`.  `Option` only because tokens minted before families existed stay valid until they expire; a refresh presenting one is honoured once and given a family on the way through.
     */
    'sid'?: string | null;
    'sub': string;
    'token_type': TokenType;
    'token_version': number;
    'user_type': string;
    /**
     * WorkOS\'s id for the session behind this token, when the user signed in through WorkOS. `None` for a password sign-in.  **Not authorization, and never read as such** — it is an opaque handle we give back to WorkOS at logout so their session ends when ours does. Like `auth_time`, it is preserved across refresh rather than re-derived, because it describes the *session* and the database has nowhere to keep it.
     */
    'workos_session_id'?: string | null;
}


export interface ClearedCheckList {
    'items': Array<ClearedCheckReturn>;
    'total': number;
}
/**
 * A check that posted against an account, as the bank reports it. Live bank data — nothing here is stored. `check_id` is the key the image endpoints take, an 18-digit integer carried as a **string** because it does not fit a JavaScript number.
 */
export interface ClearedCheckReturn {
    'account_number'?: string | null;
    /**
     * The literal the bank sent, e.g. `\"25\"`.
     */
    'amount'?: string | null;
    'check_id'?: string | null;
    'check_number'?: string | null;
    /**
     * `Standard` | `RDC`, when the bank reports it.
     */
    'check_type'?: string | null;
    'effective_date'?: string | null;
    /**
     * Whether the bank holds front and back images.
     */
    'has_images': boolean;
    'processed_date'?: string | null;
    /**
     * The bank\'s `TransactionID` for the posting, as a string.
     */
    'transaction_id'?: string | null;
}
/**
 * Close the current tenure and open the next one.
 */
export interface CloseTenureRequest {
    /**
     * Defaults to today on the association\'s calendar.
     */
    'closing_date'?: string | null;
    'closing_reason': ClosingReason;
}


/**
 * Why a tenure ended.
 */

export const ClosingReason = {
    SALE: 'sale',
    FORECLOSURE: 'foreclosure',
    TRANSFER: 'transfer',
    CORRECTION: 'correction'
} as const;

export type ClosingReason = typeof ClosingReason[keyof typeof ClosingReason];


/**
 * What happened on a collections case.
 */

export const CollectionsEventType = {
    OPENED: 'opened',
    NOTICE_SENT: 'notice_sent',
    NOTICE_FLAGGED: 'notice_flagged',
    ADVANCED: 'advanced',
    PAYMENT_RECEIVED: 'payment_received',
    NOTE: 'note',
    RESOLVED: 'resolved'
} as const;

export type CollectionsEventType = typeof CollectionsEventType[keyof typeof CollectionsEventType];


/**
 * Which identity table the author lives in.  The two realms are disjoint: a management user is a `users` row with a `user_` id, a homeowner is a `members` row with an `mbr_` id, and neither id means anything in the other table.
 */

export const CommentAuthorRealm = {
    USER: 'user',
    MEMBER: 'member'
} as const;

export type CommentAuthorRealm = typeof CommentAuthorRealm[keyof typeof CommentAuthorRealm];


/**
 * Schema for creating a new comment  Names its subject with **exactly one** of `violation_id`, `work_order_id` and `reservation_id`; more or none is a 400.
 */
export interface CommentCreate {
    'content': string;
    'parent_id'?: string | null;
    'reservation_id'?: string | null;
    'violation_id'?: string | null;
    /**
     * Which thread to post to. **Defaults to `internal`.**  Publishing to the homeowner is an explicit act: a caller that says nothing gets the management-only thread, which is also what every client written before the split was doing.
     */
    'visibility'?: CommentVisibility | null;
    'work_order_id'?: string | null;
}


/**
 * Schema for returning a list of comments with total count
 */
export interface CommentList {
    'comments': Array<CommentReturn>;
    'total': number;
}
/**
 * Schema returned when retrieving a single comment or list of comments
 */
export interface CommentReturn {
    /**
     * Photos and documents attached to this comment.  Empty from `From<Model>`; the service fills it. See `ViolationReturn::attachments`.
     */
    'attachments'?: Array<AttachmentReturn>;
    /**
     * Always set: the author\'s id in whichever realm `author_realm` names.
     */
    'author_id': string;
    'author_realm': CommentAuthorRealm;
    'content': string;
    'created_at': string;
    'id': string;
    'is_approved': boolean;
    'is_deleted': boolean;
    'is_edited': boolean;
    /**
     * The homeowner author, absent for management comments.
     */
    'member_id'?: string | null;
    'parent_id'?: string | null;
    /**
     * The amenity reservation this comment is on.
     */
    'reservation_id'?: string | null;
    'updated_at': string;
    /**
     * The management-realm author.  **Absent for homeowner-authored comments** — a homeowner is a `members` row and has no `users.id`. Read `author_id` instead, which is always present; this field remains only for the management comments it has always described.
     */
    'user_id'?: string | null;
    /**
     * The violation this comment is on. Absent on a work-order comment; exactly one of `violation_id` and `work_order_id` is present.
     */
    'violation_id'?: string | null;
    'visibility': CommentVisibility;
    /**
     * The work order this comment is on. Absent on a violation comment.
     */
    'work_order_id'?: string | null;
}


/**
 * Schema for updating an existing comment  **Deliberately carries no `visibility`.** Visibility is fixed at creation. Moving a comment from `Shared` to `Internal` is a lie — the homeowner has already read it — and moving it the other way retroactively publishes something written in confidence. Promoting a point to the shared thread means posting it there.
 */
export interface CommentUpdate {
    'content'?: string | null;
    'is_approved'?: boolean | null;
}
/**
 * Which thread a comment belongs to.  `Internal` is the management company talking among itself about an enforcement action. `Shared` adds the association\'s board members and the homeowner who was cited.  On a work order the same two threads exist but `Shared` stops at the board: there is no homeowner party to a work order. See [`CommentSubject`].  The board is outside `Internal` on purpose — they are the association\'s own volunteers, usually neighbours of the person being cited, and the manager is often managing the board\'s expectations about an action as much as the homeowner\'s. See `knowledge/notes/decision-violation-threads-exclude-the-board.md`.
 */

export const CommentVisibility = {
    INTERNAL: 'internal',
    SHARED: 'shared'
} as const;

export type CommentVisibility = typeof CommentVisibility[keyof typeof CommentVisibility];


/**
 * All accounts visible to a company across its active connections.
 */
export interface CompanyBankAccountsReturn {
    'accounts': Array<BankAccountReturn>;
    'company_id': string;
    'total': number;
    /**
     * Connections that could not be reached. `accounts` is everything the *other* connections returned, so the list is usable but incomplete — and this says by how much.
     */
    'unreachable': Array<UnreachableConnection>;
}
/**
 * Schema for creating a new company
 */
export interface CompanyCreate {
    'city': string;
    'country'?: string | null;
    'email': string;
    'name': string;
    'phone'?: string | null;
    'state': string;
    'street': string;
    'street2'?: string | null;
    'website'?: string | null;
    'zip': string;
}
/**
 * Schema returned when retrieving a company
 */
export interface CompanyReturn {
    'city': string;
    'country'?: string | null;
    'date_created': number;
    'date_updated': number;
    'email': string;
    'id': string;
    'is_active': boolean;
    'name': string;
    'phone'?: string | null;
    'state': string;
    'street': string;
    'street2'?: string | null;
    'website'?: string | null;
    'zip': string;
}
/**
 * Schema for updating an existing company
 */
export interface CompanyUpdate {
    'city'?: string | null;
    'country'?: string | null;
    'email'?: string | null;
    'name'?: string | null;
    'phone'?: string | null;
    'state'?: string | null;
    'street'?: string | null;
    'street2'?: string | null;
    'website'?: string | null;
    'zip'?: string | null;
}
/**
 * Schema for contact form submission from marketing website
 */
export interface ContactFormRequest {
    'communities'?: string | null;
    'company'?: string | null;
    'contactMethod'?: string | null;
    'email': string;
    'firstName': string;
    'lastName': string;
    'message': string;
    'phone'?: string | null;
    /**
     * Honeypot field for bot detection - should always be empty
     */
    'validator'?: string | null;
}
/**
 * Response returned after contact form submission
 */
export interface ContactFormResponse {
    'message': string;
    'success': boolean;
}
/**
 * A counterparty\'s bank details as any reader sees them: the routing number, the last four of the account, and who it is for.
 */
export interface CounterpartyBankAccountReturn {
    'account_last4': string;
    'account_type': string;
    'ach_name': string;
    'address1'?: string | null;
    'address2'?: string | null;
    'address3'?: string | null;
    /**
     * Which wording of the debit authorisation was accepted, and when.
     */
    'authorization_text_version'?: string | null;
    'authorized_at'?: string | null;
    'created_by': string;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'entity_type': string;
    'hoa_id': string;
    /**
     * `vendor` | `homeowner`
     */
    'holder_type': string;
    'home_id'?: string | null;
    'id': string;
    'label'?: string | null;
    /**
     * The homeowner the account belongs to (homeowner holder only).
     */
    'member_id'?: string | null;
    'routing_number': string;
    'sec_code'?: string | null;
    /**
     * `active` | `retired`
     */
    'status': string;
    'vendor_id'?: string | null;
}
/**
 * Create or replace a vendor\'s bank details. The account number is **write-only**: it is encrypted on arrival and never returned.
 */
export interface CounterpartyBankAccountUpsert {
    /**
     * Four to seventeen digits. Encrypted at rest; never read back.
     */
    'account_number': string;
    /**
     * `checking` | `savings`
     */
    'account_type': string;
    /**
     * What the bank prints on the ACH entry. At most 22 characters.
     */
    'ach_name': string;
    'address1'?: string | null;
    'address2'?: string | null;
    'address3'?: string | null;
    /**
     * `corporate` | `personal`
     */
    'entity_type': string;
    'label'?: string | null;
    /**
     * Nine digits, ABA checksum validated.
     */
    'routing_number': string;
}
/**
 * The payment coupon a homeowner returns with their cheque.
 */
export interface CouponReturn {
    'account_number': string;
    'amount_due': string;
    /**
     * What the next period charges, where a schedule says so.
     */
    'current_period_amount'?: string | null;
    'due_date'?: string | null;
    /**
     * After this date a late fee applies, where the HOA charges one.
     */
    'grace_date'?: string | null;
    'hoa_id': string;
    'home_id': string;
    /**
     * \"Make Check Payable To\" — the association\'s name, which is also the payer file\'s column 19.
     */
    'payable_to': string;
    'payer_name': string;
    /**
     * The machine-readable line: the payer file\'s three identifiers in order, then the amount in cents. The sample\'s trailing digit is omitted — it is probably a check digit, but the algorithm was never confirmed and a guessed one is worse than none.
     */
    'scanline': string;
}
/**
 * Post a credit by hand — a waiver, a write-off, or an adjustment. `amount` is a **positive** decimal string; the ledger stores it negative.
 */
export interface CreditCreate {
    /**
     * Positive decimal string, e.g. `\"50.00\"`.
     */
    'amount': string;
    'description': string;
    'effective_date'?: string | null;
    'entry_type': string;
    'idempotency_key'?: string | null;
}
/**
 * A CSP report as the browser posts it: one object under `csp-report`.
 */
export interface CspReport {
    'csp-report': CspReportBody;
}
/**
 * The envelope browsers wrap a CSP violation in.  Every field is **browser-supplied and attacker-influenceable** — a page can provoke a violation naming a URI of its choosing, and the report arrives unauthenticated. Treat all of it as untrusted text: log it, never render it, never key a decision on it.  Deliberately partial. The full report has a dozen fields and the spec\'s `report-uri` and `report-to` bodies disagree on several of them; these are the five that answer \"what broke, and on which page\", and `serde` ignores the rest.
 */
export interface CspReportBody {
    'blocked-uri'?: string | null;
    'document-uri'?: string | null;
    'effective-directive'?: string | null;
    'script-sample'?: string | null;
    'violated-directive'?: string | null;
}
/**
 * The day-count convention interest accrues on.  Not cosmetic: a 360-day year makes the daily rate about 1.4% higher than a 365-day one, and declarations and statutes do specify which applies.
 */

export const DayCount = {
    ACTUAL365: 'actual365',
    ACTUAL360: 'actual360',
    THIRTY360: 'thirty360'
} as const;

export type DayCount = typeof DayCount[keyof typeof DayCount];



export const DocumentType = {
    COVENANT: 'covenant',
    BOARD_MINUTES: 'board_minutes',
    FINANCIAL: 'financial',
    NOTICE: 'notice',
    FORM: 'form',
    OTHER: 'other'
} as const;

export type DocumentType = typeof DocumentType[keyof typeof DocumentType];


/**
 * How a period\'s due date is computed.  A plain day-of-month integer is not enough: day 31 is not a date in February, so \"the last day of the period\" has to be expressible rather than approximated.
 */

export const DueRule = {
    DAY_OF_MONTH: 'day_of_month',
    LAST_DAY_OF_PERIOD: 'last_day_of_period',
    NTH_WEEKDAY: 'nth_weekday'
} as const;

export type DueRule = typeof DueRule[keyof typeof DueRule];


/**
 * Finish an email change with the code from the new address.
 */
export interface EmailChangeConfirmRequest {
    'code': string;
}
/**
 * What both email-change steps answer with.  Deliberately says nothing about the address. The send step must not confirm whether the new address already belongs to someone, and the confirm step has nothing to add that the next profile load will not show.
 */
export interface EmailChangeResponse {
    'message': string;
    'success': boolean;
}
/**
 * Start an email change: the address being claimed.  No password field, unlike the flow this replaces. Proof of control is the one-time code WorkOS sends to the *new* address — a password would prove nothing about it, and most accounts no longer have a usable one.
 */
export interface EmailChangeSendRequest {
    'new_email': string;
}
/**
 * Mark a certificate as delivered.
 */
export interface EstoppelDeliverRequest {
    'method': string;
}
/**
 * The itemised breakdown, stored verbatim on the request.
 */
export interface EstoppelFigures {
    'lines': Array<EstoppelLine>;
    'total': string;
}
export interface EstoppelLine {
    'amount': string;
    'category': string;
}
/**
 * One page of estoppel certificates issued for a home.
 */
export interface EstoppelList {
    'page': number;
    'per_page': number;
    'requests': Array<EstoppelReturn>;
    'total': number;
}
/**
 * Request an estoppel certificate.
 */
export interface EstoppelRequestCreate {
    /**
     * Defaults to today on the association\'s calendar.
     */
    'as_of_date'?: string | null;
    /**
     * Override both configured fees.
     */
    'fee_override'?: string | null;
    'requester_company'?: string | null;
    'requester_email'?: string | null;
    'requester_name'?: string | null;
    /**
     * Charge the rush fee instead of the standard one.
     */
    'rush'?: boolean;
}
export interface EstoppelReturn {
    'as_of_date': string;
    'delivered_at'?: number | null;
    'delivery_method'?: string | null;
    'fee_charged'?: string | null;
    'fee_entry_id'?: string | null;
    'figures': EstoppelFigures;
    /**
     * Dues keep accruing, so the figure is only good to this date.
     */
    'good_through': string;
    'hoa_id': string;
    'home_id': string;
    'id': string;
    'ownership_tenure_id'?: string | null;
    'requester_company'?: string | null;
    'requester_email'?: string | null;
    'requester_name'?: string | null;
    'total_outstanding': string;
}
/**
 * What a late fee or interest charge is calculated against.
 */

export const FeeBasis = {
    OVERDUE_CHARGE: 'overdue_charge',
    TOTAL_BALANCE: 'total_balance'
} as const;

export type FeeBasis = typeof FeeBasis[keyof typeof FeeBasis];


/**
 * What a fee or interest run did.
 */
export interface FeeRunReport {
    'as_of': string;
    'entries_written': number;
    'hoa_id': string;
    'homes_charged': number;
    /**
     * Set when the run did nothing because the HOA is not configured for it — said out loud rather than reported as a silent zero.
     */
    'skipped_reason'?: string | null;
    'total_amount': string;
}
/**
 * Schema for returning a list of files with pagination
 */
export interface FileList {
    'files': Array<FileReturn>;
    'page': number;
    'per_page': number;
    'total': number;
    'total_pages': number;
}
/**
 * Schema returned when retrieving a file or list of files
 */
export interface FileReturn {
    'date_created': string;
    'date_updated': string;
    'deleted_at'?: string | null;
    'deleted_by'?: string | null;
    'description'?: string | null;
    'document_type': DocumentType;
    'file_extension': string;
    'file_name': string;
    'file_size': number;
    'hoa_id': string;
    'id': string;
    'is_public': boolean;
    'uploaded_by': string;
}


/**
 * Schema for updating file metadata
 */
export interface FileUpdate {
    'description'?: string | null;
    'document_type'?: DocumentType | null;
    'file_name'?: string | null;
    'is_public'?: boolean | null;
}


/**
 * Record money paid against a fine.
 */
export interface FinePaymentRequest {
    /**
     * Defaults to whatever is still outstanding.
     */
    'amount'?: string | null;
    /**
     * Defaults to today on the association\'s calendar.
     */
    'received_date'?: string | null;
}
/**
 * The fine on a violation, read from the ledger.
 */
export interface FineReturn {
    'amount': string;
    'amount_outstanding': string;
    'amount_paid': string;
    'due_date'?: string | null;
    /**
     * The day of the violation — what aging measures from.
     */
    'effective_date': string;
    /**
     * The `violation_fine` ledger entry this describes.
     */
    'entry_id': string;
    'status': FineStatus;
}


/**
 * Where a fine stands.  Derived from the ledger rather than stored: `paid` means payments have covered the charge, and `reversed` means the fine was withdrawn or superseded. Nothing here is a column somebody can set by hand.
 */

export const FineStatus = {
    UNPAID: 'unpaid',
    PARTIALLY_PAID: 'partially_paid',
    PAID: 'paid',
    REVERSED: 'reversed'
} as const;

export type FineStatus = typeof FineStatus[keyof typeof FineStatus];


/**
 * How often a dues schedule bills.
 */

export const Frequency = {
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    SEMIANNUAL: 'semiannual',
    ANNUAL: 'annual',
    ONE_TIME: 'one_time'
} as const;

export type Frequency = typeof Frequency[keyof typeof Frequency];


export interface FundCreate {
    /**
     * Lowercase, no spaces — it is matched on, not displayed.
     */
    'code': string;
    'name': string;
    'sort_order'?: number | null;
}
export interface FundList {
    'funds': Array<FundReturn>;
}
export interface FundReturn {
    /**
     * Stable identifier; corresponds by convention to `hoa_bank_accounts.purpose`.
     */
    'code': string;
    'hoa_id': string;
    'id': string;
    'is_active': boolean;
    'name': string;
    'sort_order': number;
}
/**
 * `code` is absent on purpose. It is what seeding and the report split match on, so renaming is a display change and re-coding is not.
 */
export interface FundUpdate {
    'is_active'?: boolean | null;
    'name'?: string | null;
    'sort_order'?: number | null;
}
/**
 * Generate the periods that have come due.
 */
export interface GenerateRequest {
    /**
     * Generate every period that started on or before this date. Defaults to today.
     */
    'through_date'?: string | null;
}
/**
 * The result of a generate call — one entry per period generated.  More than one means a catch-up: a period was missed, or the schedule is being brought current on purpose. The caller sees every period and total before anything posts.
 */
export interface GenerateReturn {
    /**
     * True when more than one period came back.
     */
    'is_catch_up': boolean;
    'periods_generated': number;
    'runs': Array<RunReturn>;
    'schedule_id': string;
}
export interface GlAccountCreate {
    'account_number': string;
    'account_type': GlAccountType;
    'description'?: string | null;
    'hoa_bank_account_id'?: string | null;
    'name': string;
    /**
     * Omit for the convention for this type. Supply it only to create a contra account — income-typed and debit-balanced, or the reverse.
     */
    'normal_balance'?: NormalBalance | null;
    'parent_id'?: string | null;
}


export interface GlAccountList {
    'accounts': Array<GlAccountReturn>;
}
export interface GlAccountReturn {
    'account_number': string;
    'account_type': string;
    'description'?: string | null;
    /**
     * Set on cash accounts only: the bank account this one reconciles against.
     */
    'hoa_bank_account_id'?: string | null;
    'hoa_id': string;
    'id': string;
    'is_active': boolean;
    /**
     * True while the row is exactly as the template seeded it.
     */
    'is_template_seeded': boolean;
    'name': string;
    'normal_balance': string;
    'parent_id'?: string | null;
    /**
     * Which money event posts here. Null on most accounts.
     */
    'posting_role'?: string | null;
}

export const GlAccountType = {
    ASSET: 'asset',
    LIABILITY: 'liability',
    EQUITY: 'equity',
    INCOME: 'income',
    EXPENSE: 'expense'
} as const;

export type GlAccountType = typeof GlAccountType[keyof typeof GlAccountType];


/**
 * `account_number` and `account_type` are absent on purpose. They are what posted entries are read back against, and a chart is edited by adding and deactivating rather than by renumbering — which is how a mid-bank-change chart looks in production: the old cash account drains to 0.00 and stays in place beside the new one.
 */
export interface GlAccountUpdate {
    'description'?: string | null;
    'hoa_bank_account_id'?: string | null;
    'is_active'?: boolean | null;
    'name'?: string | null;
    'parent_id'?: string | null;
    /**
     * Move a posting role to this account, taking it off whichever account holds it now. This is how an association points late fees at its own account rather than the template\'s.
     */
    'posting_role'?: string | null;
}
export interface GlSettingsReturn {
    'expense_basis': string;
    'fiscal_year_start_month': number;
    'hoa_id': string;
    'id': string;
    'revenue_basis': string;
}
export interface GlSettingsUpdate {
    'expense_basis'?: AccountingBasis | null;
    'fiscal_year_start_month'?: number | null;
    'revenue_basis'?: AccountingBasis | null;
}


/**
 * Request to assign a bank account to an HOA.
 */
export interface HoaAccountAssign {
    'account_name'?: string | null;
    'account_number': string;
    'account_type'?: string | null;
    'bank_connection_id': string;
    'purpose': BankAccountPurpose;
}


export interface HoaBankAccountList {
    'accounts': Array<HoaBankAccountReturn>;
    'total': number;
}
/**
 * An assigned account enriched with live balance/status from the bank. `found` is false when the assigned account_number no longer appears in the bank\'s live account list.
 */
export interface HoaBankAccountLive {
    'account_name'?: string | null;
    'account_number': string;
    'balance_as_of'?: string | null;
    'bank_connection_id': string;
    'current_balance'?: number | null;
    'found': boolean;
    'hoa_id': string;
    'id': string;
    'is_open'?: boolean | null;
    'purpose'?: string | null;
    'status'?: string | null;
}
export interface HoaBankAccountLiveList {
    'accounts': Array<HoaBankAccountLive>;
    'total': number;
}
/**
 * A stored HOA→account assignment (metadata only, no live call).
 */
export interface HoaBankAccountReturn {
    'account_name'?: string | null;
    'account_number': string;
    'account_type'?: string | null;
    'bank_connection_id': string;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'hoa_id': string;
    'id': string;
    'is_active': boolean;
    'last_known_status'?: string | null;
    'purpose'?: string | null;
}
/**
 * Schema for creating a new HOA
 */
export interface HoaCreate {
    'city': string;
    'company_id': string;
    'contact_email'?: string | null;
    'contact_phone'?: string | null;
    'country'?: string | null;
    'description'?: string | null;
    'name': string;
    'state': string;
    'street': string;
    'street2'?: string | null;
    /**
     * An explicit IANA zone, e.g. `America/Denver`. Leave it out to derive the zone from `state`; set it at onboarding for an association in the minority half of a split-zone state.
     */
    'timezone'?: string | null;
    'website_url'?: string | null;
    'zip': string;
}
/**
 * Request to send a message to all members of an HOA
 */
export interface HoaMessageRequest {
    'hoa_id': string;
    'message_body': string;
    'send_email'?: boolean;
    'sender_name': string;
    'subject': string;
}
/**
 * Schema returned when retrieving an HOA
 */
export interface HoaReturn {
    /**
     * May this association\'s **board members** open a read-only view of one of its homeowners\' portals? Defaults false; set by the management company, never by the board.
     */
    'board_member_impersonation': boolean;
    'city': string;
    'company_id': string;
    'contact_email'?: string | null;
    'contact_phone'?: string | null;
    'country'?: string | null;
    'date_created': number;
    'date_updated': number;
    'description'?: string | null;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'id': string;
    'is_active': boolean;
    'name': string;
    /**
     * Whether the daily payer file goes out for this association without anyone asking.  **Read-only here.** Changing it goes through `PUT /api/v1/bank/hoas/{hoa_id}/payer-file/auto-upload`, which is gated on the payer-file bar rather than the generic edit-an-HOA bar — the two happen to be the same roles today, and this switch sends a real file to a live bank, so it should not widen if HOA editing ever does.
     */
    'payer_file_auto_upload': boolean;
    'state': string;
    /**
     * What the state alone would give. Shown next to an override so it is clear what clearing it would go back to.
     */
    'state_timezone': string;
    'street': string;
    'street2'?: string | null;
    /**
     * The IANA zone this association\'s dates are computed in — due dates, grace periods, billing periods, amenity hours. Always present: `timezone_override` if set, otherwise `state_timezone`.
     */
    'timezone': string;
    /**
     * The explicit zone a manager set, if any. Absent means the zone is derived from the state. **Read-only here**; changing it goes through `PATCH /api/v1/hoa/{id}/timezone`.
     */
    'timezone_override'?: string | null;
    'website_url'?: string | null;
    'zip': string;
}
/**
 * The body of `PATCH /hoa/{id}/timezone`.  `timezone` is **required but nullable**: a name sets the override, `null` clears it so the zone is derived from the state again. An empty body is refused rather than read as \"clear\", because this moves every billing date the association has.
 */
export interface HoaTimezoneUpdate {
    'timezone': string | null;
}
/**
 * Schema for updating an existing HOA
 */
export interface HoaUpdate {
    'city'?: string | null;
    'contact_email'?: string | null;
    'contact_phone'?: string | null;
    'country'?: string | null;
    'description'?: string | null;
    'name'?: string | null;
    'state'?: string | null;
    'street'?: string | null;
    'street2'?: string | null;
    'website_url'?: string | null;
    'zip'?: string | null;
}
/**
 * Force or suppress the payer file\'s HOLD for a tenure.  Tri-state on purpose: a manager has to be able to override the collections policy in either direction — a disputed fine, a payment plan, a unit in litigation.
 */
export interface HoldRequest {
    'hold'?: boolean | null;
}
/**
 * Schema for creating a new home
 */
export interface HomeCreate {
    'city': string;
    'country'?: string | null;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'hoa_id': string;
    'property_type': string;
    'state': string;
    'status': string;
    'street': string;
    'street2'?: string | null;
    'zip': string;
}
/**
 * One home whose ledger contribution disagrees with its subledger.
 */
export interface HomeDrift {
    'drift': string;
    'home_id': string;
    'home_label'?: string | null;
    /**
     * What reached the general ledger for that home.
     */
    'ledger': string;
    /**
     * What the home\'s entries since cutover add up to.
     */
    'subledger': string;
}
/**
 * Schema returned when retrieving a home
 */
export interface HomeReturn {
    'city': string;
    'country'?: string | null;
    'date_created'?: number | null;
    'date_updated'?: number | null;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'hoa_id': string;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'id': string;
    'property_type': string;
    'state': string;
    'status': string;
    'street': string;
    'street2'?: string | null;
    'zip': string;
}
/**
 * Schema for updating an existing home
 */
export interface HomeUpdate {
    'city'?: string | null;
    'country'?: string | null;
    'property_type'?: string | null;
    'state'?: string | null;
    'status'?: string | null;
    'street'?: string | null;
    'street2'?: string | null;
    'zip'?: string | null;
}
/**
 * A homeowner\'s own bank account for paying the association. The number is write-only; the authorisation must be accepted in the same request, and the version accepted is stored with the account.
 */
export interface HomeownerBankAccountUpsert {
    'account_number': string;
    /**
     * `checking` | `savings`
     */
    'account_type': string;
    /**
     * The name as it appears on the bank account, at most 22 characters.
     */
    'ach_name': string;
    /**
     * Must be `true`; the debit authorisation shown on the screen.
     */
    'authorization_accepted': boolean;
    /**
     * The `authorization_version` the screen showed, echoed back so the record says which wording was accepted.
     */
    'authorization_version': string;
    'routing_number': string;
}
export interface IncomeStatement {
    'fiscal_year': number;
    'hoa_id': string;
    /**
     * Across every fund. A month whose only inter-fund movement was the reserve contribution nets to **zero** here, because the paired transfer accounts cancel — which is the packet\'s own Net Total line.
     */
    'net_total_period': string;
    'net_total_ytd': string;
    'period_end': string;
    'period_number': number;
    'period_start': string;
    /**
     * One per fund that has activity, in the funds\' own sort order so Operating precedes Reserve.
     */
    'sections': Array<IncomeStatementSection>;
    /**
     * The start of the fiscal year the period belongs to — the left edge of the YTD column.
     */
    'year_start': string;
}
export interface IncomeStatementLine {
    'account_name': string;
    'account_number': string;
    /**
     * The whole fiscal year\'s budget, **summed from its periods** — there is no stored annual figure to disagree with them.
     */
    'annual_budget': string;
    /**
     * The header this account rolls up into, where it has one.
     */
    'parent_number'?: string | null;
    /**
     * Income positive, expenses positive. A **contra-income** account comes out negative and prints as a deduction, which is how the packet shows a reserve contribution against operating income.
     */
    'period_amount': string;
    'period_budget': string;
    /**
     * `actual - budget`, on the same basis as the amount columns.  Read it by section: in income, positive means more was earned than planned; in expenses, positive means more was spent. One arithmetic, two readings — which is how every statement of this shape works, and inverting the sign for expenses so that \"positive is good\" everywhere would make the column stop being checkable against the two beside it.
     */
    'period_variance': string;
    'ytd_amount': string;
    'ytd_budget': string;
    'ytd_variance': string;
}
/**
 * One fund\'s section. The packet is two reports — Operating and Reserve — and this is one of them.
 */
export interface IncomeStatementSection {
    'expenses': Array<IncomeStatementLine>;
    'fund_code': string;
    'fund_id': string;
    'fund_name': string;
    'income': Array<IncomeStatementLine>;
    'net_budget_annual': string;
    'net_budget_period': string;
    'net_budget_ytd': string;
    'net_period': string;
    'net_ytd': string;
    'total_expenses_budget_annual': string;
    'total_expenses_budget_period': string;
    'total_expenses_budget_ytd': string;
    'total_expenses_period': string;
    'total_expenses_ytd': string;
    'total_income_budget_annual': string;
    'total_income_budget_period': string;
    'total_income_budget_ytd': string;
    'total_income_period': string;
    'total_income_ytd': string;
}
/**
 * How often interest is applied.
 */

export const InterestAccrual = {
    DAILY: 'daily',
    MONTHLY: 'monthly'
} as const;

export type InterestAccrual = typeof InterestAccrual[keyof typeof InterestAccrual];


/**
 * Whether interest accrues on the principal alone.
 */

export const InterestMethod = {
    SIMPLE: 'simple',
    COMPOUND: 'compound'
} as const;

export type InterestMethod = typeof InterestMethod[keyof typeof InterestMethod];


/**
 * A run together with everything it touched.
 */
export interface JobRunDetail {
    /**
     * Whether this run deliberately did no work. A dry run makes no bank call at all, so it is not evidence that anything was reachable — and without this field it is indistinguishable from a run that found nothing.
     */
    'dry_run': boolean;
    /**
     * Set only when the run itself could not proceed.
     */
    'error_message'?: string;
    'finished_at'?: number;
    'id': string;
    'initiated_by'?: string;
    /**
     * e.g. `payer_file_upload`.
     */
    'job': string;
    /**
     * The calendar day this run represents, on the job\'s own calendar — Central for anything with a bank deadline, not the server\'s.
     */
    'scheduled_for': string;
    'started_at': number;
    /**
     * running | succeeded | partial | failed | aborted.  `succeeded` with zero targets is the ordinary quiet morning: the job fired, found nothing due, and said so.
     */
    'status': string;
    'targets_failed': number;
    'targets_skipped': number;
    'targets_succeeded': number;
    'targets_total': number;
    /**
     * schedule | manual.
     */
    'trigger': string;
    'targets': Array<JobRunTargetReturn>;
}
export interface JobRunList {
    'runs': Array<JobRunReturn>;
    'total': number;
}
/**
 * One execution of a scheduled job.
 */
export interface JobRunReturn {
    /**
     * Whether this run deliberately did no work. A dry run makes no bank call at all, so it is not evidence that anything was reachable — and without this field it is indistinguishable from a run that found nothing.
     */
    'dry_run': boolean;
    /**
     * Set only when the run itself could not proceed.
     */
    'error_message'?: string | null;
    'finished_at'?: number | null;
    'id': string;
    'initiated_by'?: string | null;
    /**
     * e.g. `payer_file_upload`.
     */
    'job': string;
    /**
     * The calendar day this run represents, on the job\'s own calendar — Central for anything with a bank deadline, not the server\'s.
     */
    'scheduled_for': string;
    'started_at': number;
    /**
     * running | succeeded | partial | failed | aborted.  `succeeded` with zero targets is the ordinary quiet morning: the job fired, found nothing due, and said so.
     */
    'status': string;
    'targets_failed': number;
    'targets_skipped': number;
    'targets_succeeded': number;
    'targets_total': number;
    /**
     * schedule | manual.
     */
    'trigger': string;
}
export interface JobRunTargetList {
    'targets': Array<JobRunTargetReturn>;
    'total': number;
}
/**
 * What one run did to one association.
 */
export interface JobRunTargetReturn {
    'attempts': number;
    'bank_connection_id'?: string | null;
    'date_created'?: number | null;
    /**
     * none | auth | transient | permanent.
     */
    'error_class': string;
    'error_message'?: string | null;
    /**
     * The owning association. Absent when the job\'s unit of work has none — a bank account enabled for transaction pull need not belong to any association.
     */
    'hoa_id'?: string | null;
    'id': string;
    'job_run_id': string;
    /**
     * The artefact produced, e.g. a payer submission id.
     */
    'result_ref'?: string | null;
    /**
     * Why an association was passed over. This is the field that answers \"why did this one not get a file today?\" — a question the absence of a submission cannot answer.
     */
    'skip_reason'?: string | null;
    /**
     * succeeded | failed | skipped.
     */
    'status': string;
    /**
     * The unit of work when it is finer than the association — a bank account id for the transaction pull. Absent for the payer sweep, whose unit already is the association.
     */
    'target_ref'?: string | null;
}
export interface JournalEntryReturn {
    'accounting_period_id': string;
    'created_by'?: string | null;
    'description': string;
    'effective_date': string;
    'hoa_id': string;
    'id': string;
    'lines': Array<JournalLineReturn>;
    'posted_at': number;
    'reverses_entry_id'?: string | null;
    'source_id'?: string | null;
    'source_type': string;
}
export interface JournalLineReturn {
    'account_id': string;
    'account_name': string;
    'account_number': string;
    /**
     * Signed; positive is a debit. `debit` and `credit` below carry the same figure split the way a trial balance prints it.
     */
    'amount': string;
    'credit': string;
    'debit': string;
    'description'?: string | null;
    'fund_id': string;
    'home_id'?: string | null;
    'id': string;
    'line_number': number;
}
/**
 * How a late fee is calculated.
 */

export const LateFeeType = {
    NONE: 'none',
    FLAT: 'flat',
    PERCENT: 'percent'
} as const;

export type LateFeeType = typeof LateFeeType[keyof typeof LateFeeType];


export interface LedgerEntryLine {
    /**
     * Signed decimal string — positive increases what is owed.
     */
    'amount': string;
    'created_by'?: string;
    'description': string;
    'due_date'?: string;
    'effective_date': string;
    'entry_type': string;
    'hoa_id': string;
    'home_id': string;
    'id': string;
    'ownership_tenure_id'?: string;
    'posted_at': number;
    'reverses_entry_id'?: string;
    'source_id'?: string;
    'source_type'?: string;
    /**
     * Balance after this entry.
     */
    'running_balance': string;
}
export interface LedgerEntryReturn {
    /**
     * Signed decimal string — positive increases what is owed.
     */
    'amount': string;
    'created_by'?: string | null;
    'description': string;
    'due_date'?: string | null;
    'effective_date': string;
    'entry_type': string;
    'hoa_id': string;
    'home_id': string;
    'id': string;
    'ownership_tenure_id'?: string | null;
    'posted_at': number;
    'reverses_entry_id'?: string | null;
    'source_id'?: string | null;
    'source_type'?: string | null;
}
/**
 * What a ledger entry represents. The sign of `amount` follows from the type: charges are positive (they increase what the homeowner owes), credits are negative. See `docs/DUES_DESIGN.md` §2.1.
 */

export const LedgerEntryType = {
    ASSESSMENT: 'assessment',
    SPECIAL_ASSESSMENT: 'special_assessment',
    LATE_FEE: 'late_fee',
    INTEREST: 'interest',
    VIOLATION_FINE: 'violation_fine',
    NSF_FEE: 'nsf_fee',
    OTHER_CHARGE: 'other_charge',
    CHARGE_ADJUSTMENT: 'charge_adjustment',
    REFUND: 'refund',
    PAYMENT: 'payment',
    WAIVER: 'waiver',
    WRITE_OFF: 'write_off',
    CREDIT_ADJUSTMENT: 'credit_adjustment',
    REVERSAL: 'reversal',
    TRANSFER_OUT: 'transfer_out',
    TRANSFER_IN: 'transfer_in',
    AMENITY_FEE: 'amenity_fee',
    AMENITY_DEPOSIT: 'amenity_deposit',
    AMENITY_FEE_RETURN: 'amenity_fee_return',
    AMENITY_DEPOSIT_RETURN: 'amenity_deposit_return'
} as const;

export type LedgerEntryType = typeof LedgerEntryType[keyof typeof LedgerEntryType];


/**
 * What to include in a ledger read.  Filtering changes which rows are *shown*, never what they say: a filtered page still reports the account\'s real running balance. See `BillingService::get_ledger`.
 */
export interface LedgerFilter {
    /**
     * Keep only these entry types. Empty means every type.
     */
    'entry_types'?: Array<LedgerEntryType>;
    /**
     * Entries on or after this effective date.
     */
    'from'?: string | null;
    'order'?: SortOrder;
    /**
     * Keep only entries from this source — `assessment_run`, `payment`, `violation`, `manual`, `aging`.
     */
    'source_type'?: string | null;
    /**
     * Entries on or before this effective date.
     */
    'to'?: string | null;
}


/**
 * A page of ledger entries, oldest first, with the balance carried forward so a statement can be rendered without re-summing client-side.
 */
export interface LedgerPage {
    /**
     * Balance after the last entry on this page.
     */
    'closing_balance': string;
    'entries': Array<LedgerEntryLine>;
    'home_id': string;
    /**
     * Balance of everything *before* the first entry on this page.
     */
    'opening_balance': string;
    'page': number;
    'per_page': number;
    'total': number;
}
/**
 * What logout hands back.  Logout used to answer `204 No Content`. It now returns a body, because ending our session is no longer the whole job: a user who signed in through WorkOS also has a session *there*, and only the browser can end it — what has to be cleared is a cookie on WorkOS\'s domain.  `workos_logout_url` is `None` for a password sign-in, and for a WorkOS sign-in whose session id could not be read. A caller that ignores the field entirely still signs out correctly here; it just leaves the WorkOS session alive.
 */
export interface LogoutResponse {
    'workos_logout_url'?: string | null;
}
/**
 * The run was claimed; the work continues in the background.
 */
export interface ManualRunAccepted {
    'job': string;
    /**
     * Where to look for the outcome.
     */
    'poll': string;
    'run_id': string;
    'status': string;
}
/**
 * Ask for a job to run now.
 */
export interface ManualRunRequest {
    /**
     * Assemble everything but make no bank call.  With no sandbox this is how a sweep is exercised in an environment without a live file leaving the building.
     */
    'dry_run'?: boolean;
    /**
     * Restrict the sweep to one association.
     */
    'hoa_id'?: string | null;
}
export interface MemberClaims {
    /**
     * The **management** user acting as this member, when the session was opened by impersonation rather than by the homeowner signing in. `None` for every ordinary session.  `act` is the RFC 8693 name for the actor in a delegated session, and both WorkOS and Auth0 put the operator there — worth matching so an `auth_events` row reads the same way as a vendor\'s would.  **Its presence is what makes the session read-only, and that is deliberately not a second claim.** A separate `read_only` flag could disagree with this one, and the disagreement would be invisible until somebody wrote through an impersonated session. Read-write impersonation, if it is ever wanted, is a new claim and a new decision — not a value this one can take.  Enforced in `member_auth::authenticate`, the one place every authenticated member request passes through.
     */
    'act'?: string | null;
    /**
     * Which realm this token is for — see `MEMBER_AUDIENCE`. Both realms share a signing secret, so this is what keeps a management token from being replayed against the portal.
     */
    'aud': string;
    /**
     * When this session began, preserved across refreshes so the absolute session cap measures the session rather than the token.
     */
    'auth_time': number;
    'company_id': string;
    'exp': number;
    /**
     * ✅ CHANGED: From Vec<i32> to Vec<String>
     */
    'hoa_ids': Array<string>;
    /**
     * **Not authoritative.** Re-derived from `member_home_map` on every request in `member_auth::authenticate`. Carried forward from the token, a member who sold a home kept read access to it for the life of their refresh chain. ✅ CHANGED: From Vec<i32> to Vec<String>
     */
    'home_ids': Array<string>;
    'iat': number;
    'is_primary': boolean;
    /**
     * This refresh token\'s own id, and the thing rotation turns over. The family accepts exactly one, so presenting a superseded one after the grace window is what reuse detection sees.  **Refresh tokens only.** An access token is not rotated and carries `None`; nothing may key anything on a `jti` from one.
     */
    'jti'?: string | null;
    /**
     * The refresh-token **family** this token belongs to — our session id, not WorkOS\'s.  Carried on access tokens as well as refresh tokens, so that revoking a family (by signing out, or by detecting reuse) stops requests on the next one rather than at the end of the access token\'s hour. Same reasoning as every other value `authenticate` re-reads.  **Not `workos_session_id`, and the two must not be conflated.** That one is WorkOS\'s, absent on the break-glass password path, and unchanged by our rotation. This one is ours, always present on a session opened after 2026-09-19, and the key to `refresh_sessions`.  `Option` only because tokens minted before families existed stay valid until they expire; a refresh presenting one is honoured once and given a family on the way through.
     */
    'sid'?: string | null;
    'sub': string;
    'token_type': MemberTokenType;
    'token_version': number;
    /**
     * WorkOS\'s id for the session behind this token.  Every member session now comes from `/workos-login`, so in practice this is always present — `Option` because tokens minted before the cutover, and before the claim existed, do not carry it, and they stay valid until they expire.  **Not authorization, and never read as such** — it is an opaque handle handed back to WorkOS at logout so their session ends when ours does. Like `auth_time`, it is preserved across refresh rather than re-derived, because it describes the *session* and the database has nowhere to keep it.
     */
    'workos_session_id'?: string | null;
}


/**
 * The body a member\'s own comment is created and edited with.  Deliberately not `CommentCreate`: that type carries `visibility`, and a homeowner has no thread to choose between. Accepting the field and ignoring it would put a control in the generated client that does nothing.
 */
export interface MemberCommentCreate {
    'content': string;
    /**
     * The comment being replied to. Must be in the same thread, which for a member is always the shared one.
     */
    'parent_id'?: string | null;
    'violation_id': string;
}
/**
 * Only the text. A member cannot moderate, so `is_approved` is absent.
 */
export interface MemberCommentUpdate {
    'content': string;
}
/**
 * Schema for creating a new member (by managers) Note: Password is NOT set by managers - members receive a setup email
 */
export interface MemberCreate {
    /**
     * ✅ CHANGED: From i32 to String
     */
    'company_id': string;
    'email': string;
    'first_name': string;
    /**
     * Optional home ID to link the member to a home upon creation If provided, the member will be automatically added to this home
     */
    'home_id'?: string | null;
    'is_primary'?: boolean | null;
    'last_name': string;
    'middle_name'?: string | null;
    'phone'?: string | null;
    'preferred_name'?: string | null;
}
/**
 * Schema returned when creating a new member
 */
export interface MemberCreatedResponse {
    /**
     * If a home was provided during creation, this indicates if the member was successfully linked
     */
    'home_linked'?: boolean | null;
    'member': MemberReturn;
    'message': string;
    'setup_email_sent': boolean;
}
/**
 * Credentials for the non-`prod` dev sign-in.  **Deliberately not called `MemberLogin`.** That name belonged to the password sign-in removed on 2026-09-12, and reusing it would make `git log` read as though the endpoint came back. It did not: this one cannot exist in a `prod` namespace, and the routes it serves are not registered when `dev_password_login_enabled()` is false.
 */
export interface MemberDevLogin {
    'email': string;
    'password': string;
}
/**
 * Book \"any\" amenity of a group — the first in booking order with the time free. `agreement_ids` lists every agreement the household accepted; the chosen amenity\'s current one must be among them when it requires one.
 */
export interface MemberGroupReservationCreate {
    'agreement_ids'?: Array<string> | null;
    'end_local': string;
    'party_size'?: number | null;
    'purpose'?: string | null;
    /**
     * The fee the household was shown, decimal string. Checked against the chosen amenity.
     */
    'quoted_fee'?: string | null;
    'start_local': string;
    'vehicle_description'?: string | null;
    'vehicle_plate'?: string | null;
    'vehicle_state'?: string | null;
}
/**
 * What a manager gets back when they open a homeowner\'s portal.  **There is no `refresh_token` field, and its absence is the design.** An impersonated view is not renewable — `refresh_session` refuses a token carrying `act` — so a nullable field would only invite a client to try. Both vendors land in the same place: WorkOS caps impersonation at 60 minutes, Auth0 issues no refresh token for a delegated session.  `member_name` is here so the portal\'s banner can say *whose* account this is without a second round trip. It is display text and nothing reads it back.
 */
export interface MemberImpersonationResponse {
    'access_token': string;
    'expires_at': number;
    'expires_in': number;
    'member_id': string;
    'member_name': string;
}
/**
 * What resending a member\'s WorkOS invitation did.  Typed so the manager\'s screen can say something true. The endpoint has always returned this body; until 2026-09-12 it was undeclared, so utoipa published the route with no response schema and every generated client typed it `void` — the message existed and no caller could read it.
 */
export interface MemberInviteResendResponse {
    /**
     * Whether WorkOS has an invitation out for this member as a result.  True for a fresh send *and* for an invitation that was already pending, because in both cases the member has a live link. False only when WorkOS could not be reached.
     */
    'email_sent': boolean;
    /**
     * Written for a person to read, and distinguishing the outcomes a manager would act on differently — \"already pending\" means wait, \"already has a sign-in\" means they can sign in now, \"could not send\" means try again.
     */
    'message': string;
}
/**
 * What sign-out hands back to the portal.
 */
export interface MemberLogoutResponse {
    /**
     * Where the browser must go to end WorkOS\'s session as well as ours.  `None` when the token\'s `sid` could not be read — a pre-cutover token, or one minted before the claim existed. Our session has still ended, which is the half that matters.
     */
    'workos_logout_url'?: string | null;
}
/**
 * Request to send a message to a single member
 */
export interface MemberMessageRequest {
    'hoa_id': string;
    'member_id': string;
    'message_body': string;
    'send_email'?: boolean;
    'sender_name': string;
    'subject': string;
}
export interface MemberRefreshRequest {
    'refresh_token': string;
}
export interface MemberReservationCancel {
    'reason'?: string | null;
}
/**
 * A homeowner\'s message in a reservation\'s shared thread. The thread is always the shared one: there is no other a homeowner may write to.
 */
export interface MemberReservationCommentCreate {
    'content': string;
    /**
     * The comment being replied to, in the same thread.
     */
    'parent_id'?: string | null;
}
/**
 * A homeowner books for one of their homes (the home is in the path).
 */
export interface MemberReservationCreate {
    /**
     * The current agreement\'s id, sent when the homeowner ticked \"I agree\". Required when the amenity requires an agreement.
     */
    'agreement_id'?: string | null;
    'amenity_id': string;
    'end_local': string;
    'party_size'?: number | null;
    'purpose'?: string | null;
    /**
     * The fee the homeowner was shown, as a decimal string.
     */
    'quoted_fee'?: string | null;
    'start_local': string;
    'vehicle_description'?: string | null;
    /**
     * Required when the amenity `requires_vehicle` (guest parking).
     */
    'vehicle_plate'?: string | null;
    'vehicle_state'?: string | null;
}
/**
 * A homeowner moves their own booking. Allowed while it is pending or approved and the cancellation deadline has not passed.
 */
export interface MemberReservationReschedule {
    'end_local': string;
    'party_size'?: number | null;
    'start_local': string;
}
/**
 * Schema returned when retrieving a member
 */
export interface MemberReturn {
    /**
     * ✅ CHANGED: From Option<i32> to Option<String>
     */
    'company_id'?: string | null;
    'created_at': number;
    /**
     * When a manager deactivated this member (Unix seconds). Absent unless they are deactivated now.
     */
    'deactivated_at'?: number | null;
    'display_name': string;
    'email': string;
    'first_name': string;
    'full_name': string;
    'id': string;
    /**
     * False until the member accepts their invitation, and again while deactivated. `deactivated_at` tells the two apart.
     */
    'is_active': boolean;
    'is_primary': boolean;
    'last_name': string;
    'middle_name'?: string | null;
    'phone'?: string | null;
    'preferred_name'?: string | null;
    'updated_at': number;
}
/**
 * How a person relates to a home during a tenure.
 */

export const MemberRole = {
    OWNER: 'owner',
    CO_OWNER: 'co_owner',
    TENANT: 'tenant'
} as const;

export type MemberRole = typeof MemberRole[keyof typeof MemberRole];


export interface MemberTokenResponse {
    'access_token': string;
    'expires_at': number;
    'expires_in': number;
    'refresh_expires_at': number;
    'refresh_expires_in': number;
    'refresh_token': string;
}

export const MemberTokenType = {
    ACCESS: 'access',
    REFRESH: 'refresh'
} as const;

export type MemberTokenType = typeof MemberTokenType[keyof typeof MemberTokenType];


/**
 * Schema for updating an existing member (by managers) Note: Password updates are handled through the member portal
 */
export interface MemberUpdate {
    'email'?: string | null;
    'first_name'?: string | null;
    'is_primary'?: boolean | null;
    'last_name'?: string | null;
    'middle_name'?: string | null;
    'phone'?: string | null;
    'preferred_name'?: string | null;
}
export interface MemberVerifyRequest {
    'token': string;
}
export interface MemberVerifyResponse {
    'expires_at'?: number | null;
    /**
     * ✅ CHANGED: From Option<Vec<i32>> to Option<Vec<String>>
     */
    'hoa_ids'?: Array<string> | null;
    /**
     * ✅ CHANGED: From Option<Vec<i32>> to Option<Vec<String>>
     */
    'home_ids'?: Array<string> | null;
    'member_id'?: string | null;
    'valid': boolean;
}
/**
 * Take an offered slot: booked through the ordinary path, so the agreement and the fee are confirmed as for any booking.
 */
export interface MemberWaitlistAccept {
    'agreement_id'?: string | null;
    /**
     * The fee the household was shown, decimal string.
     */
    'quoted_fee'?: string | null;
    'vehicle_description'?: string | null;
    'vehicle_plate'?: string | null;
    'vehicle_state'?: string | null;
}
/**
 * Join the waitlist for a time on one day. The window must not have a free slot now — book that instead — and must otherwise be a booking the household could make.
 */
export interface MemberWaitlistJoin {
    'amenity_id': string;
    /**
     * Default: the amenity\'s minimum duration (or the whole window for an overnight amenity).
     */
    'duration_minutes'?: number | null;
    'earliest_start_local': string;
    /**
     * The latest the booking may end.
     */
    'latest_end_local': string;
    'party_size'?: number | null;
    'purpose'?: string | null;
}
/**
 * The authorization code AuthKit hands back to the member portal.
 */
export interface MemberWorkosLogin {
    /**
     * Single-use, short-lived, and exchanged server-side because the exchange carries our API key.
     */
    'code': string;
}
export interface MyAchPaymentList {
    'items': Array<MyAchPaymentReturn>;
    'total': number;
}
/**
 * One of the homeowner\'s ACH payments, in homeowner words.
 */
export interface MyAchPaymentReturn {
    'amount': string;
    /**
     * The bank\'s reference for the item, once it has one.
     */
    'bank_reference'?: string | null;
    'effective_date': string;
    'home_id': string;
    'id': string;
    /**
     * Why it failed or was returned, in the bank\'s words.
     */
    'message'?: string | null;
    /**
     * The ledger payment, once settled.
     */
    'payment_id'?: string | null;
    /**
     * `submitted` · `processing` · `paid` · `returned` · `cancelled` · `failed`
     */
    'status': string;
    'submitted_at'?: string | null;
}
/**
 * One of the member\'s homes, with enough to name it on screen.  The portal has to be able to say \"123 Main St\" rather than `home_abc123`, and to label a home switcher when a member owns more than one. `hoa_name` is included for the same reason: a member with homes in two associations needs to tell them apart.
 */
export interface MyHomeReturn {
    'city': string;
    'hoa_id': string;
    'hoa_name': string;
    'home_id': string;
    'state': string;
    'street': string;
    'street2'?: string | null;
    'zip': string;
}
/**
 * The ids of the homes the member may ask about.  Typed rather than an ad-hoc `json!` so the generated client gets a real model instead of `any`. Addresses and HOA names are **not** here — `GET /member-portal/me` carries those, and duplicating them would give the portal two places to disagree about an address.
 */
export interface MyHomesReturn {
    'home_ids': Array<string>;
}
/**
 * One page of the member\'s payments, newest first.
 */
export interface MyPaymentList {
    'page': number;
    'payments': Array<MyPaymentReturn>;
    'per_page': number;
    'total': number;
}
/**
 * One of the member\'s own payments.  **A deliberate subset of `PaymentReturn`, and the omissions are the point.** `notes` is manager freetext written *about* the homeowner; `batch_id`, `external_ref` and `check_image_url` are the management company\'s operational detail; `home_label` and `payer_name` are labels the portal already has from `GET /me`. None of it belongs in a response a homeowner receives, and narrowing the model is what stops a later field being added to `PaymentReturn` and silently shipping to them.  What is left is what somebody needs to recognise a payment as theirs: how much, when, how it arrived, which cheque, and where it stands.
 */
export interface MyPaymentReturn {
    'amount': string;
    /**
     * What could not be put against a charge and is being held. Non-zero only where the HOA disallows credit balances — see `knowledge/notes/decision-credit-balances-per-hoa.md`. The money is theirs either way; this is where it is visible.
     */
    'amount_unapplied': string;
    'check_number'?: string | null;
    'id': string;
    'method': PaymentMethod;
    'received_date': string;
    /**
     * `applied`, `overpaid`, `returned`, `voided` or `refunded`. `unmatched` cannot appear here — a payment in suspense has no `home_id`, so it never reaches a home-scoped list.
     */
    'status': PaymentStatus;
}


/**
 * The signed-in member\'s own profile.  Everything here is about the caller, resolved from `AuthMember::member_id` — there is no path parameter and no way to ask about anybody else.  **Why this exists at all:** `MemberClaims` carries ids and nothing human — no name, no email — so without this endpoint the portal cannot render a greeting, an avatar, or an address. It used to read `full_name` and `email` out of the JWT, which are management claims that a member token has never had.
 */
export interface MyProfileReturn {
    'email': string;
    'first_name': string;
    'homes': Array<MyHomeReturn>;
    'is_primary': boolean;
    'last_name': string;
    'member_id': string;
    'middle_name'?: string | null;
    'phone'?: string | null;
    'preferred_name'?: string | null;
}

export const NormalBalance = {
    DEBIT: 'debit',
    CREDIT: 'credit'
} as const;

export type NormalBalance = typeof NormalBalance[keyof typeof NormalBalance];


/**
 * Whether a notice goes out by itself or waits for a person.
 */

export const NoticeAction = {
    SEND: 'send',
    FLAG: 'flag'
} as const;

export type NoticeAction = typeof NoticeAction[keyof typeof NoticeAction];


/**
 * How a notice reaches the homeowner.
 */

export const NoticeChannel = {
    EMAIL: 'email',
    PORTAL: 'portal',
    MAIL: 'mail'
} as const;

export type NoticeChannel = typeof NoticeChannel[keyof typeof NoticeChannel];


/**
 * Create a notice rule.
 */
export interface NoticeRuleCreate {
    'action': NoticeAction;
    'channel'?: NoticeChannel | null;
    'enabled'?: boolean | null;
    'min_balance'?: string | null;
    'offset_days'?: number;
    'template_key': string;
    'trigger': NoticeTrigger;
}


/**
 * The notice schedule in force for a scope, and where it comes from.  **Resolution is first-non-empty-scope-wins**, not a merge: an HOA with rules of its own uses exactly those, and inherits the company\'s entire list only while it has none. So `inherited` is not a decoration — it says whether adding a single rule here will *replace* everything currently in effect.
 */
export interface NoticeRuleList {
    'effective_scope_id'?: string | null;
    /**
     * Where the rules actually come from — the same scope, or the one it inherits from.
     */
    'effective_scope_type': string;
    /**
     * True when these rules belong to a wider scope than the one asked about.
     */
    'inherited': boolean;
    'rules': Array<NoticeRuleReturn>;
    'scope_id'?: string | null;
    /**
     * The scope that was asked about.
     */
    'scope_type': string;
    'total': number;
}
export interface NoticeRuleReturn {
    'action': NoticeAction;
    'channel': NoticeChannel;
    'enabled': boolean;
    'id': string;
    'min_balance'?: string | null;
    'offset_days': number;
    'scope_id'?: string | null;
    'scope_type': string;
    'template_key': string;
    'trigger': NoticeTrigger;
}


/**
 * A change to one notice rule. Omitted fields are left alone.
 */
export interface NoticeRuleUpdate {
    'action'?: NoticeAction | null;
    'channel'?: NoticeChannel | null;
    'enabled'?: boolean | null;
    'min_balance'?: string | null;
    'offset_days'?: number | null;
    'template_key'?: string | null;
    'trigger'?: NoticeTrigger | null;
}


/**
 * What a notice run did.
 */
export interface NoticeRunReport {
    'as_of': string;
    'hoa_id': string;
    /**
     * Raised for review rather than dispatched — a `flag` rule.
     */
    'notices_flagged': number;
    'notices_sent': number;
    'rules_evaluated': number;
}
/**
 * What triggers a notice.
 */

export const NoticeTrigger = {
    DAYS_BEFORE_DUE: 'days_before_due',
    DAYS_AFTER_DUE: 'days_after_due',
    BALANCE_THRESHOLD: 'balance_threshold'
} as const;

export type NoticeTrigger = typeof NoticeTrigger[keyof typeof NoticeTrigger];


/**
 * Paginated list of notifications
 */
export interface NotificationList {
    'notifications': Array<NotificationResponse>;
    'total': number;
}
/**
 * Extended notification response with related entity details
 */
export interface NotificationResponse {
    /**
     * The comment on the `entity_*` record this is about — a homeowner\'s reply — so opening it can point at the message.
     */
    'comment_id'?: string | null;
    'created_at': string;
    'email_sent': boolean;
    'entity_id'?: string | null;
    'entity_type'?: string | null;
    'hoa_id': string;
    'hoa_name': string;
    'id': string;
    'is_for_member': boolean;
    'is_for_staff': boolean;
    'is_read': boolean;
    'member_id'?: string | null;
    'message': string;
    'notification_type': string;
    'recipient_email'?: string | null;
    'recipient_name': string;
    'title': string;
    'updated_at': string;
    'user_id'?: string | null;
}

export const NotificationType = {
    VIOLATION: 'violation',
    PAYMENT: 'payment',
    MAINTENANCE: 'maintenance',
    ANNOUNCEMENT: 'announcement',
    DOCUMENT: 'document',
    APPROVAL: 'approval',
    COMMENT: 'comment',
    MEMBER_CHANGE: 'member_change',
    CUSTOM_MESSAGE: 'custom_message',
    RESERVATION: 'reservation'
} as const;

export type NotificationType = typeof NotificationType[keyof typeof NotificationType];


/**
 * Assess the returned-payment fee on a payment that already bounced.  Separate from [`PaymentReturnRequest`] because the reversal and the fee are deliberately separate acts — see `docs/CHARGEBACK_DESIGN.md` §3.
 */
export interface NsfFeeRequest {
    /**
     * Overrides the HOA\'s configured `nsf_fee`. Omit to use policy.
     */
    'nsf_fee_amount'?: string | null;
}
/**
 * One NSF item as stored, with our decision alongside the bank\'s view.
 */
export interface NsfItemReturn {
    'account_number'?: string | null;
    'account_type'?: string | null;
    'ach_company_name'?: string | null;
    'ach_individual_id'?: string | null;
    'ach_individual_name'?: string | null;
    /**
     * The literal the bank sent — display-grade, e.g. `\"25\"`.
     */
    'amount'?: string | null;
    'balance_after'?: string | null;
    'bank_account_id': string;
    'bank_connection_id': string;
    'bank_request_error_code'?: string | null;
    'bank_request_error_message'?: string | null;
    'bank_request_id'?: string | null;
    'bank_request_status'?: string | null;
    /**
     * `Pay` | `Return`, as the bank reports it.
     */
    'bank_request_type'?: string | null;
    /**
     * Whether a person may still pay or return this item right now: undecided, and before the cutoff. After it, only a phone call to bank support changes the outcome.
     */
    'can_decide': boolean;
    /**
     * `\"0\"` on an ACH item.
     */
    'check_number'?: string | null;
    'date_created'?: string | null;
    'decided_at'?: string | null;
    'decided_by'?: string | null;
    /**
     * `undecided` | `pay_requested` | `return_requested` | `decided_at_bank` | `expired`.
     */
    'decision': string;
    /**
     * The instant the bank stops taking a decision: **1pm Central on `expire_date`**. Null when the bank gave no expiry.
     */
    'decision_cutoff_at'?: string | null;
    'decision_reason'?: string | null;
    /**
     * The decision deadline, in the bank\'s calendar.
     */
    'expire_date'?: string | null;
    'fee_amount'?: string | null;
    'funding_details'?: string | null;
    /**
     * Whether the bank holds front and back images for this item.
     */
    'has_images': boolean;
    'hoa_bank_account_id'?: string | null;
    'hoa_id'?: string | null;
    'id': string;
    'last_seen_at'?: string | null;
    /**
     * The register check this presents against, when one matched.
     */
    'matched_check_id'?: string | null;
    /**
     * Set when the row came from the mock bank, never from Columbia.
     */
    'mock_source_url'?: string | null;
    'nsf_date'?: string | null;
    /**
     * The bank\'s `NsfID`.
     */
    'provider_nsf_id': string;
    /**
     * `POD` | `ACH`.
     */
    'source'?: string | null;
    'source_trace_number'?: string | null;
}
export interface NsfList {
    'items': Array<NsfItemReturn>;
    /**
     * Items a person can still act on.
     */
    'open': number;
    /**
     * How far the pull has read, least-advanced account first. Null for the HOA view.
     */
    'synced_through'?: string | null;
    'total': number;
}
/**
 * Ask the bank to **pay** an NSF item. The one decision that moves money, and final through the API.
 */
export interface NsfPayDecision {
    /**
     * Required. Free text the bank reads when it asks how the funds are covered; it may ask for evidence such as another account\'s balance.
     */
    'funding_details': string;
    /**
     * How the account will be funded to cover the item — the bank\'s enum: `Wire`, `ACH`, `RDC`, `Book Transfer`, `Walk in deposit`.
     */
    'reason': string;
}
/**
 * Ask the bank to **return** an NSF item — what it does by default when nobody decides, made explicit and attributable. Final through the API.
 */
export interface NsfReturnDecision {
    /**
     * Optional note, kept on our row only; the bank takes nothing but the id on a return.
     */
    'funding_details'?: string | null;
}
export interface OpenTenureRequest {
    'started_on'?: string | null;
}
/**
 * One line of an opening entry: a debit or a credit, never both.
 */
export interface OpeningBalanceLine {
    'account_id': string;
    'credit'?: string | null;
    'debit'?: string | null;
    'description'?: string | null;
    'fund_id': string;
}
export interface OpeningBalancesPost {
    'description'?: string | null;
    /**
     * Must balance within each fund. Accounts Receivable and Prepaid Assessments must match `GET .../opening-balances`.
     */
    'lines': Array<OpeningBalanceLine>;
}
/**
 * What an opening entry must carry on the two subledger control accounts: what the homes owe and have paid ahead that the ledger does not already hold. Everything else on the opening entry comes off the prior system\'s balance sheet.
 */
export interface OpeningBalancesRequired {
    /**
     * False when periods were generated without one. There is then nothing to bring forward into.
     */
    'has_opening_period': boolean;
    'hoa_id': string;
    'opening_balances_pending': boolean;
    /**
     * The day the opening entry is dated: the day before period 1.
     */
    'opening_date'?: string | null;
    'prepaid_account_id'?: string | null;
    /**
     * Credit Prepaid Assessments by exactly this.
     */
    'prepaid_to_bring_forward': string;
    'receivable_account_id'?: string | null;
    /**
     * Debit Accounts Receivable by exactly this.
     */
    'receivable_to_bring_forward': string;
}
/**
 * A per-home dues amount, effective-dated.
 */
export interface OverrideCreate {
    'amount': string;
    'effective_from'?: string | null;
    'effective_to'?: string | null;
    'schedule_id': string;
}
/**
 * A home\'s dues overrides. Bounded, and effective-dated rather than replaced, so the list is the history.
 */
export interface OverrideList {
    'overrides': Array<OverrideReturn>;
    'total': number;
}
export interface OverrideReturn {
    'amount': string;
    'effective_from'?: string | null;
    'effective_to'?: string | null;
    'home_id': string;
    'id': string;
    'schedule_id': string;
}
/**
 * A correction to an override that has not billed anything yet.  Deliberately small: an override is a statement that *this home pays this much between these dates*, so the only honest edits are fixing a figure nobody has been charged, or closing the window. Changing a rate that has already billed is a new override plus, if the old charge was wrong, a reversal.
 */
export interface OverrideUpdate {
    'amount'?: string | null;
    'effective_from'?: string | null;
    /**
     * Set this to end an override rather than deleting it — the row stays as the record of what was charged while it applied.
     */
    'effective_to'?: string | null;
}
/**
 * A signed-in user changing their own password.  No email field: the account is whoever holds the bearer token. Taking an address here would let the two disagree, and the only sensible resolution of that disagreement is to ignore the body — so it is better not to accept it.
 */
export interface PasswordChangeRequest {
    'current_password': string;
    'new_password': string;
}
/**
 * The answer to a successful change, carrying a replacement session.  Changing the password bumps `token_version`, which ends every existing session — including the one that made this request. Rather than bounce the caller to the sign-in screen, the new pair is minted here and returned: they have just proved they know both passwords, so making them type one again buys nothing.  **This body is credential-bearing.** It must not be logged, and the endpoint is POST so it cannot end up in a URL.
 */
export interface PasswordChangeResponse {
    'message': string;
    'success': boolean;
    /**
     * A fresh access/refresh pair for the caller to store in place of the ones this change just invalidated.
     */
    'tokens': TokenResponse;
}
export interface PasswordEmailResetRequest {
    'email': string;
}
export interface PasswordEmailResetResponse {
    'message': string;
    'success': boolean;
}
export interface PasswordResetRequest {
    'email': string;
    'password': string;
    'token': string;
}
export interface PasswordResetResponse {
    'email': string;
    'message': string;
    'success': boolean;
}
export interface PayableList {
    'items': Array<PayableReturn>;
    /**
     * Items waiting for a person to settle.
     */
    'pending': number;
    'total': number;
}
/**
 * One payable as stored: our status and the bank\'s, side by side.
 */
export interface PayableReturn {
    'account_number': string;
    'ach_company_id'?: string | null;
    'amount': string;
    'bank_connection_id': string;
    'bank_feed_transaction_id'?: string | null;
    /**
     * `Pending` … `Error`, as the bank reports it.
     */
    'bank_status'?: string | null;
    'bank_status_message'?: string | null;
    'bank_transaction_id'?: string | null;
    'cancelled_at'?: string | null;
    'cancelled_by'?: string | null;
    'counterparty_id': string;
    'created_by': string;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    /**
     * `payment` | `collection`
     */
    'direction': string;
    'effective_date': string;
    'error_code'?: string | null;
    'error_message'?: string | null;
    'hoa_bank_account_id': string;
    'hoa_id': string;
    'home_id'?: string | null;
    'id': string;
    'journal_entry_id'?: string | null;
    'memo'?: string | null;
    /**
     * Set when the row came from the mock bank, never from Columbia.
     */
    'mock_source_url'?: string | null;
    'payment_id'?: string | null;
    'settle_batch_id'?: string | null;
    'settled_at'?: string | null;
    'settled_by'?: string | null;
    /**
     * draft · tokened · adding · pending · settling · settled · cancelled · failed · returned
     */
    'status': string;
    'token_validation'?: string | null;
    'vendor_id'?: string | null;
}
/**
 * Settle pending items on a connection with a one-time RSA code. The code is sent to the bank once and never stored.
 */
export interface PayableSettleRequest {
    'payable_ids': Array<string>;
    'rsa_code': string;
    'rsa_username': string;
}
/**
 * What one settle did, item by item.
 */
export interface PayableSettleReturn {
    'bank_message'?: string | null;
    'batch_id': string;
    'items': Array<PayableReturn>;
    'not_settled': number;
    /**
     * `sent` | `refused` | `error`
     */
    'outcome': string;
    'settled': number;
}
/**
 * The current setting.
 */
export interface PayerAutoUploadReturn {
    'enabled': boolean;
    'hoa_id': string;
}
/**
 * Turn the scheduled daily payer upload on or off for one association.
 */
export interface PayerAutoUploadUpdate {
    'enabled': boolean;
}
/**
 * Result of a preview: the assembled file text and record count, with no bank call. Callers can inspect/download it before submitting.
 */
export interface PayerFilePreviewReturn {
    'bank_connection_id': string;
    /**
     * The exact tab-delimited file that would be uploaded.
     */
    'file_content': string;
    'file_date': string;
    'hoa_id': string;
    'lockbox_company_id': string;
    'record_count': number;
}
/**
 * Optional body for a per-HOA preview/submit. A payer file is always scoped to one HOA (the path). `bank_connection_id` selects the lockbox when the HOA\'s company has more than one active connection; omit it when there is exactly one.
 */
export interface PayerFileRequest {
    'bank_connection_id'?: string | null;
}
/**
 * Lifecycle of a payer-file submission.
 */

export const PayerFileStatus = {
    BUILDING: 'building',
    PROCESSING: 'processing',
    COMPLETE: 'complete',
    FAILED: 'failed'
} as const;

export type PayerFileStatus = typeof PayerFileStatus[keyof typeof PayerFileStatus];


export interface PayerSubmissionList {
    'submissions': Array<PayerSubmissionReturn>;
    'total': number;
}
/**
 * A payer-file submission record (audit + status).
 */
export interface PayerSubmissionReturn {
    'bank_connection_id': string;
    'bank_file_hash'?: string | null;
    'bank_file_id'?: string | null;
    'bank_file_uid'?: string | null;
    'company_id': string;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'error_code'?: string | null;
    'error_message'?: string | null;
    'file_name': string;
    'hoa_id'?: string | null;
    'id': string;
    'initiated_by'?: string | null;
    'lockbox_company_id': string;
    'record_count': number;
    'reference_id': string;
    'status': string;
    'submitted_at'?: string | null;
}
/**
 * Record money arriving. `amount` is a positive decimal string.  Either `home_id` or `account_number` identifies the payer. Supplying neither, or an account number that resolves to nothing, is not an error — the payment lands in suspense for somebody to match.
 */
export interface PaymentCreate {
    /**
     * The 16 digits it arrived on.
     */
    'account_number'?: string | null;
    'amount': string;
    'batch_id'?: string | null;
    'check_image_url'?: string | null;
    'check_number'?: string | null;
    /**
     * The bank\'s own id. Unique — an ingestion that fetches the same batch twice cannot post twice.
     */
    'external_ref'?: string | null;
    'home_id'?: string | null;
    'method'?: PaymentMethod | null;
    'notes'?: string | null;
    'raw_snapshot'?: string | null;
    /**
     * Defaults to today on the association\'s calendar.
     */
    'received_date'?: string | null;
}


/**
 * One page of payments, newest first.
 */
export interface PaymentList {
    'page': number;
    'payments': Array<PaymentReturn>;
    'per_page': number;
    'total': number;
}
/**
 * Resolve a suspense payment to a home.
 */
export interface PaymentMatchRequest {
    'home_id': string;
}
/**
 * How money arrived.
 */

export const PaymentMethod = {
    CHECK: 'check',
    ACH: 'ach',
    E_CHECK: 'e_check',
    CARD: 'card',
    CASH: 'cash',
    MANUAL: 'manual',
    OTHER: 'other'
} as const;

export type PaymentMethod = typeof PaymentMethod[keyof typeof PaymentMethod];


export interface PaymentRefundRequest {
    'reason': string;
}
export interface PaymentReturn {
    'account_number'?: string | null;
    'amount': string;
    /**
     * What could not be put against a charge, and is being held.
     */
    'amount_unapplied': string;
    'applications': Array<ApplicationReturn>;
    'batch_id'?: string | null;
    'check_image_url'?: string | null;
    'check_number'?: string | null;
    'external_ref'?: string | null;
    'hoa_id': string;
    'home_id'?: string | null;
    /**
     * The home\'s street address, resolved at read time. `None` on an unmatched payment — which is the point of the suspense queue, and shows as unknown rather than blank.
     */
    'home_label'?: string | null;
    'id': string;
    'method': PaymentMethod;
    'notes'?: string | null;
    /**
     * The returned-payment fee this payment caused, if one was charged.
     */
    'nsf_fee_entry_id'?: string | null;
    'ownership_tenure_id'?: string | null;
    /**
     * Who is on the account: the tenure\'s billing payer.
     */
    'payer_name'?: string | null;
    'received_date': string;
    'status': PaymentStatus;
}


/**
 * A returned (NSF) payment.
 */
export interface PaymentReturnRequest {
    /**
     * Charge the HOA\'s NSF fee at the same time.
     */
    'charge_nsf_fee'?: boolean;
    /**
     * Overrides the policy amount when charging one.
     */
    'nsf_fee_amount'?: string | null;
    'reason': string;
}
/**
 * Where a payment stands.
 */

export const PaymentStatus = {
    UNMATCHED: 'unmatched',
    APPLIED: 'applied',
    OVERPAID: 'overpaid',
    RETURNED: 'returned',
    VOIDED: 'voided',
    REFUNDED: 'refunded'
} as const;

export type PaymentStatus = typeof PaymentStatus[keyof typeof PaymentStatus];


/**
 * Pending transactions for one account.
 */
export interface PendingTransactionList {
    'account_number': string;
    'total': number;
    'transactions': Array<PendingTransactionReturn>;
}
/**
 * A pending transaction (memo post) as returned live from the bank.
 */
export interface PendingTransactionReturn {
    'account_number': string;
    'account_type'?: string | null;
    'amount'?: number | null;
    'check_number'?: string | null;
    'current_balance'?: number | null;
    'description'?: string | null;
    'direction'?: string | null;
    'source_id'?: string | null;
    'trancode_id'?: string | null;
    'transaction_date'?: string | null;
}
export interface PeriodGenerateRequest {
    /**
     * The year the fiscal year STARTS in. FY2027 with a July start is 2027-07-01 through 2028-06-30.
     */
    'fiscal_year': number;
    /**
     * Create the opening period ahead of period 1 as well. Valid only for an association\'s first generated year — there is exactly one opening period, and it holds the brought-forward balances.
     */
    'with_opening_period'?: boolean | null;
}
/**
 * `open` accepts entries. `closed` does not — a correction is a new entry in the open period, never an edit to a closed one. `locked` is closed against administrators too, for a year that has been audited.
 */

export const PeriodStatus = {
    OPEN: 'open',
    CLOSED: 'closed',
    LOCKED: 'locked'
} as const;

export type PeriodStatus = typeof PeriodStatus[keyof typeof PeriodStatus];


export interface PeriodStatusUpdate {
    'status': PeriodStatus;
}


/**
 * How a partial period is charged when a home is only part-way into it (`docs/DUES_DESIGN.md` §2.3).
 */

export const ProrationMethod = {
    NONE: 'none',
    BY_DAY: 'by_day',
    BY_MONTH: 'by_month'
} as const;

export type ProrationMethod = typeof ProrationMethod[keyof typeof ProrationMethod];


/**
 * A cash or investment account that *can* be reconciled but has no reconciliation in the range asked for.  Carried alongside the reconciliations so a close screen can say \"2 of 3\" without reimplementing which accounts are reconcilable — the rule is [`ReconciliationService::require_cash_account`]\'s, and a second copy of it in a front end would drift.
 */
export interface ReconcilableAccount {
    'account_name': string;
    'account_number': string;
    'gl_account_id': string;
    'hoa_bank_account_id': string;
}
export interface ReconciledItem {
    'amount': string;
    'bank_transaction_id': string;
    'description': string;
    'effective_date': string;
    'item_id': string;
    'journal_line_id': string;
    /**
     * True when the matcher proposed it rather than a person making it. Which ones to doubt, when a statement will not balance.
     */
    'matched_automatically': boolean;
    /**
     * The journal entry\'s own source, so a match made by neither the matcher nor a person can say so.  A `bank_activity` line was **posted from the very transaction it clears** — interest or a service charge the institution originated — so it is neither a proposal to check nor a human judgement to trust. Calling it \"confirmed\" would put it on the same footing as a match somebody actually looked at, and `matched_automatically` cannot tell them apart: it is `true` for both, because no person judged either.
     */
    'source_type': string;
}
export interface ReconciliationList {
    'reconciliations': Array<ReconciliationSummary>;
    /**
     * Reconcilable accounts with nothing opened in this range. Empty when the range is unbounded and every account has been reconciled at least once.
     */
    'unreconciled_accounts': Array<ReconcilableAccount>;
}
export interface ReconciliationMatch {
    'bank_transaction_id': string;
    'journal_line_id': string;
}
export interface ReconciliationOpen {
    /**
     * The GL cash account. Its linked bank account is where the statement\'s transactions come from.
     */
    'gl_account_id': string;
    /**
     * Signed in **our** sense — positive is money the association has.
     */
    'statement_balance': string;
    'statement_date': string;
}
export interface ReconciliationReport {
    'account_name': string;
    'account_number': string;
    /**
     * `statement + deposits in transit − outstanding items`.
     */
    'adjusted_balance': string;
    /**
     * The GL cash account\'s balance at the statement date.
     */
    'book_balance': string;
    /**
     * Our postings not yet seen at the bank, positive side.
     */
    'deposits_in_transit': string;
    /**
     * `adjusted − book`. Zero is a reconciled account.
     */
    'difference': string;
    'gl_account_id': string;
    'hoa_bank_account_id'?: string | null;
    'hoa_id': string;
    'id': string;
    'matched': Array<ReconciledItem>;
    /**
     * Our postings not yet seen at the bank, negative side, stated positive — the cheques still outstanding.
     */
    'outstanding_items': string;
    'reconciled': boolean;
    'statement_balance': string;
    'statement_date': string;
    'status': string;
    'uncleared_book_items': Array<UnclearedBookItem>;
    'unexplained_bank_items': Array<UnexplainedBankItem>;
}
/**
 * One reconciliation as a close screen reads it: the headline numbers and four counts, without the three lists.
 */
export interface ReconciliationSummary {
    'account_name': string;
    'account_number': string;
    'completed_at'?: number | null;
    'completed_by'?: string | null;
    /**
     * `adjusted − book`. Zero is a reconciled account.
     */
    'difference': string;
    'gl_account_id': string;
    'id': string;
    'matched_count': number;
    /**
     * Of `matched_count`, the ones the matcher proposed rather than a person judging — **the accountant\'s to-do list**, and the count worth showing next to a reconciliation that will not balance.
     */
    'proposed_count': number;
    'reconciled': boolean;
    'statement_balance': string;
    'statement_date': string;
    /**
     * The institution\'s statement, when one has been attached.
     */
    'statement_id'?: string | null;
    'status': string;
    'uncleared_count': number;
    'unexplained_count': number;
}
export interface RefreshRequest {
    'refresh_token': string;
}
/**
 * What one connection has taken in, and how far it has been read.
 */
export interface RemittanceList {
    'bank_connection_id': string;
    'records': Array<RemittanceRecordReturn>;
    /**
     * **Absent means never ingested**, which is a fact about us rather than about the lockbox. An empty list under this means \"we have not looked yet\", not \"nothing arrived\" — they mean opposite things and only one is a reason to worry.
     */
    'synced_through'?: string | null;
    'total': number;
}
/**
 * Narrowing for the connection remittance read.
 */
export interface RemittanceQuery {
    /**
     * Only records that did not resolve to a home and have not become a payment — the ones that need somebody.
     */
    'unmatched_only'?: boolean | null;
}
/**
 * One inbound remittance record, as a manager sees it.  **The payer\'s account number and the depository account are separate fields on purpose.** They are separate on the wire too, and the whole class of catastrophe here is treating the second as the first — it is identical across every record in a batch, so matching on it would put every payment in a batch on one home.
 */
export interface RemittanceRecordReturn {
    'bank_connection_id': string;
    /**
     * The lockbox\'s own depository account. **Not the payer\'s**, and the same on every record in a batch.
     */
    'deposit_account_number'?: string | null;
    'hoa_id'?: string | null;
    'hold_reason'?: string | null;
    'home_id'?: string | null;
    'id': string;
    /**
     * The amount, as a decimal string. Absent when the bank sent none, or when what it sent could not be represented — in which case `item_amount_raw` carries the original characters.
     */
    'item_amount'?: string | null;
    /**
     * Set **only** when the amount would not convert. Non-null here is a defect, not an unusual payment.
     */
    'item_amount_raw'?: string | null;
    /**
     * `resolved` | `hoa_only` | `unresolved` | `ambiguous`.
     */
    'match_status': string;
    /**
     * The lockbox\'s confidence in its own match, verbatim.
     */
    'match_type'?: string | null;
    /**
     * **Set when this record came from a mock, not from the bank.**  Absent on every real record. Present, it is the only thing on the page distinguishing a payment that arrived from one somebody invented to test with — so it is surfaced rather than left for whoever thinks to query the column.
     */
    'mock_source_url'?: string | null;
    /**
     * Payer file Column D — **the homeowner\'s account number**, and what a payment matches on.
     */
    'payer_account_number'?: string | null;
    /**
     * Payer file Column C — the association the lockbox says this is.
     */
    'payer_association_id'?: string | null;
    /**
     * The association name as the lockbox spells it, which is not necessarily how we spell it. Shown to help a person recognise the record, never used to resolve one.
     */
    'payer_association_name'?: string | null;
    /**
     * The payer\'s name, where the source sends one.  **The most useful field on the page** when somebody is working out whose money an unmatched record is: an account number that resolved to nothing plus a name is a question a manager can answer, and an account number alone usually is not.
     */
    'payer_customer_name'?: string | null;
    /**
     * Set once it has become a payment.
     */
    'payment_id'?: string | null;
    /**
     * `pending` | `posted` | `held` | `skipped` | `failed`.
     */
    'post_status': string;
    /**
     * The processing day it belongs to.
     */
    'processed_on'?: string | null;
    /**
     * The bank\'s own id for the record.
     */
    'provider_record_id': string;
    /**
     * The delimited detail exactly as the lockbox sent it. The last resort when the parse is not enough.
     */
    'raw_detail'?: string | null;
    /**
     * Keys in the lockbox\'s detail string we do not recognise.  Surfaced rather than swallowed, but **no longer a warning**: real records carry several, so the alert moved to a *missing* key a payment is matched on. This is diagnostic detail, not a flag.
     */
    'unknown_detail_keys'?: string | null;
}
export interface ReservationApprove {
    /**
     * Shown to the household.
     */
    'decision_note'?: string | null;
    /**
     * Charge a different fee than the one quoted. Requires `reason`.
     */
    'fee_override'?: string | null;
    'override_reason'?: string | null;
    /**
     * Rules that have started failing since the request (a limit filled, standing lapsed) may be overridden here, with a reason.
     */
    'overrides'?: Array<ReservationRuleCode> | null;
    'reason'?: string | null;
}
export interface ReservationCancel {
    /**
     * Required. Shown to the household.
     */
    'reason': string;
    /**
     * Give the fee back (default): reversed when unpaid, returned when paid (#703) — `credited` or `refunded` by the association\'s credit policy. `false` keeps it, paid or not; the reason says why.
     */
    'reverse_fee'?: boolean | null;
}
export interface ReservationCancelOutcome {
    /**
     * What the household would still owe for this booking, as a decimal string (\"0.00\" when nothing).
     */
    'charge': string;
    'outcome': ReservationCancelOutcomeKind;
}


/**
 * What a household\'s cancellation would do to its fee.
 */

export const ReservationCancelOutcomeKind = {
    FREE: 'free',
    REVERSED: 'reversed',
    KEPT: 'kept',
    LATE_FEE: 'late_fee',
    RETURNED: 'returned'
} as const;

export type ReservationCancelOutcomeKind = typeof ReservationCancelOutcomeKind[keyof typeof ReservationCancelOutcomeKind];



export const ReservationCancelledByKind = {
    HOUSEHOLD: 'household',
    MANAGEMENT: 'management',
    SYSTEM: 'system'
} as const;

export type ReservationCancelledByKind = typeof ReservationCancelledByKind[keyof typeof ReservationCancelledByKind];


export interface ReservationChecklistItem {
    'item': string;
    'note'?: string | null;
    'ok': boolean;
}
/**
 * The 409 body when a closure or an archive would cancel bookings.
 */
export interface ReservationConflictReturn {
    /**
     * Always `BOOKINGS_IN_THE_WAY`.
     */
    'code': ReservationErrorCode;
    'conflicts': Array<ReservationReturn>;
    'error': string;
}


export interface ReservationDeny {
    /**
     * Required. Shown to the household.
     */
    'reason': string;
}
/**
 * Settle a held deposit after the booking. `release_amount` goes back to the household; the rest is kept, which needs a reason the household sees.
 */
export interface ReservationDepositSettle {
    /**
     * Required when keeping any of it.
     */
    'deduction_reason'?: string | null;
    /**
     * Decimal string, 0 to the deposit.
     */
    'release_amount': string;
}
/**
 * What became of a reservation\'s refundable deposit (phase 3).
 */

export const ReservationDepositStatus = {
    NONE: 'none',
    HELD: 'held',
    RELEASED: 'released',
    PARTIALLY_RELEASED: 'partially_released',
    FORFEITED: 'forfeited',
    REFUND_DUE: 'refund_due',
    CREDITED: 'credited',
    REFUNDED: 'refunded'
} as const;

export type ReservationDepositStatus = typeof ReservationDepositStatus[keyof typeof ReservationDepositStatus];


/**
 * One requirement and where this booking stands on it.
 */
export interface ReservationDocumentRequirementStatus {
    /**
     * The local date the document is due.
     */
    'due_on': string;
    /**
     * The newest submission, whatever its status.
     */
    'latest'?: ReservationDocumentReturn | null;
    'requirement': AmenityDocumentRequirementReturn;
    /**
     * An accepted document that (when the requirement asks) covers the booking\'s end.
     */
    'satisfied': boolean;
}
export interface ReservationDocumentReturn {
    'date_created'?: number | null;
    'expires_on'?: string | null;
    'file_id': string;
    'file_name': string;
    'id': string;
    /**
     * Whether the inline endpoint will display it.
     */
    'renderable': boolean;
    'requirement_id': string;
    'requirement_title': string;
    'reservation_id': string;
    /**
     * Shown to the household.
     */
    'review_note'?: string | null;
    'reviewed_at'?: number | null;
    'status': ReservationDocumentStatus;
    /**
     * `member` or `user`: who uploaded it.
     */
    'submitted_by': ReservationEventActorKind;
}


/**
 * Accept or reject a submitted document.
 */
export interface ReservationDocumentReview {
    /**
     * `accepted` or `rejected`.
     */
    'decision': ReservationDocumentStatus;
    /**
     * Required to reject. Shown to the household.
     */
    'note'?: string | null;
}


/**
 * Where a submitted document stands.
 */

export const ReservationDocumentStatus = {
    SUBMITTED: 'submitted',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected'
} as const;

export type ReservationDocumentStatus = typeof ReservationDocumentStatus[keyof typeof ReservationDocumentStatus];


export interface ReservationDocumentsReturn {
    /**
     * Every submission, newest first.
     */
    'documents': Array<ReservationDocumentReturn>;
    'requirements': Array<ReservationDocumentRequirementStatus>;
    'reservation_id': string;
}
/**
 * Why an amenity or reservation request was refused, for a client to branch on — `error` is the sentence to show. A rule refusal (422) carries rule codes in `violations` instead.
 */

export const ReservationErrorCode = {
    NOT_FOUND: 'NOT_FOUND',
    INVALID_REQUEST: 'INVALID_REQUEST',
    NAME_TAKEN: 'NAME_TAKEN',
    AMENITY_ARCHIVED: 'AMENITY_ARCHIVED',
    STALE_AMENITY: 'STALE_AMENITY',
    SEASON_OVERLAP: 'SEASON_OVERLAP',
    WRONG_STATUS: 'WRONG_STATUS',
    ALREADY_STARTED: 'ALREADY_STARTED',
    ALREADY_OVER: 'ALREADY_OVER',
    NOT_STARTED: 'NOT_STARTED',
    DEADLINE_PASSED: 'DEADLINE_PASSED',
    FEE_PAID: 'FEE_PAID',
    BOOKINGS_IN_THE_WAY: 'BOOKINGS_IN_THE_WAY',
    BILLING_CONFLICT: 'BILLING_CONFLICT',
    GROUP_CHANGED: 'GROUP_CHANGED',
    DEPOSIT_NOT_HELD: 'DEPOSIT_NOT_HELD',
    DEPOSIT_PARTLY_PAID: 'DEPOSIT_PARTLY_PAID',
    BOOKING_NOT_OVER: 'BOOKING_NOT_OVER',
    WAITLIST_SLOT_FREE: 'WAITLIST_SLOT_FREE',
    WAITLIST_LIMIT: 'WAITLIST_LIMIT',
    WAITLIST_NO_OFFER: 'WAITLIST_NO_OFFER',
    WAITLIST_CLOSED: 'WAITLIST_CLOSED',
    CHECK_IN_CLOSED: 'CHECK_IN_CLOSED',
    NOT_CHECKED_IN: 'NOT_CHECKED_IN',
    ALREADY_CHECKED_IN: 'ALREADY_CHECKED_IN',
    SUSPENSION_LIFTED: 'SUSPENSION_LIFTED',
    NOTHING_OWED_BACK: 'NOTHING_OWED_BACK',
    INTERNAL: 'INTERNAL'
} as const;

export type ReservationErrorCode = typeof ReservationErrorCode[keyof typeof ReservationErrorCode];


/**
 * Any amenity or reservation error other than a rule refusal or a list of bookings in the way.
 */
export interface ReservationErrorReturn {
    'code': ReservationErrorCode;
    'error': string;
}


/**
 * Who an audit event is attributed to.
 */

export const ReservationEventActorKind = {
    MEMBER: 'member',
    USER: 'user',
    SYSTEM: 'system'
} as const;

export type ReservationEventActorKind = typeof ReservationEventActorKind[keyof typeof ReservationEventActorKind];


export interface ReservationEventList {
    'events': Array<ReservationEventReturn>;
    'total': number;
}
export interface ReservationEventReturn {
    'actor_id'?: string | null;
    'actor_kind': ReservationEventActorKind;
    'date_created'?: number | null;
    /**
     * Event-specific detail — the fee posted, the rule codes an override covered, the times a closure cancelled. A JSON object; null when the event has none.
     */
    'detail'?: object | null;
    'event_type': string;
    'from_status'?: string | null;
    'id': string;
    'reason'?: string | null;
    'reservation_id': string;
    'to_status'?: string | null;
}


export interface ReservationFeeChange {
    /**
     * Decimal string.
     */
    'from': string;
    /**
     * Decimal string.
     */
    'to': string;
}
/**
 * One line of a booking\'s fee.
 */
export interface ReservationFeeLine {
    /**
     * Decimal string.
     */
    'amount': string;
    'kind': ReservationFeeLineKind;
    /**
     * \"Weekend rate\", \"3 guests × $10.00\".
     */
    'label': string;
}


/**
 * What a line of a fee is.
 */

export const ReservationFeeLineKind = {
    BASE: 'base',
    PRICE_RULE: 'price_rule',
    GUESTS: 'guests'
} as const;

export type ReservationFeeLineKind = typeof ReservationFeeLineKind[keyof typeof ReservationFeeLineKind];


/**
 * What became of a reservation\'s fee.
 */

export const ReservationFeeStatus = {
    NONE: 'none',
    POSTED: 'posted',
    REVERSED: 'reversed',
    KEPT: 'kept',
    REFUND_DUE: 'refund_due',
    LATE_FEE: 'late_fee',
    CREDITED: 'credited',
    REFUNDED: 'refunded'
} as const;

export type ReservationFeeStatus = typeof ReservationFeeStatus[keyof typeof ReservationFeeStatus];


/**
 * Which amenity \"any\" would pick, and its preview.  **`violations` is the authoritative list.** With an amenity picked it is exactly `preview.violations` (the rules that would still stop the booking there — a limit, the agreement); with none picked (`amenity_id` null, `preview` null) it says why the first amenity is not free. Read this one; `preview.violations` is the shared preview shape and always equal to it.
 */
export interface ReservationGroupPreviewReturn {
    'amenity_id'?: string | null;
    'amenity_name'?: string | null;
    'preview'?: ReservationPreviewReturn | null;
    'violations': Array<ReservationRuleViolation>;
}
/**
 * How much of its allowance a household has used.
 */
export interface ReservationHomeUsage {
    'active': number;
    'active_limit'?: number | null;
    'daily_limit'?: number | null;
    'day': number;
    'month': number;
    'month_hours': string;
    'monthly_hours_limit'?: string | null;
    'monthly_limit'?: number | null;
    'week': number;
    /**
     * Clock hours held this week / month, as decimal strings.
     */
    'week_hours': string;
    'weekly_hours_limit'?: string | null;
    'weekly_limit'?: number | null;
}
export interface ReservationInspectionCreate {
    /**
     * The amenity\'s checklist, filled in. Items beyond it are allowed.
     */
    'checklist': Array<ReservationChecklistItem>;
    'kind': ReservationInspectionKind;
    'notes'?: string | null;
    'outcome': ReservationInspectionOutcome;
}



export const ReservationInspectionKind = {
    PRE: 'pre',
    POST: 'post'
} as const;

export type ReservationInspectionKind = typeof ReservationInspectionKind[keyof typeof ReservationInspectionKind];


export interface ReservationInspectionList {
    'inspections': Array<ReservationInspectionReturn>;
    'total': number;
}

export const ReservationInspectionOutcome = {
    OK: 'ok',
    ISSUES: 'issues'
} as const;

export type ReservationInspectionOutcome = typeof ReservationInspectionOutcome[keyof typeof ReservationInspectionOutcome];


export interface ReservationInspectionReturn {
    'checklist': Array<ReservationChecklistItem>;
    'id': string;
    /**
     * Seconds since the epoch.
     */
    'inspected_at': number;
    'inspected_by_user_id'?: string | null;
    'kind': ReservationInspectionKind;
    'notes'?: string | null;
    'outcome': ReservationInspectionOutcome;
    'photos': Array<AttachmentReturn>;
    'reservation_id': string;
}


export interface ReservationList {
    'page': number;
    'per_page': number;
    'reservations': Array<ReservationReturn>;
    'total': number;
}
/**
 * What moving a booking would do to its fee.
 */

export const ReservationMoveFeeOutcome = {
    UNCHANGED: 'unchanged',
    REQUOTED: 'requoted',
    REPOSTED: 'reposted',
    RETURNED: 'returned',
    KEPT_PAID: 'kept_paid',
    REFUSED_FEE_PAID: 'refused_fee_paid'
} as const;

export type ReservationMoveFeeOutcome = typeof ReservationMoveFeeOutcome[keyof typeof ReservationMoveFeeOutcome];


export interface ReservationNoShow {
    /**
     * Required. Recorded in the audit trail.
     */
    'reason': string;
}
/**
 * Only the notes — and, from phase 3, the access code — are editable; everything else is a transition.
 */
export interface ReservationPatch {
    /**
     * Set the booking\'s own access code; an empty string clears it.
     */
    'access_code'?: string | null;
    'decision_note'?: string | null;
    'internal_notes'?: string | null;
}
export interface ReservationPolicyReturn {
    /**
     * How early before its start a booking may be checked in.
     */
    'check_in_grace_minutes': number;
    /**
     * Days after a completed booking ends that its deposit is released unless a post inspection found issues. Null: never automatically.
     */
    'deposit_auto_release_days'?: number | null;
    'hoa_id': string;
    /**
     * No-shows within `no_show_window_days` that suspend a household for `suspension_days`. Null: off (the default).
     */
    'no_show_threshold'?: number | null;
    'no_show_window_days': number;
    'pending_expiry_hours': number;
    'reminder_hours_before': number;
    /**
     * Off by default. Suspending amenity use for delinquency is regulated differently in every state.
     */
    'standing_gate_enabled': boolean;
    /**
     * Decimal string.
     */
    'standing_max_balance'?: string | null;
    'standing_max_days_past_due'?: number | null;
    'suspension_days': number;
    'timezone': string;
    /**
     * How long a waitlist offer holds its slot.
     */
    'waitlist_offer_hours': number;
    /**
     * 0 = Sunday … 6 = Saturday.
     */
    'week_starts_on': number;
}
/**
 * Replace the policy. With the gate on and both thresholds empty, any amount past due blocks booking amenities that require good standing.
 */
export interface ReservationPolicyUpdate {
    'check_in_grace_minutes'?: number | null;
    /**
     * **Phase-3 settings: absent leaves the stored value unchanged**, so a client saving the older fields cannot wipe these. Null clears a nullable one (never auto-release; no-show suspensions off).
     */
    'deposit_auto_release_days'?: number | null;
    'no_show_threshold'?: number | null;
    'no_show_window_days'?: number | null;
    'pending_expiry_hours': number;
    'reminder_hours_before': number;
    'standing_gate_enabled': boolean;
    'standing_max_balance'?: string | null;
    'standing_max_days_past_due'?: number | null;
    'suspension_days'?: number | null;
    'waitlist_offer_hours'?: number | null;
    'week_starts_on': number;
}
/**
 * A dry run: what booking this would cost, and what it would break.
 */
export interface ReservationPreviewReturn {
    'agreement'?: AmenityAgreementReturn | null;
    /**
     * The refundable deposit that would be held on approval, decimal string; null when the amenity takes none.
     */
    'deposit'?: string | null;
    'fee': string;
    /**
     * How `fee` was arrived at.
     */
    'fee_breakdown': Array<ReservationFeeLine>;
    'fee_refundable_until_local'?: string | null;
    /**
     * `approved` or `pending`, if booked as sent.
     */
    'status_if_booked': ReservationStatus;
    'violations': Array<ReservationRuleViolation>;
}


/**
 * Move a booking to a new time, re-checked against every rule as if newly booked (it does not conflict with itself). An approved booking whose fee changes has the old charge reversed and the new one posted; a fee already paid refuses with 409 unless `allow_fee_change_when_paid`, which keeps the paid charge and gives any difference back (#703).
 */
export interface ReservationReschedule {
    'allow_fee_change_when_paid'?: boolean | null;
    'end_local': string;
    'override_reason'?: string | null;
    'overrides'?: Array<ReservationRuleCode> | null;
    /**
     * Unchanged when absent.
     */
    'party_size'?: number | null;
    /**
     * Shown to the household.
     */
    'reason'?: string | null;
    'start_local': string;
}
/**
 * A dry run of moving a booking: the verdict at the new time, judged exactly as the move would be — the booking does not conflict with or count against itself — and what it would do to the fee. Writes nothing.
 */
export interface ReservationReschedulePreviewReturn {
    /**
     * The fee at the new time, decimal string.
     */
    'fee': string;
    'fee_change': ReservationFeeChange;
    'fee_outcome': ReservationMoveFeeOutcome;
    'fee_refundable_until_local'?: string | null;
    /**
     * The part of `violations` the request\'s `overrides` do not cover — what would make the move a 422. A homeowner overrides nothing.
     */
    'refused': Array<ReservationRuleViolation>;
    /**
     * Everything the new time breaks, overridable or not.
     */
    'violations': Array<ReservationRuleViolation>;
    /**
     * Whether the move would go through as sent.
     */
    'would_move': boolean;
}


export interface ReservationReturn {
    /**
     * The way in. On a homeowner\'s read, present only for an approved booking from `access_visible_from_local` until it ends; staff always see it.
     */
    'access_code'?: string | null;
    'access_instructions'?: string | null;
    /**
     * When the household is shown the code (local time). Null when the amenity gives no code.
     */
    'access_visible_from_local'?: string | null;
    'agreement_accepted_at'?: number | null;
    'agreement_id'?: string | null;
    'agreement_version'?: number | null;
    'amenity_id': string;
    'amenity_name': string;
    'booked_by_member_id'?: string | null;
    /**
     * The booker\'s name: the homeowner\'s (\"Jo Smith\") or the staff member\'s. Staff reads always carry it. On a homeowner\'s read it is present only when a member of that same home booked it, and null when management booked on the household\'s behalf.
     */
    'booked_by_name'?: string | null;
    'booked_by_user_id'?: string | null;
    /**
     * What cancelling would cost **if the household cancelled now** — for the homeowner\'s cancel dialog. Null once it cannot be cancelled by them.
     */
    'cancel_outcome_now'?: ReservationCancelOutcome | null;
    'cancel_reason'?: string | null;
    'cancelled_at'?: number | null;
    /**
     * Who ended the booking: `household` (a homeowner cancelled or withdrew), `management` (staff cancelled, including through a closure or an archive) or `system` (a request expired unanswered, or nobody is recorded). Null unless `status` is `cancelled` or `expired`.
     */
    'cancelled_by_kind'?: ReservationCancelledByKind | null;
    'check_in_closes_at_local'?: string | null;
    /**
     * When staff may check this booking in, local wall clock: from the association\'s check-in grace before the start until the end (`[opens, closes)`, the same window the check-in endpoint judges). Null when check-in does not apply — not approved, already checked in, or over — and on a household\'s read.
     */
    'check_in_opens_at_local'?: string | null;
    'checked_in_at'?: number | null;
    'checked_out_at'?: number | null;
    'date_created'?: number | null;
    'decided_at'?: number | null;
    'decided_by_user_id'?: string | null;
    'decision_note'?: string | null;
    /**
     * The refundable deposit, decimal string (\"0.00\" when none).
     */
    'deposit_amount': string;
    /**
     * What a settlement kept, decimal string.
     */
    'deposit_deduction_amount'?: string | null;
    'deposit_deduction_entry_id'?: string | null;
    /**
     * Why — shown to the household.
     */
    'deposit_deduction_reason'?: string | null;
    'deposit_entry_id'?: string | null;
    /**
     * The `refund` that paid a returned deposit out by cheque. The return credit itself is `deposit_release_entry_id`.
     */
    'deposit_payout_entry_id'?: string | null;
    'deposit_release_entry_id'?: string | null;
    'deposit_settled_at'?: number | null;
    'deposit_status': ReservationDepositStatus;
    /**
     * Required documents not yet accepted. Null when the amenity requires none.
     */
    'documents_outstanding'?: number | null;
    'end_local': string;
    'ends_at': number;
    'fee_amount': string;
    /**
     * How the fee was arrived at — the base or a pricing rule, and guests. Empty for a booking made before phase 3.
     */
    'fee_breakdown': Array<ReservationFeeLine>;
    'fee_entry_id'?: string | null;
    /**
     * The `refund` that paid the returned fee out by cheque. Null when it was left on the account.
     */
    'fee_payout_entry_id'?: string | null;
    /**
     * Cancelling before this (local time) reverses the fee.
     */
    'fee_refundable_until_local'?: string | null;
    /**
     * The `amenity_fee_return` credit that gave a paid fee back.
     */
    'fee_return_entry_id'?: string | null;
    'fee_reversal_entry_id'?: string | null;
    'fee_status': ReservationFeeStatus;
    'hoa_id': string;
    /**
     * \"12 Oak St\" — staff reads only; empty for other households.
     */
    'home_address': string;
    'home_id': string;
    'id': string;
    /**
     * Managers only; null for the board and for homeowners.
     */
    'internal_notes'?: string | null;
    'late_cancel_fee_entry_id'?: string | null;
    'local_date': string;
    /**
     * For a stay that crosses midnight (`overnight`): the nights.
     */
    'nights'?: number | null;
    'party_size': number;
    'pending_expires_at'?: number | null;
    'purpose'?: string | null;
    'series_id'?: string | null;
    'start_local': string;
    /**
     * Seconds since the epoch.
     */
    'starts_at': number;
    'status': ReservationStatus;
    'timezone': string;
    'vehicle_description'?: string | null;
    /**
     * Staff and the booking household only.
     */
    'vehicle_plate'?: string | null;
    'vehicle_state'?: string | null;
    /**
     * The waitlist offer this booking was made from.
     */
    'waitlist_entry_id'?: string | null;
}


/**
 * Give back what a booking from before #703 left at `refund_due`.
 */
export interface ReservationReturnMoney {
    /**
     * Must be true: the person has checked the home\'s ledger and nobody has already credited or refunded this by hand (the only way to before #703). Returning it twice pays the household twice.
     */
    'confirm_not_already_returned': boolean;
}
export interface ReservationRevenueAmenity {
    'amenity_id': string;
    'months': Array<ReservationRevenueMonth>;
    'name': string;
    'totals': ReservationRevenueMonth;
}
/**
 * One month of an amenity\'s money, every figure a decimal string.
 */
export interface ReservationRevenueMonth {
    /**
     * What settlements kept — income, from `amenity_income`.
     */
    'deposit_deductions': string;
    'deposits_charged': string;
    /**
     * Paid deposits left owed back (refund_due) by bookings from before #703, released in the month.
     */
    'deposits_refund_due': string;
    /**
     * Given back: an unpaid deposit\'s charge reversed, or a paid one returned (#703).
     */
    'deposits_returned': string;
    /**
     * Fees and late-cancellation charges posted.
     */
    'fees_charged': string;
    /**
     * Fees given back: reversed when unpaid, returned when paid (#703).
     */
    'fees_reversed': string;
    /**
     * Of `fees_charged`, late-cancellation charges.
     */
    'late_cancel_fees': string;
    /**
     * \"2026-10\"; \"total\" for a totals row.
     */
    'month': string;
    'net_fee_revenue': string;
}
export interface ReservationRevenueReport {
    'amenities': Array<ReservationRevenueAmenity>;
    'from': string;
    'hoa_id': string;
    'to': string;
    'totals': ReservationRevenueMonth;
}
/**
 * Every reason the rule engine can refuse a booking.  A manager booking on a household\'s behalf, or approving, may override the **overridable** codes one by one, with a reason. The others can never be overridden.
 */

export const ReservationRuleCode = {
    AMENITY_INACTIVE: 'AMENITY_INACTIVE',
    AMENITY_NOT_BOOKABLE: 'AMENITY_NOT_BOOKABLE',
    HOME_NOT_IN_HOA: 'HOME_NOT_IN_HOA',
    INVALID_TIME_RANGE: 'INVALID_TIME_RANGE',
    IN_PAST: 'IN_PAST',
    SLOT_CONFLICT: 'SLOT_CONFLICT',
    QUOTE_CHANGED: 'QUOTE_CHANGED',
    DURATION_TOO_SHORT: 'DURATION_TOO_SHORT',
    DURATION_TOO_LONG: 'DURATION_TOO_LONG',
    OUTSIDE_OPEN_HOURS: 'OUTSIDE_OPEN_HOURS',
    CLOSED: 'CLOSED',
    TOO_SOON: 'TOO_SOON',
    TOO_FAR_AHEAD: 'TOO_FAR_AHEAD',
    PARTY_SIZE_EXCEEDED: 'PARTY_SIZE_EXCEEDED',
    HOME_DAILY_LIMIT: 'HOME_DAILY_LIMIT',
    HOME_WEEKLY_LIMIT: 'HOME_WEEKLY_LIMIT',
    HOME_MONTHLY_LIMIT: 'HOME_MONTHLY_LIMIT',
    HOME_ACTIVE_LIMIT: 'HOME_ACTIVE_LIMIT',
    ROLE_NOT_ELIGIBLE: 'ROLE_NOT_ELIGIBLE',
    NOT_IN_GOOD_STANDING: 'NOT_IN_GOOD_STANDING',
    AGREEMENT_REQUIRED: 'AGREEMENT_REQUIRED',
    AGREEMENT_VERSION_STALE: 'AGREEMENT_VERSION_STALE',
    CAPACITY_EXCEEDED: 'CAPACITY_EXCEEDED',
    NIGHTS_TOO_FEW: 'NIGHTS_TOO_FEW',
    NIGHTS_TOO_MANY: 'NIGHTS_TOO_MANY',
    VEHICLE_REQUIRED: 'VEHICLE_REQUIRED',
    OUT_OF_SEASON: 'OUT_OF_SEASON',
    HOME_WEEKLY_HOURS_LIMIT: 'HOME_WEEKLY_HOURS_LIMIT',
    HOME_MONTHLY_HOURS_LIMIT: 'HOME_MONTHLY_HOURS_LIMIT',
    GROUP_DAILY_LIMIT: 'GROUP_DAILY_LIMIT',
    GROUP_WEEKLY_LIMIT: 'GROUP_WEEKLY_LIMIT',
    GROUP_MONTHLY_LIMIT: 'GROUP_MONTHLY_LIMIT',
    GROUP_ACTIVE_LIMIT: 'GROUP_ACTIVE_LIMIT',
    HOME_SUSPENDED: 'HOME_SUSPENDED',
    DOCUMENTS_MISSING: 'DOCUMENTS_MISSING'
} as const;

export type ReservationRuleCode = typeof ReservationRuleCode[keyof typeof ReservationRuleCode];


/**
 * One reason a booking was refused.
 */
export interface ReservationRuleViolation {
    'code': ReservationRuleCode;
    'message': string;
    /**
     * Whether a manager may override it here.
     */
    'overridable': boolean;
}


/**
 * The 422 body when a booking breaks the rules.
 */
export interface ReservationRuleViolations {
    'error': string;
    'violations': Array<ReservationRuleViolation>;
}
/**
 * Cancel every occurrence that has not started yet.
 */
export interface ReservationSeriesCancel {
    /**
     * Required. Sent to the household.
     */
    'reason': string;
    /**
     * Reverse each fee (default).
     */
    'reverse_fee'?: boolean | null;
}
/**
 * A dry run of cancelling a series: no reason needed, nothing written.
 */
export interface ReservationSeriesCancelPreview {
    /**
     * As on the real cancel: reverse each fee (default).
     */
    'reverse_fee'?: boolean | null;
}
/**
 * One occurrence the series cancel would cancel, and what it would do to that occurrence\'s fee.
 */
export interface ReservationSeriesCancelPreviewOccurrence {
    'end_local': string;
    /**
     * Decimal string.
     */
    'fee_amount': string;
    /**
     * `free`, `reversed`, `kept` or `returned` — a staff cancel never charges a late fee. `charge` is what the household still owes.
     */
    'outcome': ReservationCancelOutcome;
    'reservation_id': string;
    'start_local': string;
    'status': ReservationStatus;
}


/**
 * What `POST /reservations/series/{id}/cancel` would do, without doing it: every occurrence not yet started, and the money in total.
 */
export interface ReservationSeriesCancelPreviewReturn {
    /**
     * Already cancelled: the real cancel would change nothing.
     */
    'already_cancelled': boolean;
    'occurrences': Array<ReservationSeriesCancelPreviewOccurrence>;
    'series_id': string;
    /**
     * Fees that would stand (reverse_fee false).
     */
    'total_kept': string;
    /**
     * Fees already paid that would be given back (reverse_fee true).
     */
    'total_returned': string;
    /**
     * Fees that would be reversed off the household\'s account.
     */
    'total_reversed': string;
}
/**
 * Book a recurring series on a household\'s behalf: one ordinary reservation per occurrence, each checked against every rule. An occurrence that breaks a rule the overrides do not cover is skipped and reported; the rest are booked.
 */
export interface ReservationSeriesCreate {
    'amenity_id': string;
    /**
     * Check every occurrence and report, writing nothing.
     */
    'dry_run'?: boolean | null;
    'duration_minutes': number;
    'frequency': ReservationSeriesFrequency;
    'home_id': string;
    'internal_notes'?: string | null;
    /**
     * `monthly_nth_weekday`: 1–4, or -1 for the last.
     */
    'month_week'?: number | null;
    'occurrence_count'?: number | null;
    'override_reason'?: string | null;
    'overrides'?: Array<ReservationRuleCode> | null;
    'party_size'?: number | null;
    'purpose'?: string | null;
    /**
     * Minutes from local midnight each occurrence starts.
     */
    'start_minute': number;
    'starts_on': string;
    /**
     * One of `until_on` and `occurrence_count` is required; at most 52 occurrences either way.
     */
    'until_on'?: string | null;
    /**
     * 0 = Sunday … 6 = Saturday. `monthly_nth_weekday` uses the first.
     */
    'weekdays': Array<number>;
}


export interface ReservationSeriesDetail {
    'reservations': Array<ReservationReturn>;
    'series': ReservationSeriesReturn;
}

export const ReservationSeriesFrequency = {
    WEEKLY: 'weekly',
    BIWEEKLY: 'biweekly',
    MONTHLY_NTH_WEEKDAY: 'monthly_nth_weekday'
} as const;

export type ReservationSeriesFrequency = typeof ReservationSeriesFrequency[keyof typeof ReservationSeriesFrequency];


/**
 * One generated occurrence and what became of it.
 */
export interface ReservationSeriesOccurrence {
    /**
     * Booked (or, on a dry run, would be).
     */
    'booked': boolean;
    'end_local': string;
    'fee': string;
    'reservation_id'?: string | null;
    'start_local': string;
    /**
     * Why it was skipped. Empty when booked.
     */
    'violations': Array<ReservationRuleViolation>;
}
export interface ReservationSeriesResult {
    'booked': number;
    'dry_run': boolean;
    'occurrences': Array<ReservationSeriesOccurrence>;
    /**
     * Null on a dry run, or when no occurrence could be booked.
     */
    'series'?: ReservationSeriesReturn | null;
    'skipped': number;
}
export interface ReservationSeriesReturn {
    'amenity_id': string;
    'cancel_reason'?: string | null;
    'cancelled_at'?: number | null;
    'created_by_user_id'?: string | null;
    'date_created'?: number | null;
    'duration_minutes': number;
    'frequency': ReservationSeriesFrequency;
    'hoa_id': string;
    'home_id': string;
    'id': string;
    'month_week'?: number | null;
    'occurrence_count'?: number | null;
    'party_size': number;
    'purpose'?: string | null;
    'start_minute': number;
    'starts_on': string;
    'until_on'?: string | null;
    'weekdays': Array<number>;
}


/**
 * A manager books on a household\'s behalf. Confirmed immediately — the manager is the approver — and the fee posts at once.
 */
export interface ReservationStaffCreate {
    /**
     * The agreement version the household accepted, if collected.
     */
    'agreement_id'?: string | null;
    'amenity_id': string;
    /**
     * Shown to the household.
     */
    'decision_note'?: string | null;
    'end_local': string;
    'home_id': string;
    'internal_notes'?: string | null;
    /**
     * Required whenever `overrides` is used.
     */
    'override_reason'?: string | null;
    /**
     * Rule codes to override. Each must be overridable, and every violation must be listed — there is no blanket force.
     */
    'overrides'?: Array<ReservationRuleCode> | null;
    /**
     * Default 1.
     */
    'party_size'?: number | null;
    'purpose'?: string | null;
    /**
     * The fee the manager was shown. A mismatch answers `QUOTE_CHANGED` rather than charging a different figure.
     */
    'quoted_fee'?: string | null;
    'start_local': string;
    'vehicle_description'?: string | null;
    /**
     * Required when the amenity `requires_vehicle` (guest parking).
     */
    'vehicle_plate'?: string | null;
    'vehicle_state'?: string | null;
}

export const ReservationStatus = {
    PENDING: 'pending',
    APPROVED: 'approved',
    DENIED: 'denied',
    CANCELLED: 'cancelled',
    EXPIRED: 'expired',
    COMPLETED: 'completed',
    NO_SHOW: 'no_show'
} as const;

export type ReservationStatus = typeof ReservationStatus[keyof typeof ReservationStatus];


/**
 * Bookings in a status, for one amenity over the report\'s range.
 */
export interface ReservationStatusCounts {
    'approved': number;
    'cancelled': number;
    'completed': number;
    'denied': number;
    'expired': number;
    'no_show': number;
    'pending': number;
}
export interface ReservationUtilizationAmenity {
    'amenity_id': string;
    /**
     * Clock hours of approved, completed and no-show bookings.
     */
    'booked_hours': number;
    'booking_mode': string;
    'bookings': ReservationStatusCounts;
    /**
     * Cancelled before / after the cancellation deadline.
     */
    'cancellations_early': number;
    'cancellations_late': number;
    /**
     * Bookings in the range that were checked in (phase 3).
     */
    'check_ins': number;
    /**
     * Every reservation charge less every reversal, as a decimal string. Late-cancellation charges included.
     */
    'fee_revenue': string;
    /**
     * Booked hours by local weekday (0 = Sunday) and hour of day: seven rows of 24.
     */
    'hours_by_weekday_hour': Array<Array<number>>;
    'name': string;
    'no_shows': number;
    /**
     * Clock hours the amenity was open in the range: its hours (by season) less closures. 24 a day where hours are not configured.
     */
    'open_hours': number;
    /**
     * `booked_hours / open_hours`, as a percentage. Null when it was never open. Shared-capacity bookings overlap, so this can pass 100 for them; it measures occupied slot-hours, not headcount.
     */
    'utilization_percent'?: number | null;
}
export interface ReservationUtilizationReport {
    'amenities': Array<ReservationUtilizationAmenity>;
    'from': string;
    'hoa_id': string;
    'timezone': string;
    'to': string;
}
export interface ReservationWaitlistList {
    'entries': Array<ReservationWaitlistReturn>;
    'total': number;
}
export interface ReservationWaitlistReturn {
    'amenity_id': string;
    'amenity_name': string;
    'date_created'?: number | null;
    'duration_minutes': number;
    'earliest_start_local': string;
    'hoa_id': string;
    /**
     * Staff reads only; empty on a household\'s.
     */
    'home_address': string;
    'home_id': string;
    'id': string;
    'latest_end_local': string;
    'local_date': string;
    /**
     * Seconds since the epoch.
     */
    'offer_expires_at'?: number | null;
    /**
     * The refundable deposit approval would hold, decimal string; null unless offered, or when the amenity holds none.
     */
    'offered_deposit'?: string | null;
    'offered_end_local'?: string | null;
    /**
     * What taking the offer costs, decimal string — the same quote a booking preview gives for the offered time and party. Null unless `status` is `offered`. Send it back as `quoted_fee` on accept.
     */
    'offered_fee'?: string | null;
    /**
     * How `offered_fee` is made up; empty unless offered.
     */
    'offered_fee_breakdown': Array<ReservationFeeLine>;
    'offered_start_local'?: string | null;
    'party_size': number;
    /**
     * 1-based place among those waiting for the amenity; null unless waiting.
     */
    'position'?: number | null;
    'purpose'?: string | null;
    /**
     * The booking an accepted offer became.
     */
    'reservation_id'?: string | null;
    'status': ReservationWaitlistStatus;
    'timezone': string;
}


/**
 * Where a waitlist entry stands.
 */

export const ReservationWaitlistStatus = {
    WAITING: 'waiting',
    OFFERED: 'offered',
    ACCEPTED: 'accepted',
    EXPIRED: 'expired',
    WITHDRAWN: 'withdrawn',
    DECLINED: 'declined'
} as const;

export type ReservationWaitlistStatus = typeof ReservationWaitlistStatus[keyof typeof ReservationWaitlistStatus];


export interface RevaluationRequest {
    'as_of': string;
    /**
     * The fund the change belongs to. Not derivable from the account — fund lives on the line, and an account may be hit in more than one.
     */
    'fund_id': string;
    /**
     * The investment account being marked to market.
     */
    'gl_account_id': string;
    'statement_balance': string;
}
export interface RevaluationReturn {
    'change': string;
    /**
     * Null when the statement matched the books and nothing needed posting — a month the market did not move is not an error.
     */
    'entry'?: JournalEntryReturn | null;
}
/**
 * Reverse an entry. The ledger is append-only, so this writes a new opposing entry rather than touching the original.
 */
export interface ReverseRequest {
    /**
     * Accounting date of the reversal. Defaults to today.
     */
    'effective_date'?: string | null;
    'reason': string;
}
/**
 * A condition that needs a person to decide something.  These are **not** an inbox: each is a predicate over state that exists anyway, and each resolves through a real action with a ledger consequence rather than by being dismissed. See `docs/DUES_DESIGN.md` §2.10.
 */

export const ReviewCondition = {
    OVERPAYMENT: 'overpayment',
    UNMATCHED_PAYMENT: 'unmatched_payment',
    PAYMENT_ON_RETIRED_TENURE: 'payment_on_retired_tenure',
    RETURNED_PAYMENT: 'returned_payment',
    BALANCE_DRIFT: 'balance_drift',
    RUN_PENDING: 'run_pending',
    DELINQUENT_PAST_THRESHOLD: 'delinquent_past_threshold',
    RETIRED_TENURE_WITH_BALANCE: 'retired_tenure_with_balance',
    REMITTANCE_UNMATCHED: 'remittance_unmatched',
    RECEIVABLE_DRIFT: 'receivable_drift',
    PREPAID_DRIFT: 'prepaid_drift'
} as const;

export type ReviewCondition = typeof ReviewCondition[keyof typeof ReviewCondition];


/**
 * One thing waiting on a person.
 */
export interface ReviewItem {
    /**
     * The money at stake, where the condition has an amount.
     */
    'amount'?: string | null;
    'condition': ReviewCondition;
    'entity_id': string;
    /**
     * What to load to act on it — `payment`, `assessment_run`, `collections_case`, `ownership_tenure` or `home`.
     */
    'entity_type': string;
    'home_id'?: string | null;
    'home_label'?: string | null;
    /**
     * When it became true, as far as the record shows.
     */
    'since'?: string | null;
    /**
     * A sentence a manager can act on without opening the record.
     */
    'summary': string;
}


/**
 * Everything in an HOA that needs a human, with a count per condition.
 */
export interface ReviewList {
    /**
     * Every condition appears, including the ones at zero — an empty queue has to be visibly empty rather than missing.
     */
    'counts': { [key: string]: number; };
    'hoa_id': string;
    'items': Array<ReviewItem>;
    'page': number;
    'per_page': number;
    'total': number;
}
export interface Roadmap {
    /**
     * When the snapshot was last synced from GitHub, `YYYY-MM-DD`.
     */
    'generated_at': string;
    'items': Array<RoadmapItem>;
    /**
     * `owner/name` on GitHub, for building issue links.
     */
    'repo': string;
}
export interface RoadmapItem {
    /**
     * `YYYY-MM-DD`: when it closed, or, for `current` and `planned`, the estimate. Absent for `exploring`.
     */
    'date'?: string | null;
    'labels': Array<string>;
    'lane': RoadmapLane;
    /**
     * Why an estimate is what it is.
     */
    'note'?: string | null;
    /**
     * The GitHub issue number.
     */
    'number': number;
    /**
     * When the issue was opened, `YYYY-MM-DD`.
     */
    'opened': string;
    'status': RoadmapStatus;
    'title': string;
}


/**
 * Which lane of the roadmap an issue sits in.
 */

export const RoadmapLane = {
    BANK: 'bank',
    INFRA: 'infra',
    PRODUCT: 'product'
} as const;

export type RoadmapLane = typeof RoadmapLane[keyof typeof RoadmapLane];


/**
 * Where an issue stands. `shipped` and `archived` come from GitHub (a closed issue); the other three are set by hand on open issues.
 */

export const RoadmapStatus = {
    SHIPPED: 'shipped',
    CURRENT: 'current',
    PLANNED: 'planned',
    EXPLORING: 'exploring',
    ARCHIVED: 'archived'
} as const;

export type RoadmapStatus = typeof RoadmapStatus[keyof typeof RoadmapStatus];


/**
 * How a money value is rounded to cents.  Some jurisdictions require consumer charges to round in the consumer\'s favour, which is `down`.
 */

export const RoundingMode = {
    HALF_UP: 'half_up',
    HALF_EVEN: 'half_even',
    DOWN: 'down',
    UP: 'up'
} as const;

export type RoundingMode = typeof RoundingMode[keyof typeof RoundingMode];


/**
 * A run\'s optional as-of date, for backdating or replaying.
 */
export interface RunAsOfRequest {
    'as_of'?: string | null;
}
/**
 * A run with the lines a reviewer needs to see before posting it.
 */
export interface RunDetail {
    'charge_count': number;
    'due_date': string;
    'generated_by'?: string;
    /**
     * Set when an unattended run is being held for review.
     */
    'held_reason'?: string;
    'hoa_id': string;
    'id': string;
    'period_end': string;
    'period_start': string;
    'posted_at'?: number;
    'posted_by'?: string;
    'schedule_id': string;
    'status': RunStatus;
    'total_amount': string;
    'unattended': boolean;
    /**
     * Movement against the previous posted period, in percent.
     */
    'variance_percent'?: string;
    'lines': Array<RunLineReturn>;
}


export interface RunLineReturn {
    'amount': string;
    /**
     * The full-period amount, before any proration.
     */
    'base_amount': string;
    'description': string;
    'home_id': string;
    /**
     * The home\'s street address, resolved at read time. A run is reviewed line by line before it posts, and an id is not something a reviewer can check.
     */
    'home_label'?: string | null;
    'id': string;
    'ledger_entry_id'?: string | null;
    'proration_denominator'?: number | null;
    'proration_numerator'?: number | null;
}
/**
 * One page of billing runs, newest period first.
 */
export interface RunList {
    'page': number;
    'per_page': number;
    'runs': Array<RunReturn>;
    'total': number;
}
export interface RunReturn {
    'charge_count': number;
    'due_date': string;
    'generated_by'?: string | null;
    /**
     * Set when an unattended run is being held for review.
     */
    'held_reason'?: string | null;
    'hoa_id': string;
    'id': string;
    'period_end': string;
    'period_start': string;
    'posted_at'?: number | null;
    'posted_by'?: string | null;
    'schedule_id': string;
    'status': RunStatus;
    'total_amount': string;
    'unattended': boolean;
    /**
     * Movement against the previous posted period, in percent.
     */
    'variance_percent'?: string | null;
}


/**
 * Where an assessment run is in its life.
 */

export const RunStatus = {
    PENDING: 'pending',
    POSTED: 'posted',
    REVERSED: 'reversed'
} as const;

export type RunStatus = typeof RunStatus[keyof typeof RunStatus];


/**
 * Create a dues schedule. `amount` is a positive decimal string.
 */
export interface ScheduleCreate {
    'amount': string;
    /**
     * Required when `due_rule` is `day_of_month`.
     */
    'due_day'?: number | null;
    'due_offset_days'?: number;
    'due_rule': DueRule;
    'effective_to'?: string | null;
    /**
     * Anchors the period grid. Every period steps from here, so a month-end clamp never drifts the whole schedule forward.
     */
    'first_period_start': string;
    'frequency': Frequency;
    'name': string;
    /**
     * Defaults to `draft` — a schedule bills nothing until activated.
     */
    'status'?: ScheduleStatus | null;
}


/**
 * An HOA\'s dues schedules. Bounded — an association has a handful.
 */
export interface ScheduleList {
    'schedules': Array<ScheduleReturn>;
    'total': number;
}
export interface ScheduleReturn {
    'amount': string;
    'created_by'?: string | null;
    'due_day'?: number | null;
    'due_offset_days': number;
    'due_rule': DueRule;
    'effective_to'?: string | null;
    'first_period_start': string;
    'frequency': Frequency;
    'hoa_id': string;
    'id': string;
    'name': string;
    'status': ScheduleStatus;
}


/**
 * Whether a dues schedule is billing.
 */

export const ScheduleStatus = {
    DRAFT: 'draft',
    ACTIVE: 'active',
    ENDED: 'ended'
} as const;

export type ScheduleStatus = typeof ScheduleStatus[keyof typeof ScheduleStatus];


export interface ScheduleUpdate {
    'amount'?: string | null;
    'due_day'?: number | null;
    'due_offset_days'?: number | null;
    'due_rule'?: DueRule | null;
    'effective_to'?: string | null;
    'name'?: string | null;
    'status'?: ScheduleStatus | null;
}


/**
 * Which way a ledger page runs.
 */

export const SortOrder = {
    ASC: 'asc',
    DESC: 'desc'
} as const;

export type SortOrder = typeof SortOrder[keyof typeof SortOrder];


/**
 * Point a reconciliation at the institution\'s own statement.
 */
export interface StatementAttach {
    /**
     * The provider\'s statement id, from the candidate list. `null` or empty detaches — attaching the wrong statement has to be undoable.
     */
    'statement_id'?: string | null;
}
/**
 * Available statements for one account.
 */
export interface StatementList {
    'account_number': string;
    'statements': Array<StatementReturn>;
    'total': number;
}
/**
 * An available statement (metadata) as returned live from the bank.
 */
export interface StatementReturn {
    'account_number': string;
    'available_date'?: string | null;
    'file_type'?: string | null;
    'statement_date'?: string | null;
    'statement_id': string;
    'statement_type'?: string | null;
}
/**
 * The result of both tie-outs.
 */
export interface SubledgerTieOut {
    /**
     * False when the association has no accounting periods. Every figure below is then zero and means nothing — the books are not open, so there is nothing to tie out.
     */
    'books_are_open': boolean;
    /**
     * When the check ran — **not a cutoff**. Both sides are compared in full: a home\'s balance is its whole history and so is the ledger\'s receivable, so filtering either by date would compare two different questions and call the difference drift.
     */
    'checked_on': string;
    'hoa_id': string;
    /**
     * Homes whose own post-cutover activity did not reach the ledger intact. Empty when the disagreement is only at the association level, which points at the opening entry rather than at any home.
     */
    'homes_in_drift': Array<HomeDrift>;
    /**
     * The GL Prepaid Assessments balance, stated positive.
     */
    'ledger_prepaid': string;
    /**
     * The GL Accounts Receivable balance.
     */
    'ledger_receivable': string;
    /**
     * True while the opening period is still open. Every home\'s pre-cutover balance is then genuinely absent from the ledger, so the totals disagree for a reason that is not a bug — it is an onboarding step nobody has done yet.  An association with nothing to bring forward settles this by closing the opening period, not by posting an entry of zero.
     */
    'opening_balances_pending': boolean;
    'prepaid_drift': string;
    'receivable_drift': string;
    /**
     * SUM of per-home negative balances, stated positive.
     */
    'subledger_prepaid': string;
    /**
     * SUM of per-home positive balances.
     */
    'subledger_receivable': string;
}
/**
 * A closing balance proposed for a statement, with its derivation.  **Suggested, not pulled.** `statement/find` returns metadata only and the closing figure lives inside the PDF; the only balance the API offers is a *live* one, and posted transactions carry no running balance. So the live figure is rolled back over the stored feed to the statement date.  Deliberately not authoritative. The roll-back trusts the feed to be complete, and testing whether the feed is complete is what reconciling *is* — making this the target would be circular. As a figure the accountant confirms against the PDF in the next pane, it saves the typing without making the claim.
 */
export interface SuggestedBalance {
    'gl_account_id': string;
    /**
     * The live figure it was rolled back from.
     */
    'live_balance'?: string | null;
    'live_balance_as_of'?: string | null;
    /**
     * What the roll-back removed. Shown so the derivation can be checked rather than trusted.
     */
    'rolled_back_count': number;
    'rolled_back_total'?: string | null;
    'statement_date': string;
    /**
     * The proposal. **`None` whenever it cannot be derived honestly.** A wrong suggestion is worse than none here, because the UI accepts it with one click.
     */
    'suggested_balance'?: string | null;
    /**
     * Why there is no figure, in words a manager can act on. Present exactly when `suggested_balance` is absent.
     */
    'unavailable_reason'?: string | null;
}
/**
 * What a turnover did — including the figure an estoppel certificate would state, which is why it comes from the ledger rather than from anybody\'s typing.
 */
export interface TenureCloseReturn {
    'balance_policy': TurnoverBalancePolicy;
    /**
     * True when the outstanding balance moved to the new tenure.
     */
    'carried_forward': boolean;
    'closing_date': string;
    'opened': TenureReturn;
    'outstanding_at_closing': string;
    'retired': TenureReturn;
}


/**
 * A home\'s ownership history, oldest first. Bounded by how often the property has changed hands.
 */
export interface TenureList {
    'tenures': Array<TenureReturn>;
    'total': number;
}
export interface TenureMemberCreate {
    'include_on_payer_file'?: boolean | null;
    'is_billing_payer'?: boolean;
    'member_id': string;
    'role'?: MemberRole | null;
}


/**
 * Everyone on one account.
 */
export interface TenureMemberList {
    'members': Array<TenureMemberReturn>;
    'total': number;
}
export interface TenureMemberReturn {
    'id': string;
    'include_on_payer_file': boolean;
    'is_billing_payer': boolean;
    'member_id': string;
    'role': MemberRole;
    'tenure_id': string;
}


export interface TenureReturn {
    /**
     * The 16-digit payer account number for this tenure.
     */
    'account_number': string;
    'closing_reason'?: ClosingReason | null;
    'ended_on'?: string | null;
    'hoa_id': string;
    /**
     * `true` forces the payer file\'s HOLD, `false` suppresses it, absent defers to collections policy.
     */
    'hold_override'?: boolean | null;
    'home_id': string;
    'id': string;
    'started_on'?: string | null;
    'status': TenureStatus;
    'tenure_seq': number;
}


/**
 * Where an ownership tenure is in its life.
 */

export const TenureStatus = {
    PENDING: 'pending',
    ACTIVE: 'active',
    RETIRED: 'retired'
} as const;

export type TenureStatus = typeof TenureStatus[keyof typeof TenureStatus];


export interface TokenResponse {
    'access_token': string;
    'expires_at': number;
    'expires_in': number;
    'refresh_expires_at': number;
    'refresh_expires_in': number;
    'refresh_token': string;
}

export const TokenType = {
    ACCESS: 'access',
    REFRESH: 'refresh'
} as const;

export type TokenType = typeof TokenType[keyof typeof TokenType];


/**
 * Posted transactions for one account, served from our own copy.  The three fields beyond `transactions` exist because **an empty list would otherwise mean three different things** — nobody is watching this account, nobody has pulled it yet, or it genuinely has no activity in the range. Those have opposite remedies, and a client that cannot tell them apart shows \"no transactions\" for all three.
 */
export interface TransactionList {
    'account_number': string;
    /**
     * The earliest day we believe is complete. A `from` earlier than this is not covered by our data, however many rows come back — the bank only keeps about 90 days, so history before our first pull does not exist anywhere we can reach.
     */
    'covered_from'?: string | null;
    /**
     * When the account was last successfully pulled. **Null means never synced**: enabled, but no run has reached it yet.
     */
    'last_synced_at'?: string | null;
    'total': number;
    /**
     * Whether the daily pull covers this account at all. **False means nobody enabled it** — a setting somebody can change, not a fact about the account.
     */
    'tracking_enabled': boolean;
    'transactions': Array<TransactionReturn>;
}
/**
 * A posted (settled) transaction as returned live from the bank.
 */
export interface TransactionReturn {
    'account_number': string;
    'account_type'?: string | null;
    'additional_description'?: string | null;
    'additional_info'?: string | null;
    'additional_info2'?: string | null;
    /**
     * A **decimal string** (`\"1234.56\"`), not a float.  Served from our stored `NUMERIC(12,2)` and rendered through the same two-decimal formatter every other money field in the API uses, so a client never has to guess whether a figure is dollars or cents. Was an `f64` while this endpoint proxied the bank directly; once the value is stored, putting it back through a float would undo the point of storing it.  **Null means the bank sent a value we could not represent** — the row is kept, the original is preserved server-side, and no number is invented. Rare enough to be an alerting condition.
     */
    'amount'?: string | null;
    'check_effective_date'?: string | null;
    'check_number'?: string | null;
    'check_processed_date'?: string | null;
    'description'?: string | null;
    /**
     * `\"D\"` = debit, `\"C\"` = credit.
     */
    'direction'?: string | null;
    'source_id'?: string | null;
    'source_trace_number'?: string | null;
    'trancode_id'?: string | null;
    'transaction_date'?: string | null;
    'transaction_id': string;
}
/**
 * How often a transfer schedule fires.  Deliberately **not** the billing [`Frequency`], which has no `daily` or `weekly` and expresses every period in whole months. Widening a well-tested billing type with two variants billing does not have, so that one caller could share thirty lines of date arithmetic, is a worse trade than restating the arithmetic and testing it here.  [`Frequency`]: crate::api::v1::billing::models::Frequency `ToSchema` is derived here rather than on a copy in the API layer. The alternative is two lists of the same four values that drift the first time one gains a variant, and the generated TypeScript client is materially better for having the union type — a dropdown cannot offer a frequency the backend does not accept.
 */

export const TransferFrequency = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly'
} as const;

export type TransferFrequency = typeof TransferFrequency[keyof typeof TransferFrequency];


export interface TransferList {
    'total': number;
    'transfers': Array<TransferReturn>;
}
/**
 * Request to preview a transfer between two of an HOA\'s accounts. `amount` is a decimal string (e.g. `\"1.50\"`) — never a float.
 */
export interface TransferPreviewRequest {
    /**
     * Amount as a decimal string, e.g. `\"1.50\"`.
     */
    'amount': string;
    /**
     * `hoa_bank_accounts` id of the destination (money in).
     */
    'dest_hoa_account_id': string;
    /**
     * `hoa_bank_accounts` id of the source (money out).
     */
    'source_hoa_account_id': string;
}
/**
 * Result of a preview: the created (unconfirmed) transfer plus the live balance context the caller confirms against. `transfer_id` is the handle passed to the confirm endpoint.
 */
export interface TransferPreviewReturn {
    'amount': string;
    'dest_account_number': string;
    'dest_purpose'?: string | null;
    /**
     * The configured per-transfer cap, for display.
     */
    'max_amount': string;
    'source_account_number': string;
    'source_current_balance'?: number | null;
    'source_projected_balance'?: number | null;
    'source_purpose'?: string | null;
    'status': TransferStatus;
    'transfer_id': string;
}


/**
 * A transfer record (also the money-movement audit entry).
 */
export interface TransferReturn {
    'amount': string;
    'bank_submit_date'?: string | null;
    'bank_transfer_id'?: string | null;
    'confirmed_by'?: string | null;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'dest_account_number': string;
    'error_code'?: string | null;
    'error_message'?: string | null;
    'hoa_id': string;
    'id': string;
    'initiated_by'?: string | null;
    /**
     * The day the schedule was due, not the day the row was written.
     */
    'occurrence_date'?: string | null;
    /**
     * The schedule this transfer is an occurrence of. **Null for every manual transfer.**  Not decoration. A `pending` transfer means different things depending on this field: a manual one is waiting on a person, a scheduled one **will not be retried** and needs somebody to go and look. A reader that cannot tell them apart cannot act on either.
     */
    'schedule_id'?: string | null;
    'source_account_number': string;
    'status': string;
    'transfer_reference': string;
}
/**
 * Request to preview a transfer schedule.  `amount` is a decimal string (e.g. `\"150.00\"`) — never a float. The day fields are validated **against the frequency**: a field that has no meaning for the chosen frequency is rejected rather than ignored, because `daily` plus `day_of_month` has two readings and no way to choose between them.
 */
export interface TransferScheduleCreate {
    /**
     * Amount as a decimal string, e.g. `\"150.00\"`.
     */
    'amount': string;
    /**
     * Which month of the calendar quarter, 1..=3. Required for `quarterly`, refused otherwise.
     */
    'anchor_month'?: number | null;
    /**
     * 1..=31, **clamped** to the month\'s length. Required for `monthly` and `quarterly`, refused otherwise.
     */
    'day_of_month'?: number | null;
    /**
     * 1 = Monday .. 7 = Sunday. Required for `weekly`, refused otherwise.
     */
    'day_of_week'?: number | null;
    /**
     * `hoa_bank_accounts` id of the destination (money in).
     */
    'dest_hoa_account_id': string;
    /**
     * A draft this preview **replaces**, from an earlier preview in the same sitting.  Preview persists a row, so without this every call creates another one — and a person who previews, goes back to change the amount, and previews again leaves an orphan behind. Reported from the UI on 2026-09-01: one journey, two schedules, one of them never confirmed.  Passing the previous draft\'s id updates it in place instead. The server still checks it is a draft on this association, so this can only ever rewrite something the caller just created.
     */
    'draft_id'?: string | null;
    'ends_on'?: string | null;
    'frequency': TransferFrequency;
    /**
     * What a person calls it, e.g. \"Monthly reserve funding\".
     */
    'name': string;
    /**
     * `hoa_bank_accounts` id of the source (money out).
     */
    'source_hoa_account_id': string;
    /**
     * The first day this may fire. **Not an anchor** — a monthly schedule starting on the 8th with `day_of_month = 1` first fires on the 1st of the following month.
     */
    'starts_on': string;
}


export interface TransferScheduleList {
    'schedules': Array<TransferScheduleReturn>;
    'total': number;
}
/**
 * Result of a schedule preview: the created **draft** plus everything a person needs to decide whether to confirm it.  `next_occurrences` is the part that earns its keep. It is the only place *\"I meant the 1st of the quarter, not the 1st of every month\"* is visible before it becomes twelve transfers instead of four.
 */
export interface TransferSchedulePreviewReturn {
    'amount': string;
    'dest_account_number': string;
    'dest_purpose'?: string | null;
    /**
     * The configured per-transfer cap, for display.
     */
    'max_amount': string;
    'name': string;
    /**
     * The next five days this schedule would fire.
     */
    'next_occurrences': Array<string>;
    'schedule_id': string;
    'source_account_number': string;
    /**
     * Live balance on the source account **right now**, for context.  Deliberately **not** a gate at this point: a schedule created on the 20th for the 1st would be refused on a balance that has nothing to do with the day it fires. The balance check that matters runs on the morning of each occurrence, where it is about the money actually being moved.
     */
    'source_current_balance'?: number | null;
    'source_purpose'?: string | null;
    'status': TransferScheduleStatus;
}


/**
 * A stored transfer schedule.
 */
export interface TransferScheduleReturn {
    'amount': string;
    'anchor_month'?: number | null;
    'bank_connection_id': string;
    'confirmed_by'?: string | null;
    'created_by'?: string | null;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'day_of_month'?: number | null;
    'day_of_week'?: number | null;
    'dest_account_number'?: string | null;
    'dest_hoa_account_id': string;
    'ends_on'?: string | null;
    'frequency': TransferFrequency;
    'hoa_id': string;
    'id': string;
    'name': string;
    /**
     * The next days it will fire, from today. Empty for a schedule that has ended or will never fire again — which is a different fact from a schedule that is paused, and the UI shows them differently.
     */
    'next_occurrences': Array<string>;
    /**
     * The account numbers the ids currently resolve to.  Resolved on read rather than stored, for the same reason the schedule holds ids: a number turns over with ownership, so the pair a schedule names today is not necessarily the pair it named last year. Absent if an account has since been removed — which is itself worth showing, because that schedule will not fire.
     */
    'source_account_number'?: string | null;
    'source_hoa_account_id': string;
    'starts_on': string;
    'status': TransferScheduleStatus;
}


/**
 * Where a schedule is in its life.  Named `TransferScheduleStatus` rather than `ScheduleStatus` because billing already has the latter, and [two identically-named types silently overwrite each other in the OpenAPI spec](../../../../knowledge/notes/gotcha-utoipa-schema-name-collision.md). Cheaper to avoid now than to discover when the API arrives.
 */

export const TransferScheduleStatus = {
    DRAFT: 'draft',
    ACTIVE: 'active',
    PAUSED: 'paused',
    ENDED: 'ended'
} as const;

export type TransferScheduleStatus = typeof TransferScheduleStatus[keyof typeof TransferScheduleStatus];


/**
 * Fields a schedule may be changed by. Every one is optional; omitted fields are left alone.  Editing does not touch occurrences already sent, and takes effect from the next evaluation. Deliberately not effective-dated, unlike `home_assessment_overrides`: a transfer is never reconstructed from its schedule, because the `bank_transfers` row permanently records the exact amount that moved.
 */
export interface TransferScheduleUpdate {
    'amount'?: string | null;
    'anchor_month'?: number | null;
    'day_of_month'?: number | null;
    /**
     * Present-and-null clears the field, which is how a frequency change drops the day that no longer applies.
     */
    'day_of_week'?: number | null;
    'ends_on'?: string | null;
    'frequency'?: TransferFrequency | null;
    'name'?: string | null;
    'starts_on'?: string | null;
}


/**
 * Lifecycle of a transfer record.
 */

export const TransferStatus = {
    AWAITING_CONFIRMATION: 'awaiting_confirmation',
    PENDING: 'pending',
    COMPLETED: 'completed',
    FAILED: 'failed'
} as const;

export type TransferStatus = typeof TransferStatus[keyof typeof TransferStatus];


export interface TrialBalance {
    /**
     * Debits equal credits. False is a defect in the posting service, not in the association\'s bookkeeping — every entry is checked at write, so this can only be false if something bypassed it.
     */
    'balanced': boolean;
    'fiscal_year': number;
    'hoa_id': string;
    /**
     * True while the opening period is still open. The opening-balance column is then incomplete, and the report says so rather than looking authoritative.
     */
    'opening_balances_pending': boolean;
    'period_end': string;
    'period_number': number;
    'period_start': string;
    'period_status': string;
    'rows': Array<TrialBalanceRow>;
    'total_credits': string;
    'total_debits': string;
}
/**
 * One posting behind a trial balance row.  The packet specifies the trial balance **with full transaction detail**, which is what makes it the report an accountant actually works from: every other report states a figure, this one shows the entries that produced it.
 */
export interface TrialBalanceEntry {
    'credit': string;
    'debit': string;
    'description': string;
    'effective_date': string;
    'entry_id': string;
    'fund_id': string;
    'home_id'?: string | null;
    /**
     * `opening` sorts and reads apart from period activity on purpose — a brought-forward balance is not a transaction.
     */
    'source_type': string;
}
export interface TrialBalanceRow {
    'account_id': string;
    'account_name': string;
    'account_number': string;
    'account_type': string;
    'ending_balance': string;
    'entries': Array<TrialBalanceEntry>;
    /**
     * Signed, positive as a debit — as every figure on this report is, because a trial balance is where the signs are the point.
     */
    'opening_balance': string;
    'period_credits': string;
    'period_debits': string;
}
/**
 * What happens to an unpaid balance when a home changes hands (`docs/DUES_DESIGN.md` §2.7).
 */

export const TurnoverBalancePolicy = {
    SETTLE_AT_CLOSING: 'settle_at_closing',
    CARRY_FORWARD: 'carry_forward'
} as const;

export type TurnoverBalancePolicy = typeof TurnoverBalancePolicy[keyof typeof TurnoverBalancePolicy];


export interface UnclearedBookItem {
    /**
     * Signed: positive is a deposit we recorded, negative an item we paid out.
     */
    'amount': string;
    'description': string;
    'effective_date': string;
    'journal_entry_id': string;
    'journal_line_id': string;
    'source_type': string;
}
/**
 * A movement at the bank that our books do not explain.  **The most interesting list on this report.** An uncleared cheque is ordinary; money on a statement that we have no record of is either a posting somebody forgot or something happening to the account that nobody authorised.
 */
export interface UnexplainedBankItem {
    'amount': string;
    'bank_transaction_id': string;
    'check_number'?: string | null;
    'description'?: string | null;
    /**
     * What the institution\'s own wording suggests this is, when it suggests anything.  **A hint for a dropdown, not a decision.** Nothing posts on the strength of it: the string is the bank\'s, we have no corpus of real ones to calibrate against, and an unattended job that posted a journal entry off a guess would err toward the one outcome that is not reversible.
     */
    'looks_like'?: BankItemKind | null;
    'posted_on'?: string | null;
}


/**
 * A connection that did not answer, and why.  Reported rather than swallowed. These accounts are a **picker** — somebody chooses one to assign to an association — and a bank whose accounts are quietly missing is how the wrong account gets picked. An empty list is the healthy case.
 */
export interface UnreachableConnection {
    'connection_id': string;
    'label'?: string | null;
    'reason': string;
}
/**
 * Schema for creating a new user
 */
export interface UserCreate {
    'company_id': string;
    'email': string;
    'full_name': string;
    'password': string;
    'user_type'?: string | null;
}
/**
 * An administrative correction to an address WorkOS owns.  Separate from [`UserUpdate`] on purpose. `update_user` refuses to write `users.email` for a WorkOS-backed row, and this is not a way around that refusal — it is a different operation, against a different system, with its own authorization. Keeping it a separate request body means nobody can reach it by adding a field to an ordinary update.
 */
export interface UserEmailCorrection {
    'email': string;
}
export interface UserLogin {
    'email': string;
    'password': string;
}
/**
 * Schema returned when retrieving a user
 */
export interface UserReturn {
    'company_id'?: string | null;
    'created_at': number;
    'email': string;
    'full_name': string;
    'id': string;
    'is_active'?: boolean | null;
    'updated_at': number;
    'user_type': string;
}

export const UserType = {
    BOARD_MEMBER: 'board_member',
    MANAGER: 'manager',
    OWNER: 'owner',
    ADMIN: 'admin',
    SUPER_ADMIN: 'super_admin'
} as const;

export type UserType = typeof UserType[keyof typeof UserType];


/**
 * Schema for updating an existing user
 */
export interface UserUpdate {
    'email'?: string | null;
    'full_name'?: string | null;
    'user_type'?: string | null;
}
/**
 * Schema for creating a new vendor
 */
export interface VendorCreate {
    'address'?: string | null;
    'city'?: string | null;
    'contact_name'?: string | null;
    'description'?: string | null;
    'email'?: string | null;
    'hoa_id': string;
    'insurance_expiry'?: number | null;
    'insurance_policy_number'?: string | null;
    'name': string;
    'notes'?: string | null;
    'payment_terms'?: string | null;
    'phone'?: string | null;
    'service_types'?: string | null;
    'state'?: string | null;
    'status'?: string;
    'tax_id'?: string | null;
    'website'?: string | null;
    'zip'?: string | null;
}
/**
 * Schema for returning a list of vendors with total count
 */
export interface VendorList {
    'total': number;
    'vendors': Array<VendorReturn>;
}
/**
 * Ask the association to pay a vendor by ACH: `token` then `add`. The item then waits in the connection\'s settle queue; nothing moves until a person settles it with an RSA code.
 */
export interface VendorPaymentCreate {
    /**
     * Decimal string, e.g. `\"125.00\"`.
     */
    'amount': string;
    /**
     * On or after today in the bank\'s calendar; the bank moves it to its next processing day.
     */
    'effective_date': string;
    /**
     * The expense the payment is for. Required: a payment the ledger cannot post is refused rather than recorded off-book.
     */
    'expense_gl_account_id': string;
    'fund_id'?: string | null;
    /**
     * The association\'s account to pay from (`hoa_bank_accounts.id`).
     */
    'hoa_bank_account_id': string;
    /**
     * What the bank prints as the description. Required by the bank.
     */
    'memo'?: string | null;
    'vendor_id': string;
}
/**
 * Schema returned when retrieving a single vendor or list of vendors
 */
export interface VendorReturn {
    'address'?: string | null;
    'city'?: string | null;
    'contact_name'?: string | null;
    'created_by': string;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'description'?: string | null;
    'email'?: string | null;
    'hoa_id': string;
    'id': string;
    'insurance_expiry'?: string | null;
    'insurance_policy_number'?: string | null;
    'name': string;
    'notes'?: string | null;
    'payment_terms'?: string | null;
    'phone'?: string | null;
    'service_types'?: string | null;
    'state'?: string | null;
    'status': string;
    'tax_id'?: string | null;
    'updated_by'?: string | null;
    'website'?: string | null;
    'zip'?: string | null;
}

export const VendorStatus = {
    ACTIVE: 'active',
    INACTIVE: 'inactive'
} as const;

export type VendorStatus = typeof VendorStatus[keyof typeof VendorStatus];


/**
 * Schema for updating an existing vendor
 */
export interface VendorUpdate {
    'address'?: string | null;
    'city'?: string | null;
    'contact_name'?: string | null;
    'description'?: string | null;
    'email'?: string | null;
    'insurance_expiry'?: number | null;
    'insurance_policy_number'?: string | null;
    'name'?: string | null;
    'notes'?: string | null;
    'payment_terms'?: string | null;
    'phone'?: string | null;
    'service_types'?: string | null;
    'state'?: string | null;
    'status'?: string | null;
    'tax_id'?: string | null;
    'website'?: string | null;
    'zip'?: string | null;
}
export interface VerifyRequest {
    'token': string;
}
export interface VerifyResponse {
    'expires_at'?: number | null;
    'user_id'?: string | null;
    'user_type'?: string | null;
    'valid': boolean;
}
/**
 * Schema for creating a new violation
 */
export interface ViolationCreate {
    'date_of_violation': number;
    'description': string;
    /**
     * **Deprecated — use `POST /violations/{id}/attachments`.**
     * @deprecated
     */
    'document_url'?: string | null;
    'due_date'?: number | null;
    'fine_amount'?: string | null;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'home_id': string;
    /**
     * **Deprecated — use `POST /violations/{id}/attachments`.**  Still accepted so existing clients do not break, and still written verbatim. It is not validated, not fetched, and not related to anything the file service stores.
     * @deprecated
     */
    'image_url'?: string | null;
    'message': string;
    /**
     * Whether to send notification to home members (default: true)
     */
    'send_notification'?: boolean;
    'severity': string;
    'status'?: string;
    'violation_type': string;
}
/**
 * Schema for returning a list of violations with total count
 */
export interface ViolationList {
    'total': number;
    'violations': Array<ViolationReturn>;
}
/**
 * Schema returned when retrieving a single violation or list of violations
 */
export interface ViolationReturn {
    /**
     * Photos and documents attached to this violation.  Returned inline rather than behind a `GET .../attachments`, because \"pulling violation data should return image data\" is literally what #126 asks for — one round trip, one fewer endpoint to authorize.  Empty from the plain `From<Model>` conversion; the service fills it, exactly as `fine` works. A code path that builds a `ViolationReturn` with `.into()` and skips the service will silently return none.
     */
    'attachments'?: Array<AttachmentReturn>;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'created_by': string;
    'date_created'?: string | null;
    'date_of_violation': string;
    'date_updated'?: string | null;
    'description': string;
    /**
     * **Deprecated — read `attachments` instead.** See `image_url`.
     * @deprecated
     */
    'document_url'?: string | null;
    'due_date'?: string | null;
    /**
     * The fine, read from the ledger. `None` when this violation carries no fine.  Replaced `fine_amount` and `fine_paid_date` in phase 1b: a fine is an ordinary charge, so what is owed and what has been paid come from the same place as everything else a homeowner owes.
     */
    'fine'?: FineReturn | null;
    'follow_up_date'?: string | null;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'home_id': string;
    /**
     * ✅ CHANGED: From i32 to String
     */
    'id': string;
    /**
     * **Deprecated — read `attachments` instead.**  An opaque string with no relationship to the file service: there has never been an upload path that sets it, and nothing checks that what it points at exists or is ours to serve. Superseded by `file_attachments` in #126/#127.  Still returned because rows written before that still carry values, and there is no backfill: these may point at externally hosted URLs, and `files` needs actual bytes. A migration cannot fetch them.
     * @deprecated
     */
    'image_url'?: string | null;
    'message': string;
    'notification_sent_date'?: string | null;
    'resolution_date'?: string | null;
    /**
     * The user who closed the violation. Absent while it is open, and on violations resolved before this was recorded (#116).
     */
    'resolved_by'?: string | null;
    'response_received_date'?: string | null;
    'severity': string;
    'status': string;
    /**
     * ✅ CHANGED: From Option<i32> to Option<String>
     */
    'updated_by'?: string | null;
    'violation_type': string;
}
export interface ViolationStats {
    'escalated': number;
    'in_progress': number;
    'pending': number;
    'resolved': number;
    'total': number;
}
/**
 * Schema for updating an existing violation
 */
export interface ViolationUpdate {
    'description'?: string | null;
    /**
     * **Deprecated — use `POST /violations/{id}/attachments`.**
     * @deprecated
     */
    'document_url'?: string | null;
    'due_date'?: number | null;
    /**
     * Set, change, or — with `null` or `\"0\"` — withdraw the fine.  Changing it reverses the existing charge and posts a new one, so the statement shows both. There is no `fine_paid_date` any more: a payment is recorded through `PUT /violations/{id}/pay-fine`, which writes a real payment applied to the fine.
     */
    'fine_amount'?: string | null;
    'follow_up_date'?: number | null;
    /**
     * **Deprecated — use `POST /violations/{id}/attachments`.**
     * @deprecated
     */
    'image_url'?: string | null;
    'message'?: string | null;
    'resolution_date'?: number | null;
    'severity'?: string | null;
    'status'?: string | null;
    'violation_type'?: string | null;
}
/**
 * Schema for creating a new work order
 */
export interface WorkOrderCreate {
    'assigned_to'?: string | null;
    'description': string;
    'estimated_cost'?: string | null;
    'hoa_id': string;
    'home_id'?: string | null;
    'notes'?: string | null;
    'priority'?: string;
    'scheduled_date'?: number | null;
    'status'?: string;
    'title': string;
    'vendor_id'?: string | null;
    'vendor_name'?: string | null;
    'work_order_type': string;
}
/**
 * Schema for returning a list of work orders with total count
 */
export interface WorkOrderList {
    'total': number;
    'work_orders': Array<WorkOrderReturn>;
}

export const WorkOrderPriority = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    URGENT: 'urgent'
} as const;

export type WorkOrderPriority = typeof WorkOrderPriority[keyof typeof WorkOrderPriority];


/**
 * Schema returned when retrieving a single work order or list of work orders
 */
export interface WorkOrderReturn {
    'actual_cost'?: string | null;
    'assigned_to'?: string | null;
    'completion_date'?: string | null;
    'created_by': string;
    'date_created'?: string | null;
    'date_updated'?: string | null;
    'description': string;
    'estimated_cost'?: string | null;
    'hoa_id': string;
    'home_id'?: string | null;
    'id': string;
    'notes'?: string | null;
    'priority': string;
    'scheduled_date'?: string | null;
    'status': string;
    'title': string;
    'updated_by'?: string | null;
    'vendor_id'?: string | null;
    'vendor_name'?: string | null;
    'work_order_type': string;
}
/**
 * Schema for work order statistics
 */
export interface WorkOrderStats {
    'approved': number;
    'cancelled': number;
    'completed': number;
    'in_progress': number;
    'pending': number;
    'scheduled': number;
    'total': number;
}

export const WorkOrderStatus = {
    PENDING: 'pending',
    APPROVED: 'approved',
    SCHEDULED: 'scheduled',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled'
} as const;

export type WorkOrderStatus = typeof WorkOrderStatus[keyof typeof WorkOrderStatus];



export const WorkOrderType = {
    MAINTENANCE: 'maintenance',
    REPAIR: 'repair',
    IMPROVEMENT: 'improvement',
    INSPECTION: 'inspection',
    LANDSCAPING: 'landscaping',
    CLEANING: 'cleaning',
    OTHER: 'other'
} as const;

export type WorkOrderType = typeof WorkOrderType[keyof typeof WorkOrderType];


/**
 * Schema for updating an existing work order
 */
export interface WorkOrderUpdate {
    'actual_cost'?: string | null;
    'assigned_to'?: string | null;
    'completion_date'?: number | null;
    'description'?: string | null;
    'estimated_cost'?: string | null;
    'home_id'?: string | null;
    'notes'?: string | null;
    'priority'?: string | null;
    'scheduled_date'?: number | null;
    'status'?: string | null;
    'title'?: string | null;
    'vendor_id'?: string | null;
    'vendor_name'?: string | null;
    'work_order_type'?: string | null;
}
/**
 * The authorization code AuthKit handed back to the browser.  Not a token: a single-use code that only means anything once we have exchanged it server-side with our secret key. The browser cannot make that exchange, which is why it posts the code here instead of completing the flow itself.
 */
export interface WorkosLoginRequest {
    'code': string;
}

/**
 * AmenityExtrasApi - axios parameter creator
 */
export const AmenityExtrasApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityDocumentRequirementCreate} amenityDocumentRequirementCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityDocumentRequirement: async (amenityId: string, amenityDocumentRequirementCreate: AmenityDocumentRequirementCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('createAmenityDocumentRequirement', 'amenityId', amenityId)
            // verify required parameter 'amenityDocumentRequirementCreate' is not null or undefined
            assertParamExists('createAmenityDocumentRequirement', 'amenityDocumentRequirementCreate', amenityDocumentRequirementCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/document-requirements`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityDocumentRequirementCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityPriceRuleCreate} amenityPriceRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityPriceRule: async (amenityId: string, amenityPriceRuleCreate: AmenityPriceRuleCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('createAmenityPriceRule', 'amenityId', amenityId)
            // verify required parameter 'amenityPriceRuleCreate' is not null or undefined
            assertParamExists('createAmenityPriceRule', 'amenityPriceRuleCreate', amenityPriceRuleCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/price-rules`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityPriceRuleCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} requirementId Requirement ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityDocumentRequirement: async (amenityId: string, requirementId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('deleteAmenityDocumentRequirement', 'amenityId', amenityId)
            // verify required parameter 'requirementId' is not null or undefined
            assertParamExists('deleteAmenityDocumentRequirement', 'requirementId', requirementId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/document-requirements/{requirement_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"requirement_id"}}`, encodeURIComponent(String(requirementId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} ruleId Pricing rule ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityPriceRule: async (amenityId: string, ruleId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('deleteAmenityPriceRule', 'amenityId', amenityId)
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('deleteAmenityPriceRule', 'ruleId', ruleId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/price-rules/{rule_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {boolean} [includeInactive] Retired ones too
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityDocumentRequirements: async (amenityId: string, includeInactive?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('listAmenityDocumentRequirements', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/document-requirements`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (includeInactive !== undefined) {
                localVarQueryParameter['include_inactive'] = includeInactive;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityPriceRules: async (amenityId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('listAmenityPriceRules', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/price-rules`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {boolean} [includeClosed] Closed entries too
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityWaitlist: async (amenityId: string, includeClosed?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('listAmenityWaitlist', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/waitlist`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (includeClosed !== undefined) {
                localVarQueryParameter['include_closed'] = includeClosed;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} requirementId Requirement ID
         * @param {AmenityDocumentRequirementCreate} amenityDocumentRequirementCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenityDocumentRequirement: async (amenityId: string, requirementId: string, amenityDocumentRequirementCreate: AmenityDocumentRequirementCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('updateAmenityDocumentRequirement', 'amenityId', amenityId)
            // verify required parameter 'requirementId' is not null or undefined
            assertParamExists('updateAmenityDocumentRequirement', 'requirementId', requirementId)
            // verify required parameter 'amenityDocumentRequirementCreate' is not null or undefined
            assertParamExists('updateAmenityDocumentRequirement', 'amenityDocumentRequirementCreate', amenityDocumentRequirementCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/document-requirements/{requirement_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"requirement_id"}}`, encodeURIComponent(String(requirementId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityDocumentRequirementCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} ruleId Pricing rule ID
         * @param {AmenityPriceRuleCreate} amenityPriceRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenityPriceRule: async (amenityId: string, ruleId: string, amenityPriceRuleCreate: AmenityPriceRuleCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('updateAmenityPriceRule', 'amenityId', amenityId)
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('updateAmenityPriceRule', 'ruleId', ruleId)
            // verify required parameter 'amenityPriceRuleCreate' is not null or undefined
            assertParamExists('updateAmenityPriceRule', 'amenityPriceRuleCreate', amenityPriceRuleCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/price-rules/{rule_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityPriceRuleCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AmenityExtrasApi - functional programming interface
 */
export const AmenityExtrasApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AmenityExtrasApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityDocumentRequirementCreate} amenityDocumentRequirementCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenityDocumentRequirement(amenityId: string, amenityDocumentRequirementCreate: AmenityDocumentRequirementCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityDocumentRequirementReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenityDocumentRequirement(amenityId, amenityDocumentRequirementCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.createAmenityDocumentRequirement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityPriceRuleCreate} amenityPriceRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenityPriceRule(amenityId: string, amenityPriceRuleCreate: AmenityPriceRuleCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityPriceRuleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenityPriceRule(amenityId, amenityPriceRuleCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.createAmenityPriceRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} requirementId Requirement ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAmenityDocumentRequirement(amenityId: string, requirementId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAmenityDocumentRequirement(amenityId, requirementId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.deleteAmenityDocumentRequirement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} ruleId Pricing rule ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAmenityPriceRule(amenityId: string, ruleId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAmenityPriceRule(amenityId, ruleId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.deleteAmenityPriceRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {boolean} [includeInactive] Retired ones too
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenityDocumentRequirements(amenityId: string, includeInactive?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityDocumentRequirementList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenityDocumentRequirements(amenityId, includeInactive, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.listAmenityDocumentRequirements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenityPriceRules(amenityId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityPriceRuleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenityPriceRules(amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.listAmenityPriceRules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {boolean} [includeClosed] Closed entries too
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenityWaitlist(amenityId: string, includeClosed?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationWaitlistList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenityWaitlist(amenityId, includeClosed, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.listAmenityWaitlist']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} requirementId Requirement ID
         * @param {AmenityDocumentRequirementCreate} amenityDocumentRequirementCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAmenityDocumentRequirement(amenityId: string, requirementId: string, amenityDocumentRequirementCreate: AmenityDocumentRequirementCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityDocumentRequirementReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAmenityDocumentRequirement(amenityId, requirementId, amenityDocumentRequirementCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.updateAmenityDocumentRequirement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} ruleId Pricing rule ID
         * @param {AmenityPriceRuleCreate} amenityPriceRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAmenityPriceRule(amenityId: string, ruleId: string, amenityPriceRuleCreate: AmenityPriceRuleCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityPriceRuleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAmenityPriceRule(amenityId, ruleId, amenityPriceRuleCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityExtrasApi.updateAmenityPriceRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AmenityExtrasApi - factory interface
 */
export const AmenityExtrasApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AmenityExtrasApiFp(configuration)
    return {
        /**
         * 
         * @param {AmenityExtrasApiCreateAmenityDocumentRequirementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityDocumentRequirement(requestParameters: AmenityExtrasApiCreateAmenityDocumentRequirementRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityDocumentRequirementReturn> {
            return localVarFp.createAmenityDocumentRequirement(requestParameters.amenityId, requestParameters.amenityDocumentRequirementCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiCreateAmenityPriceRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityPriceRule(requestParameters: AmenityExtrasApiCreateAmenityPriceRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityPriceRuleReturn> {
            return localVarFp.createAmenityPriceRule(requestParameters.amenityId, requestParameters.amenityPriceRuleCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiDeleteAmenityDocumentRequirementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityDocumentRequirement(requestParameters: AmenityExtrasApiDeleteAmenityDocumentRequirementRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAmenityDocumentRequirement(requestParameters.amenityId, requestParameters.requirementId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiDeleteAmenityPriceRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityPriceRule(requestParameters: AmenityExtrasApiDeleteAmenityPriceRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAmenityPriceRule(requestParameters.amenityId, requestParameters.ruleId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiListAmenityDocumentRequirementsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityDocumentRequirements(requestParameters: AmenityExtrasApiListAmenityDocumentRequirementsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityDocumentRequirementList> {
            return localVarFp.listAmenityDocumentRequirements(requestParameters.amenityId, requestParameters.includeInactive, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiListAmenityPriceRulesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityPriceRules(requestParameters: AmenityExtrasApiListAmenityPriceRulesRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityPriceRuleList> {
            return localVarFp.listAmenityPriceRules(requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiListAmenityWaitlistRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityWaitlist(requestParameters: AmenityExtrasApiListAmenityWaitlistRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationWaitlistList> {
            return localVarFp.listAmenityWaitlist(requestParameters.amenityId, requestParameters.includeClosed, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiUpdateAmenityDocumentRequirementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenityDocumentRequirement(requestParameters: AmenityExtrasApiUpdateAmenityDocumentRequirementRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityDocumentRequirementReturn> {
            return localVarFp.updateAmenityDocumentRequirement(requestParameters.amenityId, requestParameters.requirementId, requestParameters.amenityDocumentRequirementCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityExtrasApiUpdateAmenityPriceRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenityPriceRule(requestParameters: AmenityExtrasApiUpdateAmenityPriceRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityPriceRuleReturn> {
            return localVarFp.updateAmenityPriceRule(requestParameters.amenityId, requestParameters.ruleId, requestParameters.amenityPriceRuleCreate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createAmenityDocumentRequirement operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiCreateAmenityDocumentRequirementRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenityDocumentRequirementCreate: AmenityDocumentRequirementCreate
}

/**
 * Request parameters for createAmenityPriceRule operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiCreateAmenityPriceRuleRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenityPriceRuleCreate: AmenityPriceRuleCreate
}

/**
 * Request parameters for deleteAmenityDocumentRequirement operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiDeleteAmenityDocumentRequirementRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Requirement ID
     */
    readonly requirementId: string
}

/**
 * Request parameters for deleteAmenityPriceRule operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiDeleteAmenityPriceRuleRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Pricing rule ID
     */
    readonly ruleId: string
}

/**
 * Request parameters for listAmenityDocumentRequirements operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiListAmenityDocumentRequirementsRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Retired ones too
     */
    readonly includeInactive?: boolean
}

/**
 * Request parameters for listAmenityPriceRules operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiListAmenityPriceRulesRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string
}

/**
 * Request parameters for listAmenityWaitlist operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiListAmenityWaitlistRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Closed entries too
     */
    readonly includeClosed?: boolean
}

/**
 * Request parameters for updateAmenityDocumentRequirement operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiUpdateAmenityDocumentRequirementRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Requirement ID
     */
    readonly requirementId: string

    readonly amenityDocumentRequirementCreate: AmenityDocumentRequirementCreate
}

/**
 * Request parameters for updateAmenityPriceRule operation in AmenityExtrasApi.
 */
export interface AmenityExtrasApiUpdateAmenityPriceRuleRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Pricing rule ID
     */
    readonly ruleId: string

    readonly amenityPriceRuleCreate: AmenityPriceRuleCreate
}

/**
 * AmenityExtrasApi - object-oriented interface
 */
export class AmenityExtrasApi extends BaseAPI {
    /**
     * 
     * @param {AmenityExtrasApiCreateAmenityDocumentRequirementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenityDocumentRequirement(requestParameters: AmenityExtrasApiCreateAmenityDocumentRequirementRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).createAmenityDocumentRequirement(requestParameters.amenityId, requestParameters.amenityDocumentRequirementCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiCreateAmenityPriceRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenityPriceRule(requestParameters: AmenityExtrasApiCreateAmenityPriceRuleRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).createAmenityPriceRule(requestParameters.amenityId, requestParameters.amenityPriceRuleCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiDeleteAmenityDocumentRequirementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAmenityDocumentRequirement(requestParameters: AmenityExtrasApiDeleteAmenityDocumentRequirementRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).deleteAmenityDocumentRequirement(requestParameters.amenityId, requestParameters.requirementId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiDeleteAmenityPriceRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAmenityPriceRule(requestParameters: AmenityExtrasApiDeleteAmenityPriceRuleRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).deleteAmenityPriceRule(requestParameters.amenityId, requestParameters.ruleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiListAmenityDocumentRequirementsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenityDocumentRequirements(requestParameters: AmenityExtrasApiListAmenityDocumentRequirementsRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).listAmenityDocumentRequirements(requestParameters.amenityId, requestParameters.includeInactive, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiListAmenityPriceRulesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenityPriceRules(requestParameters: AmenityExtrasApiListAmenityPriceRulesRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).listAmenityPriceRules(requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiListAmenityWaitlistRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenityWaitlist(requestParameters: AmenityExtrasApiListAmenityWaitlistRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).listAmenityWaitlist(requestParameters.amenityId, requestParameters.includeClosed, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiUpdateAmenityDocumentRequirementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAmenityDocumentRequirement(requestParameters: AmenityExtrasApiUpdateAmenityDocumentRequirementRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).updateAmenityDocumentRequirement(requestParameters.amenityId, requestParameters.requirementId, requestParameters.amenityDocumentRequirementCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityExtrasApiUpdateAmenityPriceRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAmenityPriceRule(requestParameters: AmenityExtrasApiUpdateAmenityPriceRuleRequest, options?: RawAxiosRequestConfig) {
        return AmenityExtrasApiFp(this.configuration).updateAmenityPriceRule(requestParameters.amenityId, requestParameters.ruleId, requestParameters.amenityPriceRuleCreate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AmenityGroupHandlersApi - axios parameter creator
 */
export const AmenityGroupHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {AmenityGroupCreate} amenityGroupCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityGroup: async (amenityGroupCreate: AmenityGroupCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityGroupCreate' is not null or undefined
            assertParamExists('createAmenityGroup', 'amenityGroupCreate', amenityGroupCreate)
            const localVarPath = `/api/v1/amenity-groups`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityGroupCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityGroup: async (groupId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('deleteAmenityGroup', 'groupId', groupId)
            const localVarPath = `/api/v1/amenity-groups/{group_id}`
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenityGroup: async (groupId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('getAmenityGroup', 'groupId', groupId)
            const localVarPath = `/api/v1/amenity-groups/{group_id}`
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {string} [homeId] Count a household\&#39;s limits
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenityGroupAvailability: async (groupId: string, date: string, homeId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('getAmenityGroupAvailability', 'groupId', groupId)
            // verify required parameter 'date' is not null or undefined
            assertParamExists('getAmenityGroupAvailability', 'date', date)
            const localVarPath = `/api/v1/amenity-groups/{group_id}/availability`
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (date !== undefined) {
                localVarQueryParameter['date'] = date;
            }

            if (homeId !== undefined) {
                localVarQueryParameter['home_id'] = homeId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityGroups: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listAmenityGroups', 'hoaId', hoaId)
            const localVarPath = `/api/v1/amenity-groups`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {AmenityGroupUpdate} amenityGroupUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenityGroup: async (groupId: string, amenityGroupUpdate: AmenityGroupUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('updateAmenityGroup', 'groupId', groupId)
            // verify required parameter 'amenityGroupUpdate' is not null or undefined
            assertParamExists('updateAmenityGroup', 'amenityGroupUpdate', amenityGroupUpdate)
            const localVarPath = `/api/v1/amenity-groups/{group_id}`
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityGroupUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AmenityGroupHandlersApi - functional programming interface
 */
export const AmenityGroupHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AmenityGroupHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {AmenityGroupCreate} amenityGroupCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenityGroup(amenityGroupCreate: AmenityGroupCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenityGroup(amenityGroupCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityGroupHandlersApi.createAmenityGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAmenityGroup(groupId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAmenityGroup(groupId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityGroupHandlersApi.deleteAmenityGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAmenityGroup(groupId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAmenityGroup(groupId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityGroupHandlersApi.getAmenityGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {string} [homeId] Count a household\&#39;s limits
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAmenityGroupAvailability(groupId: string, date: string, homeId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupAvailabilityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAmenityGroupAvailability(groupId, date, homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityGroupHandlersApi.getAmenityGroupAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenityGroups(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenityGroups(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityGroupHandlersApi.listAmenityGroups']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} groupId Group ID
         * @param {AmenityGroupUpdate} amenityGroupUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAmenityGroup(groupId: string, amenityGroupUpdate: AmenityGroupUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAmenityGroup(groupId, amenityGroupUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityGroupHandlersApi.updateAmenityGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AmenityGroupHandlersApi - factory interface
 */
export const AmenityGroupHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AmenityGroupHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {AmenityGroupHandlersApiCreateAmenityGroupRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityGroup(requestParameters: AmenityGroupHandlersApiCreateAmenityGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupReturn> {
            return localVarFp.createAmenityGroup(requestParameters.amenityGroupCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityGroupHandlersApiDeleteAmenityGroupRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityGroup(requestParameters: AmenityGroupHandlersApiDeleteAmenityGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAmenityGroup(requestParameters.groupId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityGroupHandlersApiGetAmenityGroupRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenityGroup(requestParameters: AmenityGroupHandlersApiGetAmenityGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupReturn> {
            return localVarFp.getAmenityGroup(requestParameters.groupId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityGroupHandlersApiGetAmenityGroupAvailabilityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenityGroupAvailability(requestParameters: AmenityGroupHandlersApiGetAmenityGroupAvailabilityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupAvailabilityReturn> {
            return localVarFp.getAmenityGroupAvailability(requestParameters.groupId, requestParameters.date, requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityGroupHandlersApiListAmenityGroupsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityGroups(requestParameters: AmenityGroupHandlersApiListAmenityGroupsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupList> {
            return localVarFp.listAmenityGroups(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityGroupHandlersApiUpdateAmenityGroupRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenityGroup(requestParameters: AmenityGroupHandlersApiUpdateAmenityGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupReturn> {
            return localVarFp.updateAmenityGroup(requestParameters.groupId, requestParameters.amenityGroupUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createAmenityGroup operation in AmenityGroupHandlersApi.
 */
export interface AmenityGroupHandlersApiCreateAmenityGroupRequest {
    readonly amenityGroupCreate: AmenityGroupCreate
}

/**
 * Request parameters for deleteAmenityGroup operation in AmenityGroupHandlersApi.
 */
export interface AmenityGroupHandlersApiDeleteAmenityGroupRequest {
    /**
     * Group ID
     */
    readonly groupId: string
}

/**
 * Request parameters for getAmenityGroup operation in AmenityGroupHandlersApi.
 */
export interface AmenityGroupHandlersApiGetAmenityGroupRequest {
    /**
     * Group ID
     */
    readonly groupId: string
}

/**
 * Request parameters for getAmenityGroupAvailability operation in AmenityGroupHandlersApi.
 */
export interface AmenityGroupHandlersApiGetAmenityGroupAvailabilityRequest {
    /**
     * Group ID
     */
    readonly groupId: string

    /**
     * Local date, YYYY-MM-DD
     */
    readonly date: string

    /**
     * Count a household\&#39;s limits
     */
    readonly homeId?: string
}

/**
 * Request parameters for listAmenityGroups operation in AmenityGroupHandlersApi.
 */
export interface AmenityGroupHandlersApiListAmenityGroupsRequest {
    /**
     * The association
     */
    readonly hoaId: string
}

/**
 * Request parameters for updateAmenityGroup operation in AmenityGroupHandlersApi.
 */
export interface AmenityGroupHandlersApiUpdateAmenityGroupRequest {
    /**
     * Group ID
     */
    readonly groupId: string

    readonly amenityGroupUpdate: AmenityGroupUpdate
}

/**
 * AmenityGroupHandlersApi - object-oriented interface
 */
export class AmenityGroupHandlersApi extends BaseAPI {
    /**
     * 
     * @param {AmenityGroupHandlersApiCreateAmenityGroupRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenityGroup(requestParameters: AmenityGroupHandlersApiCreateAmenityGroupRequest, options?: RawAxiosRequestConfig) {
        return AmenityGroupHandlersApiFp(this.configuration).createAmenityGroup(requestParameters.amenityGroupCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityGroupHandlersApiDeleteAmenityGroupRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAmenityGroup(requestParameters: AmenityGroupHandlersApiDeleteAmenityGroupRequest, options?: RawAxiosRequestConfig) {
        return AmenityGroupHandlersApiFp(this.configuration).deleteAmenityGroup(requestParameters.groupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityGroupHandlersApiGetAmenityGroupRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAmenityGroup(requestParameters: AmenityGroupHandlersApiGetAmenityGroupRequest, options?: RawAxiosRequestConfig) {
        return AmenityGroupHandlersApiFp(this.configuration).getAmenityGroup(requestParameters.groupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityGroupHandlersApiGetAmenityGroupAvailabilityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAmenityGroupAvailability(requestParameters: AmenityGroupHandlersApiGetAmenityGroupAvailabilityRequest, options?: RawAxiosRequestConfig) {
        return AmenityGroupHandlersApiFp(this.configuration).getAmenityGroupAvailability(requestParameters.groupId, requestParameters.date, requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityGroupHandlersApiListAmenityGroupsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenityGroups(requestParameters: AmenityGroupHandlersApiListAmenityGroupsRequest, options?: RawAxiosRequestConfig) {
        return AmenityGroupHandlersApiFp(this.configuration).listAmenityGroups(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityGroupHandlersApiUpdateAmenityGroupRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAmenityGroup(requestParameters: AmenityGroupHandlersApiUpdateAmenityGroupRequest, options?: RawAxiosRequestConfig) {
        return AmenityGroupHandlersApiFp(this.configuration).updateAmenityGroup(requestParameters.groupId, requestParameters.amenityGroupUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AmenityHandlersApi - axios parameter creator
 */
export const AmenityHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {boolean} [cancelFuture] Cancel upcoming bookings (fees reversed) instead of refusing
         * @param {string} [reason] Required with cancel_future; sent to the households
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveAmenity: async (amenityId: string, cancelFuture?: boolean, reason?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('archiveAmenity', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (cancelFuture !== undefined) {
                localVarQueryParameter['cancel_future'] = cancelFuture;
            }

            if (reason !== undefined) {
                localVarQueryParameter['reason'] = reason;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {AmenityCreate} amenityCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenity: async (amenityCreate: AmenityCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityCreate' is not null or undefined
            assertParamExists('createAmenity', 'amenityCreate', amenityCreate)
            const localVarPath = `/api/v1/amenities`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityClosureCreate} amenityClosureCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityClosure: async (amenityId: string, amenityClosureCreate: AmenityClosureCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('createAmenityClosure', 'amenityId', amenityId)
            // verify required parameter 'amenityClosureCreate' is not null or undefined
            assertParamExists('createAmenityClosure', 'amenityClosureCreate', amenityClosureCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/closures`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityClosureCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenitySeasonCreate} amenitySeasonCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenitySeason: async (amenityId: string, amenitySeasonCreate: AmenitySeasonCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('createAmenitySeason', 'amenityId', amenityId)
            // verify required parameter 'amenitySeasonCreate' is not null or undefined
            assertParamExists('createAmenitySeason', 'amenitySeasonCreate', amenitySeasonCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/seasons`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenitySeasonCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} closureId Closure ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityClosure: async (amenityId: string, closureId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('deleteAmenityClosure', 'amenityId', amenityId)
            // verify required parameter 'closureId' is not null or undefined
            assertParamExists('deleteAmenityClosure', 'closureId', closureId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/closures/{closure_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"closure_id"}}`, encodeURIComponent(String(closureId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} attachmentId Attachment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityPhoto: async (amenityId: string, attachmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('deleteAmenityPhoto', 'amenityId', amenityId)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('deleteAmenityPhoto', 'attachmentId', attachmentId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/photos/{attachment_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"attachment_id"}}`, encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} seasonId Season ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenitySeason: async (amenityId: string, seasonId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('deleteAmenitySeason', 'amenityId', amenityId)
            // verify required parameter 'seasonId' is not null or undefined
            assertParamExists('deleteAmenitySeason', 'seasonId', seasonId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/seasons/{season_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"season_id"}}`, encodeURIComponent(String(seasonId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenity: async (amenityId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('getAmenity', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {string} [to] Overnight amenities: the last night to report (default 30 days on, at most 92)
         * @param {string} [homeId] Include this household\&#39;s usage against its limits
         * @param {string} [excludeReservationId] A booking being moved: left out of the grid, the capacity and the usage, so its own slot shows free. Ignored unless it is in this association.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenityAvailability: async (amenityId: string, date: string, to?: string, homeId?: string, excludeReservationId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('getAmenityAvailability', 'amenityId', amenityId)
            // verify required parameter 'date' is not null or undefined
            assertParamExists('getAmenityAvailability', 'date', date)
            const localVarPath = `/api/v1/amenities/{amenity_id}/availability`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (date !== undefined) {
                localVarQueryParameter['date'] = date;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (homeId !== undefined) {
                localVarQueryParameter['home_id'] = homeId;
            }

            if (excludeReservationId !== undefined) {
                localVarQueryParameter['exclude_reservation_id'] = excludeReservationId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {boolean} [includeInactive] Include amenities switched off (default false)
         * @param {boolean} [includeArchived] Include archived amenities (default false)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenities: async (hoaId: string, includeInactive?: boolean, includeArchived?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listAmenities', 'hoaId', hoaId)
            const localVarPath = `/api/v1/amenities`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            if (includeInactive !== undefined) {
                localVarQueryParameter['include_inactive'] = includeInactive;
            }

            if (includeArchived !== undefined) {
                localVarQueryParameter['include_archived'] = includeArchived;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityAgreements: async (amenityId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('listAmenityAgreements', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/agreements`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} [from] Local date, YYYY-MM-DD: only closures overlapping from this day on
         * @param {string} [to] Local date, YYYY-MM-DD: only closures overlapping up to and including this day
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityClosures: async (amenityId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('listAmenityClosures', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/closures`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenitySeasons: async (amenityId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('listAmenitySeasons', 'amenityId', amenityId)
            const localVarPath = `/api/v1/amenities/{amenity_id}/seasons`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityAgreementCreate} amenityAgreementCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        publishAmenityAgreement: async (amenityId: string, amenityAgreementCreate: AmenityAgreementCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('publishAmenityAgreement', 'amenityId', amenityId)
            // verify required parameter 'amenityAgreementCreate' is not null or undefined
            assertParamExists('publishAmenityAgreement', 'amenityAgreementCreate', amenityAgreementCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/agreements`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityAgreementCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityHoursReplace} amenityHoursReplace 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        replaceAmenityHours: async (amenityId: string, amenityHoursReplace: AmenityHoursReplace, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('replaceAmenityHours', 'amenityId', amenityId)
            // verify required parameter 'amenityHoursReplace' is not null or undefined
            assertParamExists('replaceAmenityHours', 'amenityHoursReplace', amenityHoursReplace)
            const localVarPath = `/api/v1/amenities/{amenity_id}/hours`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityHoursReplace, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityUpdate} amenityUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenity: async (amenityId: string, amenityUpdate: AmenityUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('updateAmenity', 'amenityId', amenityId)
            // verify required parameter 'amenityUpdate' is not null or undefined
            assertParamExists('updateAmenity', 'amenityUpdate', amenityUpdate)
            const localVarPath = `/api/v1/amenities/{amenity_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenityUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} seasonId Season ID
         * @param {AmenitySeasonCreate} amenitySeasonCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenitySeason: async (amenityId: string, seasonId: string, amenitySeasonCreate: AmenitySeasonCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('updateAmenitySeason', 'amenityId', amenityId)
            // verify required parameter 'seasonId' is not null or undefined
            assertParamExists('updateAmenitySeason', 'seasonId', seasonId)
            // verify required parameter 'amenitySeasonCreate' is not null or undefined
            assertParamExists('updateAmenitySeason', 'amenitySeasonCreate', amenitySeasonCreate)
            const localVarPath = `/api/v1/amenities/{amenity_id}/seasons/{season_id}`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)))
                .replace(`{${"season_id"}}`, encodeURIComponent(String(seasonId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenitySeasonCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadAmenityPhoto: async (amenityId: string, file: File, description?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('uploadAmenityPhoto', 'amenityId', amenityId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadAmenityPhoto', 'file', file)
            const localVarPath = `/api/v1/amenities/{amenity_id}/photos`
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AmenityHandlersApi - functional programming interface
 */
export const AmenityHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AmenityHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {boolean} [cancelFuture] Cancel upcoming bookings (fees reversed) instead of refusing
         * @param {string} [reason] Required with cancel_future; sent to the households
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveAmenity(amenityId: string, cancelFuture?: boolean, reason?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveAmenity(amenityId, cancelFuture, reason, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.archiveAmenity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {AmenityCreate} amenityCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenity(amenityCreate: AmenityCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenity(amenityCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.createAmenity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityClosureCreate} amenityClosureCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenityClosure(amenityId: string, amenityClosureCreate: AmenityClosureCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityClosureReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenityClosure(amenityId, amenityClosureCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.createAmenityClosure']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenitySeasonCreate} amenitySeasonCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenitySeason(amenityId: string, amenitySeasonCreate: AmenitySeasonCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenitySeasonReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenitySeason(amenityId, amenitySeasonCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.createAmenitySeason']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} closureId Closure ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAmenityClosure(amenityId: string, closureId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAmenityClosure(amenityId, closureId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.deleteAmenityClosure']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} attachmentId Attachment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAmenityPhoto(amenityId: string, attachmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAmenityPhoto(amenityId, attachmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.deleteAmenityPhoto']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} seasonId Season ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAmenitySeason(amenityId: string, seasonId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAmenitySeason(amenityId, seasonId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.deleteAmenitySeason']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAmenity(amenityId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAmenity(amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.getAmenity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {string} [to] Overnight amenities: the last night to report (default 30 days on, at most 92)
         * @param {string} [homeId] Include this household\&#39;s usage against its limits
         * @param {string} [excludeReservationId] A booking being moved: left out of the grid, the capacity and the usage, so its own slot shows free. Ignored unless it is in this association.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAmenityAvailability(amenityId: string, date: string, to?: string, homeId?: string, excludeReservationId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityAvailabilityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAmenityAvailability(amenityId, date, to, homeId, excludeReservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.getAmenityAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {boolean} [includeInactive] Include amenities switched off (default false)
         * @param {boolean} [includeArchived] Include archived amenities (default false)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenities(hoaId: string, includeInactive?: boolean, includeArchived?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenities(hoaId, includeInactive, includeArchived, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.listAmenities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenityAgreements(amenityId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityAgreementList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenityAgreements(amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.listAmenityAgreements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} [from] Local date, YYYY-MM-DD: only closures overlapping from this day on
         * @param {string} [to] Local date, YYYY-MM-DD: only closures overlapping up to and including this day
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenityClosures(amenityId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityClosureList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenityClosures(amenityId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.listAmenityClosures']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenitySeasons(amenityId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenitySeasonList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenitySeasons(amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.listAmenitySeasons']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityAgreementCreate} amenityAgreementCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async publishAmenityAgreement(amenityId: string, amenityAgreementCreate: AmenityAgreementCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityAgreementReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.publishAmenityAgreement(amenityId, amenityAgreementCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.publishAmenityAgreement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityHoursReplace} amenityHoursReplace 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async replaceAmenityHours(amenityId: string, amenityHoursReplace: AmenityHoursReplace, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.replaceAmenityHours(amenityId, amenityHoursReplace, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.replaceAmenityHours']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {AmenityUpdate} amenityUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAmenity(amenityId: string, amenityUpdate: AmenityUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAmenity(amenityId, amenityUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.updateAmenity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {string} seasonId Season ID
         * @param {AmenitySeasonCreate} amenitySeasonCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAmenitySeason(amenityId: string, seasonId: string, amenitySeasonCreate: AmenitySeasonCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenitySeasonReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAmenitySeason(amenityId, seasonId, amenitySeasonCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.updateAmenitySeason']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} amenityId Amenity ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadAmenityPhoto(amenityId: string, file: File, description?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadAmenityPhoto(amenityId, file, description, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AmenityHandlersApi.uploadAmenityPhoto']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AmenityHandlersApi - factory interface
 */
export const AmenityHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AmenityHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {AmenityHandlersApiArchiveAmenityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveAmenity(requestParameters: AmenityHandlersApiArchiveAmenityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityReturn> {
            return localVarFp.archiveAmenity(requestParameters.amenityId, requestParameters.cancelFuture, requestParameters.reason, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiCreateAmenityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenity(requestParameters: AmenityHandlersApiCreateAmenityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityReturn> {
            return localVarFp.createAmenity(requestParameters.amenityCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiCreateAmenityClosureRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenityClosure(requestParameters: AmenityHandlersApiCreateAmenityClosureRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityClosureReturn> {
            return localVarFp.createAmenityClosure(requestParameters.amenityId, requestParameters.amenityClosureCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiCreateAmenitySeasonRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenitySeason(requestParameters: AmenityHandlersApiCreateAmenitySeasonRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenitySeasonReturn> {
            return localVarFp.createAmenitySeason(requestParameters.amenityId, requestParameters.amenitySeasonCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiDeleteAmenityClosureRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityClosure(requestParameters: AmenityHandlersApiDeleteAmenityClosureRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAmenityClosure(requestParameters.amenityId, requestParameters.closureId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiDeleteAmenityPhotoRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenityPhoto(requestParameters: AmenityHandlersApiDeleteAmenityPhotoRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAmenityPhoto(requestParameters.amenityId, requestParameters.attachmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiDeleteAmenitySeasonRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAmenitySeason(requestParameters: AmenityHandlersApiDeleteAmenitySeasonRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAmenitySeason(requestParameters.amenityId, requestParameters.seasonId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiGetAmenityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenity(requestParameters: AmenityHandlersApiGetAmenityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityReturn> {
            return localVarFp.getAmenity(requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiGetAmenityAvailabilityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAmenityAvailability(requestParameters: AmenityHandlersApiGetAmenityAvailabilityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityAvailabilityReturn> {
            return localVarFp.getAmenityAvailability(requestParameters.amenityId, requestParameters.date, requestParameters.to, requestParameters.homeId, requestParameters.excludeReservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiListAmenitiesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenities(requestParameters: AmenityHandlersApiListAmenitiesRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityList> {
            return localVarFp.listAmenities(requestParameters.hoaId, requestParameters.includeInactive, requestParameters.includeArchived, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiListAmenityAgreementsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityAgreements(requestParameters: AmenityHandlersApiListAmenityAgreementsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityAgreementList> {
            return localVarFp.listAmenityAgreements(requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiListAmenityClosuresRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenityClosures(requestParameters: AmenityHandlersApiListAmenityClosuresRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityClosureList> {
            return localVarFp.listAmenityClosures(requestParameters.amenityId, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiListAmenitySeasonsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenitySeasons(requestParameters: AmenityHandlersApiListAmenitySeasonsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenitySeasonList> {
            return localVarFp.listAmenitySeasons(requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiPublishAmenityAgreementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        publishAmenityAgreement(requestParameters: AmenityHandlersApiPublishAmenityAgreementRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityAgreementReturn> {
            return localVarFp.publishAmenityAgreement(requestParameters.amenityId, requestParameters.amenityAgreementCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiReplaceAmenityHoursRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        replaceAmenityHours(requestParameters: AmenityHandlersApiReplaceAmenityHoursRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityReturn> {
            return localVarFp.replaceAmenityHours(requestParameters.amenityId, requestParameters.amenityHoursReplace, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiUpdateAmenityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenity(requestParameters: AmenityHandlersApiUpdateAmenityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityReturn> {
            return localVarFp.updateAmenity(requestParameters.amenityId, requestParameters.amenityUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiUpdateAmenitySeasonRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAmenitySeason(requestParameters: AmenityHandlersApiUpdateAmenitySeasonRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenitySeasonReturn> {
            return localVarFp.updateAmenitySeason(requestParameters.amenityId, requestParameters.seasonId, requestParameters.amenitySeasonCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AmenityHandlersApiUploadAmenityPhotoRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadAmenityPhoto(requestParameters: AmenityHandlersApiUploadAmenityPhotoRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentReturn> {
            return localVarFp.uploadAmenityPhoto(requestParameters.amenityId, requestParameters.file, requestParameters.description, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for archiveAmenity operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiArchiveAmenityRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Cancel upcoming bookings (fees reversed) instead of refusing
     */
    readonly cancelFuture?: boolean

    /**
     * Required with cancel_future; sent to the households
     */
    readonly reason?: string
}

/**
 * Request parameters for createAmenity operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiCreateAmenityRequest {
    readonly amenityCreate: AmenityCreate
}

/**
 * Request parameters for createAmenityClosure operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiCreateAmenityClosureRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenityClosureCreate: AmenityClosureCreate
}

/**
 * Request parameters for createAmenitySeason operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiCreateAmenitySeasonRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenitySeasonCreate: AmenitySeasonCreate
}

/**
 * Request parameters for deleteAmenityClosure operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiDeleteAmenityClosureRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Closure ID
     */
    readonly closureId: string
}

/**
 * Request parameters for deleteAmenityPhoto operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiDeleteAmenityPhotoRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Attachment ID
     */
    readonly attachmentId: string
}

/**
 * Request parameters for deleteAmenitySeason operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiDeleteAmenitySeasonRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Season ID
     */
    readonly seasonId: string
}

/**
 * Request parameters for getAmenity operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiGetAmenityRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string
}

/**
 * Request parameters for getAmenityAvailability operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiGetAmenityAvailabilityRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Local date, YYYY-MM-DD
     */
    readonly date: string

    /**
     * Overnight amenities: the last night to report (default 30 days on, at most 92)
     */
    readonly to?: string

    /**
     * Include this household\&#39;s usage against its limits
     */
    readonly homeId?: string

    /**
     * A booking being moved: left out of the grid, the capacity and the usage, so its own slot shows free. Ignored unless it is in this association.
     */
    readonly excludeReservationId?: string
}

/**
 * Request parameters for listAmenities operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiListAmenitiesRequest {
    /**
     * The association
     */
    readonly hoaId: string

    /**
     * Include amenities switched off (default false)
     */
    readonly includeInactive?: boolean

    /**
     * Include archived amenities (default false)
     */
    readonly includeArchived?: boolean
}

/**
 * Request parameters for listAmenityAgreements operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiListAmenityAgreementsRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string
}

/**
 * Request parameters for listAmenityClosures operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiListAmenityClosuresRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Local date, YYYY-MM-DD: only closures overlapping from this day on
     */
    readonly from?: string

    /**
     * Local date, YYYY-MM-DD: only closures overlapping up to and including this day
     */
    readonly to?: string
}

/**
 * Request parameters for listAmenitySeasons operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiListAmenitySeasonsRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string
}

/**
 * Request parameters for publishAmenityAgreement operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiPublishAmenityAgreementRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenityAgreementCreate: AmenityAgreementCreate
}

/**
 * Request parameters for replaceAmenityHours operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiReplaceAmenityHoursRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenityHoursReplace: AmenityHoursReplace
}

/**
 * Request parameters for updateAmenity operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiUpdateAmenityRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    readonly amenityUpdate: AmenityUpdate
}

/**
 * Request parameters for updateAmenitySeason operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiUpdateAmenitySeasonRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Season ID
     */
    readonly seasonId: string

    readonly amenitySeasonCreate: AmenitySeasonCreate
}

/**
 * Request parameters for uploadAmenityPhoto operation in AmenityHandlersApi.
 */
export interface AmenityHandlersApiUploadAmenityPhotoRequest {
    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    readonly description?: string | null
}

/**
 * AmenityHandlersApi - object-oriented interface
 */
export class AmenityHandlersApi extends BaseAPI {
    /**
     * 
     * @param {AmenityHandlersApiArchiveAmenityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveAmenity(requestParameters: AmenityHandlersApiArchiveAmenityRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).archiveAmenity(requestParameters.amenityId, requestParameters.cancelFuture, requestParameters.reason, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiCreateAmenityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenity(requestParameters: AmenityHandlersApiCreateAmenityRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).createAmenity(requestParameters.amenityCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiCreateAmenityClosureRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenityClosure(requestParameters: AmenityHandlersApiCreateAmenityClosureRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).createAmenityClosure(requestParameters.amenityId, requestParameters.amenityClosureCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiCreateAmenitySeasonRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenitySeason(requestParameters: AmenityHandlersApiCreateAmenitySeasonRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).createAmenitySeason(requestParameters.amenityId, requestParameters.amenitySeasonCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiDeleteAmenityClosureRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAmenityClosure(requestParameters: AmenityHandlersApiDeleteAmenityClosureRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).deleteAmenityClosure(requestParameters.amenityId, requestParameters.closureId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiDeleteAmenityPhotoRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAmenityPhoto(requestParameters: AmenityHandlersApiDeleteAmenityPhotoRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).deleteAmenityPhoto(requestParameters.amenityId, requestParameters.attachmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiDeleteAmenitySeasonRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAmenitySeason(requestParameters: AmenityHandlersApiDeleteAmenitySeasonRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).deleteAmenitySeason(requestParameters.amenityId, requestParameters.seasonId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiGetAmenityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAmenity(requestParameters: AmenityHandlersApiGetAmenityRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).getAmenity(requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiGetAmenityAvailabilityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAmenityAvailability(requestParameters: AmenityHandlersApiGetAmenityAvailabilityRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).getAmenityAvailability(requestParameters.amenityId, requestParameters.date, requestParameters.to, requestParameters.homeId, requestParameters.excludeReservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiListAmenitiesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenities(requestParameters: AmenityHandlersApiListAmenitiesRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).listAmenities(requestParameters.hoaId, requestParameters.includeInactive, requestParameters.includeArchived, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiListAmenityAgreementsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenityAgreements(requestParameters: AmenityHandlersApiListAmenityAgreementsRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).listAmenityAgreements(requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiListAmenityClosuresRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenityClosures(requestParameters: AmenityHandlersApiListAmenityClosuresRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).listAmenityClosures(requestParameters.amenityId, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiListAmenitySeasonsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenitySeasons(requestParameters: AmenityHandlersApiListAmenitySeasonsRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).listAmenitySeasons(requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiPublishAmenityAgreementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public publishAmenityAgreement(requestParameters: AmenityHandlersApiPublishAmenityAgreementRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).publishAmenityAgreement(requestParameters.amenityId, requestParameters.amenityAgreementCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiReplaceAmenityHoursRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public replaceAmenityHours(requestParameters: AmenityHandlersApiReplaceAmenityHoursRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).replaceAmenityHours(requestParameters.amenityId, requestParameters.amenityHoursReplace, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiUpdateAmenityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAmenity(requestParameters: AmenityHandlersApiUpdateAmenityRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).updateAmenity(requestParameters.amenityId, requestParameters.amenityUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiUpdateAmenitySeasonRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAmenitySeason(requestParameters: AmenityHandlersApiUpdateAmenitySeasonRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).updateAmenitySeason(requestParameters.amenityId, requestParameters.seasonId, requestParameters.amenitySeasonCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AmenityHandlersApiUploadAmenityPhotoRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadAmenityPhoto(requestParameters: AmenityHandlersApiUploadAmenityPhotoRequest, options?: RawAxiosRequestConfig) {
        return AmenityHandlersApiFp(this.configuration).uploadAmenityPhoto(requestParameters.amenityId, requestParameters.file, requestParameters.description, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AuthHandlersApi - axios parameter creator
 */
export const AuthHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * **Not dead — but no longer reachable from front-porch, as of 2026-09-12.** `AuthService::change_password` refuses any account that is not a SuperAdmin, so what survives here is the break-glass credential\'s management surface (migration plan §1.7). The Settings card that called this was deleted, on the same reasoning that keeps the break-glass out of `LoginPage`: someone using the password path is doing so knowingly, with a tool, and a UI for half of it implied otherwise.  So this stays, gated, and is exercised with a tool. Do **not** mark it superseded — nothing replaced it for the accounts it still serves.  Authenticated: the account is whoever holds the bearer token, and the body carries no email. Verifying `current_password` is what keeps a stolen session from becoming a stolen account.  **Ends every other session and replaces this one.** The change bumps `token_version`, which `authenticate_access_token` checks per request, so all existing tokens — including the one used to call this — stop working immediately. A fresh pair comes back in the response body for the caller to store in their place.
         * @summary Change your own password, having proved the current one.
         * @param {PasswordChangeRequest} passwordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        changePassword: async (passwordChangeRequest: PasswordChangeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordChangeRequest' is not null or undefined
            assertParamExists('changePassword', 'passwordChangeRequest', passwordChangeRequest)
            const localVarPath = `/api/v1/auth/change-password`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(passwordChangeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A success here does **not** mean our row has changed yet: WorkOS emits `user.updated` and the webhook applies it, usually within a second. The message says \"updated\" rather than \"will update\" because that gap is not something a person can act on, and the next profile load will show the new address.
         * @summary Finish an email change with the code sent to the new address.
         * @param {EmailChangeConfirmRequest} emailChangeConfirmRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmEmailChange: async (emailChangeConfirmRequest: EmailChangeConfirmRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'emailChangeConfirmRequest' is not null or undefined
            assertParamExists('confirmEmailChange', 'emailChangeConfirmRequest', emailChangeConfirmRequest)
            const localVarPath = `/api/v1/auth/email-change/confirm`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(emailChangeConfirmRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {UserLogin} userLogin 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loginUser: async (userLogin: UserLogin, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userLogin' is not null or undefined
            assertParamExists('loginUser', 'userLogin', userLogin)
            const localVarPath = `/api/v1/auth/login`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userLogin, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary This signs the user out **on every device**. Per-device sign-out would need a session table keyed by a `jti` claim; ending all sessions is the safer default and the one a compromised-account user wants.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        logout: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/auth/logout`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Not dead, and not replaced: `AuthService::password_reset_request` answers success for everyone but only *acts* for a SuperAdmin, so this is how a lost break-glass password is recovered. Managers reset through AuthKit, which owns their credential.  **`ForgotPasswordPage` was deleted 2026-09-12** along with the rest of the password UI, so reaching this now takes a tool — deliberately, for the same reason `LoginPage` offers no password form. A reset that still needs an inbox is also the one part of break-glass that works during a WorkOS outage, which is the point of keeping it.
         * @summary Email a password reset link — **the break-glass recovery path.**
         * @param {PasswordEmailResetRequest} passwordEmailResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        passwordResetRequest: async (passwordEmailResetRequest: PasswordEmailResetRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordEmailResetRequest' is not null or undefined
            assertParamExists('passwordResetRequest', 'passwordEmailResetRequest', passwordEmailResetRequest)
            const localVarPath = `/api/v1/auth/password-reset-request`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(passwordEmailResetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {RefreshRequest} refreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refreshToken: async (refreshRequest: RefreshRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'refreshRequest' is not null or undefined
            assertParamExists('refreshToken', 'refreshRequest', refreshRequest)
            const localVarPath = `/api/v1/auth/refresh`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(refreshRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The other half of `password_reset_request`, and alive for the same reason: `AuthService::reset_password` refuses any account that is not a SuperAdmin — including a token minted before that gate existed, because a token already in an inbox is a credential and this is the only place that can refuse it.  **`ResetPasswordPage` was deleted 2026-09-12.** The link in the email no longer lands on a form in front-porch; completing a reset takes a tool, which matches how the break-glass is used everywhere else.
         * @summary Complete a break-glass password reset with the emailed token.
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetPassword: async (passwordResetRequest: PasswordResetRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordResetRequest' is not null or undefined
            assertParamExists('resetPassword', 'passwordResetRequest', passwordResetRequest)
            const localVarPath = `/api/v1/auth/reset-password`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(passwordResetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Bumps `token_version`, which `authenticate_access_token` compares against the row on every request — so the caller\'s access tokens stop working immediately, not whenever they happen to expire.  Start changing your own email address.  Authenticated: the account is whoever holds the bearer token, and the body names only the *new* address. There is no `user_id` field, so one user cannot start a change on another\'s account.  **No password is required**, unlike the flow this replaces. Proof of control is the code WorkOS sends to the new address — a password would prove nothing about it, and since sign-in moved to WorkOS most accounts no longer have one worth asking for.
         * @summary Sign out, ending every session for the caller.
         * @param {EmailChangeSendRequest} emailChangeSendRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendEmailChange: async (emailChangeSendRequest: EmailChangeSendRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'emailChangeSendRequest' is not null or undefined
            assertParamExists('sendEmailChange', 'emailChangeSendRequest', emailChangeSendRequest)
            const localVarPath = `/api/v1/auth/email-change/send`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(emailChangeSendRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {VerifyRequest} verifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyToken: async (verifyRequest: VerifyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'verifyRequest' is not null or undefined
            assertParamExists('verifyToken', 'verifyRequest', verifyRequest)
            const localVarPath = `/api/v1/auth/verify`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(verifyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {WorkosLoginRequest} workosLoginRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        workosLogin: async (workosLoginRequest: WorkosLoginRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workosLoginRequest' is not null or undefined
            assertParamExists('workosLogin', 'workosLoginRequest', workosLoginRequest)
            const localVarPath = `/api/v1/auth/workos-login`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workosLoginRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AuthHandlersApi - functional programming interface
 */
export const AuthHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AuthHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * **Not dead — but no longer reachable from front-porch, as of 2026-09-12.** `AuthService::change_password` refuses any account that is not a SuperAdmin, so what survives here is the break-glass credential\'s management surface (migration plan §1.7). The Settings card that called this was deleted, on the same reasoning that keeps the break-glass out of `LoginPage`: someone using the password path is doing so knowingly, with a tool, and a UI for half of it implied otherwise.  So this stays, gated, and is exercised with a tool. Do **not** mark it superseded — nothing replaced it for the accounts it still serves.  Authenticated: the account is whoever holds the bearer token, and the body carries no email. Verifying `current_password` is what keeps a stolen session from becoming a stolen account.  **Ends every other session and replaces this one.** The change bumps `token_version`, which `authenticate_access_token` checks per request, so all existing tokens — including the one used to call this — stop working immediately. A fresh pair comes back in the response body for the caller to store in their place.
         * @summary Change your own password, having proved the current one.
         * @param {PasswordChangeRequest} passwordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async changePassword(passwordChangeRequest: PasswordChangeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PasswordChangeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.changePassword(passwordChangeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.changePassword']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A success here does **not** mean our row has changed yet: WorkOS emits `user.updated` and the webhook applies it, usually within a second. The message says \"updated\" rather than \"will update\" because that gap is not something a person can act on, and the next profile load will show the new address.
         * @summary Finish an email change with the code sent to the new address.
         * @param {EmailChangeConfirmRequest} emailChangeConfirmRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async confirmEmailChange(emailChangeConfirmRequest: EmailChangeConfirmRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EmailChangeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.confirmEmailChange(emailChangeConfirmRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.confirmEmailChange']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {UserLogin} userLogin 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async loginUser(userLogin: UserLogin, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TokenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.loginUser(userLogin, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.loginUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary This signs the user out **on every device**. Per-device sign-out would need a session table keyed by a `jti` claim; ending all sessions is the safer default and the one a compromised-account user wants.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async logout(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LogoutResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.logout(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.logout']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Not dead, and not replaced: `AuthService::password_reset_request` answers success for everyone but only *acts* for a SuperAdmin, so this is how a lost break-glass password is recovered. Managers reset through AuthKit, which owns their credential.  **`ForgotPasswordPage` was deleted 2026-09-12** along with the rest of the password UI, so reaching this now takes a tool — deliberately, for the same reason `LoginPage` offers no password form. A reset that still needs an inbox is also the one part of break-glass that works during a WorkOS outage, which is the point of keeping it.
         * @summary Email a password reset link — **the break-glass recovery path.**
         * @param {PasswordEmailResetRequest} passwordEmailResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async passwordResetRequest(passwordEmailResetRequest: PasswordEmailResetRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PasswordEmailResetResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.passwordResetRequest(passwordEmailResetRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.passwordResetRequest']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {RefreshRequest} refreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async refreshToken(refreshRequest: RefreshRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TokenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.refreshToken(refreshRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.refreshToken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The other half of `password_reset_request`, and alive for the same reason: `AuthService::reset_password` refuses any account that is not a SuperAdmin — including a token minted before that gate existed, because a token already in an inbox is a credential and this is the only place that can refuse it.  **`ResetPasswordPage` was deleted 2026-09-12.** The link in the email no longer lands on a form in front-porch; completing a reset takes a tool, which matches how the break-glass is used everywhere else.
         * @summary Complete a break-glass password reset with the emailed token.
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resetPassword(passwordResetRequest: PasswordResetRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PasswordResetResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resetPassword(passwordResetRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.resetPassword']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Bumps `token_version`, which `authenticate_access_token` compares against the row on every request — so the caller\'s access tokens stop working immediately, not whenever they happen to expire.  Start changing your own email address.  Authenticated: the account is whoever holds the bearer token, and the body names only the *new* address. There is no `user_id` field, so one user cannot start a change on another\'s account.  **No password is required**, unlike the flow this replaces. Proof of control is the code WorkOS sends to the new address — a password would prove nothing about it, and since sign-in moved to WorkOS most accounts no longer have one worth asking for.
         * @summary Sign out, ending every session for the caller.
         * @param {EmailChangeSendRequest} emailChangeSendRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendEmailChange(emailChangeSendRequest: EmailChangeSendRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EmailChangeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendEmailChange(emailChangeSendRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.sendEmailChange']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {VerifyRequest} verifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verifyToken(verifyRequest: VerifyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VerifyResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verifyToken(verifyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.verifyToken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {WorkosLoginRequest} workosLoginRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async workosLogin(workosLoginRequest: WorkosLoginRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TokenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.workosLogin(workosLoginRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthHandlersApi.workosLogin']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AuthHandlersApi - factory interface
 */
export const AuthHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AuthHandlersApiFp(configuration)
    return {
        /**
         * **Not dead — but no longer reachable from front-porch, as of 2026-09-12.** `AuthService::change_password` refuses any account that is not a SuperAdmin, so what survives here is the break-glass credential\'s management surface (migration plan §1.7). The Settings card that called this was deleted, on the same reasoning that keeps the break-glass out of `LoginPage`: someone using the password path is doing so knowingly, with a tool, and a UI for half of it implied otherwise.  So this stays, gated, and is exercised with a tool. Do **not** mark it superseded — nothing replaced it for the accounts it still serves.  Authenticated: the account is whoever holds the bearer token, and the body carries no email. Verifying `current_password` is what keeps a stolen session from becoming a stolen account.  **Ends every other session and replaces this one.** The change bumps `token_version`, which `authenticate_access_token` checks per request, so all existing tokens — including the one used to call this — stop working immediately. A fresh pair comes back in the response body for the caller to store in their place.
         * @summary Change your own password, having proved the current one.
         * @param {AuthHandlersApiChangePasswordRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        changePassword(requestParameters: AuthHandlersApiChangePasswordRequest, options?: RawAxiosRequestConfig): AxiosPromise<PasswordChangeResponse> {
            return localVarFp.changePassword(requestParameters.passwordChangeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * A success here does **not** mean our row has changed yet: WorkOS emits `user.updated` and the webhook applies it, usually within a second. The message says \"updated\" rather than \"will update\" because that gap is not something a person can act on, and the next profile load will show the new address.
         * @summary Finish an email change with the code sent to the new address.
         * @param {AuthHandlersApiConfirmEmailChangeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmEmailChange(requestParameters: AuthHandlersApiConfirmEmailChangeRequest, options?: RawAxiosRequestConfig): AxiosPromise<EmailChangeResponse> {
            return localVarFp.confirmEmailChange(requestParameters.emailChangeConfirmRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AuthHandlersApiLoginUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        loginUser(requestParameters: AuthHandlersApiLoginUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<TokenResponse> {
            return localVarFp.loginUser(requestParameters.userLogin, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary This signs the user out **on every device**. Per-device sign-out would need a session table keyed by a `jti` claim; ending all sessions is the safer default and the one a compromised-account user wants.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        logout(options?: RawAxiosRequestConfig): AxiosPromise<LogoutResponse> {
            return localVarFp.logout(options).then((request) => request(axios, basePath));
        },
        /**
         * Not dead, and not replaced: `AuthService::password_reset_request` answers success for everyone but only *acts* for a SuperAdmin, so this is how a lost break-glass password is recovered. Managers reset through AuthKit, which owns their credential.  **`ForgotPasswordPage` was deleted 2026-09-12** along with the rest of the password UI, so reaching this now takes a tool — deliberately, for the same reason `LoginPage` offers no password form. A reset that still needs an inbox is also the one part of break-glass that works during a WorkOS outage, which is the point of keeping it.
         * @summary Email a password reset link — **the break-glass recovery path.**
         * @param {AuthHandlersApiPasswordResetRequestRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        passwordResetRequest(requestParameters: AuthHandlersApiPasswordResetRequestRequest, options?: RawAxiosRequestConfig): AxiosPromise<PasswordEmailResetResponse> {
            return localVarFp.passwordResetRequest(requestParameters.passwordEmailResetRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AuthHandlersApiRefreshTokenRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refreshToken(requestParameters: AuthHandlersApiRefreshTokenRequest, options?: RawAxiosRequestConfig): AxiosPromise<TokenResponse> {
            return localVarFp.refreshToken(requestParameters.refreshRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * The other half of `password_reset_request`, and alive for the same reason: `AuthService::reset_password` refuses any account that is not a SuperAdmin — including a token minted before that gate existed, because a token already in an inbox is a credential and this is the only place that can refuse it.  **`ResetPasswordPage` was deleted 2026-09-12.** The link in the email no longer lands on a form in front-porch; completing a reset takes a tool, which matches how the break-glass is used everywhere else.
         * @summary Complete a break-glass password reset with the emailed token.
         * @param {AuthHandlersApiResetPasswordRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetPassword(requestParameters: AuthHandlersApiResetPasswordRequest, options?: RawAxiosRequestConfig): AxiosPromise<PasswordResetResponse> {
            return localVarFp.resetPassword(requestParameters.passwordResetRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Bumps `token_version`, which `authenticate_access_token` compares against the row on every request — so the caller\'s access tokens stop working immediately, not whenever they happen to expire.  Start changing your own email address.  Authenticated: the account is whoever holds the bearer token, and the body names only the *new* address. There is no `user_id` field, so one user cannot start a change on another\'s account.  **No password is required**, unlike the flow this replaces. Proof of control is the code WorkOS sends to the new address — a password would prove nothing about it, and since sign-in moved to WorkOS most accounts no longer have one worth asking for.
         * @summary Sign out, ending every session for the caller.
         * @param {AuthHandlersApiSendEmailChangeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendEmailChange(requestParameters: AuthHandlersApiSendEmailChangeRequest, options?: RawAxiosRequestConfig): AxiosPromise<EmailChangeResponse> {
            return localVarFp.sendEmailChange(requestParameters.emailChangeSendRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AuthHandlersApiVerifyTokenRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyToken(requestParameters: AuthHandlersApiVerifyTokenRequest, options?: RawAxiosRequestConfig): AxiosPromise<VerifyResponse> {
            return localVarFp.verifyToken(requestParameters.verifyRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AuthHandlersApiWorkosLoginRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        workosLogin(requestParameters: AuthHandlersApiWorkosLoginRequest, options?: RawAxiosRequestConfig): AxiosPromise<TokenResponse> {
            return localVarFp.workosLogin(requestParameters.workosLoginRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for changePassword operation in AuthHandlersApi.
 */
export interface AuthHandlersApiChangePasswordRequest {
    readonly passwordChangeRequest: PasswordChangeRequest
}

/**
 * Request parameters for confirmEmailChange operation in AuthHandlersApi.
 */
export interface AuthHandlersApiConfirmEmailChangeRequest {
    readonly emailChangeConfirmRequest: EmailChangeConfirmRequest
}

/**
 * Request parameters for loginUser operation in AuthHandlersApi.
 */
export interface AuthHandlersApiLoginUserRequest {
    readonly userLogin: UserLogin
}

/**
 * Request parameters for passwordResetRequest operation in AuthHandlersApi.
 */
export interface AuthHandlersApiPasswordResetRequestRequest {
    readonly passwordEmailResetRequest: PasswordEmailResetRequest
}

/**
 * Request parameters for refreshToken operation in AuthHandlersApi.
 */
export interface AuthHandlersApiRefreshTokenRequest {
    readonly refreshRequest: RefreshRequest
}

/**
 * Request parameters for resetPassword operation in AuthHandlersApi.
 */
export interface AuthHandlersApiResetPasswordRequest {
    readonly passwordResetRequest: PasswordResetRequest
}

/**
 * Request parameters for sendEmailChange operation in AuthHandlersApi.
 */
export interface AuthHandlersApiSendEmailChangeRequest {
    readonly emailChangeSendRequest: EmailChangeSendRequest
}

/**
 * Request parameters for verifyToken operation in AuthHandlersApi.
 */
export interface AuthHandlersApiVerifyTokenRequest {
    readonly verifyRequest: VerifyRequest
}

/**
 * Request parameters for workosLogin operation in AuthHandlersApi.
 */
export interface AuthHandlersApiWorkosLoginRequest {
    readonly workosLoginRequest: WorkosLoginRequest
}

/**
 * AuthHandlersApi - object-oriented interface
 */
export class AuthHandlersApi extends BaseAPI {
    /**
     * **Not dead — but no longer reachable from front-porch, as of 2026-09-12.** `AuthService::change_password` refuses any account that is not a SuperAdmin, so what survives here is the break-glass credential\'s management surface (migration plan §1.7). The Settings card that called this was deleted, on the same reasoning that keeps the break-glass out of `LoginPage`: someone using the password path is doing so knowingly, with a tool, and a UI for half of it implied otherwise.  So this stays, gated, and is exercised with a tool. Do **not** mark it superseded — nothing replaced it for the accounts it still serves.  Authenticated: the account is whoever holds the bearer token, and the body carries no email. Verifying `current_password` is what keeps a stolen session from becoming a stolen account.  **Ends every other session and replaces this one.** The change bumps `token_version`, which `authenticate_access_token` checks per request, so all existing tokens — including the one used to call this — stop working immediately. A fresh pair comes back in the response body for the caller to store in their place.
     * @summary Change your own password, having proved the current one.
     * @param {AuthHandlersApiChangePasswordRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public changePassword(requestParameters: AuthHandlersApiChangePasswordRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).changePassword(requestParameters.passwordChangeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A success here does **not** mean our row has changed yet: WorkOS emits `user.updated` and the webhook applies it, usually within a second. The message says \"updated\" rather than \"will update\" because that gap is not something a person can act on, and the next profile load will show the new address.
     * @summary Finish an email change with the code sent to the new address.
     * @param {AuthHandlersApiConfirmEmailChangeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public confirmEmailChange(requestParameters: AuthHandlersApiConfirmEmailChangeRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).confirmEmailChange(requestParameters.emailChangeConfirmRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AuthHandlersApiLoginUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public loginUser(requestParameters: AuthHandlersApiLoginUserRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).loginUser(requestParameters.userLogin, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary This signs the user out **on every device**. Per-device sign-out would need a session table keyed by a `jti` claim; ending all sessions is the safer default and the one a compromised-account user wants.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public logout(options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).logout(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Not dead, and not replaced: `AuthService::password_reset_request` answers success for everyone but only *acts* for a SuperAdmin, so this is how a lost break-glass password is recovered. Managers reset through AuthKit, which owns their credential.  **`ForgotPasswordPage` was deleted 2026-09-12** along with the rest of the password UI, so reaching this now takes a tool — deliberately, for the same reason `LoginPage` offers no password form. A reset that still needs an inbox is also the one part of break-glass that works during a WorkOS outage, which is the point of keeping it.
     * @summary Email a password reset link — **the break-glass recovery path.**
     * @param {AuthHandlersApiPasswordResetRequestRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public passwordResetRequest(requestParameters: AuthHandlersApiPasswordResetRequestRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).passwordResetRequest(requestParameters.passwordEmailResetRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AuthHandlersApiRefreshTokenRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public refreshToken(requestParameters: AuthHandlersApiRefreshTokenRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).refreshToken(requestParameters.refreshRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The other half of `password_reset_request`, and alive for the same reason: `AuthService::reset_password` refuses any account that is not a SuperAdmin — including a token minted before that gate existed, because a token already in an inbox is a credential and this is the only place that can refuse it.  **`ResetPasswordPage` was deleted 2026-09-12.** The link in the email no longer lands on a form in front-porch; completing a reset takes a tool, which matches how the break-glass is used everywhere else.
     * @summary Complete a break-glass password reset with the emailed token.
     * @param {AuthHandlersApiResetPasswordRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resetPassword(requestParameters: AuthHandlersApiResetPasswordRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).resetPassword(requestParameters.passwordResetRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Bumps `token_version`, which `authenticate_access_token` compares against the row on every request — so the caller\'s access tokens stop working immediately, not whenever they happen to expire.  Start changing your own email address.  Authenticated: the account is whoever holds the bearer token, and the body names only the *new* address. There is no `user_id` field, so one user cannot start a change on another\'s account.  **No password is required**, unlike the flow this replaces. Proof of control is the code WorkOS sends to the new address — a password would prove nothing about it, and since sign-in moved to WorkOS most accounts no longer have one worth asking for.
     * @summary Sign out, ending every session for the caller.
     * @param {AuthHandlersApiSendEmailChangeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendEmailChange(requestParameters: AuthHandlersApiSendEmailChangeRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).sendEmailChange(requestParameters.emailChangeSendRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AuthHandlersApiVerifyTokenRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verifyToken(requestParameters: AuthHandlersApiVerifyTokenRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).verifyToken(requestParameters.verifyRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AuthHandlersApiWorkosLoginRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public workosLogin(requestParameters: AuthHandlersApiWorkosLoginRequest, options?: RawAxiosRequestConfig) {
        return AuthHandlersApiFp(this.configuration).workosLogin(requestParameters.workosLoginRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * BankApi - axios parameter creator
 */
export const BankApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Assign a bank account to an HOA. **Owner/Admin only.**
         * @param {string} hoaId HOA id
         * @param {HoaAccountAssign} hoaAccountAssign 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignHoaAccount: async (hoaId: string, hoaAccountAssign: HoaAccountAssign, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('assignHoaAccount', 'hoaId', hoaId)
            // verify required parameter 'hoaAccountAssign' is not null or undefined
            assertParamExists('assignHoaAccount', 'hoaAccountAssign', hoaAccountAssign)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/accounts`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(hoaAccountAssign, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Cancel a pending or settled-but-unprocessed payable at the bank and reverse what it posted. **Owner/Admin of the company.**
         * @param {string} hoaId HOA id
         * @param {string} id Payable id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelHoaPayable: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('cancelHoaPayable', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('cancelHoaPayable', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payables/{id}/cancel`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Confirm (execute) a previewed transfer. **Owner/Admin (or SuperAdmin).** Idempotent on the transfer id — safe to retry.
         * @param {string} hoaId HOA id
         * @param {string} id bank_transfer id (from preview)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmHoaTransfer: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('confirmHoaTransfer', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('confirmHoaTransfer', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfers/{id}/confirm`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * This is the only human decision in the feature — after it, every occurrence fires unattended.
         * @summary Confirm a draft schedule, making it live. **Owner/Admin (or SuperAdmin).** No bank call. Idempotent on the schedule id.
         * @param {string} hoaId HOA id
         * @param {string} id schedule id (from preview)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmTransferSchedule: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('confirmTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('confirmTransferSchedule', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/{id}/confirm`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The candidate credentials are verified against the bank (a live account read) **before** anything is stored, so invalid creds are rejected and nothing is persisted on failure. The client secret is encrypted at rest and never returned. Re-posting for the same company/provider rotates the stored credentials.
         * @summary Store (or rotate) a company\'s bank credentials. **Owner (own company) or SuperAdmin.**
         * @param {BankConnectionCreate} bankConnectionCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBankConnection: async (bankConnectionCreate: BankConnectionCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bankConnectionCreate' is not null or undefined
            assertParamExists('createBankConnection', 'bankConnectionCreate', bankConnectionCreate)
            const localVarPath = `/api/v1/bank/connections`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bankConnectionCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Soft-delete a bank connection (`is_active = false`). **Owner (own company) or SuperAdmin.** Blocked (409) while HOA accounts are still assigned to it.
         * @param {string} id bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteBankConnection: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteBankConnection', 'id', id)
            const localVarPath = `/api/v1/bank/connections/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Terminal, and **not a delete**: `bank_transfers.schedule_id` points at this row, and the record that money moved must outlive the instruction that moved it.
         * @summary End a schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        endTransferSchedule: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('endTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('endTransferSchedule', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/{id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Checks that posted against this connection\'s accounts, from the bank. **Owner/Admin.** `from` is required when `to` is given.
         * @param {string} connectionId Bank connection id
         * @param {string | null} [accountNumber] 
         * @param {string | null} [checkNumber] 
         * @param {string | null} [amount] 
         * @param {string | null} [from] 
         * @param {string | null} [to] 
         * @param {string | null} [checkTypes] Comma-separated &#x60;Standard&#x60;, &#x60;RDC&#x60;. The bank defaults to &#x60;Standard&#x60; — checks we wrote — when omitted.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findConnectionClearedChecks: async (connectionId: string, accountNumber?: string | null, checkNumber?: string | null, amount?: string | null, from?: string | null, to?: string | null, checkTypes?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('findConnectionClearedChecks', 'connectionId', connectionId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/cleared-checks`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (accountNumber !== undefined) {
                localVarQueryParameter['account_number'] = accountNumber;
            }

            if (checkNumber !== undefined) {
                localVarQueryParameter['check_number'] = checkNumber;
            }

            if (amount !== undefined) {
                localVarQueryParameter['amount'] = amount;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = (from as any instanceof Date) ?
                    (from as any).toISOString().substring(0,10) :
                    from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = (to as any instanceof Date) ?
                    (to as any).toISOString().substring(0,10) :
                    to;
            }

            if (checkTypes !== undefined) {
                localVarQueryParameter['check_types'] = checkTypes;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Find files for a connection by filter (durable status search). **Owner/Admin (or SuperAdmin).**
         * @param {string} id bank_connection id
         * @param {string} [referenceId] Our ReferenceID
         * @param {string} [fileType] e.g. Payer
         * @param {string} [status] All | Pending | Complete | Error
         * @param {string} [fileUid] Bank FileUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findConnectionFiles: async (id: string, referenceId?: string, fileType?: string, status?: string, fileUid?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('findConnectionFiles', 'id', id)
            const localVarPath = `/api/v1/bank/connections/{id}/files/find`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (referenceId !== undefined) {
                localVarQueryParameter['reference_id'] = referenceId;
            }

            if (fileType !== undefined) {
                localVarQueryParameter['file_type'] = fileType;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (fileUid !== undefined) {
                localVarQueryParameter['file_uid'] = fileUid;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a single bank connection (no secret). **Owner (own company) or SuperAdmin.**
         * @param {string} id bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBankConnection: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getBankConnection', 'id', id)
            const localVarPath = `/api/v1/bank/connections/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Download a chargeback\'s cheque image. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {string} [side] front (default) or back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getChargebackImage: async (chargebackId: string, side?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'chargebackId' is not null or undefined
            assertParamExists('getChargebackImage', 'chargebackId', chargebackId)
            const localVarPath = `/api/v1/bank/chargebacks/{chargeback_id}/image`
                .replace(`{${"chargeback_id"}}`, encodeURIComponent(String(chargebackId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (side !== undefined) {
                localVarQueryParameter['side'] = side;
            }

            localVarHeaderParameter['Accept'] = 'image/png';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Download one chargeback notice PDF. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {string} statementId Statement id from the notice listing
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getChargebackNoticeFile: async (chargebackId: string, statementId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'chargebackId' is not null or undefined
            assertParamExists('getChargebackNoticeFile', 'chargebackId', chargebackId)
            // verify required parameter 'statementId' is not null or undefined
            assertParamExists('getChargebackNoticeFile', 'statementId', statementId)
            const localVarPath = `/api/v1/bank/chargebacks/{chargeback_id}/notices/{statement_id}/file`
                .replace(`{${"chargeback_id"}}`, encodeURIComponent(String(chargebackId)))
                .replace(`{${"statement_id"}}`, encodeURIComponent(String(statementId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary A posted check\'s image by the bank\'s `CheckID`. **Owner/Admin.**
         * @param {string} connectionId Bank connection id
         * @param {string} checkId The bank\&#39;s CheckID
         * @param {string | null} [side] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClearedCheckImage: async (connectionId: string, checkId: string, side?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('getClearedCheckImage', 'connectionId', connectionId)
            // verify required parameter 'checkId' is not null or undefined
            assertParamExists('getClearedCheckImage', 'checkId', checkId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/cleared-checks/{check_id}/image`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)))
                .replace(`{${"check_id"}}`, encodeURIComponent(String(checkId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (side !== undefined) {
                localVarQueryParameter['side'] = side;
            }

            localVarHeaderParameter['Accept'] = 'image/png';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Pending transactions for a company account. **Owner/Admin only.**
         * @param {string} companyId Company id
         * @param {string} accountNumber Account number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyAccountPending: async (companyId: string, accountNumber: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getCompanyAccountPending', 'companyId', companyId)
            // verify required parameter 'accountNumber' is not null or undefined
            assertParamExists('getCompanyAccountPending', 'accountNumber', accountNumber)
            const localVarPath = `/api/v1/bank/companies/{company_id}/accounts/{account_number}/transactions/pending`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)))
                .replace(`{${"account_number"}}`, encodeURIComponent(String(accountNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Available statements for a company account. **Owner/Admin only.**
         * @param {string} companyId Company id
         * @param {string} accountNumber Account number
         * @param {string} [from] Inclusive start statement date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end statement date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyAccountStatements: async (companyId: string, accountNumber: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getCompanyAccountStatements', 'companyId', companyId)
            // verify required parameter 'accountNumber' is not null or undefined
            assertParamExists('getCompanyAccountStatements', 'accountNumber', accountNumber)
            const localVarPath = `/api/v1/bank/companies/{company_id}/accounts/{account_number}/statements`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)))
                .replace(`{${"account_number"}}`, encodeURIComponent(String(accountNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Posted transactions for a company account. **Owner/Admin only.**
         * @param {string} companyId Company id
         * @param {string} accountNumber Account number
         * @param {string} [from] Inclusive start date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyAccountTransactions: async (companyId: string, accountNumber: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getCompanyAccountTransactions', 'companyId', companyId)
            // verify required parameter 'accountNumber' is not null or undefined
            assertParamExists('getCompanyAccountTransactions', 'accountNumber', accountNumber)
            const localVarPath = `/api/v1/bank/companies/{company_id}/accounts/{account_number}/transactions`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)))
                .replace(`{${"account_number"}}`, encodeURIComponent(String(accountNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Loads the company\'s active connection(s), calls the bank, and returns the accounts. Requires the bank to be reachable (whitelisted IP).
         * @summary View all bank accounts for a company, live from the bank. **Owner/Admin (or SuperAdmin) only.**
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyBankAccounts: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getCompanyBankAccounts', 'companyId', companyId)
            const localVarPath = `/api/v1/bank/companies/{company_id}/accounts`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Download a company statement PDF by statement id. **Owner/Admin.**
         * @param {string} companyId Company id
         * @param {string} statementId Statement id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyStatementFile: async (companyId: string, statementId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getCompanyStatementFile', 'companyId', companyId)
            // verify required parameter 'statementId' is not null or undefined
            assertParamExists('getCompanyStatementFile', 'statementId', statementId)
            const localVarPath = `/api/v1/bank/companies/{company_id}/statements/{statement_id}/file`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)))
                .replace(`{${"statement_id"}}`, encodeURIComponent(String(statementId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get one file\'s status by the bank\'s file id. **Owner/Admin (or SuperAdmin).**
         * @param {string} id bank_connection id
         * @param {string} fileId Bank FileID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getConnectionFile: async (id: string, fileId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getConnectionFile', 'id', id)
            // verify required parameter 'fileId' is not null or undefined
            assertParamExists('getConnectionFile', 'fileId', fileId)
            const localVarPath = `/api/v1/bank/connections/{id}/files/{file_id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"file_id"}}`, encodeURIComponent(String(fileId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary The check image behind a POD item. **Owner/Admin.** Live from the bank: finds the posted check by account, number and date, then fetches its image by the bank\'s `CheckID`.
         * @param {string} connectionId Bank connection id
         * @param {string} id NSF item id
         * @param {string | null} [side] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getConnectionNsfImage: async (connectionId: string, id: string, side?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('getConnectionNsfImage', 'connectionId', connectionId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getConnectionNsfImage', 'id', id)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/nsf/{id}/image`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (side !== undefined) {
                localVarQueryParameter['side'] = side;
            }

            localVarHeaderParameter['Accept'] = 'image/png';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Pending transactions for an HOA-assigned account. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountPending: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaAccountPending', 'id', id)
            const localVarPath = `/api/v1/bank/hoa-accounts/{id}/transactions/pending`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Available statements for an HOA-assigned account. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {string} [from] Inclusive start statement date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end statement date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountStatements: async (id: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaAccountStatements', 'id', id)
            const localVarPath = `/api/v1/bank/hoa-accounts/{id}/statements`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Posted transactions for an HOA-assigned account. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {string} [from] Inclusive start date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountTransactions: async (id: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaAccountTransactions', 'id', id)
            const localVarPath = `/api/v1/bank/hoa-accounts/{id}/transactions`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List an HOA\'s assigned accounts enriched with **live** balance/status. **Manager+ (HOA-scoped).** Requires the bank to be reachable.
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountsLive: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getHoaAccountsLive', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/accounts/live`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get one check. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id check id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaCheck: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getHoaCheck', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaCheck', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/checks/{id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary One payable. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id Payable id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaPayable: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getHoaPayable', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaPayable', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payables/{id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Download an HOA-assigned account\'s statement PDF. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {string} statementId Statement id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaStatementFile: async (id: string, statementId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaStatementFile', 'id', id)
            // verify required parameter 'statementId' is not null or undefined
            assertParamExists('getHoaStatementFile', 'statementId', statementId)
            const localVarPath = `/api/v1/bank/hoa-accounts/{id}/statements/{statement_id}/file`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"statement_id"}}`, encodeURIComponent(String(statementId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get one transfer (reconciles a still-pending one). **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id bank_transfer id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaTransfer: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getHoaTransfer', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoaTransfer', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfers/{id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary A homeowner\'s bank account on file, masked. **Manager+ on the HOA.**
         * @param {string} hoaId HOA id
         * @param {string} memberId Member id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMemberBankAccount: async (hoaId: string, memberId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getMemberBankAccount', 'hoaId', hoaId)
            // verify required parameter 'memberId' is not null or undefined
            assertParamExists('getMemberBankAccount', 'memberId', memberId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/members/{member_id}/bank-account`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"member_id"}}`, encodeURIComponent(String(memberId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get one payer submission (reconciles a still-processing one by ReferenceID). **Owner/Admin (or SuperAdmin).**
         * @param {string} submissionId payer_file_submission id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPayerSubmission: async (submissionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('getPayerSubmission', 'submissionId', submissionId)
            const localVarPath = `/api/v1/bank/payer-files/{submission_id}`
                .replace(`{${"submission_id"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the stored tab-delimited bytes as-uploaded (an audit snapshot), not a fresh re-assembly — the bank\'s get-file returns only status, so this is the only way to see what was actually sent. No bank call.
         * @summary Download the exact file we sent the bank for a submission. **Owner/Admin (or SuperAdmin).**
         * @param {string} submissionId payer_file_submission id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPayerSubmissionFile: async (submissionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('getPayerSubmissionFile', 'submissionId', submissionId)
            const localVarPath = `/api/v1/bank/payer-files/{submission_id}/file`
                .replace(`{${"submission_id"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'text/plain';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary One transfer schedule. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTransferSchedule: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getTransferSchedule', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/{id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary A vendor\'s bank details on file, masked. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} vendorId Vendor id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorBankAccount: async (hoaId: string, vendorId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getVendorBankAccount', 'hoaId', hoaId)
            // verify required parameter 'vendorId' is not null or undefined
            assertParamExists('getVendorBankAccount', 'vendorId', vendorId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/vendors/{vendor_id}/bank-account`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"vendor_id"}}`, encodeURIComponent(String(vendorId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Which accounts on a connection are pulled daily. **Owner/Admin only.**
         * @param {string} connectionId bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAccountTracking: async (connectionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('listAccountTracking', 'connectionId', connectionId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/account-tracking`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * An empty list is a legitimate answer — a notice can lag the chargeback that caused it.
         * @summary List a chargeback\'s notice documents. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listChargebackNotices: async (chargebackId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'chargebackId' is not null or undefined
            assertParamExists('listChargebackNotices', 'chargebackId', chargebackId)
            const localVarPath = `/api/v1/bank/chargebacks/{chargeback_id}/notices`
                .replace(`{${"chargeback_id"}}`, encodeURIComponent(String(chargebackId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List a company\'s bank connections (no secret). **Owner (own company) or SuperAdmin.**
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCompanyConnections: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('listCompanyConnections', 'companyId', companyId)
            const localVarPath = `/api/v1/bank/companies/{company_id}/connections`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reads our own rows — no bank call.
         * @summary What one connection has had taken back. **Owner/Admin.**
         * @param {string} connectionId Bank connection id
         * @param {boolean} [openOnly] Only rows that have not reversed
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionChargebacks: async (connectionId: string, openOnly?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('listConnectionChargebacks', 'connectionId', connectionId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/chargebacks`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (openOnly !== undefined) {
                localVarQueryParameter['open_only'] = openOnly;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List files uploaded today for a connection (bank status view). **Owner/Admin (or SuperAdmin).**
         * @param {string} id bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionFiles: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('listConnectionFiles', 'id', id)
            const localVarPath = `/api/v1/bank/connections/{id}/files`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Items presented against insufficient funds on one connection\'s accounts. **Owner/Admin.** Reads our own rows — no bank call.
         * @param {string} connectionId Bank connection id
         * @param {boolean | null} [openOnly] Only items nobody has decided and whose deadline has not passed.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionNsf: async (connectionId: string, openOnly?: boolean | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('listConnectionNsf', 'connectionId', connectionId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/nsf`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (openOnly !== undefined) {
                localVarQueryParameter['open_only'] = openOnly;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary The settle queue: a connection\'s payables, pending first. **Owner/Admin.**
         * @param {string} connectionId Bank connection id
         * @param {string | null} [direction] &#x60;payment&#x60; | &#x60;collection&#x60;
         * @param {string | null} [status] One of ours: &#x60;pending&#x60;, &#x60;settled&#x60;, …
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionPayables: async (connectionId: string, direction?: string | null, status?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('listConnectionPayables', 'connectionId', connectionId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/payables`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Connection-scoped rather than per-association, because a record whose association did not resolve has no HOA to be filed under and cannot appear in that association\'s review list. This is where it is visible.  `?unmatched_only=true` narrows to what needs somebody.
         * @summary What one connection\'s lockbox has delivered. **Owner/Admin only.**
         * @param {string} connectionId bank_connection id
         * @param {boolean} [unmatchedOnly] Only records that did not resolve to a home
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionRemittance: async (connectionId: string, unmatchedOnly?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('listConnectionRemittance', 'connectionId', connectionId)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/remittance`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (unmatchedOnly !== undefined) {
                localVarQueryParameter['unmatched_only'] = unmatchedOnly;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List an HOA\'s assigned accounts (stored metadata, no bank call). **Manager+ (HOA-scoped).**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaAccounts: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaAccounts', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/accounts`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List an HOA\'s check register, newest issue date first. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string | null} [account] Restrict to one &#x60;hoa_bank_accounts&#x60; id.
         * @param {string | null} [status] &#x60;issued&#x60; | &#x60;void&#x60;.
         * @param {string | null} [from] Inclusive issue-date range, &#x60;YYYY-MM-DD&#x60;.
         * @param {string | null} [to] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaChecks: async (hoaId: string, account?: string | null, status?: string | null, from?: string | null, to?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaChecks', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/checks`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (account !== undefined) {
                localVarQueryParameter['account'] = account;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = (from as any instanceof Date) ?
                    (from as any).toISOString().substring(0,10) :
                    from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = (to as any instanceof Date) ?
                    (to as any).toISOString().substring(0,10) :
                    to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary An association\'s NSF items. **Manager+.** No bank call.
         * @param {string} hoaId HOA id
         * @param {boolean | null} [openOnly] Only items nobody has decided and whose deadline has not passed.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaNsf: async (hoaId: string, openOnly?: boolean | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaNsf', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/nsf`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (openOnly !== undefined) {
                localVarQueryParameter['open_only'] = openOnly;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary An association\'s payables, newest first. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string | null} [direction] &#x60;payment&#x60; | &#x60;collection&#x60;
         * @param {string | null} [status] One of ours: &#x60;pending&#x60;, &#x60;settled&#x60;, …
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaPayables: async (hoaId: string, direction?: string | null, status?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaPayables', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payables`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (direction !== undefined) {
                localVarQueryParameter['direction'] = direction;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List an HOA\'s transfer records (newest first). **Manager+.**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaTransfers: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaTransfers', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfers`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List an HOA\'s payer submissions (our audit table). **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPayerSubmissions: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listPayerSubmissions', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payer-files`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary List an HOA\'s transfer schedules, newest first. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTransferSchedules: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listTransferSchedules', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stops it firing and keeps its history. Resuming does **not** catch up — the job only ever asks whether a schedule is due today.
         * @summary Pause a schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        pauseTransferSchedule: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('pauseTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('pauseTransferSchedule', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/{id}/pause`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Final through the API: a decision is changed only by phoning bank support. Refused once the item is decided, or after **1pm Central on its expiry date**. Exactly one request is ever sent for an item; a concurrent decision gets a 409.
         * @summary Ask the bank to **pay** an NSF item. **Owner/Admin (or SuperAdmin).**
         * @param {string} connectionId Bank connection id
         * @param {string} id NSF item id
         * @param {NsfPayDecision} nsfPayDecision 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        payConnectionNsf: async (connectionId: string, id: string, nsfPayDecision: NsfPayDecision, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('payConnectionNsf', 'connectionId', connectionId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('payConnectionNsf', 'id', id)
            // verify required parameter 'nsfPayDecision' is not null or undefined
            assertParamExists('payConnectionNsf', 'nsfPayDecision', nsfPayDecision)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/nsf/{id}/pay`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(nsfPayDecision, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The exit from every hold. The unattended pass declines anything it cannot attribute with confidence; this is how a person who has looked at the record turns that decision into a payment.  **Makes no bank call.** Everything it needs was stored at ingest.  It goes through `PaymentService::record` like every other payment, and it refuses the holds a person cannot clear by looking — a record that resolves to no home, or whose amount is missing, unreadable, non-positive, or disagrees with itself. Those are facts about the record, not about our willingness to act on it.
         * @summary Post one held remittance record to the ledger **by hand**. **Owner/Admin only.**
         * @param {string} recordId bank_remittance_records id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postRemittanceRecord: async (recordId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'recordId' is not null or undefined
            assertParamExists('postRemittanceRecord', 'recordId', recordId)
            const localVarPath = `/api/v1/bank/remittance/{record_id}/post`
                .replace(`{${"record_id"}}`, encodeURIComponent(String(recordId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Preview a transfer between two of an HOA\'s assigned accounts. **Owner/Admin (or SuperAdmin).** No money moves — validates and returns the created (unconfirmed) transfer plus live balance context.
         * @param {string} hoaId HOA id
         * @param {TransferPreviewRequest} transferPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewHoaTransfer: async (hoaId: string, transferPreviewRequest: TransferPreviewRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('previewHoaTransfer', 'hoaId', hoaId)
            // verify required parameter 'transferPreviewRequest' is not null or undefined
            assertParamExists('previewHoaTransfer', 'transferPreviewRequest', transferPreviewRequest)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfers/preview`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(transferPreviewRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Preview (assemble) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** No bank call — returns the exact file text + count. Optional `bank_connection_id` selects the lockbox when the company has more than one connection.
         * @param {string} hoaId HOA id
         * @param {PayerFileRequest} payerFileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewPayerFile: async (hoaId: string, payerFileRequest: PayerFileRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('previewPayerFile', 'hoaId', hoaId)
            // verify required parameter 'payerFileRequest' is not null or undefined
            assertParamExists('previewPayerFile', 'payerFileRequest', payerFileRequest)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payer-file/preview`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(payerFileRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Preview a recurring transfer schedule. **Owner/Admin (or SuperAdmin).** Stores a **draft** and returns the next five days it would fire. No money moves and no schedule is live until confirm.
         * @param {string} hoaId HOA id
         * @param {TransferScheduleCreate} transferScheduleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewTransferSchedule: async (hoaId: string, transferScheduleCreate: TransferScheduleCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('previewTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'transferScheduleCreate' is not null or undefined
            assertParamExists('previewTransferSchedule', 'transferScheduleCreate', transferScheduleCreate)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/preview`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(transferScheduleCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Record an issued check. **Owner/Admin (or SuperAdmin).** Posts the journal entry and registers the check with the bank\'s Positive Pay service in one action; a bank-side failure is returned as a `failed` row, not an error.
         * @param {string} hoaId HOA id
         * @param {CheckCreate} checkCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordHoaCheck: async (hoaId: string, checkCreate: CheckCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('recordHoaCheck', 'hoaId', hoaId)
            // verify required parameter 'checkCreate' is not null or undefined
            assertParamExists('recordHoaCheck', 'checkCreate', checkCreate)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/checks`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(checkCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * **Attaches only — it does not reverse.** The next chargeback run applies the same guards it applies to anything the matcher resolved, so the unattended path stays the single place money moves.
         * @summary Attach a held chargeback to the payment it reverses. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {ChargebackResolveRequest} chargebackResolveRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveChargeback: async (chargebackId: string, chargebackResolveRequest: ChargebackResolveRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'chargebackId' is not null or undefined
            assertParamExists('resolveChargeback', 'chargebackId', chargebackId)
            // verify required parameter 'chargebackResolveRequest' is not null or undefined
            assertParamExists('resolveChargeback', 'chargebackResolveRequest', chargebackResolveRequest)
            const localVarPath = `/api/v1/bank/chargebacks/{chargeback_id}/resolve`
                .replace(`{${"chargeback_id"}}`, encodeURIComponent(String(chargebackId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(chargebackResolveRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Re-validates the accounts first: months may have passed, and an account unassigned in the meantime should stop the resume rather than surface at 06:30 as a failed occurrence.
         * @summary Resume a paused schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resumeTransferSchedule: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('resumeTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('resumeTransferSchedule', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/{id}/resume`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Remove a homeowner\'s bank account on file. **Owner/Admin of the company.** The homeowner can add another.
         * @param {string} hoaId HOA id
         * @param {string} memberId Member id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        retireMemberBankAccount: async (hoaId: string, memberId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('retireMemberBankAccount', 'hoaId', hoaId)
            // verify required parameter 'memberId' is not null or undefined
            assertParamExists('retireMemberBankAccount', 'memberId', memberId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/members/{member_id}/bank-account`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"member_id"}}`, encodeURIComponent(String(memberId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Retire a vendor\'s bank details. **Owner/Admin of the company; not super admins.** The row is kept for the payments made to it.
         * @param {string} hoaId HOA id
         * @param {string} vendorId Vendor id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        retireVendorBankAccount: async (hoaId: string, vendorId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('retireVendorBankAccount', 'hoaId', hoaId)
            // verify required parameter 'vendorId' is not null or undefined
            assertParamExists('retireVendorBankAccount', 'vendorId', vendorId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/vendors/{vendor_id}/bank-account`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"vendor_id"}}`, encodeURIComponent(String(vendorId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Ask the bank to **return** an NSF item. **Owner/Admin (or SuperAdmin).** The bank\'s default, made explicit; same finality, cutoff and one-send rule as pay.
         * @param {string} connectionId Bank connection id
         * @param {string} id NSF item id
         * @param {NsfReturnDecision} nsfReturnDecision 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        returnConnectionNsf: async (connectionId: string, id: string, nsfReturnDecision: NsfReturnDecision, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('returnConnectionNsf', 'connectionId', connectionId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('returnConnectionNsf', 'id', id)
            // verify required parameter 'nsfReturnDecision' is not null or undefined
            assertParamExists('returnConnectionNsf', 'nsfReturnDecision', nsfReturnDecision)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/nsf/{id}/return`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(nsfReturnDecision, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Send a `pending` or `failed` Positive Pay entry again. **Owner/Admin (or SuperAdmin).** Reconciles with the bank first, so an entry that actually landed is recorded rather than repeated.
         * @param {string} hoaId HOA id
         * @param {string} id check id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendHoaCheck: async (hoaId: string, id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('sendHoaCheck', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('sendHoaCheck', 'id', id)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/checks/{id}/send`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Makes **no bank call**. Enabling leaves the account\'s backfill pending, and the next scheduled run fetches its 90 days.
         * @summary Turn the daily transaction pull on or off for one account. **Owner/Admin only.**
         * @param {string} connectionId bank_connection id
         * @param {string} accountNumber Account number at the bank
         * @param {AccountTrackingUpdate} accountTrackingUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setAccountTracking: async (connectionId: string, accountNumber: string, accountTrackingUpdate: AccountTrackingUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('setAccountTracking', 'connectionId', connectionId)
            // verify required parameter 'accountNumber' is not null or undefined
            assertParamExists('setAccountTracking', 'accountNumber', accountNumber)
            // verify required parameter 'accountTrackingUpdate' is not null or undefined
            assertParamExists('setAccountTracking', 'accountTrackingUpdate', accountTrackingUpdate)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/accounts/{account_number}/tracking`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)))
                .replace(`{${"account_number"}}`, encodeURIComponent(String(accountNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(accountTrackingUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deliberately not part of the generic HOA update. The roles happen to match today, but `can_update_hoa` is the bar for editing an association\'s address and `can_submit_payer_file` is the bar for sending its roster to a bank — and this switch does the second. If HOA editing is ever widened to Managers, this must not widen with it.  Enabling is refused for an association with no bank assigned: a switch that reads as on and does nothing is worse than an error.
         * @summary Turn the scheduled daily payer upload on or off for one HOA. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {PayerAutoUploadUpdate} payerAutoUploadUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setPayerAutoUpload: async (hoaId: string, payerAutoUploadUpdate: PayerAutoUploadUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('setPayerAutoUpload', 'hoaId', hoaId)
            // verify required parameter 'payerAutoUploadUpdate' is not null or undefined
            assertParamExists('setPayerAutoUpload', 'payerAutoUploadUpdate', payerAutoUploadUpdate)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payer-file/auto-upload`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(payerAutoUploadUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Set a vendor\'s bank details, replacing any on file. **Owner/Admin of the company; not super admins.** The account number is encrypted on arrival and never returned.
         * @param {string} hoaId HOA id
         * @param {string} vendorId Vendor id
         * @param {CounterpartyBankAccountUpsert} counterpartyBankAccountUpsert 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setVendorBankAccount: async (hoaId: string, vendorId: string, counterpartyBankAccountUpsert: CounterpartyBankAccountUpsert, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('setVendorBankAccount', 'hoaId', hoaId)
            // verify required parameter 'vendorId' is not null or undefined
            assertParamExists('setVendorBankAccount', 'vendorId', vendorId)
            // verify required parameter 'counterpartyBankAccountUpsert' is not null or undefined
            assertParamExists('setVendorBankAccount', 'counterpartyBankAccountUpsert', counterpartyBankAccountUpsert)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/vendors/{vendor_id}/bank-account`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"vendor_id"}}`, encodeURIComponent(String(vendorId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(counterpartyBankAccountUpsert, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Settle pending payables with a one-time RSA code. **Owner/Admin of the company. Moves money.** The code is sent once and never stored; the RSA token locks after three wrong codes.
         * @param {string} connectionId Bank connection id
         * @param {PayableSettleRequest} payableSettleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        settleConnectionPayables: async (connectionId: string, payableSettleRequest: PayableSettleRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'connectionId' is not null or undefined
            assertParamExists('settleConnectionPayables', 'connectionId', connectionId)
            // verify required parameter 'payableSettleRequest' is not null or undefined
            assertParamExists('settleConnectionPayables', 'payableSettleRequest', payableSettleRequest)
            const localVarPath = `/api/v1/bank/connections/{connection_id}/payables/settle`
                .replace(`{${"connection_id"}}`, encodeURIComponent(String(connectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(payableSettleRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Assemble + submit (upload) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** Records a submission (audit) and uploads; the returned record\'s `status`/`error_*` carry the outcome.
         * @param {string} hoaId HOA id
         * @param {PayerFileRequest} payerFileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitPayerFile: async (hoaId: string, payerFileRequest: PayerFileRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('submitPayerFile', 'hoaId', hoaId)
            // verify required parameter 'payerFileRequest' is not null or undefined
            assertParamExists('submitPayerFile', 'payerFileRequest', payerFileRequest)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payer-file/submit`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(payerFileRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Pay a vendor by ACH: validates with the bank and leaves the item pending in the settle queue. **Owner/Admin of the company.** Moves nothing.
         * @param {string} hoaId HOA id
         * @param {VendorPaymentCreate} vendorPaymentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitVendorPayment: async (hoaId: string, vendorPaymentCreate: VendorPaymentCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('submitVendorPayment', 'hoaId', hoaId)
            // verify required parameter 'vendorPaymentCreate' is not null or undefined
            assertParamExists('submitVendorPayment', 'vendorPaymentCreate', vendorPaymentCreate)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/payables`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(vendorPaymentCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Ask the bank which of this HOA\'s accepted entries have been imported into Positive Pay, now rather than on the job\'s schedule. **Owner/Admin (or SuperAdmin).** Reads only; sends nothing.
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncHoaChecks: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('syncHoaChecks', 'hoaId', hoaId)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/checks/sync`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Unassign a bank account from an HOA. **Owner/Admin only.**
         * @param {string} id hoa_bank_account id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignHoaAccount: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('unassignHoaAccount', 'id', id)
            const localVarPath = `/api/v1/bank/hoa-accounts/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * `label` / `client_id` / `is_active` are updated when present. A new `client_secret` (and/or `client_id`) triggers re-verification against the bank before the change is persisted; the secret is never returned.
         * @summary Update a bank connection. **Owner (own company) or SuperAdmin.**
         * @param {string} id bank_connection id
         * @param {BankConnectionUpdate} bankConnectionUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBankConnection: async (id: string, bankConnectionUpdate: BankConnectionUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateBankConnection', 'id', id)
            // verify required parameter 'bankConnectionUpdate' is not null or undefined
            assertParamExists('updateBankConnection', 'bankConnectionUpdate', bankConnectionUpdate)
            const localVarPath = `/api/v1/bank/connections/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bankConnectionUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Takes effect from the next evaluation; occurrences already sent are untouched. An omitted field is left alone, an explicit `null` clears one — which is how changing frequency drops the day that no longer applies.
         * @summary Change a transfer schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {TransferScheduleUpdate} transferScheduleUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateTransferSchedule: async (hoaId: string, id: string, transferScheduleUpdate: TransferScheduleUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('updateTransferSchedule', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateTransferSchedule', 'id', id)
            // verify required parameter 'transferScheduleUpdate' is not null or undefined
            assertParamExists('updateTransferSchedule', 'transferScheduleUpdate', transferScheduleUpdate)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/transfer-schedules/{id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(transferScheduleUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Void a recorded check. **Owner/Admin (or SuperAdmin).** Reverses the journal entry and, if the bank accepted the issue, registers the void with the bank too.
         * @param {string} hoaId HOA id
         * @param {string} id check id
         * @param {CheckVoidRequest} checkVoidRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        voidHoaCheck: async (hoaId: string, id: string, checkVoidRequest: CheckVoidRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('voidHoaCheck', 'hoaId', hoaId)
            // verify required parameter 'id' is not null or undefined
            assertParamExists('voidHoaCheck', 'id', id)
            // verify required parameter 'checkVoidRequest' is not null or undefined
            assertParamExists('voidHoaCheck', 'checkVoidRequest', checkVoidRequest)
            const localVarPath = `/api/v1/bank/hoas/{hoa_id}/checks/{id}/void`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(checkVoidRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BankApi - functional programming interface
 */
export const BankApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BankApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Assign a bank account to an HOA. **Owner/Admin only.**
         * @param {string} hoaId HOA id
         * @param {HoaAccountAssign} hoaAccountAssign 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assignHoaAccount(hoaId: string, hoaAccountAssign: HoaAccountAssign, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assignHoaAccount(hoaId, hoaAccountAssign, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.assignHoaAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Cancel a pending or settled-but-unprocessed payable at the bank and reverse what it posted. **Owner/Admin of the company.**
         * @param {string} hoaId HOA id
         * @param {string} id Payable id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelHoaPayable(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayableReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelHoaPayable(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.cancelHoaPayable']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Confirm (execute) a previewed transfer. **Owner/Admin (or SuperAdmin).** Idempotent on the transfer id — safe to retry.
         * @param {string} hoaId HOA id
         * @param {string} id bank_transfer id (from preview)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async confirmHoaTransfer(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.confirmHoaTransfer(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.confirmHoaTransfer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * This is the only human decision in the feature — after it, every occurrence fires unattended.
         * @summary Confirm a draft schedule, making it live. **Owner/Admin (or SuperAdmin).** No bank call. Idempotent on the schedule id.
         * @param {string} hoaId HOA id
         * @param {string} id schedule id (from preview)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async confirmTransferSchedule(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.confirmTransferSchedule(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.confirmTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The candidate credentials are verified against the bank (a live account read) **before** anything is stored, so invalid creds are rejected and nothing is persisted on failure. The client secret is encrypted at rest and never returned. Re-posting for the same company/provider rotates the stored credentials.
         * @summary Store (or rotate) a company\'s bank credentials. **Owner (own company) or SuperAdmin.**
         * @param {BankConnectionCreate} bankConnectionCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createBankConnection(bankConnectionCreate: BankConnectionCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BankConnectionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createBankConnection(bankConnectionCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.createBankConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Soft-delete a bank connection (`is_active = false`). **Owner (own company) or SuperAdmin.** Blocked (409) while HOA accounts are still assigned to it.
         * @param {string} id bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteBankConnection(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteBankConnection(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.deleteBankConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Terminal, and **not a delete**: `bank_transfers.schedule_id` points at this row, and the record that money moved must outlive the instruction that moved it.
         * @summary End a schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async endTransferSchedule(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.endTransferSchedule(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.endTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Checks that posted against this connection\'s accounts, from the bank. **Owner/Admin.** `from` is required when `to` is given.
         * @param {string} connectionId Bank connection id
         * @param {string | null} [accountNumber] 
         * @param {string | null} [checkNumber] 
         * @param {string | null} [amount] 
         * @param {string | null} [from] 
         * @param {string | null} [to] 
         * @param {string | null} [checkTypes] Comma-separated &#x60;Standard&#x60;, &#x60;RDC&#x60;. The bank defaults to &#x60;Standard&#x60; — checks we wrote — when omitted.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findConnectionClearedChecks(connectionId: string, accountNumber?: string | null, checkNumber?: string | null, amount?: string | null, from?: string | null, to?: string | null, checkTypes?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClearedCheckList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findConnectionClearedChecks(connectionId, accountNumber, checkNumber, amount, from, to, checkTypes, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.findConnectionClearedChecks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Find files for a connection by filter (durable status search). **Owner/Admin (or SuperAdmin).**
         * @param {string} id bank_connection id
         * @param {string} [referenceId] Our ReferenceID
         * @param {string} [fileType] e.g. Payer
         * @param {string} [status] All | Pending | Complete | Error
         * @param {string} [fileUid] Bank FileUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findConnectionFiles(id: string, referenceId?: string, fileType?: string, status?: string, fileUid?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BankFileList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findConnectionFiles(id, referenceId, fileType, status, fileUid, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.findConnectionFiles']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a single bank connection (no secret). **Owner (own company) or SuperAdmin.**
         * @param {string} id bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBankConnection(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BankConnectionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBankConnection(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getBankConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Download a chargeback\'s cheque image. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {string} [side] front (default) or back
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getChargebackImage(chargebackId: string, side?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getChargebackImage(chargebackId, side, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getChargebackImage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Download one chargeback notice PDF. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {string} statementId Statement id from the notice listing
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getChargebackNoticeFile(chargebackId: string, statementId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getChargebackNoticeFile(chargebackId, statementId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getChargebackNoticeFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary A posted check\'s image by the bank\'s `CheckID`. **Owner/Admin.**
         * @param {string} connectionId Bank connection id
         * @param {string} checkId The bank\&#39;s CheckID
         * @param {string | null} [side] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClearedCheckImage(connectionId: string, checkId: string, side?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClearedCheckImage(connectionId, checkId, side, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getClearedCheckImage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Pending transactions for a company account. **Owner/Admin only.**
         * @param {string} companyId Company id
         * @param {string} accountNumber Account number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompanyAccountPending(companyId: string, accountNumber: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PendingTransactionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompanyAccountPending(companyId, accountNumber, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getCompanyAccountPending']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Available statements for a company account. **Owner/Admin only.**
         * @param {string} companyId Company id
         * @param {string} accountNumber Account number
         * @param {string} [from] Inclusive start statement date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end statement date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompanyAccountStatements(companyId: string, accountNumber: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<StatementList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompanyAccountStatements(companyId, accountNumber, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getCompanyAccountStatements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Posted transactions for a company account. **Owner/Admin only.**
         * @param {string} companyId Company id
         * @param {string} accountNumber Account number
         * @param {string} [from] Inclusive start date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompanyAccountTransactions(companyId: string, accountNumber: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransactionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompanyAccountTransactions(companyId, accountNumber, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getCompanyAccountTransactions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Loads the company\'s active connection(s), calls the bank, and returns the accounts. Requires the bank to be reachable (whitelisted IP).
         * @summary View all bank accounts for a company, live from the bank. **Owner/Admin (or SuperAdmin) only.**
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompanyBankAccounts(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyBankAccountsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompanyBankAccounts(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getCompanyBankAccounts']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Download a company statement PDF by statement id. **Owner/Admin.**
         * @param {string} companyId Company id
         * @param {string} statementId Statement id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompanyStatementFile(companyId: string, statementId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompanyStatementFile(companyId, statementId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getCompanyStatementFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get one file\'s status by the bank\'s file id. **Owner/Admin (or SuperAdmin).**
         * @param {string} id bank_connection id
         * @param {string} fileId Bank FileID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getConnectionFile(id: string, fileId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BankFileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getConnectionFile(id, fileId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getConnectionFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary The check image behind a POD item. **Owner/Admin.** Live from the bank: finds the posted check by account, number and date, then fetches its image by the bank\'s `CheckID`.
         * @param {string} connectionId Bank connection id
         * @param {string} id NSF item id
         * @param {string | null} [side] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getConnectionNsfImage(connectionId: string, id: string, side?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getConnectionNsfImage(connectionId, id, side, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getConnectionNsfImage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Pending transactions for an HOA-assigned account. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaAccountPending(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PendingTransactionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaAccountPending(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaAccountPending']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Available statements for an HOA-assigned account. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {string} [from] Inclusive start statement date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end statement date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaAccountStatements(id: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<StatementList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaAccountStatements(id, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaAccountStatements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Posted transactions for an HOA-assigned account. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {string} [from] Inclusive start date (YYYY-MM-DD)
         * @param {string} [to] Inclusive end date (YYYY-MM-DD); requires &#x60;from&#x60;
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaAccountTransactions(id: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransactionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaAccountTransactions(id, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaAccountTransactions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List an HOA\'s assigned accounts enriched with **live** balance/status. **Manager+ (HOA-scoped).** Requires the bank to be reachable.
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaAccountsLive(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaBankAccountLiveList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaAccountsLive(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaAccountsLive']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get one check. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id check id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaCheck(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaCheck(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaCheck']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary One payable. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id Payable id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaPayable(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayableReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaPayable(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaPayable']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Download an HOA-assigned account\'s statement PDF. **Manager+.**
         * @param {string} id hoa_bank_account id
         * @param {string} statementId Statement id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaStatementFile(id: string, statementId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaStatementFile(id, statementId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaStatementFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get one transfer (reconciles a still-pending one). **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id bank_transfer id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaTransfer(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaTransfer(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getHoaTransfer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary A homeowner\'s bank account on file, masked. **Manager+ on the HOA.**
         * @param {string} hoaId HOA id
         * @param {string} memberId Member id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMemberBankAccount(hoaId: string, memberId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMemberBankAccount(hoaId, memberId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getMemberBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get one payer submission (reconciles a still-processing one by ReferenceID). **Owner/Admin (or SuperAdmin).**
         * @param {string} submissionId payer_file_submission id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPayerSubmission(submissionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayerSubmissionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPayerSubmission(submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getPayerSubmission']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the stored tab-delimited bytes as-uploaded (an audit snapshot), not a fresh re-assembly — the bank\'s get-file returns only status, so this is the only way to see what was actually sent. No bank call.
         * @summary Download the exact file we sent the bank for a submission. **Owner/Admin (or SuperAdmin).**
         * @param {string} submissionId payer_file_submission id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPayerSubmissionFile(submissionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPayerSubmissionFile(submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getPayerSubmissionFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary One transfer schedule. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTransferSchedule(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTransferSchedule(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary A vendor\'s bank details on file, masked. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} vendorId Vendor id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVendorBankAccount(hoaId: string, vendorId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVendorBankAccount(hoaId, vendorId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.getVendorBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Which accounts on a connection are pulled daily. **Owner/Admin only.**
         * @param {string} connectionId bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAccountTracking(connectionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AccountTrackingList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAccountTracking(connectionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listAccountTracking']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * An empty list is a legitimate answer — a notice can lag the chargeback that caused it.
         * @summary List a chargeback\'s notice documents. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listChargebackNotices(chargebackId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<StatementList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listChargebackNotices(chargebackId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listChargebackNotices']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List a company\'s bank connections (no secret). **Owner (own company) or SuperAdmin.**
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listCompanyConnections(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<BankConnectionReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listCompanyConnections(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listCompanyConnections']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reads our own rows — no bank call.
         * @summary What one connection has had taken back. **Owner/Admin.**
         * @param {string} connectionId Bank connection id
         * @param {boolean} [openOnly] Only rows that have not reversed
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listConnectionChargebacks(connectionId: string, openOnly?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ChargebackList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listConnectionChargebacks(connectionId, openOnly, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listConnectionChargebacks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List files uploaded today for a connection (bank status view). **Owner/Admin (or SuperAdmin).**
         * @param {string} id bank_connection id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listConnectionFiles(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BankFileList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listConnectionFiles(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listConnectionFiles']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Items presented against insufficient funds on one connection\'s accounts. **Owner/Admin.** Reads our own rows — no bank call.
         * @param {string} connectionId Bank connection id
         * @param {boolean | null} [openOnly] Only items nobody has decided and whose deadline has not passed.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listConnectionNsf(connectionId: string, openOnly?: boolean | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NsfList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listConnectionNsf(connectionId, openOnly, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listConnectionNsf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary The settle queue: a connection\'s payables, pending first. **Owner/Admin.**
         * @param {string} connectionId Bank connection id
         * @param {string | null} [direction] &#x60;payment&#x60; | &#x60;collection&#x60;
         * @param {string | null} [status] One of ours: &#x60;pending&#x60;, &#x60;settled&#x60;, …
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listConnectionPayables(connectionId: string, direction?: string | null, status?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayableList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listConnectionPayables(connectionId, direction, status, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listConnectionPayables']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Connection-scoped rather than per-association, because a record whose association did not resolve has no HOA to be filed under and cannot appear in that association\'s review list. This is where it is visible.  `?unmatched_only=true` narrows to what needs somebody.
         * @summary What one connection\'s lockbox has delivered. **Owner/Admin only.**
         * @param {string} connectionId bank_connection id
         * @param {boolean} [unmatchedOnly] Only records that did not resolve to a home
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listConnectionRemittance(connectionId: string, unmatchedOnly?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RemittanceList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listConnectionRemittance(connectionId, unmatchedOnly, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listConnectionRemittance']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List an HOA\'s assigned accounts (stored metadata, no bank call). **Manager+ (HOA-scoped).**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaAccounts(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaBankAccountList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaAccounts(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listHoaAccounts']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List an HOA\'s check register, newest issue date first. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string | null} [account] Restrict to one &#x60;hoa_bank_accounts&#x60; id.
         * @param {string | null} [status] &#x60;issued&#x60; | &#x60;void&#x60;.
         * @param {string | null} [from] Inclusive issue-date range, &#x60;YYYY-MM-DD&#x60;.
         * @param {string | null} [to] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaChecks(hoaId: string, account?: string | null, status?: string | null, from?: string | null, to?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaChecks(hoaId, account, status, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listHoaChecks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary An association\'s NSF items. **Manager+.** No bank call.
         * @param {string} hoaId HOA id
         * @param {boolean | null} [openOnly] Only items nobody has decided and whose deadline has not passed.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaNsf(hoaId: string, openOnly?: boolean | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NsfList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaNsf(hoaId, openOnly, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listHoaNsf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary An association\'s payables, newest first. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string | null} [direction] &#x60;payment&#x60; | &#x60;collection&#x60;
         * @param {string | null} [status] One of ours: &#x60;pending&#x60;, &#x60;settled&#x60;, …
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaPayables(hoaId: string, direction?: string | null, status?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayableList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaPayables(hoaId, direction, status, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listHoaPayables']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List an HOA\'s transfer records (newest first). **Manager+.**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaTransfers(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaTransfers(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listHoaTransfers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List an HOA\'s payer submissions (our audit table). **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listPayerSubmissions(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayerSubmissionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listPayerSubmissions(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listPayerSubmissions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary List an HOA\'s transfer schedules, newest first. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listTransferSchedules(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listTransferSchedules(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.listTransferSchedules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stops it firing and keeps its history. Resuming does **not** catch up — the job only ever asks whether a schedule is due today.
         * @summary Pause a schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async pauseTransferSchedule(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.pauseTransferSchedule(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.pauseTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Final through the API: a decision is changed only by phoning bank support. Refused once the item is decided, or after **1pm Central on its expiry date**. Exactly one request is ever sent for an item; a concurrent decision gets a 409.
         * @summary Ask the bank to **pay** an NSF item. **Owner/Admin (or SuperAdmin).**
         * @param {string} connectionId Bank connection id
         * @param {string} id NSF item id
         * @param {NsfPayDecision} nsfPayDecision 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async payConnectionNsf(connectionId: string, id: string, nsfPayDecision: NsfPayDecision, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NsfItemReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.payConnectionNsf(connectionId, id, nsfPayDecision, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.payConnectionNsf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The exit from every hold. The unattended pass declines anything it cannot attribute with confidence; this is how a person who has looked at the record turns that decision into a payment.  **Makes no bank call.** Everything it needs was stored at ingest.  It goes through `PaymentService::record` like every other payment, and it refuses the holds a person cannot clear by looking — a record that resolves to no home, or whose amount is missing, unreadable, non-positive, or disagrees with itself. Those are facts about the record, not about our willingness to act on it.
         * @summary Post one held remittance record to the ledger **by hand**. **Owner/Admin only.**
         * @param {string} recordId bank_remittance_records id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postRemittanceRecord(recordId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RemittanceRecordReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postRemittanceRecord(recordId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.postRemittanceRecord']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Preview a transfer between two of an HOA\'s assigned accounts. **Owner/Admin (or SuperAdmin).** No money moves — validates and returns the created (unconfirmed) transfer plus live balance context.
         * @param {string} hoaId HOA id
         * @param {TransferPreviewRequest} transferPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewHoaTransfer(hoaId: string, transferPreviewRequest: TransferPreviewRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferPreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewHoaTransfer(hoaId, transferPreviewRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.previewHoaTransfer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Preview (assemble) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** No bank call — returns the exact file text + count. Optional `bank_connection_id` selects the lockbox when the company has more than one connection.
         * @param {string} hoaId HOA id
         * @param {PayerFileRequest} payerFileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewPayerFile(hoaId: string, payerFileRequest: PayerFileRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayerFilePreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewPayerFile(hoaId, payerFileRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.previewPayerFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Preview a recurring transfer schedule. **Owner/Admin (or SuperAdmin).** Stores a **draft** and returns the next five days it would fire. No money moves and no schedule is live until confirm.
         * @param {string} hoaId HOA id
         * @param {TransferScheduleCreate} transferScheduleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewTransferSchedule(hoaId: string, transferScheduleCreate: TransferScheduleCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferSchedulePreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewTransferSchedule(hoaId, transferScheduleCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.previewTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Record an issued check. **Owner/Admin (or SuperAdmin).** Posts the journal entry and registers the check with the bank\'s Positive Pay service in one action; a bank-side failure is returned as a `failed` row, not an error.
         * @param {string} hoaId HOA id
         * @param {CheckCreate} checkCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async recordHoaCheck(hoaId: string, checkCreate: CheckCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.recordHoaCheck(hoaId, checkCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.recordHoaCheck']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * **Attaches only — it does not reverse.** The next chargeback run applies the same guards it applies to anything the matcher resolved, so the unattended path stays the single place money moves.
         * @summary Attach a held chargeback to the payment it reverses. **Owner/Admin.**
         * @param {string} chargebackId Chargeback id
         * @param {ChargebackResolveRequest} chargebackResolveRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveChargeback(chargebackId: string, chargebackResolveRequest: ChargebackResolveRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ChargebackReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveChargeback(chargebackId, chargebackResolveRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.resolveChargeback']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Re-validates the accounts first: months may have passed, and an account unassigned in the meantime should stop the resume rather than surface at 06:30 as a failed occurrence.
         * @summary Resume a paused schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resumeTransferSchedule(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resumeTransferSchedule(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.resumeTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Remove a homeowner\'s bank account on file. **Owner/Admin of the company.** The homeowner can add another.
         * @param {string} hoaId HOA id
         * @param {string} memberId Member id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async retireMemberBankAccount(hoaId: string, memberId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.retireMemberBankAccount(hoaId, memberId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.retireMemberBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Retire a vendor\'s bank details. **Owner/Admin of the company; not super admins.** The row is kept for the payments made to it.
         * @param {string} hoaId HOA id
         * @param {string} vendorId Vendor id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async retireVendorBankAccount(hoaId: string, vendorId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.retireVendorBankAccount(hoaId, vendorId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.retireVendorBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Ask the bank to **return** an NSF item. **Owner/Admin (or SuperAdmin).** The bank\'s default, made explicit; same finality, cutoff and one-send rule as pay.
         * @param {string} connectionId Bank connection id
         * @param {string} id NSF item id
         * @param {NsfReturnDecision} nsfReturnDecision 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async returnConnectionNsf(connectionId: string, id: string, nsfReturnDecision: NsfReturnDecision, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NsfItemReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.returnConnectionNsf(connectionId, id, nsfReturnDecision, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.returnConnectionNsf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Send a `pending` or `failed` Positive Pay entry again. **Owner/Admin (or SuperAdmin).** Reconciles with the bank first, so an entry that actually landed is recorded rather than repeated.
         * @param {string} hoaId HOA id
         * @param {string} id check id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendHoaCheck(hoaId: string, id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendHoaCheck(hoaId, id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.sendHoaCheck']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Makes **no bank call**. Enabling leaves the account\'s backfill pending, and the next scheduled run fetches its 90 days.
         * @summary Turn the daily transaction pull on or off for one account. **Owner/Admin only.**
         * @param {string} connectionId bank_connection id
         * @param {string} accountNumber Account number at the bank
         * @param {AccountTrackingUpdate} accountTrackingUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setAccountTracking(connectionId: string, accountNumber: string, accountTrackingUpdate: AccountTrackingUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AccountTrackingReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setAccountTracking(connectionId, accountNumber, accountTrackingUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.setAccountTracking']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deliberately not part of the generic HOA update. The roles happen to match today, but `can_update_hoa` is the bar for editing an association\'s address and `can_submit_payer_file` is the bar for sending its roster to a bank — and this switch does the second. If HOA editing is ever widened to Managers, this must not widen with it.  Enabling is refused for an association with no bank assigned: a switch that reads as on and does nothing is worse than an error.
         * @summary Turn the scheduled daily payer upload on or off for one HOA. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {PayerAutoUploadUpdate} payerAutoUploadUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setPayerAutoUpload(hoaId: string, payerAutoUploadUpdate: PayerAutoUploadUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayerAutoUploadReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setPayerAutoUpload(hoaId, payerAutoUploadUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.setPayerAutoUpload']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Set a vendor\'s bank details, replacing any on file. **Owner/Admin of the company; not super admins.** The account number is encrypted on arrival and never returned.
         * @param {string} hoaId HOA id
         * @param {string} vendorId Vendor id
         * @param {CounterpartyBankAccountUpsert} counterpartyBankAccountUpsert 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setVendorBankAccount(hoaId: string, vendorId: string, counterpartyBankAccountUpsert: CounterpartyBankAccountUpsert, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setVendorBankAccount(hoaId, vendorId, counterpartyBankAccountUpsert, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.setVendorBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Settle pending payables with a one-time RSA code. **Owner/Admin of the company. Moves money.** The code is sent once and never stored; the RSA token locks after three wrong codes.
         * @param {string} connectionId Bank connection id
         * @param {PayableSettleRequest} payableSettleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async settleConnectionPayables(connectionId: string, payableSettleRequest: PayableSettleRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayableSettleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.settleConnectionPayables(connectionId, payableSettleRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.settleConnectionPayables']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Assemble + submit (upload) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** Records a submission (audit) and uploads; the returned record\'s `status`/`error_*` carry the outcome.
         * @param {string} hoaId HOA id
         * @param {PayerFileRequest} payerFileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async submitPayerFile(hoaId: string, payerFileRequest: PayerFileRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayerSubmissionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.submitPayerFile(hoaId, payerFileRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.submitPayerFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Pay a vendor by ACH: validates with the bank and leaves the item pending in the settle queue. **Owner/Admin of the company.** Moves nothing.
         * @param {string} hoaId HOA id
         * @param {VendorPaymentCreate} vendorPaymentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async submitVendorPayment(hoaId: string, vendorPaymentCreate: VendorPaymentCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PayableReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.submitVendorPayment(hoaId, vendorPaymentCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.submitVendorPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Ask the bank which of this HOA\'s accepted entries have been imported into Positive Pay, now rather than on the job\'s schedule. **Owner/Admin (or SuperAdmin).** Reads only; sends nothing.
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async syncHoaChecks(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckSyncReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.syncHoaChecks(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.syncHoaChecks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Unassign a bank account from an HOA. **Owner/Admin only.**
         * @param {string} id hoa_bank_account id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unassignHoaAccount(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unassignHoaAccount(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.unassignHoaAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * `label` / `client_id` / `is_active` are updated when present. A new `client_secret` (and/or `client_id`) triggers re-verification against the bank before the change is persisted; the secret is never returned.
         * @summary Update a bank connection. **Owner (own company) or SuperAdmin.**
         * @param {string} id bank_connection id
         * @param {BankConnectionUpdate} bankConnectionUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateBankConnection(id: string, bankConnectionUpdate: BankConnectionUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BankConnectionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateBankConnection(id, bankConnectionUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.updateBankConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Takes effect from the next evaluation; occurrences already sent are untouched. An omitted field is left alone, an explicit `null` clears one — which is how changing frequency drops the day that no longer applies.
         * @summary Change a transfer schedule. **Owner/Admin (or SuperAdmin).**
         * @param {string} hoaId HOA id
         * @param {string} id schedule id
         * @param {TransferScheduleUpdate} transferScheduleUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateTransferSchedule(hoaId: string, id: string, transferScheduleUpdate: TransferScheduleUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TransferScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateTransferSchedule(hoaId, id, transferScheduleUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.updateTransferSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Void a recorded check. **Owner/Admin (or SuperAdmin).** Reverses the journal entry and, if the bank accepted the issue, registers the void with the bank too.
         * @param {string} hoaId HOA id
         * @param {string} id check id
         * @param {CheckVoidRequest} checkVoidRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async voidHoaCheck(hoaId: string, id: string, checkVoidRequest: CheckVoidRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.voidHoaCheck(hoaId, id, checkVoidRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BankApi.voidHoaCheck']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BankApi - factory interface
 */
export const BankApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BankApiFp(configuration)
    return {
        /**
         * 
         * @summary Assign a bank account to an HOA. **Owner/Admin only.**
         * @param {BankApiAssignHoaAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignHoaAccount(requestParameters: BankApiAssignHoaAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaBankAccountReturn> {
            return localVarFp.assignHoaAccount(requestParameters.hoaId, requestParameters.hoaAccountAssign, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Cancel a pending or settled-but-unprocessed payable at the bank and reverse what it posted. **Owner/Admin of the company.**
         * @param {BankApiCancelHoaPayableRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelHoaPayable(requestParameters: BankApiCancelHoaPayableRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayableReturn> {
            return localVarFp.cancelHoaPayable(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Confirm (execute) a previewed transfer. **Owner/Admin (or SuperAdmin).** Idempotent on the transfer id — safe to retry.
         * @param {BankApiConfirmHoaTransferRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmHoaTransfer(requestParameters: BankApiConfirmHoaTransferRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferReturn> {
            return localVarFp.confirmHoaTransfer(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * This is the only human decision in the feature — after it, every occurrence fires unattended.
         * @summary Confirm a draft schedule, making it live. **Owner/Admin (or SuperAdmin).** No bank call. Idempotent on the schedule id.
         * @param {BankApiConfirmTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmTransferSchedule(requestParameters: BankApiConfirmTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleReturn> {
            return localVarFp.confirmTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * The candidate credentials are verified against the bank (a live account read) **before** anything is stored, so invalid creds are rejected and nothing is persisted on failure. The client secret is encrypted at rest and never returned. Re-posting for the same company/provider rotates the stored credentials.
         * @summary Store (or rotate) a company\'s bank credentials. **Owner (own company) or SuperAdmin.**
         * @param {BankApiCreateBankConnectionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBankConnection(requestParameters: BankApiCreateBankConnectionRequest, options?: RawAxiosRequestConfig): AxiosPromise<BankConnectionReturn> {
            return localVarFp.createBankConnection(requestParameters.bankConnectionCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Soft-delete a bank connection (`is_active = false`). **Owner (own company) or SuperAdmin.** Blocked (409) while HOA accounts are still assigned to it.
         * @param {BankApiDeleteBankConnectionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteBankConnection(requestParameters: BankApiDeleteBankConnectionRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteBankConnection(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * Terminal, and **not a delete**: `bank_transfers.schedule_id` points at this row, and the record that money moved must outlive the instruction that moved it.
         * @summary End a schedule. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiEndTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        endTransferSchedule(requestParameters: BankApiEndTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleReturn> {
            return localVarFp.endTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Checks that posted against this connection\'s accounts, from the bank. **Owner/Admin.** `from` is required when `to` is given.
         * @param {BankApiFindConnectionClearedChecksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findConnectionClearedChecks(requestParameters: BankApiFindConnectionClearedChecksRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClearedCheckList> {
            return localVarFp.findConnectionClearedChecks(requestParameters.connectionId, requestParameters.accountNumber, requestParameters.checkNumber, requestParameters.amount, requestParameters.from, requestParameters.to, requestParameters.checkTypes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Find files for a connection by filter (durable status search). **Owner/Admin (or SuperAdmin).**
         * @param {BankApiFindConnectionFilesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findConnectionFiles(requestParameters: BankApiFindConnectionFilesRequest, options?: RawAxiosRequestConfig): AxiosPromise<BankFileList> {
            return localVarFp.findConnectionFiles(requestParameters.id, requestParameters.referenceId, requestParameters.fileType, requestParameters.status, requestParameters.fileUid, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a single bank connection (no secret). **Owner (own company) or SuperAdmin.**
         * @param {BankApiGetBankConnectionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBankConnection(requestParameters: BankApiGetBankConnectionRequest, options?: RawAxiosRequestConfig): AxiosPromise<BankConnectionReturn> {
            return localVarFp.getBankConnection(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Download a chargeback\'s cheque image. **Owner/Admin.**
         * @param {BankApiGetChargebackImageRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getChargebackImage(requestParameters: BankApiGetChargebackImageRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getChargebackImage(requestParameters.chargebackId, requestParameters.side, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Download one chargeback notice PDF. **Owner/Admin.**
         * @param {BankApiGetChargebackNoticeFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getChargebackNoticeFile(requestParameters: BankApiGetChargebackNoticeFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getChargebackNoticeFile(requestParameters.chargebackId, requestParameters.statementId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary A posted check\'s image by the bank\'s `CheckID`. **Owner/Admin.**
         * @param {BankApiGetClearedCheckImageRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClearedCheckImage(requestParameters: BankApiGetClearedCheckImageRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getClearedCheckImage(requestParameters.connectionId, requestParameters.checkId, requestParameters.side, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Pending transactions for a company account. **Owner/Admin only.**
         * @param {BankApiGetCompanyAccountPendingRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyAccountPending(requestParameters: BankApiGetCompanyAccountPendingRequest, options?: RawAxiosRequestConfig): AxiosPromise<PendingTransactionList> {
            return localVarFp.getCompanyAccountPending(requestParameters.companyId, requestParameters.accountNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Available statements for a company account. **Owner/Admin only.**
         * @param {BankApiGetCompanyAccountStatementsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyAccountStatements(requestParameters: BankApiGetCompanyAccountStatementsRequest, options?: RawAxiosRequestConfig): AxiosPromise<StatementList> {
            return localVarFp.getCompanyAccountStatements(requestParameters.companyId, requestParameters.accountNumber, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Posted transactions for a company account. **Owner/Admin only.**
         * @param {BankApiGetCompanyAccountTransactionsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyAccountTransactions(requestParameters: BankApiGetCompanyAccountTransactionsRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransactionList> {
            return localVarFp.getCompanyAccountTransactions(requestParameters.companyId, requestParameters.accountNumber, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * Loads the company\'s active connection(s), calls the bank, and returns the accounts. Requires the bank to be reachable (whitelisted IP).
         * @summary View all bank accounts for a company, live from the bank. **Owner/Admin (or SuperAdmin) only.**
         * @param {BankApiGetCompanyBankAccountsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyBankAccounts(requestParameters: BankApiGetCompanyBankAccountsRequest, options?: RawAxiosRequestConfig): AxiosPromise<CompanyBankAccountsReturn> {
            return localVarFp.getCompanyBankAccounts(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Download a company statement PDF by statement id. **Owner/Admin.**
         * @param {BankApiGetCompanyStatementFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyStatementFile(requestParameters: BankApiGetCompanyStatementFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getCompanyStatementFile(requestParameters.companyId, requestParameters.statementId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get one file\'s status by the bank\'s file id. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiGetConnectionFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getConnectionFile(requestParameters: BankApiGetConnectionFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<BankFileReturn> {
            return localVarFp.getConnectionFile(requestParameters.id, requestParameters.fileId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary The check image behind a POD item. **Owner/Admin.** Live from the bank: finds the posted check by account, number and date, then fetches its image by the bank\'s `CheckID`.
         * @param {BankApiGetConnectionNsfImageRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getConnectionNsfImage(requestParameters: BankApiGetConnectionNsfImageRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getConnectionNsfImage(requestParameters.connectionId, requestParameters.id, requestParameters.side, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Pending transactions for an HOA-assigned account. **Manager+.**
         * @param {BankApiGetHoaAccountPendingRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountPending(requestParameters: BankApiGetHoaAccountPendingRequest, options?: RawAxiosRequestConfig): AxiosPromise<PendingTransactionList> {
            return localVarFp.getHoaAccountPending(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Available statements for an HOA-assigned account. **Manager+.**
         * @param {BankApiGetHoaAccountStatementsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountStatements(requestParameters: BankApiGetHoaAccountStatementsRequest, options?: RawAxiosRequestConfig): AxiosPromise<StatementList> {
            return localVarFp.getHoaAccountStatements(requestParameters.id, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Posted transactions for an HOA-assigned account. **Manager+.**
         * @param {BankApiGetHoaAccountTransactionsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountTransactions(requestParameters: BankApiGetHoaAccountTransactionsRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransactionList> {
            return localVarFp.getHoaAccountTransactions(requestParameters.id, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List an HOA\'s assigned accounts enriched with **live** balance/status. **Manager+ (HOA-scoped).** Requires the bank to be reachable.
         * @param {BankApiGetHoaAccountsLiveRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaAccountsLive(requestParameters: BankApiGetHoaAccountsLiveRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaBankAccountLiveList> {
            return localVarFp.getHoaAccountsLive(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get one check. **Manager+.**
         * @param {BankApiGetHoaCheckRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaCheck(requestParameters: BankApiGetHoaCheckRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckReturn> {
            return localVarFp.getHoaCheck(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary One payable. **Manager+.**
         * @param {BankApiGetHoaPayableRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaPayable(requestParameters: BankApiGetHoaPayableRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayableReturn> {
            return localVarFp.getHoaPayable(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Download an HOA-assigned account\'s statement PDF. **Manager+.**
         * @param {BankApiGetHoaStatementFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaStatementFile(requestParameters: BankApiGetHoaStatementFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getHoaStatementFile(requestParameters.id, requestParameters.statementId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get one transfer (reconciles a still-pending one). **Manager+.**
         * @param {BankApiGetHoaTransferRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaTransfer(requestParameters: BankApiGetHoaTransferRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferReturn> {
            return localVarFp.getHoaTransfer(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary A homeowner\'s bank account on file, masked. **Manager+ on the HOA.**
         * @param {BankApiGetMemberBankAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMemberBankAccount(requestParameters: BankApiGetMemberBankAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.getMemberBankAccount(requestParameters.hoaId, requestParameters.memberId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get one payer submission (reconciles a still-processing one by ReferenceID). **Owner/Admin (or SuperAdmin).**
         * @param {BankApiGetPayerSubmissionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPayerSubmission(requestParameters: BankApiGetPayerSubmissionRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayerSubmissionReturn> {
            return localVarFp.getPayerSubmission(requestParameters.submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the stored tab-delimited bytes as-uploaded (an audit snapshot), not a fresh re-assembly — the bank\'s get-file returns only status, so this is the only way to see what was actually sent. No bank call.
         * @summary Download the exact file we sent the bank for a submission. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiGetPayerSubmissionFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPayerSubmissionFile(requestParameters: BankApiGetPayerSubmissionFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getPayerSubmissionFile(requestParameters.submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary One transfer schedule. **Manager+.**
         * @param {BankApiGetTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTransferSchedule(requestParameters: BankApiGetTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleReturn> {
            return localVarFp.getTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary A vendor\'s bank details on file, masked. **Manager+.**
         * @param {BankApiGetVendorBankAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorBankAccount(requestParameters: BankApiGetVendorBankAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.getVendorBankAccount(requestParameters.hoaId, requestParameters.vendorId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Which accounts on a connection are pulled daily. **Owner/Admin only.**
         * @param {BankApiListAccountTrackingRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAccountTracking(requestParameters: BankApiListAccountTrackingRequest, options?: RawAxiosRequestConfig): AxiosPromise<AccountTrackingList> {
            return localVarFp.listAccountTracking(requestParameters.connectionId, options).then((request) => request(axios, basePath));
        },
        /**
         * An empty list is a legitimate answer — a notice can lag the chargeback that caused it.
         * @summary List a chargeback\'s notice documents. **Owner/Admin.**
         * @param {BankApiListChargebackNoticesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listChargebackNotices(requestParameters: BankApiListChargebackNoticesRequest, options?: RawAxiosRequestConfig): AxiosPromise<StatementList> {
            return localVarFp.listChargebackNotices(requestParameters.chargebackId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List a company\'s bank connections (no secret). **Owner (own company) or SuperAdmin.**
         * @param {BankApiListCompanyConnectionsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCompanyConnections(requestParameters: BankApiListCompanyConnectionsRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<BankConnectionReturn>> {
            return localVarFp.listCompanyConnections(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * Reads our own rows — no bank call.
         * @summary What one connection has had taken back. **Owner/Admin.**
         * @param {BankApiListConnectionChargebacksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionChargebacks(requestParameters: BankApiListConnectionChargebacksRequest, options?: RawAxiosRequestConfig): AxiosPromise<ChargebackList> {
            return localVarFp.listConnectionChargebacks(requestParameters.connectionId, requestParameters.openOnly, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List files uploaded today for a connection (bank status view). **Owner/Admin (or SuperAdmin).**
         * @param {BankApiListConnectionFilesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionFiles(requestParameters: BankApiListConnectionFilesRequest, options?: RawAxiosRequestConfig): AxiosPromise<BankFileList> {
            return localVarFp.listConnectionFiles(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Items presented against insufficient funds on one connection\'s accounts. **Owner/Admin.** Reads our own rows — no bank call.
         * @param {BankApiListConnectionNsfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionNsf(requestParameters: BankApiListConnectionNsfRequest, options?: RawAxiosRequestConfig): AxiosPromise<NsfList> {
            return localVarFp.listConnectionNsf(requestParameters.connectionId, requestParameters.openOnly, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary The settle queue: a connection\'s payables, pending first. **Owner/Admin.**
         * @param {BankApiListConnectionPayablesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionPayables(requestParameters: BankApiListConnectionPayablesRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayableList> {
            return localVarFp.listConnectionPayables(requestParameters.connectionId, requestParameters.direction, requestParameters.status, options).then((request) => request(axios, basePath));
        },
        /**
         * Connection-scoped rather than per-association, because a record whose association did not resolve has no HOA to be filed under and cannot appear in that association\'s review list. This is where it is visible.  `?unmatched_only=true` narrows to what needs somebody.
         * @summary What one connection\'s lockbox has delivered. **Owner/Admin only.**
         * @param {BankApiListConnectionRemittanceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listConnectionRemittance(requestParameters: BankApiListConnectionRemittanceRequest, options?: RawAxiosRequestConfig): AxiosPromise<RemittanceList> {
            return localVarFp.listConnectionRemittance(requestParameters.connectionId, requestParameters.unmatchedOnly, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List an HOA\'s assigned accounts (stored metadata, no bank call). **Manager+ (HOA-scoped).**
         * @param {BankApiListHoaAccountsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaAccounts(requestParameters: BankApiListHoaAccountsRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaBankAccountList> {
            return localVarFp.listHoaAccounts(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List an HOA\'s check register, newest issue date first. **Manager+.**
         * @param {BankApiListHoaChecksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaChecks(requestParameters: BankApiListHoaChecksRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckList> {
            return localVarFp.listHoaChecks(requestParameters.hoaId, requestParameters.account, requestParameters.status, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary An association\'s NSF items. **Manager+.** No bank call.
         * @param {BankApiListHoaNsfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaNsf(requestParameters: BankApiListHoaNsfRequest, options?: RawAxiosRequestConfig): AxiosPromise<NsfList> {
            return localVarFp.listHoaNsf(requestParameters.hoaId, requestParameters.openOnly, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary An association\'s payables, newest first. **Manager+.**
         * @param {BankApiListHoaPayablesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaPayables(requestParameters: BankApiListHoaPayablesRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayableList> {
            return localVarFp.listHoaPayables(requestParameters.hoaId, requestParameters.direction, requestParameters.status, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List an HOA\'s transfer records (newest first). **Manager+.**
         * @param {BankApiListHoaTransfersRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaTransfers(requestParameters: BankApiListHoaTransfersRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferList> {
            return localVarFp.listHoaTransfers(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List an HOA\'s payer submissions (our audit table). **Owner/Admin (or SuperAdmin).**
         * @param {BankApiListPayerSubmissionsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPayerSubmissions(requestParameters: BankApiListPayerSubmissionsRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayerSubmissionList> {
            return localVarFp.listPayerSubmissions(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary List an HOA\'s transfer schedules, newest first. **Manager+.**
         * @param {BankApiListTransferSchedulesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTransferSchedules(requestParameters: BankApiListTransferSchedulesRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleList> {
            return localVarFp.listTransferSchedules(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * Stops it firing and keeps its history. Resuming does **not** catch up — the job only ever asks whether a schedule is due today.
         * @summary Pause a schedule. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiPauseTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        pauseTransferSchedule(requestParameters: BankApiPauseTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleReturn> {
            return localVarFp.pauseTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * Final through the API: a decision is changed only by phoning bank support. Refused once the item is decided, or after **1pm Central on its expiry date**. Exactly one request is ever sent for an item; a concurrent decision gets a 409.
         * @summary Ask the bank to **pay** an NSF item. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiPayConnectionNsfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        payConnectionNsf(requestParameters: BankApiPayConnectionNsfRequest, options?: RawAxiosRequestConfig): AxiosPromise<NsfItemReturn> {
            return localVarFp.payConnectionNsf(requestParameters.connectionId, requestParameters.id, requestParameters.nsfPayDecision, options).then((request) => request(axios, basePath));
        },
        /**
         * The exit from every hold. The unattended pass declines anything it cannot attribute with confidence; this is how a person who has looked at the record turns that decision into a payment.  **Makes no bank call.** Everything it needs was stored at ingest.  It goes through `PaymentService::record` like every other payment, and it refuses the holds a person cannot clear by looking — a record that resolves to no home, or whose amount is missing, unreadable, non-positive, or disagrees with itself. Those are facts about the record, not about our willingness to act on it.
         * @summary Post one held remittance record to the ledger **by hand**. **Owner/Admin only.**
         * @param {BankApiPostRemittanceRecordRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postRemittanceRecord(requestParameters: BankApiPostRemittanceRecordRequest, options?: RawAxiosRequestConfig): AxiosPromise<RemittanceRecordReturn> {
            return localVarFp.postRemittanceRecord(requestParameters.recordId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Preview a transfer between two of an HOA\'s assigned accounts. **Owner/Admin (or SuperAdmin).** No money moves — validates and returns the created (unconfirmed) transfer plus live balance context.
         * @param {BankApiPreviewHoaTransferRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewHoaTransfer(requestParameters: BankApiPreviewHoaTransferRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferPreviewReturn> {
            return localVarFp.previewHoaTransfer(requestParameters.hoaId, requestParameters.transferPreviewRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Preview (assemble) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** No bank call — returns the exact file text + count. Optional `bank_connection_id` selects the lockbox when the company has more than one connection.
         * @param {BankApiPreviewPayerFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewPayerFile(requestParameters: BankApiPreviewPayerFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayerFilePreviewReturn> {
            return localVarFp.previewPayerFile(requestParameters.hoaId, requestParameters.payerFileRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Preview a recurring transfer schedule. **Owner/Admin (or SuperAdmin).** Stores a **draft** and returns the next five days it would fire. No money moves and no schedule is live until confirm.
         * @param {BankApiPreviewTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewTransferSchedule(requestParameters: BankApiPreviewTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferSchedulePreviewReturn> {
            return localVarFp.previewTransferSchedule(requestParameters.hoaId, requestParameters.transferScheduleCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Record an issued check. **Owner/Admin (or SuperAdmin).** Posts the journal entry and registers the check with the bank\'s Positive Pay service in one action; a bank-side failure is returned as a `failed` row, not an error.
         * @param {BankApiRecordHoaCheckRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordHoaCheck(requestParameters: BankApiRecordHoaCheckRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckReturn> {
            return localVarFp.recordHoaCheck(requestParameters.hoaId, requestParameters.checkCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * **Attaches only — it does not reverse.** The next chargeback run applies the same guards it applies to anything the matcher resolved, so the unattended path stays the single place money moves.
         * @summary Attach a held chargeback to the payment it reverses. **Owner/Admin.**
         * @param {BankApiResolveChargebackRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveChargeback(requestParameters: BankApiResolveChargebackRequest, options?: RawAxiosRequestConfig): AxiosPromise<ChargebackReturn> {
            return localVarFp.resolveChargeback(requestParameters.chargebackId, requestParameters.chargebackResolveRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Re-validates the accounts first: months may have passed, and an account unassigned in the meantime should stop the resume rather than surface at 06:30 as a failed occurrence.
         * @summary Resume a paused schedule. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiResumeTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resumeTransferSchedule(requestParameters: BankApiResumeTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleReturn> {
            return localVarFp.resumeTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Remove a homeowner\'s bank account on file. **Owner/Admin of the company.** The homeowner can add another.
         * @param {BankApiRetireMemberBankAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        retireMemberBankAccount(requestParameters: BankApiRetireMemberBankAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.retireMemberBankAccount(requestParameters.hoaId, requestParameters.memberId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Retire a vendor\'s bank details. **Owner/Admin of the company; not super admins.** The row is kept for the payments made to it.
         * @param {BankApiRetireVendorBankAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        retireVendorBankAccount(requestParameters: BankApiRetireVendorBankAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.retireVendorBankAccount(requestParameters.hoaId, requestParameters.vendorId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Ask the bank to **return** an NSF item. **Owner/Admin (or SuperAdmin).** The bank\'s default, made explicit; same finality, cutoff and one-send rule as pay.
         * @param {BankApiReturnConnectionNsfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        returnConnectionNsf(requestParameters: BankApiReturnConnectionNsfRequest, options?: RawAxiosRequestConfig): AxiosPromise<NsfItemReturn> {
            return localVarFp.returnConnectionNsf(requestParameters.connectionId, requestParameters.id, requestParameters.nsfReturnDecision, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Send a `pending` or `failed` Positive Pay entry again. **Owner/Admin (or SuperAdmin).** Reconciles with the bank first, so an entry that actually landed is recorded rather than repeated.
         * @param {BankApiSendHoaCheckRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendHoaCheck(requestParameters: BankApiSendHoaCheckRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckReturn> {
            return localVarFp.sendHoaCheck(requestParameters.hoaId, requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * Makes **no bank call**. Enabling leaves the account\'s backfill pending, and the next scheduled run fetches its 90 days.
         * @summary Turn the daily transaction pull on or off for one account. **Owner/Admin only.**
         * @param {BankApiSetAccountTrackingRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setAccountTracking(requestParameters: BankApiSetAccountTrackingRequest, options?: RawAxiosRequestConfig): AxiosPromise<AccountTrackingReturn> {
            return localVarFp.setAccountTracking(requestParameters.connectionId, requestParameters.accountNumber, requestParameters.accountTrackingUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * Deliberately not part of the generic HOA update. The roles happen to match today, but `can_update_hoa` is the bar for editing an association\'s address and `can_submit_payer_file` is the bar for sending its roster to a bank — and this switch does the second. If HOA editing is ever widened to Managers, this must not widen with it.  Enabling is refused for an association with no bank assigned: a switch that reads as on and does nothing is worse than an error.
         * @summary Turn the scheduled daily payer upload on or off for one HOA. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiSetPayerAutoUploadRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setPayerAutoUpload(requestParameters: BankApiSetPayerAutoUploadRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayerAutoUploadReturn> {
            return localVarFp.setPayerAutoUpload(requestParameters.hoaId, requestParameters.payerAutoUploadUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Set a vendor\'s bank details, replacing any on file. **Owner/Admin of the company; not super admins.** The account number is encrypted on arrival and never returned.
         * @param {BankApiSetVendorBankAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setVendorBankAccount(requestParameters: BankApiSetVendorBankAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.setVendorBankAccount(requestParameters.hoaId, requestParameters.vendorId, requestParameters.counterpartyBankAccountUpsert, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Settle pending payables with a one-time RSA code. **Owner/Admin of the company. Moves money.** The code is sent once and never stored; the RSA token locks after three wrong codes.
         * @param {BankApiSettleConnectionPayablesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        settleConnectionPayables(requestParameters: BankApiSettleConnectionPayablesRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayableSettleReturn> {
            return localVarFp.settleConnectionPayables(requestParameters.connectionId, requestParameters.payableSettleRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Assemble + submit (upload) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** Records a submission (audit) and uploads; the returned record\'s `status`/`error_*` carry the outcome.
         * @param {BankApiSubmitPayerFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitPayerFile(requestParameters: BankApiSubmitPayerFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayerSubmissionReturn> {
            return localVarFp.submitPayerFile(requestParameters.hoaId, requestParameters.payerFileRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Pay a vendor by ACH: validates with the bank and leaves the item pending in the settle queue. **Owner/Admin of the company.** Moves nothing.
         * @param {BankApiSubmitVendorPaymentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitVendorPayment(requestParameters: BankApiSubmitVendorPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<PayableReturn> {
            return localVarFp.submitVendorPayment(requestParameters.hoaId, requestParameters.vendorPaymentCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Ask the bank which of this HOA\'s accepted entries have been imported into Positive Pay, now rather than on the job\'s schedule. **Owner/Admin (or SuperAdmin).** Reads only; sends nothing.
         * @param {BankApiSyncHoaChecksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncHoaChecks(requestParameters: BankApiSyncHoaChecksRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckSyncReturn> {
            return localVarFp.syncHoaChecks(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Unassign a bank account from an HOA. **Owner/Admin only.**
         * @param {BankApiUnassignHoaAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignHoaAccount(requestParameters: BankApiUnassignHoaAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.unassignHoaAccount(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * `label` / `client_id` / `is_active` are updated when present. A new `client_secret` (and/or `client_id`) triggers re-verification against the bank before the change is persisted; the secret is never returned.
         * @summary Update a bank connection. **Owner (own company) or SuperAdmin.**
         * @param {BankApiUpdateBankConnectionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBankConnection(requestParameters: BankApiUpdateBankConnectionRequest, options?: RawAxiosRequestConfig): AxiosPromise<BankConnectionReturn> {
            return localVarFp.updateBankConnection(requestParameters.id, requestParameters.bankConnectionUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * Takes effect from the next evaluation; occurrences already sent are untouched. An omitted field is left alone, an explicit `null` clears one — which is how changing frequency drops the day that no longer applies.
         * @summary Change a transfer schedule. **Owner/Admin (or SuperAdmin).**
         * @param {BankApiUpdateTransferScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateTransferSchedule(requestParameters: BankApiUpdateTransferScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<TransferScheduleReturn> {
            return localVarFp.updateTransferSchedule(requestParameters.hoaId, requestParameters.id, requestParameters.transferScheduleUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Void a recorded check. **Owner/Admin (or SuperAdmin).** Reverses the journal entry and, if the bank accepted the issue, registers the void with the bank too.
         * @param {BankApiVoidHoaCheckRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        voidHoaCheck(requestParameters: BankApiVoidHoaCheckRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckReturn> {
            return localVarFp.voidHoaCheck(requestParameters.hoaId, requestParameters.id, requestParameters.checkVoidRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for assignHoaAccount operation in BankApi.
 */
export interface BankApiAssignHoaAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly hoaAccountAssign: HoaAccountAssign
}

/**
 * Request parameters for cancelHoaPayable operation in BankApi.
 */
export interface BankApiCancelHoaPayableRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Payable id
     */
    readonly id: string
}

/**
 * Request parameters for confirmHoaTransfer operation in BankApi.
 */
export interface BankApiConfirmHoaTransferRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * bank_transfer id (from preview)
     */
    readonly id: string
}

/**
 * Request parameters for confirmTransferSchedule operation in BankApi.
 */
export interface BankApiConfirmTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * schedule id (from preview)
     */
    readonly id: string
}

/**
 * Request parameters for createBankConnection operation in BankApi.
 */
export interface BankApiCreateBankConnectionRequest {
    readonly bankConnectionCreate: BankConnectionCreate
}

/**
 * Request parameters for deleteBankConnection operation in BankApi.
 */
export interface BankApiDeleteBankConnectionRequest {
    /**
     * bank_connection id
     */
    readonly id: string
}

/**
 * Request parameters for endTransferSchedule operation in BankApi.
 */
export interface BankApiEndTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * schedule id
     */
    readonly id: string
}

/**
 * Request parameters for findConnectionClearedChecks operation in BankApi.
 */
export interface BankApiFindConnectionClearedChecksRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    readonly accountNumber?: string | null

    readonly checkNumber?: string | null

    readonly amount?: string | null

    readonly from?: string | null

    readonly to?: string | null

    /**
     * Comma-separated &#x60;Standard&#x60;, &#x60;RDC&#x60;. The bank defaults to &#x60;Standard&#x60; — checks we wrote — when omitted.
     */
    readonly checkTypes?: string | null
}

/**
 * Request parameters for findConnectionFiles operation in BankApi.
 */
export interface BankApiFindConnectionFilesRequest {
    /**
     * bank_connection id
     */
    readonly id: string

    /**
     * Our ReferenceID
     */
    readonly referenceId?: string

    /**
     * e.g. Payer
     */
    readonly fileType?: string

    /**
     * All | Pending | Complete | Error
     */
    readonly status?: string

    /**
     * Bank FileUID
     */
    readonly fileUid?: string
}

/**
 * Request parameters for getBankConnection operation in BankApi.
 */
export interface BankApiGetBankConnectionRequest {
    /**
     * bank_connection id
     */
    readonly id: string
}

/**
 * Request parameters for getChargebackImage operation in BankApi.
 */
export interface BankApiGetChargebackImageRequest {
    /**
     * Chargeback id
     */
    readonly chargebackId: string

    /**
     * front (default) or back
     */
    readonly side?: string
}

/**
 * Request parameters for getChargebackNoticeFile operation in BankApi.
 */
export interface BankApiGetChargebackNoticeFileRequest {
    /**
     * Chargeback id
     */
    readonly chargebackId: string

    /**
     * Statement id from the notice listing
     */
    readonly statementId: string
}

/**
 * Request parameters for getClearedCheckImage operation in BankApi.
 */
export interface BankApiGetClearedCheckImageRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * The bank\&#39;s CheckID
     */
    readonly checkId: string

    readonly side?: string | null
}

/**
 * Request parameters for getCompanyAccountPending operation in BankApi.
 */
export interface BankApiGetCompanyAccountPendingRequest {
    /**
     * Company id
     */
    readonly companyId: string

    /**
     * Account number
     */
    readonly accountNumber: string
}

/**
 * Request parameters for getCompanyAccountStatements operation in BankApi.
 */
export interface BankApiGetCompanyAccountStatementsRequest {
    /**
     * Company id
     */
    readonly companyId: string

    /**
     * Account number
     */
    readonly accountNumber: string

    /**
     * Inclusive start statement date (YYYY-MM-DD)
     */
    readonly from?: string

    /**
     * Inclusive end statement date (YYYY-MM-DD); requires &#x60;from&#x60;
     */
    readonly to?: string
}

/**
 * Request parameters for getCompanyAccountTransactions operation in BankApi.
 */
export interface BankApiGetCompanyAccountTransactionsRequest {
    /**
     * Company id
     */
    readonly companyId: string

    /**
     * Account number
     */
    readonly accountNumber: string

    /**
     * Inclusive start date (YYYY-MM-DD)
     */
    readonly from?: string

    /**
     * Inclusive end date (YYYY-MM-DD); requires &#x60;from&#x60;
     */
    readonly to?: string
}

/**
 * Request parameters for getCompanyBankAccounts operation in BankApi.
 */
export interface BankApiGetCompanyBankAccountsRequest {
    /**
     * Company id
     */
    readonly companyId: string
}

/**
 * Request parameters for getCompanyStatementFile operation in BankApi.
 */
export interface BankApiGetCompanyStatementFileRequest {
    /**
     * Company id
     */
    readonly companyId: string

    /**
     * Statement id
     */
    readonly statementId: string
}

/**
 * Request parameters for getConnectionFile operation in BankApi.
 */
export interface BankApiGetConnectionFileRequest {
    /**
     * bank_connection id
     */
    readonly id: string

    /**
     * Bank FileID
     */
    readonly fileId: string
}

/**
 * Request parameters for getConnectionNsfImage operation in BankApi.
 */
export interface BankApiGetConnectionNsfImageRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * NSF item id
     */
    readonly id: string

    readonly side?: string | null
}

/**
 * Request parameters for getHoaAccountPending operation in BankApi.
 */
export interface BankApiGetHoaAccountPendingRequest {
    /**
     * hoa_bank_account id
     */
    readonly id: string
}

/**
 * Request parameters for getHoaAccountStatements operation in BankApi.
 */
export interface BankApiGetHoaAccountStatementsRequest {
    /**
     * hoa_bank_account id
     */
    readonly id: string

    /**
     * Inclusive start statement date (YYYY-MM-DD)
     */
    readonly from?: string

    /**
     * Inclusive end statement date (YYYY-MM-DD); requires &#x60;from&#x60;
     */
    readonly to?: string
}

/**
 * Request parameters for getHoaAccountTransactions operation in BankApi.
 */
export interface BankApiGetHoaAccountTransactionsRequest {
    /**
     * hoa_bank_account id
     */
    readonly id: string

    /**
     * Inclusive start date (YYYY-MM-DD)
     */
    readonly from?: string

    /**
     * Inclusive end date (YYYY-MM-DD); requires &#x60;from&#x60;
     */
    readonly to?: string
}

/**
 * Request parameters for getHoaAccountsLive operation in BankApi.
 */
export interface BankApiGetHoaAccountsLiveRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for getHoaCheck operation in BankApi.
 */
export interface BankApiGetHoaCheckRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * check id
     */
    readonly id: string
}

/**
 * Request parameters for getHoaPayable operation in BankApi.
 */
export interface BankApiGetHoaPayableRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Payable id
     */
    readonly id: string
}

/**
 * Request parameters for getHoaStatementFile operation in BankApi.
 */
export interface BankApiGetHoaStatementFileRequest {
    /**
     * hoa_bank_account id
     */
    readonly id: string

    /**
     * Statement id
     */
    readonly statementId: string
}

/**
 * Request parameters for getHoaTransfer operation in BankApi.
 */
export interface BankApiGetHoaTransferRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * bank_transfer id
     */
    readonly id: string
}

/**
 * Request parameters for getMemberBankAccount operation in BankApi.
 */
export interface BankApiGetMemberBankAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Member id
     */
    readonly memberId: string
}

/**
 * Request parameters for getPayerSubmission operation in BankApi.
 */
export interface BankApiGetPayerSubmissionRequest {
    /**
     * payer_file_submission id
     */
    readonly submissionId: string
}

/**
 * Request parameters for getPayerSubmissionFile operation in BankApi.
 */
export interface BankApiGetPayerSubmissionFileRequest {
    /**
     * payer_file_submission id
     */
    readonly submissionId: string
}

/**
 * Request parameters for getTransferSchedule operation in BankApi.
 */
export interface BankApiGetTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * schedule id
     */
    readonly id: string
}

/**
 * Request parameters for getVendorBankAccount operation in BankApi.
 */
export interface BankApiGetVendorBankAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Vendor id
     */
    readonly vendorId: string
}

/**
 * Request parameters for listAccountTracking operation in BankApi.
 */
export interface BankApiListAccountTrackingRequest {
    /**
     * bank_connection id
     */
    readonly connectionId: string
}

/**
 * Request parameters for listChargebackNotices operation in BankApi.
 */
export interface BankApiListChargebackNoticesRequest {
    /**
     * Chargeback id
     */
    readonly chargebackId: string
}

/**
 * Request parameters for listCompanyConnections operation in BankApi.
 */
export interface BankApiListCompanyConnectionsRequest {
    /**
     * Company id
     */
    readonly companyId: string
}

/**
 * Request parameters for listConnectionChargebacks operation in BankApi.
 */
export interface BankApiListConnectionChargebacksRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * Only rows that have not reversed
     */
    readonly openOnly?: boolean
}

/**
 * Request parameters for listConnectionFiles operation in BankApi.
 */
export interface BankApiListConnectionFilesRequest {
    /**
     * bank_connection id
     */
    readonly id: string
}

/**
 * Request parameters for listConnectionNsf operation in BankApi.
 */
export interface BankApiListConnectionNsfRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * Only items nobody has decided and whose deadline has not passed.
     */
    readonly openOnly?: boolean | null
}

/**
 * Request parameters for listConnectionPayables operation in BankApi.
 */
export interface BankApiListConnectionPayablesRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * &#x60;payment&#x60; | &#x60;collection&#x60;
     */
    readonly direction?: string | null

    /**
     * One of ours: &#x60;pending&#x60;, &#x60;settled&#x60;, …
     */
    readonly status?: string | null
}

/**
 * Request parameters for listConnectionRemittance operation in BankApi.
 */
export interface BankApiListConnectionRemittanceRequest {
    /**
     * bank_connection id
     */
    readonly connectionId: string

    /**
     * Only records that did not resolve to a home
     */
    readonly unmatchedOnly?: boolean
}

/**
 * Request parameters for listHoaAccounts operation in BankApi.
 */
export interface BankApiListHoaAccountsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listHoaChecks operation in BankApi.
 */
export interface BankApiListHoaChecksRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Restrict to one &#x60;hoa_bank_accounts&#x60; id.
     */
    readonly account?: string | null

    /**
     * &#x60;issued&#x60; | &#x60;void&#x60;.
     */
    readonly status?: string | null

    /**
     * Inclusive issue-date range, &#x60;YYYY-MM-DD&#x60;.
     */
    readonly from?: string | null

    readonly to?: string | null
}

/**
 * Request parameters for listHoaNsf operation in BankApi.
 */
export interface BankApiListHoaNsfRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Only items nobody has decided and whose deadline has not passed.
     */
    readonly openOnly?: boolean | null
}

/**
 * Request parameters for listHoaPayables operation in BankApi.
 */
export interface BankApiListHoaPayablesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * &#x60;payment&#x60; | &#x60;collection&#x60;
     */
    readonly direction?: string | null

    /**
     * One of ours: &#x60;pending&#x60;, &#x60;settled&#x60;, …
     */
    readonly status?: string | null
}

/**
 * Request parameters for listHoaTransfers operation in BankApi.
 */
export interface BankApiListHoaTransfersRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listPayerSubmissions operation in BankApi.
 */
export interface BankApiListPayerSubmissionsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listTransferSchedules operation in BankApi.
 */
export interface BankApiListTransferSchedulesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for pauseTransferSchedule operation in BankApi.
 */
export interface BankApiPauseTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * schedule id
     */
    readonly id: string
}

/**
 * Request parameters for payConnectionNsf operation in BankApi.
 */
export interface BankApiPayConnectionNsfRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * NSF item id
     */
    readonly id: string

    readonly nsfPayDecision: NsfPayDecision
}

/**
 * Request parameters for postRemittanceRecord operation in BankApi.
 */
export interface BankApiPostRemittanceRecordRequest {
    /**
     * bank_remittance_records id
     */
    readonly recordId: string
}

/**
 * Request parameters for previewHoaTransfer operation in BankApi.
 */
export interface BankApiPreviewHoaTransferRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly transferPreviewRequest: TransferPreviewRequest
}

/**
 * Request parameters for previewPayerFile operation in BankApi.
 */
export interface BankApiPreviewPayerFileRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly payerFileRequest: PayerFileRequest
}

/**
 * Request parameters for previewTransferSchedule operation in BankApi.
 */
export interface BankApiPreviewTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly transferScheduleCreate: TransferScheduleCreate
}

/**
 * Request parameters for recordHoaCheck operation in BankApi.
 */
export interface BankApiRecordHoaCheckRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly checkCreate: CheckCreate
}

/**
 * Request parameters for resolveChargeback operation in BankApi.
 */
export interface BankApiResolveChargebackRequest {
    /**
     * Chargeback id
     */
    readonly chargebackId: string

    readonly chargebackResolveRequest: ChargebackResolveRequest
}

/**
 * Request parameters for resumeTransferSchedule operation in BankApi.
 */
export interface BankApiResumeTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * schedule id
     */
    readonly id: string
}

/**
 * Request parameters for retireMemberBankAccount operation in BankApi.
 */
export interface BankApiRetireMemberBankAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Member id
     */
    readonly memberId: string
}

/**
 * Request parameters for retireVendorBankAccount operation in BankApi.
 */
export interface BankApiRetireVendorBankAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Vendor id
     */
    readonly vendorId: string
}

/**
 * Request parameters for returnConnectionNsf operation in BankApi.
 */
export interface BankApiReturnConnectionNsfRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    /**
     * NSF item id
     */
    readonly id: string

    readonly nsfReturnDecision: NsfReturnDecision
}

/**
 * Request parameters for sendHoaCheck operation in BankApi.
 */
export interface BankApiSendHoaCheckRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * check id
     */
    readonly id: string
}

/**
 * Request parameters for setAccountTracking operation in BankApi.
 */
export interface BankApiSetAccountTrackingRequest {
    /**
     * bank_connection id
     */
    readonly connectionId: string

    /**
     * Account number at the bank
     */
    readonly accountNumber: string

    readonly accountTrackingUpdate: AccountTrackingUpdate
}

/**
 * Request parameters for setPayerAutoUpload operation in BankApi.
 */
export interface BankApiSetPayerAutoUploadRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly payerAutoUploadUpdate: PayerAutoUploadUpdate
}

/**
 * Request parameters for setVendorBankAccount operation in BankApi.
 */
export interface BankApiSetVendorBankAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Vendor id
     */
    readonly vendorId: string

    readonly counterpartyBankAccountUpsert: CounterpartyBankAccountUpsert
}

/**
 * Request parameters for settleConnectionPayables operation in BankApi.
 */
export interface BankApiSettleConnectionPayablesRequest {
    /**
     * Bank connection id
     */
    readonly connectionId: string

    readonly payableSettleRequest: PayableSettleRequest
}

/**
 * Request parameters for submitPayerFile operation in BankApi.
 */
export interface BankApiSubmitPayerFileRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly payerFileRequest: PayerFileRequest
}

/**
 * Request parameters for submitVendorPayment operation in BankApi.
 */
export interface BankApiSubmitVendorPaymentRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly vendorPaymentCreate: VendorPaymentCreate
}

/**
 * Request parameters for syncHoaChecks operation in BankApi.
 */
export interface BankApiSyncHoaChecksRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for unassignHoaAccount operation in BankApi.
 */
export interface BankApiUnassignHoaAccountRequest {
    /**
     * hoa_bank_account id
     */
    readonly id: string
}

/**
 * Request parameters for updateBankConnection operation in BankApi.
 */
export interface BankApiUpdateBankConnectionRequest {
    /**
     * bank_connection id
     */
    readonly id: string

    readonly bankConnectionUpdate: BankConnectionUpdate
}

/**
 * Request parameters for updateTransferSchedule operation in BankApi.
 */
export interface BankApiUpdateTransferScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * schedule id
     */
    readonly id: string

    readonly transferScheduleUpdate: TransferScheduleUpdate
}

/**
 * Request parameters for voidHoaCheck operation in BankApi.
 */
export interface BankApiVoidHoaCheckRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * check id
     */
    readonly id: string

    readonly checkVoidRequest: CheckVoidRequest
}

/**
 * BankApi - object-oriented interface
 */
export class BankApi extends BaseAPI {
    /**
     * 
     * @summary Assign a bank account to an HOA. **Owner/Admin only.**
     * @param {BankApiAssignHoaAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public assignHoaAccount(requestParameters: BankApiAssignHoaAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).assignHoaAccount(requestParameters.hoaId, requestParameters.hoaAccountAssign, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Cancel a pending or settled-but-unprocessed payable at the bank and reverse what it posted. **Owner/Admin of the company.**
     * @param {BankApiCancelHoaPayableRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelHoaPayable(requestParameters: BankApiCancelHoaPayableRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).cancelHoaPayable(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Confirm (execute) a previewed transfer. **Owner/Admin (or SuperAdmin).** Idempotent on the transfer id — safe to retry.
     * @param {BankApiConfirmHoaTransferRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public confirmHoaTransfer(requestParameters: BankApiConfirmHoaTransferRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).confirmHoaTransfer(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * This is the only human decision in the feature — after it, every occurrence fires unattended.
     * @summary Confirm a draft schedule, making it live. **Owner/Admin (or SuperAdmin).** No bank call. Idempotent on the schedule id.
     * @param {BankApiConfirmTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public confirmTransferSchedule(requestParameters: BankApiConfirmTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).confirmTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The candidate credentials are verified against the bank (a live account read) **before** anything is stored, so invalid creds are rejected and nothing is persisted on failure. The client secret is encrypted at rest and never returned. Re-posting for the same company/provider rotates the stored credentials.
     * @summary Store (or rotate) a company\'s bank credentials. **Owner (own company) or SuperAdmin.**
     * @param {BankApiCreateBankConnectionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createBankConnection(requestParameters: BankApiCreateBankConnectionRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).createBankConnection(requestParameters.bankConnectionCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Soft-delete a bank connection (`is_active = false`). **Owner (own company) or SuperAdmin.** Blocked (409) while HOA accounts are still assigned to it.
     * @param {BankApiDeleteBankConnectionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteBankConnection(requestParameters: BankApiDeleteBankConnectionRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).deleteBankConnection(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Terminal, and **not a delete**: `bank_transfers.schedule_id` points at this row, and the record that money moved must outlive the instruction that moved it.
     * @summary End a schedule. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiEndTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public endTransferSchedule(requestParameters: BankApiEndTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).endTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Checks that posted against this connection\'s accounts, from the bank. **Owner/Admin.** `from` is required when `to` is given.
     * @param {BankApiFindConnectionClearedChecksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findConnectionClearedChecks(requestParameters: BankApiFindConnectionClearedChecksRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).findConnectionClearedChecks(requestParameters.connectionId, requestParameters.accountNumber, requestParameters.checkNumber, requestParameters.amount, requestParameters.from, requestParameters.to, requestParameters.checkTypes, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Find files for a connection by filter (durable status search). **Owner/Admin (or SuperAdmin).**
     * @param {BankApiFindConnectionFilesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findConnectionFiles(requestParameters: BankApiFindConnectionFilesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).findConnectionFiles(requestParameters.id, requestParameters.referenceId, requestParameters.fileType, requestParameters.status, requestParameters.fileUid, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a single bank connection (no secret). **Owner (own company) or SuperAdmin.**
     * @param {BankApiGetBankConnectionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBankConnection(requestParameters: BankApiGetBankConnectionRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getBankConnection(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Download a chargeback\'s cheque image. **Owner/Admin.**
     * @param {BankApiGetChargebackImageRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getChargebackImage(requestParameters: BankApiGetChargebackImageRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getChargebackImage(requestParameters.chargebackId, requestParameters.side, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Download one chargeback notice PDF. **Owner/Admin.**
     * @param {BankApiGetChargebackNoticeFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getChargebackNoticeFile(requestParameters: BankApiGetChargebackNoticeFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getChargebackNoticeFile(requestParameters.chargebackId, requestParameters.statementId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary A posted check\'s image by the bank\'s `CheckID`. **Owner/Admin.**
     * @param {BankApiGetClearedCheckImageRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClearedCheckImage(requestParameters: BankApiGetClearedCheckImageRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getClearedCheckImage(requestParameters.connectionId, requestParameters.checkId, requestParameters.side, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Pending transactions for a company account. **Owner/Admin only.**
     * @param {BankApiGetCompanyAccountPendingRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompanyAccountPending(requestParameters: BankApiGetCompanyAccountPendingRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getCompanyAccountPending(requestParameters.companyId, requestParameters.accountNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Available statements for a company account. **Owner/Admin only.**
     * @param {BankApiGetCompanyAccountStatementsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompanyAccountStatements(requestParameters: BankApiGetCompanyAccountStatementsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getCompanyAccountStatements(requestParameters.companyId, requestParameters.accountNumber, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Posted transactions for a company account. **Owner/Admin only.**
     * @param {BankApiGetCompanyAccountTransactionsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompanyAccountTransactions(requestParameters: BankApiGetCompanyAccountTransactionsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getCompanyAccountTransactions(requestParameters.companyId, requestParameters.accountNumber, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Loads the company\'s active connection(s), calls the bank, and returns the accounts. Requires the bank to be reachable (whitelisted IP).
     * @summary View all bank accounts for a company, live from the bank. **Owner/Admin (or SuperAdmin) only.**
     * @param {BankApiGetCompanyBankAccountsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompanyBankAccounts(requestParameters: BankApiGetCompanyBankAccountsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getCompanyBankAccounts(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Download a company statement PDF by statement id. **Owner/Admin.**
     * @param {BankApiGetCompanyStatementFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompanyStatementFile(requestParameters: BankApiGetCompanyStatementFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getCompanyStatementFile(requestParameters.companyId, requestParameters.statementId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get one file\'s status by the bank\'s file id. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiGetConnectionFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getConnectionFile(requestParameters: BankApiGetConnectionFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getConnectionFile(requestParameters.id, requestParameters.fileId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary The check image behind a POD item. **Owner/Admin.** Live from the bank: finds the posted check by account, number and date, then fetches its image by the bank\'s `CheckID`.
     * @param {BankApiGetConnectionNsfImageRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getConnectionNsfImage(requestParameters: BankApiGetConnectionNsfImageRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getConnectionNsfImage(requestParameters.connectionId, requestParameters.id, requestParameters.side, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Pending transactions for an HOA-assigned account. **Manager+.**
     * @param {BankApiGetHoaAccountPendingRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaAccountPending(requestParameters: BankApiGetHoaAccountPendingRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaAccountPending(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Available statements for an HOA-assigned account. **Manager+.**
     * @param {BankApiGetHoaAccountStatementsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaAccountStatements(requestParameters: BankApiGetHoaAccountStatementsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaAccountStatements(requestParameters.id, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Posted transactions for an HOA-assigned account. **Manager+.**
     * @param {BankApiGetHoaAccountTransactionsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaAccountTransactions(requestParameters: BankApiGetHoaAccountTransactionsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaAccountTransactions(requestParameters.id, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List an HOA\'s assigned accounts enriched with **live** balance/status. **Manager+ (HOA-scoped).** Requires the bank to be reachable.
     * @param {BankApiGetHoaAccountsLiveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaAccountsLive(requestParameters: BankApiGetHoaAccountsLiveRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaAccountsLive(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get one check. **Manager+.**
     * @param {BankApiGetHoaCheckRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaCheck(requestParameters: BankApiGetHoaCheckRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaCheck(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary One payable. **Manager+.**
     * @param {BankApiGetHoaPayableRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaPayable(requestParameters: BankApiGetHoaPayableRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaPayable(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Download an HOA-assigned account\'s statement PDF. **Manager+.**
     * @param {BankApiGetHoaStatementFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaStatementFile(requestParameters: BankApiGetHoaStatementFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaStatementFile(requestParameters.id, requestParameters.statementId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get one transfer (reconciles a still-pending one). **Manager+.**
     * @param {BankApiGetHoaTransferRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaTransfer(requestParameters: BankApiGetHoaTransferRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getHoaTransfer(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary A homeowner\'s bank account on file, masked. **Manager+ on the HOA.**
     * @param {BankApiGetMemberBankAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMemberBankAccount(requestParameters: BankApiGetMemberBankAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getMemberBankAccount(requestParameters.hoaId, requestParameters.memberId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get one payer submission (reconciles a still-processing one by ReferenceID). **Owner/Admin (or SuperAdmin).**
     * @param {BankApiGetPayerSubmissionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPayerSubmission(requestParameters: BankApiGetPayerSubmissionRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getPayerSubmission(requestParameters.submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the stored tab-delimited bytes as-uploaded (an audit snapshot), not a fresh re-assembly — the bank\'s get-file returns only status, so this is the only way to see what was actually sent. No bank call.
     * @summary Download the exact file we sent the bank for a submission. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiGetPayerSubmissionFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPayerSubmissionFile(requestParameters: BankApiGetPayerSubmissionFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getPayerSubmissionFile(requestParameters.submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary One transfer schedule. **Manager+.**
     * @param {BankApiGetTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTransferSchedule(requestParameters: BankApiGetTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary A vendor\'s bank details on file, masked. **Manager+.**
     * @param {BankApiGetVendorBankAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getVendorBankAccount(requestParameters: BankApiGetVendorBankAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).getVendorBankAccount(requestParameters.hoaId, requestParameters.vendorId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Which accounts on a connection are pulled daily. **Owner/Admin only.**
     * @param {BankApiListAccountTrackingRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAccountTracking(requestParameters: BankApiListAccountTrackingRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listAccountTracking(requestParameters.connectionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * An empty list is a legitimate answer — a notice can lag the chargeback that caused it.
     * @summary List a chargeback\'s notice documents. **Owner/Admin.**
     * @param {BankApiListChargebackNoticesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listChargebackNotices(requestParameters: BankApiListChargebackNoticesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listChargebackNotices(requestParameters.chargebackId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List a company\'s bank connections (no secret). **Owner (own company) or SuperAdmin.**
     * @param {BankApiListCompanyConnectionsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listCompanyConnections(requestParameters: BankApiListCompanyConnectionsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listCompanyConnections(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reads our own rows — no bank call.
     * @summary What one connection has had taken back. **Owner/Admin.**
     * @param {BankApiListConnectionChargebacksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listConnectionChargebacks(requestParameters: BankApiListConnectionChargebacksRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listConnectionChargebacks(requestParameters.connectionId, requestParameters.openOnly, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List files uploaded today for a connection (bank status view). **Owner/Admin (or SuperAdmin).**
     * @param {BankApiListConnectionFilesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listConnectionFiles(requestParameters: BankApiListConnectionFilesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listConnectionFiles(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Items presented against insufficient funds on one connection\'s accounts. **Owner/Admin.** Reads our own rows — no bank call.
     * @param {BankApiListConnectionNsfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listConnectionNsf(requestParameters: BankApiListConnectionNsfRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listConnectionNsf(requestParameters.connectionId, requestParameters.openOnly, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary The settle queue: a connection\'s payables, pending first. **Owner/Admin.**
     * @param {BankApiListConnectionPayablesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listConnectionPayables(requestParameters: BankApiListConnectionPayablesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listConnectionPayables(requestParameters.connectionId, requestParameters.direction, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Connection-scoped rather than per-association, because a record whose association did not resolve has no HOA to be filed under and cannot appear in that association\'s review list. This is where it is visible.  `?unmatched_only=true` narrows to what needs somebody.
     * @summary What one connection\'s lockbox has delivered. **Owner/Admin only.**
     * @param {BankApiListConnectionRemittanceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listConnectionRemittance(requestParameters: BankApiListConnectionRemittanceRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listConnectionRemittance(requestParameters.connectionId, requestParameters.unmatchedOnly, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List an HOA\'s assigned accounts (stored metadata, no bank call). **Manager+ (HOA-scoped).**
     * @param {BankApiListHoaAccountsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaAccounts(requestParameters: BankApiListHoaAccountsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listHoaAccounts(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List an HOA\'s check register, newest issue date first. **Manager+.**
     * @param {BankApiListHoaChecksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaChecks(requestParameters: BankApiListHoaChecksRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listHoaChecks(requestParameters.hoaId, requestParameters.account, requestParameters.status, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary An association\'s NSF items. **Manager+.** No bank call.
     * @param {BankApiListHoaNsfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaNsf(requestParameters: BankApiListHoaNsfRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listHoaNsf(requestParameters.hoaId, requestParameters.openOnly, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary An association\'s payables, newest first. **Manager+.**
     * @param {BankApiListHoaPayablesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaPayables(requestParameters: BankApiListHoaPayablesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listHoaPayables(requestParameters.hoaId, requestParameters.direction, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List an HOA\'s transfer records (newest first). **Manager+.**
     * @param {BankApiListHoaTransfersRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaTransfers(requestParameters: BankApiListHoaTransfersRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listHoaTransfers(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List an HOA\'s payer submissions (our audit table). **Owner/Admin (or SuperAdmin).**
     * @param {BankApiListPayerSubmissionsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listPayerSubmissions(requestParameters: BankApiListPayerSubmissionsRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listPayerSubmissions(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary List an HOA\'s transfer schedules, newest first. **Manager+.**
     * @param {BankApiListTransferSchedulesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listTransferSchedules(requestParameters: BankApiListTransferSchedulesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).listTransferSchedules(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stops it firing and keeps its history. Resuming does **not** catch up — the job only ever asks whether a schedule is due today.
     * @summary Pause a schedule. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiPauseTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public pauseTransferSchedule(requestParameters: BankApiPauseTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).pauseTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Final through the API: a decision is changed only by phoning bank support. Refused once the item is decided, or after **1pm Central on its expiry date**. Exactly one request is ever sent for an item; a concurrent decision gets a 409.
     * @summary Ask the bank to **pay** an NSF item. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiPayConnectionNsfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public payConnectionNsf(requestParameters: BankApiPayConnectionNsfRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).payConnectionNsf(requestParameters.connectionId, requestParameters.id, requestParameters.nsfPayDecision, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The exit from every hold. The unattended pass declines anything it cannot attribute with confidence; this is how a person who has looked at the record turns that decision into a payment.  **Makes no bank call.** Everything it needs was stored at ingest.  It goes through `PaymentService::record` like every other payment, and it refuses the holds a person cannot clear by looking — a record that resolves to no home, or whose amount is missing, unreadable, non-positive, or disagrees with itself. Those are facts about the record, not about our willingness to act on it.
     * @summary Post one held remittance record to the ledger **by hand**. **Owner/Admin only.**
     * @param {BankApiPostRemittanceRecordRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postRemittanceRecord(requestParameters: BankApiPostRemittanceRecordRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).postRemittanceRecord(requestParameters.recordId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Preview a transfer between two of an HOA\'s assigned accounts. **Owner/Admin (or SuperAdmin).** No money moves — validates and returns the created (unconfirmed) transfer plus live balance context.
     * @param {BankApiPreviewHoaTransferRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewHoaTransfer(requestParameters: BankApiPreviewHoaTransferRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).previewHoaTransfer(requestParameters.hoaId, requestParameters.transferPreviewRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Preview (assemble) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** No bank call — returns the exact file text + count. Optional `bank_connection_id` selects the lockbox when the company has more than one connection.
     * @param {BankApiPreviewPayerFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewPayerFile(requestParameters: BankApiPreviewPayerFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).previewPayerFile(requestParameters.hoaId, requestParameters.payerFileRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Preview a recurring transfer schedule. **Owner/Admin (or SuperAdmin).** Stores a **draft** and returns the next five days it would fire. No money moves and no schedule is live until confirm.
     * @param {BankApiPreviewTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewTransferSchedule(requestParameters: BankApiPreviewTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).previewTransferSchedule(requestParameters.hoaId, requestParameters.transferScheduleCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Record an issued check. **Owner/Admin (or SuperAdmin).** Posts the journal entry and registers the check with the bank\'s Positive Pay service in one action; a bank-side failure is returned as a `failed` row, not an error.
     * @param {BankApiRecordHoaCheckRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public recordHoaCheck(requestParameters: BankApiRecordHoaCheckRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).recordHoaCheck(requestParameters.hoaId, requestParameters.checkCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * **Attaches only — it does not reverse.** The next chargeback run applies the same guards it applies to anything the matcher resolved, so the unattended path stays the single place money moves.
     * @summary Attach a held chargeback to the payment it reverses. **Owner/Admin.**
     * @param {BankApiResolveChargebackRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveChargeback(requestParameters: BankApiResolveChargebackRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).resolveChargeback(requestParameters.chargebackId, requestParameters.chargebackResolveRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Re-validates the accounts first: months may have passed, and an account unassigned in the meantime should stop the resume rather than surface at 06:30 as a failed occurrence.
     * @summary Resume a paused schedule. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiResumeTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resumeTransferSchedule(requestParameters: BankApiResumeTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).resumeTransferSchedule(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Remove a homeowner\'s bank account on file. **Owner/Admin of the company.** The homeowner can add another.
     * @param {BankApiRetireMemberBankAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public retireMemberBankAccount(requestParameters: BankApiRetireMemberBankAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).retireMemberBankAccount(requestParameters.hoaId, requestParameters.memberId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Retire a vendor\'s bank details. **Owner/Admin of the company; not super admins.** The row is kept for the payments made to it.
     * @param {BankApiRetireVendorBankAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public retireVendorBankAccount(requestParameters: BankApiRetireVendorBankAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).retireVendorBankAccount(requestParameters.hoaId, requestParameters.vendorId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Ask the bank to **return** an NSF item. **Owner/Admin (or SuperAdmin).** The bank\'s default, made explicit; same finality, cutoff and one-send rule as pay.
     * @param {BankApiReturnConnectionNsfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public returnConnectionNsf(requestParameters: BankApiReturnConnectionNsfRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).returnConnectionNsf(requestParameters.connectionId, requestParameters.id, requestParameters.nsfReturnDecision, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Send a `pending` or `failed` Positive Pay entry again. **Owner/Admin (or SuperAdmin).** Reconciles with the bank first, so an entry that actually landed is recorded rather than repeated.
     * @param {BankApiSendHoaCheckRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendHoaCheck(requestParameters: BankApiSendHoaCheckRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).sendHoaCheck(requestParameters.hoaId, requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Makes **no bank call**. Enabling leaves the account\'s backfill pending, and the next scheduled run fetches its 90 days.
     * @summary Turn the daily transaction pull on or off for one account. **Owner/Admin only.**
     * @param {BankApiSetAccountTrackingRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setAccountTracking(requestParameters: BankApiSetAccountTrackingRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).setAccountTracking(requestParameters.connectionId, requestParameters.accountNumber, requestParameters.accountTrackingUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deliberately not part of the generic HOA update. The roles happen to match today, but `can_update_hoa` is the bar for editing an association\'s address and `can_submit_payer_file` is the bar for sending its roster to a bank — and this switch does the second. If HOA editing is ever widened to Managers, this must not widen with it.  Enabling is refused for an association with no bank assigned: a switch that reads as on and does nothing is worse than an error.
     * @summary Turn the scheduled daily payer upload on or off for one HOA. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiSetPayerAutoUploadRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setPayerAutoUpload(requestParameters: BankApiSetPayerAutoUploadRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).setPayerAutoUpload(requestParameters.hoaId, requestParameters.payerAutoUploadUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Set a vendor\'s bank details, replacing any on file. **Owner/Admin of the company; not super admins.** The account number is encrypted on arrival and never returned.
     * @param {BankApiSetVendorBankAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setVendorBankAccount(requestParameters: BankApiSetVendorBankAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).setVendorBankAccount(requestParameters.hoaId, requestParameters.vendorId, requestParameters.counterpartyBankAccountUpsert, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Settle pending payables with a one-time RSA code. **Owner/Admin of the company. Moves money.** The code is sent once and never stored; the RSA token locks after three wrong codes.
     * @param {BankApiSettleConnectionPayablesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public settleConnectionPayables(requestParameters: BankApiSettleConnectionPayablesRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).settleConnectionPayables(requestParameters.connectionId, requestParameters.payableSettleRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Assemble + submit (upload) one HOA\'s payer file. **Owner/Admin (or SuperAdmin).** Records a submission (audit) and uploads; the returned record\'s `status`/`error_*` carry the outcome.
     * @param {BankApiSubmitPayerFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public submitPayerFile(requestParameters: BankApiSubmitPayerFileRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).submitPayerFile(requestParameters.hoaId, requestParameters.payerFileRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Pay a vendor by ACH: validates with the bank and leaves the item pending in the settle queue. **Owner/Admin of the company.** Moves nothing.
     * @param {BankApiSubmitVendorPaymentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public submitVendorPayment(requestParameters: BankApiSubmitVendorPaymentRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).submitVendorPayment(requestParameters.hoaId, requestParameters.vendorPaymentCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Ask the bank which of this HOA\'s accepted entries have been imported into Positive Pay, now rather than on the job\'s schedule. **Owner/Admin (or SuperAdmin).** Reads only; sends nothing.
     * @param {BankApiSyncHoaChecksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public syncHoaChecks(requestParameters: BankApiSyncHoaChecksRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).syncHoaChecks(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Unassign a bank account from an HOA. **Owner/Admin only.**
     * @param {BankApiUnassignHoaAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unassignHoaAccount(requestParameters: BankApiUnassignHoaAccountRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).unassignHoaAccount(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * `label` / `client_id` / `is_active` are updated when present. A new `client_secret` (and/or `client_id`) triggers re-verification against the bank before the change is persisted; the secret is never returned.
     * @summary Update a bank connection. **Owner (own company) or SuperAdmin.**
     * @param {BankApiUpdateBankConnectionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateBankConnection(requestParameters: BankApiUpdateBankConnectionRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).updateBankConnection(requestParameters.id, requestParameters.bankConnectionUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Takes effect from the next evaluation; occurrences already sent are untouched. An omitted field is left alone, an explicit `null` clears one — which is how changing frequency drops the day that no longer applies.
     * @summary Change a transfer schedule. **Owner/Admin (or SuperAdmin).**
     * @param {BankApiUpdateTransferScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateTransferSchedule(requestParameters: BankApiUpdateTransferScheduleRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).updateTransferSchedule(requestParameters.hoaId, requestParameters.id, requestParameters.transferScheduleUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Void a recorded check. **Owner/Admin (or SuperAdmin).** Reverses the journal entry and, if the bank accepted the issue, registers the void with the bank too.
     * @param {BankApiVoidHoaCheckRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public voidHoaCheck(requestParameters: BankApiVoidHoaCheckRequest, options?: RawAxiosRequestConfig) {
        return BankApiFp(this.configuration).voidHoaCheck(requestParameters.hoaId, requestParameters.id, requestParameters.checkVoidRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * BillingHandlersApi - axios parameter creator
 */
export const BillingHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} caseId Case id
         * @param {CaseNoteRequest} caseNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addCaseNote: async (caseId: string, caseNoteRequest: CaseNoteRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'caseId' is not null or undefined
            assertParamExists('addCaseNote', 'caseId', caseId)
            // verify required parameter 'caseNoteRequest' is not null or undefined
            assertParamExists('addCaseNote', 'caseNoteRequest', caseNoteRequest)
            const localVarPath = `/api/v1/billing/collections/{case_id}/events`
                .replace(`{${"case_id"}}`, encodeURIComponent(String(caseId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(caseNoteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} tenureId Tenure id
         * @param {TenureMemberCreate} tenureMemberCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addTenureMember: async (tenureId: string, tenureMemberCreate: TenureMemberCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tenureId' is not null or undefined
            assertParamExists('addTenureMember', 'tenureId', tenureId)
            // verify required parameter 'tenureMemberCreate' is not null or undefined
            assertParamExists('addTenureMember', 'tenureMemberCreate', tenureMemberCreate)
            const localVarPath = `/api/v1/billing/tenures/{tenure_id}/members`
                .replace(`{${"tenure_id"}}`, encodeURIComponent(String(tenureId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(tenureMemberCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} caseId Case id
         * @param {CaseAdvanceRequest} caseAdvanceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        advanceCase: async (caseId: string, caseAdvanceRequest: CaseAdvanceRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'caseId' is not null or undefined
            assertParamExists('advanceCase', 'caseId', caseId)
            // verify required parameter 'caseAdvanceRequest' is not null or undefined
            assertParamExists('advanceCase', 'caseAdvanceRequest', caseAdvanceRequest)
            const localVarPath = `/api/v1/billing/collections/{case_id}/advance`
                .replace(`{${"case_id"}}`, encodeURIComponent(String(caseId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(caseAdvanceRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} [minDaysPastDue] Only homes at least this far overdue
         * @param {string} [minBalance] Only homes owing at least this much, as a decimal string
         * @param {AgingSort} [sort] days_past_due (default) or balance
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        agingReport: async (hoaId: string, minDaysPastDue?: number, minBalance?: string, sort?: AgingSort, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('agingReport', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/aging`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (minDaysPastDue !== undefined) {
                localVarQueryParameter['min_days_past_due'] = minDaysPastDue;
            }

            if (minBalance !== undefined) {
                localVarQueryParameter['min_balance'] = minBalance;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {NsfFeeRequest} nsfFeeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assessNsfFee: async (paymentId: string, nsfFeeRequest: NsfFeeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'paymentId' is not null or undefined
            assertParamExists('assessNsfFee', 'paymentId', paymentId)
            // verify required parameter 'nsfFeeRequest' is not null or undefined
            assertParamExists('assessNsfFee', 'nsfFeeRequest', nsfFeeRequest)
            const localVarPath = `/api/v1/billing/payments/{payment_id}/nsf-fee`
                .replace(`{${"payment_id"}}`, encodeURIComponent(String(paymentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(nsfFeeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {CloseTenureRequest} closeTenureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        closeTenure: async (homeId: string, closeTenureRequest: CloseTenureRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('closeTenure', 'homeId', homeId)
            // verify required parameter 'closeTenureRequest' is not null or undefined
            assertParamExists('closeTenure', 'closeTenureRequest', closeTenureRequest)
            const localVarPath = `/api/v1/billing/homes/{home_id}/tenures/close`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(closeTenureRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        copyHoaNoticeRules: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('copyHoaNoticeRules', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/notice-rules/copy-defaults`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        couponPdf: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('couponPdf', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/coupon/pdf`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {NoticeRuleCreate} noticeRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCompanyNoticeRule: async (companyId: string, noticeRuleCreate: NoticeRuleCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('createCompanyNoticeRule', 'companyId', companyId)
            // verify required parameter 'noticeRuleCreate' is not null or undefined
            assertParamExists('createCompanyNoticeRule', 'noticeRuleCreate', noticeRuleCreate)
            const localVarPath = `/api/v1/billing/companies/{company_id}/notice-rules`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(noticeRuleCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {NoticeRuleCreate} noticeRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createHoaNoticeRule: async (hoaId: string, noticeRuleCreate: NoticeRuleCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('createHoaNoticeRule', 'hoaId', hoaId)
            // verify required parameter 'noticeRuleCreate' is not null or undefined
            assertParamExists('createHoaNoticeRule', 'noticeRuleCreate', noticeRuleCreate)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/notice-rules`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(noticeRuleCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {OverrideCreate} overrideCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOverride: async (homeId: string, overrideCreate: OverrideCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('createOverride', 'homeId', homeId)
            // verify required parameter 'overrideCreate' is not null or undefined
            assertParamExists('createOverride', 'overrideCreate', overrideCreate)
            const localVarPath = `/api/v1/billing/homes/{home_id}/assessment-overrides`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(overrideCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {ScheduleCreate} scheduleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createSchedule: async (hoaId: string, scheduleCreate: ScheduleCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('createSchedule', 'hoaId', hoaId)
            // verify required parameter 'scheduleCreate' is not null or undefined
            assertParamExists('createSchedule', 'scheduleCreate', scheduleCreate)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/assessment-schedules`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(scheduleCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} ruleId Notice rule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteNoticeRule: async (ruleId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('deleteNoticeRule', 'ruleId', ruleId)
            const localVarPath = `/api/v1/billing/notice-rules/{rule_id}`
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} overrideId Override id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteOverride: async (overrideId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('deleteOverride', 'overrideId', overrideId)
            const localVarPath = `/api/v1/billing/assessment-overrides/{override_id}`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} estoppelId Estoppel request id
         * @param {EstoppelDeliverRequest} estoppelDeliverRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deliverEstoppel: async (estoppelId: string, estoppelDeliverRequest: EstoppelDeliverRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'estoppelId' is not null or undefined
            assertParamExists('deliverEstoppel', 'estoppelId', estoppelId)
            // verify required parameter 'estoppelDeliverRequest' is not null or undefined
            assertParamExists('deliverEstoppel', 'estoppelDeliverRequest', estoppelDeliverRequest)
            const localVarPath = `/api/v1/billing/estoppel/{estoppel_id}/deliver`
                .replace(`{${"estoppel_id"}}`, encodeURIComponent(String(estoppelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(estoppelDeliverRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} estoppelId Estoppel request id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        estoppelPdf: async (estoppelId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'estoppelId' is not null or undefined
            assertParamExists('estoppelPdf', 'estoppelId', estoppelId)
            const localVarPath = `/api/v1/billing/estoppel/{estoppel_id}/pdf`
                .replace(`{${"estoppel_id"}}`, encodeURIComponent(String(estoppelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} scheduleId Schedule id
         * @param {GenerateRequest} generateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateRuns: async (scheduleId: string, generateRequest: GenerateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'scheduleId' is not null or undefined
            assertParamExists('generateRuns', 'scheduleId', scheduleId)
            // verify required parameter 'generateRequest' is not null or undefined
            assertParamExists('generateRuns', 'generateRequest', generateRequest)
            const localVarPath = `/api/v1/billing/assessment-schedules/{schedule_id}/runs`
                .replace(`{${"schedule_id"}}`, encodeURIComponent(String(scheduleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(generateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyPolicy: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getCompanyPolicy', 'companyId', companyId)
            const localVarPath = `/api/v1/billing/companies/{company_id}/policy`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCoupon: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getCoupon', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/coupon`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaPolicy: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getHoaPolicy', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/policy`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomeBalance: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getHomeBalance', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/balance`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {string} [from] Entries on or after this effective date
         * @param {string} [to] Entries on or before this effective date
         * @param {string} [entryType] Comma-separated entry types; omit for all
         * @param {string} [sourceType] assessment_run | payment | violation | manual | aging
         * @param {SortOrder} [order] asc (oldest first, the default) or desc
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomeLedger: async (homeId: string, page?: number, perPage?: number, from?: string, to?: string, entryType?: string, sourceType?: string, order?: SortOrder, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getHomeLedger', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/ledger`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (entryType !== undefined) {
                localVarQueryParameter['entry_type'] = entryType;
            }

            if (sourceType !== undefined) {
                localVarQueryParameter['source_type'] = sourceType;
            }

            if (order !== undefined) {
                localVarQueryParameter['order'] = order;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} runId Run id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getRun', 'runId', runId)
            const localVarPath = `/api/v1/billing/assessment-runs/{run_id}`
                .replace(`{${"run_id"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {string} [from] Period start (default: a month back)
         * @param {string} [to] Period end (default: today)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStatement: async (homeId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getStatement', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/statement`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {EstoppelRequestCreate} estoppelRequestCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        issueEstoppel: async (homeId: string, estoppelRequestCreate: EstoppelRequestCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('issueEstoppel', 'homeId', homeId)
            // verify required parameter 'estoppelRequestCreate' is not null or undefined
            assertParamExists('issueEstoppel', 'estoppelRequestCreate', estoppelRequestCreate)
            const localVarPath = `/api/v1/billing/homes/{home_id}/estoppel`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(estoppelRequestCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} caseId Case id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCaseEvents: async (caseId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'caseId' is not null or undefined
            assertParamExists('listCaseEvents', 'caseId', caseId)
            const localVarPath = `/api/v1/billing/collections/{case_id}/events`
                .replace(`{${"case_id"}}`, encodeURIComponent(String(caseId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {boolean} [includeClosed] Include resolved cases
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCases: async (hoaId: string, includeClosed?: boolean, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listCases', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/collections`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (includeClosed !== undefined) {
                localVarQueryParameter['include_closed'] = includeClosed;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCompanyNoticeRules: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('listCompanyNoticeRules', 'companyId', companyId)
            const localVarPath = `/api/v1/billing/companies/{company_id}/notice-rules`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listEstoppel: async (homeId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listEstoppel', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/estoppel`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaNoticeRules: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaNoticeRules', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/notice-rules`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {PaymentStatus} [status] unmatched | applied | overpaid | returned | voided | refunded
         * @param {PaymentMethod} [method] check | ach | e_check | card | cash | manual | other
         * @param {string} [from] Received on or after
         * @param {string} [to] Received on or before
         * @param {string} [batchId] One deposit batch
         * @param {string} [homeId] One home
         * @param {boolean} [unappliedOnly] Only payments still holding money
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaPayments: async (hoaId: string, status?: PaymentStatus, method?: PaymentMethod, from?: string, to?: string, batchId?: string, homeId?: string, unappliedOnly?: boolean, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listHoaPayments', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/payments`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (method !== undefined) {
                localVarQueryParameter['method'] = method;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (batchId !== undefined) {
                localVarQueryParameter['batch_id'] = batchId;
            }

            if (homeId !== undefined) {
                localVarQueryParameter['home_id'] = homeId;
            }

            if (unappliedOnly !== undefined) {
                localVarQueryParameter['unapplied_only'] = unappliedOnly;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHomePayments: async (homeId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listHomePayments', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/payments`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOverrides: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listOverrides', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/assessment-overrides`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listRuns: async (hoaId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listRuns', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/assessment-runs`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSchedules: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listSchedules', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/assessment-schedules`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} tenureId Tenure id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTenureMembers: async (tenureId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tenureId' is not null or undefined
            assertParamExists('listTenureMembers', 'tenureId', tenureId)
            const localVarPath = `/api/v1/billing/tenures/{tenure_id}/members`
                .replace(`{${"tenure_id"}}`, encodeURIComponent(String(tenureId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTenures: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listTenures', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/tenures`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUnmatchedPayments: async (hoaId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listUnmatchedPayments', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/payments/unmatched`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {PaymentMatchRequest} paymentMatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        matchPayment: async (paymentId: string, paymentMatchRequest: PaymentMatchRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'paymentId' is not null or undefined
            assertParamExists('matchPayment', 'paymentId', paymentId)
            // verify required parameter 'paymentMatchRequest' is not null or undefined
            assertParamExists('matchPayment', 'paymentMatchRequest', paymentMatchRequest)
            const localVarPath = `/api/v1/billing/payments/{payment_id}/match`
                .replace(`{${"payment_id"}}`, encodeURIComponent(String(paymentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(paymentMatchRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {CaseOpenRequest} caseOpenRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        openCase: async (hoaId: string, caseOpenRequest: CaseOpenRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('openCase', 'hoaId', hoaId)
            // verify required parameter 'caseOpenRequest' is not null or undefined
            assertParamExists('openCase', 'caseOpenRequest', caseOpenRequest)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/collections`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(caseOpenRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {OpenTenureRequest} openTenureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        openTenure: async (homeId: string, openTenureRequest: OpenTenureRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('openTenure', 'homeId', homeId)
            // verify required parameter 'openTenureRequest' is not null or undefined
            assertParamExists('openTenure', 'openTenureRequest', openTenureRequest)
            const localVarPath = `/api/v1/billing/homes/{home_id}/tenures`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(openTenureRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {ChargeCreate} chargeCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postCharge: async (homeId: string, chargeCreate: ChargeCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('postCharge', 'homeId', homeId)
            // verify required parameter 'chargeCreate' is not null or undefined
            assertParamExists('postCharge', 'chargeCreate', chargeCreate)
            const localVarPath = `/api/v1/billing/homes/{home_id}/charges`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(chargeCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {CreditCreate} creditCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postCredit: async (homeId: string, creditCreate: CreditCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('postCredit', 'homeId', homeId)
            // verify required parameter 'creditCreate' is not null or undefined
            assertParamExists('postCredit', 'creditCreate', creditCreate)
            const localVarPath = `/api/v1/billing/homes/{home_id}/credits`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(creditCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} runId Run id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('postRun', 'runId', runId)
            const localVarPath = `/api/v1/billing/assessment-runs/{run_id}/post`
                .replace(`{${"run_id"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {PaymentCreate} paymentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordPayment: async (hoaId: string, paymentCreate: PaymentCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('recordPayment', 'hoaId', hoaId)
            // verify required parameter 'paymentCreate' is not null or undefined
            assertParamExists('recordPayment', 'paymentCreate', paymentCreate)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/payments`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(paymentCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {PaymentRefundRequest} paymentRefundRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refundPayment: async (paymentId: string, paymentRefundRequest: PaymentRefundRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'paymentId' is not null or undefined
            assertParamExists('refundPayment', 'paymentId', paymentId)
            // verify required parameter 'paymentRefundRequest' is not null or undefined
            assertParamExists('refundPayment', 'paymentRefundRequest', paymentRefundRequest)
            const localVarPath = `/api/v1/billing/payments/{payment_id}/refund`
                .replace(`{${"payment_id"}}`, encodeURIComponent(String(paymentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(paymentRefundRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {PaymentReturnRequest} paymentReturnRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        returnPayment: async (paymentId: string, paymentReturnRequest: PaymentReturnRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'paymentId' is not null or undefined
            assertParamExists('returnPayment', 'paymentId', paymentId)
            // verify required parameter 'paymentReturnRequest' is not null or undefined
            assertParamExists('returnPayment', 'paymentReturnRequest', paymentReturnRequest)
            const localVarPath = `/api/v1/billing/payments/{payment_id}/return`
                .replace(`{${"payment_id"}}`, encodeURIComponent(String(paymentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(paymentReturnRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} entryId Ledger entry id
         * @param {ReverseRequest} reverseRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reverseEntry: async (entryId: string, reverseRequest: ReverseRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'entryId' is not null or undefined
            assertParamExists('reverseEntry', 'entryId', entryId)
            // verify required parameter 'reverseRequest' is not null or undefined
            assertParamExists('reverseEntry', 'reverseRequest', reverseRequest)
            const localVarPath = `/api/v1/billing/entries/{entry_id}/reverse`
                .replace(`{${"entry_id"}}`, encodeURIComponent(String(entryId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reverseRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} runId Run id
         * @param {ReverseRequest} reverseRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reverseRun: async (runId: string, reverseRequest: ReverseRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('reverseRun', 'runId', runId)
            // verify required parameter 'reverseRequest' is not null or undefined
            assertParamExists('reverseRun', 'reverseRequest', reverseRequest)
            const localVarPath = `/api/v1/billing/assessment-runs/{run_id}/reverse`
                .replace(`{${"run_id"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reverseRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {ReviewCondition} [condition] Show only one condition; counts still cover all eight
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reviewQueue: async (hoaId: string, condition?: ReviewCondition, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('reviewQueue', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/review`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (condition !== undefined) {
                localVarQueryParameter['condition'] = condition;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runAging: async (hoaId: string, runAsOfRequest: RunAsOfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('runAging', 'hoaId', hoaId)
            // verify required parameter 'runAsOfRequest' is not null or undefined
            assertParamExists('runAging', 'runAsOfRequest', runAsOfRequest)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/aging`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(runAsOfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runInterest: async (hoaId: string, runAsOfRequest: RunAsOfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('runInterest', 'hoaId', hoaId)
            // verify required parameter 'runAsOfRequest' is not null or undefined
            assertParamExists('runInterest', 'runAsOfRequest', runAsOfRequest)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/interest`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(runAsOfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runLateFees: async (hoaId: string, runAsOfRequest: RunAsOfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('runLateFees', 'hoaId', hoaId)
            // verify required parameter 'runAsOfRequest' is not null or undefined
            assertParamExists('runLateFees', 'runAsOfRequest', runAsOfRequest)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/late-fees`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(runAsOfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runNotices: async (hoaId: string, runAsOfRequest: RunAsOfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('runNotices', 'hoaId', hoaId)
            // verify required parameter 'runAsOfRequest' is not null or undefined
            assertParamExists('runNotices', 'runAsOfRequest', runAsOfRequest)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/notices`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(runAsOfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} tenureId Tenure id
         * @param {HoldRequest} holdRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setTenureHold: async (tenureId: string, holdRequest: HoldRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tenureId' is not null or undefined
            assertParamExists('setTenureHold', 'tenureId', tenureId)
            // verify required parameter 'holdRequest' is not null or undefined
            assertParamExists('setTenureHold', 'holdRequest', holdRequest)
            const localVarPath = `/api/v1/billing/tenures/{tenure_id}/hold`
                .replace(`{${"tenure_id"}}`, encodeURIComponent(String(tenureId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(holdRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {string} [from] Period start (default: a month back)
         * @param {string} [to] Period end (default: today)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        statementPdf: async (homeId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('statementPdf', 'homeId', homeId)
            const localVarPath = `/api/v1/billing/homes/{home_id}/statement/pdf`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {BillingPolicyUpdate} billingPolicyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCompanyPolicy: async (companyId: string, billingPolicyUpdate: BillingPolicyUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('updateCompanyPolicy', 'companyId', companyId)
            // verify required parameter 'billingPolicyUpdate' is not null or undefined
            assertParamExists('updateCompanyPolicy', 'billingPolicyUpdate', billingPolicyUpdate)
            const localVarPath = `/api/v1/billing/companies/{company_id}/policy`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(billingPolicyUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {BillingPolicyUpdate} billingPolicyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateHoaPolicy: async (hoaId: string, billingPolicyUpdate: BillingPolicyUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('updateHoaPolicy', 'hoaId', hoaId)
            // verify required parameter 'billingPolicyUpdate' is not null or undefined
            assertParamExists('updateHoaPolicy', 'billingPolicyUpdate', billingPolicyUpdate)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/policy`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(billingPolicyUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} ruleId Notice rule id
         * @param {NoticeRuleUpdate} noticeRuleUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateNoticeRule: async (ruleId: string, noticeRuleUpdate: NoticeRuleUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('updateNoticeRule', 'ruleId', ruleId)
            // verify required parameter 'noticeRuleUpdate' is not null or undefined
            assertParamExists('updateNoticeRule', 'noticeRuleUpdate', noticeRuleUpdate)
            const localVarPath = `/api/v1/billing/notice-rules/{rule_id}`
                .replace(`{${"rule_id"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(noticeRuleUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} overrideId Override id
         * @param {OverrideUpdate} overrideUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOverride: async (overrideId: string, overrideUpdate: OverrideUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('updateOverride', 'overrideId', overrideId)
            // verify required parameter 'overrideUpdate' is not null or undefined
            assertParamExists('updateOverride', 'overrideUpdate', overrideUpdate)
            const localVarPath = `/api/v1/billing/assessment-overrides/{override_id}`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(overrideUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} scheduleId Schedule id
         * @param {ScheduleUpdate} scheduleUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSchedule: async (scheduleId: string, scheduleUpdate: ScheduleUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'scheduleId' is not null or undefined
            assertParamExists('updateSchedule', 'scheduleId', scheduleId)
            // verify required parameter 'scheduleUpdate' is not null or undefined
            assertParamExists('updateSchedule', 'scheduleUpdate', scheduleUpdate)
            const localVarPath = `/api/v1/billing/assessment-schedules/{schedule_id}`
                .replace(`{${"schedule_id"}}`, encodeURIComponent(String(scheduleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(scheduleUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {boolean} [repair] Rewrite drifted rows
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyBalances: async (hoaId: string, repair?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('verifyBalances', 'hoaId', hoaId)
            const localVarPath = `/api/v1/billing/hoas/{hoa_id}/balances/verify`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (repair !== undefined) {
                localVarQueryParameter['repair'] = repair;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BillingHandlersApi - functional programming interface
 */
export const BillingHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BillingHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} caseId Case id
         * @param {CaseNoteRequest} caseNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addCaseNote(caseId: string, caseNoteRequest: CaseNoteRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CaseEventReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addCaseNote(caseId, caseNoteRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.addCaseNote']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} tenureId Tenure id
         * @param {TenureMemberCreate} tenureMemberCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addTenureMember(tenureId: string, tenureMemberCreate: TenureMemberCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TenureMemberReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addTenureMember(tenureId, tenureMemberCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.addTenureMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} caseId Case id
         * @param {CaseAdvanceRequest} caseAdvanceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async advanceCase(caseId: string, caseAdvanceRequest: CaseAdvanceRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CaseReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.advanceCase(caseId, caseAdvanceRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.advanceCase']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} [minDaysPastDue] Only homes at least this far overdue
         * @param {string} [minBalance] Only homes owing at least this much, as a decimal string
         * @param {AgingSort} [sort] days_past_due (default) or balance
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async agingReport(hoaId: string, minDaysPastDue?: number, minBalance?: string, sort?: AgingSort, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgingReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.agingReport(hoaId, minDaysPastDue, minBalance, sort, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.agingReport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {NsfFeeRequest} nsfFeeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assessNsfFee(paymentId: string, nsfFeeRequest: NsfFeeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assessNsfFee(paymentId, nsfFeeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.assessNsfFee']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {CloseTenureRequest} closeTenureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async closeTenure(homeId: string, closeTenureRequest: CloseTenureRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TenureCloseReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.closeTenure(homeId, closeTenureRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.closeTenure']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async copyHoaNoticeRules(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRuleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.copyHoaNoticeRules(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.copyHoaNoticeRules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async couponPdf(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.couponPdf(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.couponPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {NoticeRuleCreate} noticeRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createCompanyNoticeRule(companyId: string, noticeRuleCreate: NoticeRuleCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRuleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createCompanyNoticeRule(companyId, noticeRuleCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.createCompanyNoticeRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {NoticeRuleCreate} noticeRuleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createHoaNoticeRule(hoaId: string, noticeRuleCreate: NoticeRuleCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRuleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createHoaNoticeRule(hoaId, noticeRuleCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.createHoaNoticeRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {OverrideCreate} overrideCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createOverride(homeId: string, overrideCreate: OverrideCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OverrideReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createOverride(homeId, overrideCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.createOverride']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {ScheduleCreate} scheduleCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createSchedule(hoaId: string, scheduleCreate: ScheduleCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createSchedule(hoaId, scheduleCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.createSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} ruleId Notice rule id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteNoticeRule(ruleId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteNoticeRule(ruleId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.deleteNoticeRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} overrideId Override id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteOverride(overrideId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteOverride(overrideId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.deleteOverride']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} estoppelId Estoppel request id
         * @param {EstoppelDeliverRequest} estoppelDeliverRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deliverEstoppel(estoppelId: string, estoppelDeliverRequest: EstoppelDeliverRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EstoppelReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deliverEstoppel(estoppelId, estoppelDeliverRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.deliverEstoppel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} estoppelId Estoppel request id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async estoppelPdf(estoppelId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.estoppelPdf(estoppelId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.estoppelPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} scheduleId Schedule id
         * @param {GenerateRequest} generateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateRuns(scheduleId: string, generateRequest: GenerateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GenerateReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateRuns(scheduleId, generateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.generateRuns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompanyPolicy(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingPolicyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompanyPolicy(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getCompanyPolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCoupon(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CouponReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCoupon(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getCoupon']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoaPolicy(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingPolicyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoaPolicy(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getHoaPolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHomeBalance(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BalanceReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHomeBalance(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getHomeBalance']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {string} [from] Entries on or after this effective date
         * @param {string} [to] Entries on or before this effective date
         * @param {string} [entryType] Comma-separated entry types; omit for all
         * @param {string} [sourceType] assessment_run | payment | violation | manual | aging
         * @param {SortOrder} [order] asc (oldest first, the default) or desc
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHomeLedger(homeId: string, page?: number, perPage?: number, from?: string, to?: string, entryType?: string, sourceType?: string, order?: SortOrder, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LedgerPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHomeLedger(homeId, page, perPage, from, to, entryType, sourceType, order, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getHomeLedger']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} runId Run id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RunDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {string} [from] Period start (default: a month back)
         * @param {string} [to] Period end (default: today)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getStatement(homeId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingStatementReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getStatement(homeId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.getStatement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {EstoppelRequestCreate} estoppelRequestCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async issueEstoppel(homeId: string, estoppelRequestCreate: EstoppelRequestCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EstoppelReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.issueEstoppel(homeId, estoppelRequestCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.issueEstoppel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} caseId Case id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listCaseEvents(caseId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CaseEventList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listCaseEvents(caseId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listCaseEvents']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {boolean} [includeClosed] Include resolved cases
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listCases(hoaId: string, includeClosed?: boolean, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CaseList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listCases(hoaId, includeClosed, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listCases']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listCompanyNoticeRules(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRuleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listCompanyNoticeRules(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listCompanyNoticeRules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listEstoppel(homeId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EstoppelList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listEstoppel(homeId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listEstoppel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaNoticeRules(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRuleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaNoticeRules(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listHoaNoticeRules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {PaymentStatus} [status] unmatched | applied | overpaid | returned | voided | refunded
         * @param {PaymentMethod} [method] check | ach | e_check | card | cash | manual | other
         * @param {string} [from] Received on or after
         * @param {string} [to] Received on or before
         * @param {string} [batchId] One deposit batch
         * @param {string} [homeId] One home
         * @param {boolean} [unappliedOnly] Only payments still holding money
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHoaPayments(hoaId: string, status?: PaymentStatus, method?: PaymentMethod, from?: string, to?: string, batchId?: string, homeId?: string, unappliedOnly?: boolean, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHoaPayments(hoaId, status, method, from, to, batchId, homeId, unappliedOnly, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listHoaPayments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHomePayments(homeId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHomePayments(homeId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listHomePayments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOverrides(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OverrideList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOverrides(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listOverrides']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listRuns(hoaId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RunList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listRuns(hoaId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listRuns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSchedules(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ScheduleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSchedules(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listSchedules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} tenureId Tenure id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listTenureMembers(tenureId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TenureMemberList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listTenureMembers(tenureId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listTenureMembers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listTenures(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TenureList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listTenures(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listTenures']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listUnmatchedPayments(hoaId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listUnmatchedPayments(hoaId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.listUnmatchedPayments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {PaymentMatchRequest} paymentMatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async matchPayment(paymentId: string, paymentMatchRequest: PaymentMatchRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.matchPayment(paymentId, paymentMatchRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.matchPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {CaseOpenRequest} caseOpenRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async openCase(hoaId: string, caseOpenRequest: CaseOpenRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CaseReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.openCase(hoaId, caseOpenRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.openCase']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {OpenTenureRequest} openTenureRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async openTenure(homeId: string, openTenureRequest: OpenTenureRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TenureReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.openTenure(homeId, openTenureRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.openTenure']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {ChargeCreate} chargeCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postCharge(homeId: string, chargeCreate: ChargeCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LedgerEntryReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postCharge(homeId, chargeCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.postCharge']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {CreditCreate} creditCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postCredit(homeId: string, creditCreate: CreditCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LedgerEntryReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postCredit(homeId, creditCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.postCredit']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} runId Run id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RunReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.postRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {PaymentCreate} paymentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async recordPayment(hoaId: string, paymentCreate: PaymentCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.recordPayment(hoaId, paymentCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.recordPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {PaymentRefundRequest} paymentRefundRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async refundPayment(paymentId: string, paymentRefundRequest: PaymentRefundRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.refundPayment(paymentId, paymentRefundRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.refundPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} paymentId Payment id
         * @param {PaymentReturnRequest} paymentReturnRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async returnPayment(paymentId: string, paymentReturnRequest: PaymentReturnRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaymentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.returnPayment(paymentId, paymentReturnRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.returnPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} entryId Ledger entry id
         * @param {ReverseRequest} reverseRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reverseEntry(entryId: string, reverseRequest: ReverseRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LedgerEntryReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reverseEntry(entryId, reverseRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.reverseEntry']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} runId Run id
         * @param {ReverseRequest} reverseRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reverseRun(runId: string, reverseRequest: ReverseRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RunReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reverseRun(runId, reverseRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.reverseRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {ReviewCondition} [condition] Show only one condition; counts still cover all eight
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reviewQueue(hoaId: string, condition?: ReviewCondition, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReviewList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reviewQueue(hoaId, condition, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.reviewQueue']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async runAging(hoaId: string, runAsOfRequest: RunAsOfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgingRunReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.runAging(hoaId, runAsOfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.runAging']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async runInterest(hoaId: string, runAsOfRequest: RunAsOfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FeeRunReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.runInterest(hoaId, runAsOfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.runInterest']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async runLateFees(hoaId: string, runAsOfRequest: RunAsOfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FeeRunReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.runLateFees(hoaId, runAsOfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.runLateFees']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RunAsOfRequest} runAsOfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async runNotices(hoaId: string, runAsOfRequest: RunAsOfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRunReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.runNotices(hoaId, runAsOfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.runNotices']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} tenureId Tenure id
         * @param {HoldRequest} holdRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setTenureHold(tenureId: string, holdRequest: HoldRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TenureReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setTenureHold(tenureId, holdRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.setTenureHold']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home id
         * @param {string} [from] Period start (default: a month back)
         * @param {string} [to] Period end (default: today)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async statementPdf(homeId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.statementPdf(homeId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.statementPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} companyId Company id
         * @param {BillingPolicyUpdate} billingPolicyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCompanyPolicy(companyId: string, billingPolicyUpdate: BillingPolicyUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingPolicyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCompanyPolicy(companyId, billingPolicyUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.updateCompanyPolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {BillingPolicyUpdate} billingPolicyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateHoaPolicy(hoaId: string, billingPolicyUpdate: BillingPolicyUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingPolicyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateHoaPolicy(hoaId, billingPolicyUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.updateHoaPolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} ruleId Notice rule id
         * @param {NoticeRuleUpdate} noticeRuleUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateNoticeRule(ruleId: string, noticeRuleUpdate: NoticeRuleUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NoticeRuleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateNoticeRule(ruleId, noticeRuleUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.updateNoticeRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} overrideId Override id
         * @param {OverrideUpdate} overrideUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOverride(overrideId: string, overrideUpdate: OverrideUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OverrideReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOverride(overrideId, overrideUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.updateOverride']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} scheduleId Schedule id
         * @param {ScheduleUpdate} scheduleUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateSchedule(scheduleId: string, scheduleUpdate: ScheduleUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ScheduleReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateSchedule(scheduleId, scheduleUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.updateSchedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {boolean} [repair] Rewrite drifted rows
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verifyBalances(hoaId: string, repair?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BalanceVerifyReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verifyBalances(hoaId, repair, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BillingHandlersApi.verifyBalances']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BillingHandlersApi - factory interface
 */
export const BillingHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BillingHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {BillingHandlersApiAddCaseNoteRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addCaseNote(requestParameters: BillingHandlersApiAddCaseNoteRequest, options?: RawAxiosRequestConfig): AxiosPromise<CaseEventReturn> {
            return localVarFp.addCaseNote(requestParameters.caseId, requestParameters.caseNoteRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiAddTenureMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addTenureMember(requestParameters: BillingHandlersApiAddTenureMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<TenureMemberReturn> {
            return localVarFp.addTenureMember(requestParameters.tenureId, requestParameters.tenureMemberCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiAdvanceCaseRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        advanceCase(requestParameters: BillingHandlersApiAdvanceCaseRequest, options?: RawAxiosRequestConfig): AxiosPromise<CaseReturn> {
            return localVarFp.advanceCase(requestParameters.caseId, requestParameters.caseAdvanceRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiAgingReportRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        agingReport(requestParameters: BillingHandlersApiAgingReportRequest, options?: RawAxiosRequestConfig): AxiosPromise<AgingReport> {
            return localVarFp.agingReport(requestParameters.hoaId, requestParameters.minDaysPastDue, requestParameters.minBalance, requestParameters.sort, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiAssessNsfFeeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assessNsfFee(requestParameters: BillingHandlersApiAssessNsfFeeRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentReturn> {
            return localVarFp.assessNsfFee(requestParameters.paymentId, requestParameters.nsfFeeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCloseTenureRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        closeTenure(requestParameters: BillingHandlersApiCloseTenureRequest, options?: RawAxiosRequestConfig): AxiosPromise<TenureCloseReturn> {
            return localVarFp.closeTenure(requestParameters.homeId, requestParameters.closeTenureRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCopyHoaNoticeRulesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        copyHoaNoticeRules(requestParameters: BillingHandlersApiCopyHoaNoticeRulesRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRuleList> {
            return localVarFp.copyHoaNoticeRules(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCouponPdfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        couponPdf(requestParameters: BillingHandlersApiCouponPdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.couponPdf(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCreateCompanyNoticeRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCompanyNoticeRule(requestParameters: BillingHandlersApiCreateCompanyNoticeRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRuleReturn> {
            return localVarFp.createCompanyNoticeRule(requestParameters.companyId, requestParameters.noticeRuleCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCreateHoaNoticeRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createHoaNoticeRule(requestParameters: BillingHandlersApiCreateHoaNoticeRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRuleReturn> {
            return localVarFp.createHoaNoticeRule(requestParameters.hoaId, requestParameters.noticeRuleCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCreateOverrideRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOverride(requestParameters: BillingHandlersApiCreateOverrideRequest, options?: RawAxiosRequestConfig): AxiosPromise<OverrideReturn> {
            return localVarFp.createOverride(requestParameters.homeId, requestParameters.overrideCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiCreateScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createSchedule(requestParameters: BillingHandlersApiCreateScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ScheduleReturn> {
            return localVarFp.createSchedule(requestParameters.hoaId, requestParameters.scheduleCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiDeleteNoticeRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteNoticeRule(requestParameters: BillingHandlersApiDeleteNoticeRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteNoticeRule(requestParameters.ruleId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiDeleteOverrideRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteOverride(requestParameters: BillingHandlersApiDeleteOverrideRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteOverride(requestParameters.overrideId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiDeliverEstoppelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deliverEstoppel(requestParameters: BillingHandlersApiDeliverEstoppelRequest, options?: RawAxiosRequestConfig): AxiosPromise<EstoppelReturn> {
            return localVarFp.deliverEstoppel(requestParameters.estoppelId, requestParameters.estoppelDeliverRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiEstoppelPdfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        estoppelPdf(requestParameters: BillingHandlersApiEstoppelPdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.estoppelPdf(requestParameters.estoppelId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGenerateRunsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateRuns(requestParameters: BillingHandlersApiGenerateRunsRequest, options?: RawAxiosRequestConfig): AxiosPromise<GenerateReturn> {
            return localVarFp.generateRuns(requestParameters.scheduleId, requestParameters.generateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetCompanyPolicyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompanyPolicy(requestParameters: BillingHandlersApiGetCompanyPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingPolicyReturn> {
            return localVarFp.getCompanyPolicy(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetCouponRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCoupon(requestParameters: BillingHandlersApiGetCouponRequest, options?: RawAxiosRequestConfig): AxiosPromise<CouponReturn> {
            return localVarFp.getCoupon(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetHoaPolicyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoaPolicy(requestParameters: BillingHandlersApiGetHoaPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingPolicyReturn> {
            return localVarFp.getHoaPolicy(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetHomeBalanceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomeBalance(requestParameters: BillingHandlersApiGetHomeBalanceRequest, options?: RawAxiosRequestConfig): AxiosPromise<BalanceReturn> {
            return localVarFp.getHomeBalance(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetHomeLedgerRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomeLedger(requestParameters: BillingHandlersApiGetHomeLedgerRequest, options?: RawAxiosRequestConfig): AxiosPromise<LedgerPage> {
            return localVarFp.getHomeLedger(requestParameters.homeId, requestParameters.page, requestParameters.perPage, requestParameters.from, requestParameters.to, requestParameters.entryType, requestParameters.sourceType, requestParameters.order, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetRunRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRun(requestParameters: BillingHandlersApiGetRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<RunDetail> {
            return localVarFp.getRun(requestParameters.runId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiGetStatementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getStatement(requestParameters: BillingHandlersApiGetStatementRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingStatementReturn> {
            return localVarFp.getStatement(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiIssueEstoppelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        issueEstoppel(requestParameters: BillingHandlersApiIssueEstoppelRequest, options?: RawAxiosRequestConfig): AxiosPromise<EstoppelReturn> {
            return localVarFp.issueEstoppel(requestParameters.homeId, requestParameters.estoppelRequestCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListCaseEventsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCaseEvents(requestParameters: BillingHandlersApiListCaseEventsRequest, options?: RawAxiosRequestConfig): AxiosPromise<CaseEventList> {
            return localVarFp.listCaseEvents(requestParameters.caseId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListCasesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCases(requestParameters: BillingHandlersApiListCasesRequest, options?: RawAxiosRequestConfig): AxiosPromise<CaseList> {
            return localVarFp.listCases(requestParameters.hoaId, requestParameters.includeClosed, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListCompanyNoticeRulesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCompanyNoticeRules(requestParameters: BillingHandlersApiListCompanyNoticeRulesRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRuleList> {
            return localVarFp.listCompanyNoticeRules(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListEstoppelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listEstoppel(requestParameters: BillingHandlersApiListEstoppelRequest, options?: RawAxiosRequestConfig): AxiosPromise<EstoppelList> {
            return localVarFp.listEstoppel(requestParameters.homeId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListHoaNoticeRulesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaNoticeRules(requestParameters: BillingHandlersApiListHoaNoticeRulesRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRuleList> {
            return localVarFp.listHoaNoticeRules(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListHoaPaymentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHoaPayments(requestParameters: BillingHandlersApiListHoaPaymentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentList> {
            return localVarFp.listHoaPayments(requestParameters.hoaId, requestParameters.status, requestParameters.method, requestParameters.from, requestParameters.to, requestParameters.batchId, requestParameters.homeId, requestParameters.unappliedOnly, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListHomePaymentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHomePayments(requestParameters: BillingHandlersApiListHomePaymentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentList> {
            return localVarFp.listHomePayments(requestParameters.homeId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListOverridesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOverrides(requestParameters: BillingHandlersApiListOverridesRequest, options?: RawAxiosRequestConfig): AxiosPromise<OverrideList> {
            return localVarFp.listOverrides(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListRunsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listRuns(requestParameters: BillingHandlersApiListRunsRequest, options?: RawAxiosRequestConfig): AxiosPromise<RunList> {
            return localVarFp.listRuns(requestParameters.hoaId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListSchedulesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSchedules(requestParameters: BillingHandlersApiListSchedulesRequest, options?: RawAxiosRequestConfig): AxiosPromise<ScheduleList> {
            return localVarFp.listSchedules(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListTenureMembersRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTenureMembers(requestParameters: BillingHandlersApiListTenureMembersRequest, options?: RawAxiosRequestConfig): AxiosPromise<TenureMemberList> {
            return localVarFp.listTenureMembers(requestParameters.tenureId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListTenuresRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTenures(requestParameters: BillingHandlersApiListTenuresRequest, options?: RawAxiosRequestConfig): AxiosPromise<TenureList> {
            return localVarFp.listTenures(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiListUnmatchedPaymentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUnmatchedPayments(requestParameters: BillingHandlersApiListUnmatchedPaymentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentList> {
            return localVarFp.listUnmatchedPayments(requestParameters.hoaId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiMatchPaymentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        matchPayment(requestParameters: BillingHandlersApiMatchPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentReturn> {
            return localVarFp.matchPayment(requestParameters.paymentId, requestParameters.paymentMatchRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiOpenCaseRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        openCase(requestParameters: BillingHandlersApiOpenCaseRequest, options?: RawAxiosRequestConfig): AxiosPromise<CaseReturn> {
            return localVarFp.openCase(requestParameters.hoaId, requestParameters.caseOpenRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiOpenTenureRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        openTenure(requestParameters: BillingHandlersApiOpenTenureRequest, options?: RawAxiosRequestConfig): AxiosPromise<TenureReturn> {
            return localVarFp.openTenure(requestParameters.homeId, requestParameters.openTenureRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiPostChargeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postCharge(requestParameters: BillingHandlersApiPostChargeRequest, options?: RawAxiosRequestConfig): AxiosPromise<LedgerEntryReturn> {
            return localVarFp.postCharge(requestParameters.homeId, requestParameters.chargeCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiPostCreditRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postCredit(requestParameters: BillingHandlersApiPostCreditRequest, options?: RawAxiosRequestConfig): AxiosPromise<LedgerEntryReturn> {
            return localVarFp.postCredit(requestParameters.homeId, requestParameters.creditCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiPostRunRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postRun(requestParameters: BillingHandlersApiPostRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<RunReturn> {
            return localVarFp.postRun(requestParameters.runId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiRecordPaymentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordPayment(requestParameters: BillingHandlersApiRecordPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentReturn> {
            return localVarFp.recordPayment(requestParameters.hoaId, requestParameters.paymentCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiRefundPaymentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        refundPayment(requestParameters: BillingHandlersApiRefundPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentReturn> {
            return localVarFp.refundPayment(requestParameters.paymentId, requestParameters.paymentRefundRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiReturnPaymentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        returnPayment(requestParameters: BillingHandlersApiReturnPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<PaymentReturn> {
            return localVarFp.returnPayment(requestParameters.paymentId, requestParameters.paymentReturnRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiReverseEntryRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reverseEntry(requestParameters: BillingHandlersApiReverseEntryRequest, options?: RawAxiosRequestConfig): AxiosPromise<LedgerEntryReturn> {
            return localVarFp.reverseEntry(requestParameters.entryId, requestParameters.reverseRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiReverseRunRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reverseRun(requestParameters: BillingHandlersApiReverseRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<RunReturn> {
            return localVarFp.reverseRun(requestParameters.runId, requestParameters.reverseRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiReviewQueueRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reviewQueue(requestParameters: BillingHandlersApiReviewQueueRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReviewList> {
            return localVarFp.reviewQueue(requestParameters.hoaId, requestParameters.condition, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiRunAgingRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runAging(requestParameters: BillingHandlersApiRunAgingRequest, options?: RawAxiosRequestConfig): AxiosPromise<AgingRunReport> {
            return localVarFp.runAging(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiRunInterestRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runInterest(requestParameters: BillingHandlersApiRunInterestRequest, options?: RawAxiosRequestConfig): AxiosPromise<FeeRunReport> {
            return localVarFp.runInterest(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiRunLateFeesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runLateFees(requestParameters: BillingHandlersApiRunLateFeesRequest, options?: RawAxiosRequestConfig): AxiosPromise<FeeRunReport> {
            return localVarFp.runLateFees(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiRunNoticesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runNotices(requestParameters: BillingHandlersApiRunNoticesRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRunReport> {
            return localVarFp.runNotices(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiSetTenureHoldRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setTenureHold(requestParameters: BillingHandlersApiSetTenureHoldRequest, options?: RawAxiosRequestConfig): AxiosPromise<TenureReturn> {
            return localVarFp.setTenureHold(requestParameters.tenureId, requestParameters.holdRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiStatementPdfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        statementPdf(requestParameters: BillingHandlersApiStatementPdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.statementPdf(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiUpdateCompanyPolicyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCompanyPolicy(requestParameters: BillingHandlersApiUpdateCompanyPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingPolicyReturn> {
            return localVarFp.updateCompanyPolicy(requestParameters.companyId, requestParameters.billingPolicyUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiUpdateHoaPolicyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateHoaPolicy(requestParameters: BillingHandlersApiUpdateHoaPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingPolicyReturn> {
            return localVarFp.updateHoaPolicy(requestParameters.hoaId, requestParameters.billingPolicyUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiUpdateNoticeRuleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateNoticeRule(requestParameters: BillingHandlersApiUpdateNoticeRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<NoticeRuleReturn> {
            return localVarFp.updateNoticeRule(requestParameters.ruleId, requestParameters.noticeRuleUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiUpdateOverrideRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOverride(requestParameters: BillingHandlersApiUpdateOverrideRequest, options?: RawAxiosRequestConfig): AxiosPromise<OverrideReturn> {
            return localVarFp.updateOverride(requestParameters.overrideId, requestParameters.overrideUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiUpdateScheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSchedule(requestParameters: BillingHandlersApiUpdateScheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ScheduleReturn> {
            return localVarFp.updateSchedule(requestParameters.scheduleId, requestParameters.scheduleUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingHandlersApiVerifyBalancesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyBalances(requestParameters: BillingHandlersApiVerifyBalancesRequest, options?: RawAxiosRequestConfig): AxiosPromise<BalanceVerifyReport> {
            return localVarFp.verifyBalances(requestParameters.hoaId, requestParameters.repair, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for addCaseNote operation in BillingHandlersApi.
 */
export interface BillingHandlersApiAddCaseNoteRequest {
    /**
     * Case id
     */
    readonly caseId: string

    readonly caseNoteRequest: CaseNoteRequest
}

/**
 * Request parameters for addTenureMember operation in BillingHandlersApi.
 */
export interface BillingHandlersApiAddTenureMemberRequest {
    /**
     * Tenure id
     */
    readonly tenureId: string

    readonly tenureMemberCreate: TenureMemberCreate
}

/**
 * Request parameters for advanceCase operation in BillingHandlersApi.
 */
export interface BillingHandlersApiAdvanceCaseRequest {
    /**
     * Case id
     */
    readonly caseId: string

    readonly caseAdvanceRequest: CaseAdvanceRequest
}

/**
 * Request parameters for agingReport operation in BillingHandlersApi.
 */
export interface BillingHandlersApiAgingReportRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Only homes at least this far overdue
     */
    readonly minDaysPastDue?: number

    /**
     * Only homes owing at least this much, as a decimal string
     */
    readonly minBalance?: string

    /**
     * days_past_due (default) or balance
     */
    readonly sort?: AgingSort

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for assessNsfFee operation in BillingHandlersApi.
 */
export interface BillingHandlersApiAssessNsfFeeRequest {
    /**
     * Payment id
     */
    readonly paymentId: string

    readonly nsfFeeRequest: NsfFeeRequest
}

/**
 * Request parameters for closeTenure operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCloseTenureRequest {
    /**
     * Home id
     */
    readonly homeId: string

    readonly closeTenureRequest: CloseTenureRequest
}

/**
 * Request parameters for copyHoaNoticeRules operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCopyHoaNoticeRulesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for couponPdf operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCouponPdfRequest {
    /**
     * Home id
     */
    readonly homeId: string
}

/**
 * Request parameters for createCompanyNoticeRule operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCreateCompanyNoticeRuleRequest {
    /**
     * Company id
     */
    readonly companyId: string

    readonly noticeRuleCreate: NoticeRuleCreate
}

/**
 * Request parameters for createHoaNoticeRule operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCreateHoaNoticeRuleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly noticeRuleCreate: NoticeRuleCreate
}

/**
 * Request parameters for createOverride operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCreateOverrideRequest {
    /**
     * Home id
     */
    readonly homeId: string

    readonly overrideCreate: OverrideCreate
}

/**
 * Request parameters for createSchedule operation in BillingHandlersApi.
 */
export interface BillingHandlersApiCreateScheduleRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly scheduleCreate: ScheduleCreate
}

/**
 * Request parameters for deleteNoticeRule operation in BillingHandlersApi.
 */
export interface BillingHandlersApiDeleteNoticeRuleRequest {
    /**
     * Notice rule id
     */
    readonly ruleId: string
}

/**
 * Request parameters for deleteOverride operation in BillingHandlersApi.
 */
export interface BillingHandlersApiDeleteOverrideRequest {
    /**
     * Override id
     */
    readonly overrideId: string
}

/**
 * Request parameters for deliverEstoppel operation in BillingHandlersApi.
 */
export interface BillingHandlersApiDeliverEstoppelRequest {
    /**
     * Estoppel request id
     */
    readonly estoppelId: string

    readonly estoppelDeliverRequest: EstoppelDeliverRequest
}

/**
 * Request parameters for estoppelPdf operation in BillingHandlersApi.
 */
export interface BillingHandlersApiEstoppelPdfRequest {
    /**
     * Estoppel request id
     */
    readonly estoppelId: string
}

/**
 * Request parameters for generateRuns operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGenerateRunsRequest {
    /**
     * Schedule id
     */
    readonly scheduleId: string

    readonly generateRequest: GenerateRequest
}

/**
 * Request parameters for getCompanyPolicy operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetCompanyPolicyRequest {
    /**
     * Company id
     */
    readonly companyId: string
}

/**
 * Request parameters for getCoupon operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetCouponRequest {
    /**
     * Home id
     */
    readonly homeId: string
}

/**
 * Request parameters for getHoaPolicy operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetHoaPolicyRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for getHomeBalance operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetHomeBalanceRequest {
    /**
     * Home id
     */
    readonly homeId: string
}

/**
 * Request parameters for getHomeLedger operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetHomeLedgerRequest {
    /**
     * Home id
     */
    readonly homeId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number

    /**
     * Entries on or after this effective date
     */
    readonly from?: string

    /**
     * Entries on or before this effective date
     */
    readonly to?: string

    /**
     * Comma-separated entry types; omit for all
     */
    readonly entryType?: string

    /**
     * assessment_run | payment | violation | manual | aging
     */
    readonly sourceType?: string

    /**
     * asc (oldest first, the default) or desc
     */
    readonly order?: SortOrder
}

/**
 * Request parameters for getRun operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetRunRequest {
    /**
     * Run id
     */
    readonly runId: string
}

/**
 * Request parameters for getStatement operation in BillingHandlersApi.
 */
export interface BillingHandlersApiGetStatementRequest {
    /**
     * Home id
     */
    readonly homeId: string

    /**
     * Period start (default: a month back)
     */
    readonly from?: string

    /**
     * Period end (default: today)
     */
    readonly to?: string
}

/**
 * Request parameters for issueEstoppel operation in BillingHandlersApi.
 */
export interface BillingHandlersApiIssueEstoppelRequest {
    /**
     * Home id
     */
    readonly homeId: string

    readonly estoppelRequestCreate: EstoppelRequestCreate
}

/**
 * Request parameters for listCaseEvents operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListCaseEventsRequest {
    /**
     * Case id
     */
    readonly caseId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listCases operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListCasesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Include resolved cases
     */
    readonly includeClosed?: boolean

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listCompanyNoticeRules operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListCompanyNoticeRulesRequest {
    /**
     * Company id
     */
    readonly companyId: string
}

/**
 * Request parameters for listEstoppel operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListEstoppelRequest {
    /**
     * Home id
     */
    readonly homeId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listHoaNoticeRules operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListHoaNoticeRulesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listHoaPayments operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListHoaPaymentsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * unmatched | applied | overpaid | returned | voided | refunded
     */
    readonly status?: PaymentStatus

    /**
     * check | ach | e_check | card | cash | manual | other
     */
    readonly method?: PaymentMethod

    /**
     * Received on or after
     */
    readonly from?: string

    /**
     * Received on or before
     */
    readonly to?: string

    /**
     * One deposit batch
     */
    readonly batchId?: string

    /**
     * One home
     */
    readonly homeId?: string

    /**
     * Only payments still holding money
     */
    readonly unappliedOnly?: boolean

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listHomePayments operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListHomePaymentsRequest {
    /**
     * Home id
     */
    readonly homeId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listOverrides operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListOverridesRequest {
    /**
     * Home id
     */
    readonly homeId: string
}

/**
 * Request parameters for listRuns operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListRunsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listSchedules operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListSchedulesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listTenureMembers operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListTenureMembersRequest {
    /**
     * Tenure id
     */
    readonly tenureId: string
}

/**
 * Request parameters for listTenures operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListTenuresRequest {
    /**
     * Home id
     */
    readonly homeId: string
}

/**
 * Request parameters for listUnmatchedPayments operation in BillingHandlersApi.
 */
export interface BillingHandlersApiListUnmatchedPaymentsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for matchPayment operation in BillingHandlersApi.
 */
export interface BillingHandlersApiMatchPaymentRequest {
    /**
     * Payment id
     */
    readonly paymentId: string

    readonly paymentMatchRequest: PaymentMatchRequest
}

/**
 * Request parameters for openCase operation in BillingHandlersApi.
 */
export interface BillingHandlersApiOpenCaseRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly caseOpenRequest: CaseOpenRequest
}

/**
 * Request parameters for openTenure operation in BillingHandlersApi.
 */
export interface BillingHandlersApiOpenTenureRequest {
    /**
     * Home id
     */
    readonly homeId: string

    readonly openTenureRequest: OpenTenureRequest
}

/**
 * Request parameters for postCharge operation in BillingHandlersApi.
 */
export interface BillingHandlersApiPostChargeRequest {
    /**
     * Home id
     */
    readonly homeId: string

    readonly chargeCreate: ChargeCreate
}

/**
 * Request parameters for postCredit operation in BillingHandlersApi.
 */
export interface BillingHandlersApiPostCreditRequest {
    /**
     * Home id
     */
    readonly homeId: string

    readonly creditCreate: CreditCreate
}

/**
 * Request parameters for postRun operation in BillingHandlersApi.
 */
export interface BillingHandlersApiPostRunRequest {
    /**
     * Run id
     */
    readonly runId: string
}

/**
 * Request parameters for recordPayment operation in BillingHandlersApi.
 */
export interface BillingHandlersApiRecordPaymentRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly paymentCreate: PaymentCreate
}

/**
 * Request parameters for refundPayment operation in BillingHandlersApi.
 */
export interface BillingHandlersApiRefundPaymentRequest {
    /**
     * Payment id
     */
    readonly paymentId: string

    readonly paymentRefundRequest: PaymentRefundRequest
}

/**
 * Request parameters for returnPayment operation in BillingHandlersApi.
 */
export interface BillingHandlersApiReturnPaymentRequest {
    /**
     * Payment id
     */
    readonly paymentId: string

    readonly paymentReturnRequest: PaymentReturnRequest
}

/**
 * Request parameters for reverseEntry operation in BillingHandlersApi.
 */
export interface BillingHandlersApiReverseEntryRequest {
    /**
     * Ledger entry id
     */
    readonly entryId: string

    readonly reverseRequest: ReverseRequest
}

/**
 * Request parameters for reverseRun operation in BillingHandlersApi.
 */
export interface BillingHandlersApiReverseRunRequest {
    /**
     * Run id
     */
    readonly runId: string

    readonly reverseRequest: ReverseRequest
}

/**
 * Request parameters for reviewQueue operation in BillingHandlersApi.
 */
export interface BillingHandlersApiReviewQueueRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Show only one condition; counts still cover all eight
     */
    readonly condition?: ReviewCondition

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for runAging operation in BillingHandlersApi.
 */
export interface BillingHandlersApiRunAgingRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly runAsOfRequest: RunAsOfRequest
}

/**
 * Request parameters for runInterest operation in BillingHandlersApi.
 */
export interface BillingHandlersApiRunInterestRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly runAsOfRequest: RunAsOfRequest
}

/**
 * Request parameters for runLateFees operation in BillingHandlersApi.
 */
export interface BillingHandlersApiRunLateFeesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly runAsOfRequest: RunAsOfRequest
}

/**
 * Request parameters for runNotices operation in BillingHandlersApi.
 */
export interface BillingHandlersApiRunNoticesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly runAsOfRequest: RunAsOfRequest
}

/**
 * Request parameters for setTenureHold operation in BillingHandlersApi.
 */
export interface BillingHandlersApiSetTenureHoldRequest {
    /**
     * Tenure id
     */
    readonly tenureId: string

    readonly holdRequest: HoldRequest
}

/**
 * Request parameters for statementPdf operation in BillingHandlersApi.
 */
export interface BillingHandlersApiStatementPdfRequest {
    /**
     * Home id
     */
    readonly homeId: string

    /**
     * Period start (default: a month back)
     */
    readonly from?: string

    /**
     * Period end (default: today)
     */
    readonly to?: string
}

/**
 * Request parameters for updateCompanyPolicy operation in BillingHandlersApi.
 */
export interface BillingHandlersApiUpdateCompanyPolicyRequest {
    /**
     * Company id
     */
    readonly companyId: string

    readonly billingPolicyUpdate: BillingPolicyUpdate
}

/**
 * Request parameters for updateHoaPolicy operation in BillingHandlersApi.
 */
export interface BillingHandlersApiUpdateHoaPolicyRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly billingPolicyUpdate: BillingPolicyUpdate
}

/**
 * Request parameters for updateNoticeRule operation in BillingHandlersApi.
 */
export interface BillingHandlersApiUpdateNoticeRuleRequest {
    /**
     * Notice rule id
     */
    readonly ruleId: string

    readonly noticeRuleUpdate: NoticeRuleUpdate
}

/**
 * Request parameters for updateOverride operation in BillingHandlersApi.
 */
export interface BillingHandlersApiUpdateOverrideRequest {
    /**
     * Override id
     */
    readonly overrideId: string

    readonly overrideUpdate: OverrideUpdate
}

/**
 * Request parameters for updateSchedule operation in BillingHandlersApi.
 */
export interface BillingHandlersApiUpdateScheduleRequest {
    /**
     * Schedule id
     */
    readonly scheduleId: string

    readonly scheduleUpdate: ScheduleUpdate
}

/**
 * Request parameters for verifyBalances operation in BillingHandlersApi.
 */
export interface BillingHandlersApiVerifyBalancesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Rewrite drifted rows
     */
    readonly repair?: boolean
}

/**
 * BillingHandlersApi - object-oriented interface
 */
export class BillingHandlersApi extends BaseAPI {
    /**
     * 
     * @param {BillingHandlersApiAddCaseNoteRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addCaseNote(requestParameters: BillingHandlersApiAddCaseNoteRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).addCaseNote(requestParameters.caseId, requestParameters.caseNoteRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiAddTenureMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addTenureMember(requestParameters: BillingHandlersApiAddTenureMemberRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).addTenureMember(requestParameters.tenureId, requestParameters.tenureMemberCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiAdvanceCaseRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public advanceCase(requestParameters: BillingHandlersApiAdvanceCaseRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).advanceCase(requestParameters.caseId, requestParameters.caseAdvanceRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiAgingReportRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public agingReport(requestParameters: BillingHandlersApiAgingReportRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).agingReport(requestParameters.hoaId, requestParameters.minDaysPastDue, requestParameters.minBalance, requestParameters.sort, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiAssessNsfFeeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public assessNsfFee(requestParameters: BillingHandlersApiAssessNsfFeeRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).assessNsfFee(requestParameters.paymentId, requestParameters.nsfFeeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCloseTenureRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public closeTenure(requestParameters: BillingHandlersApiCloseTenureRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).closeTenure(requestParameters.homeId, requestParameters.closeTenureRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCopyHoaNoticeRulesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public copyHoaNoticeRules(requestParameters: BillingHandlersApiCopyHoaNoticeRulesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).copyHoaNoticeRules(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCouponPdfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public couponPdf(requestParameters: BillingHandlersApiCouponPdfRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).couponPdf(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCreateCompanyNoticeRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createCompanyNoticeRule(requestParameters: BillingHandlersApiCreateCompanyNoticeRuleRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).createCompanyNoticeRule(requestParameters.companyId, requestParameters.noticeRuleCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCreateHoaNoticeRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createHoaNoticeRule(requestParameters: BillingHandlersApiCreateHoaNoticeRuleRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).createHoaNoticeRule(requestParameters.hoaId, requestParameters.noticeRuleCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCreateOverrideRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createOverride(requestParameters: BillingHandlersApiCreateOverrideRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).createOverride(requestParameters.homeId, requestParameters.overrideCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiCreateScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createSchedule(requestParameters: BillingHandlersApiCreateScheduleRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).createSchedule(requestParameters.hoaId, requestParameters.scheduleCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiDeleteNoticeRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteNoticeRule(requestParameters: BillingHandlersApiDeleteNoticeRuleRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).deleteNoticeRule(requestParameters.ruleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiDeleteOverrideRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteOverride(requestParameters: BillingHandlersApiDeleteOverrideRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).deleteOverride(requestParameters.overrideId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiDeliverEstoppelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deliverEstoppel(requestParameters: BillingHandlersApiDeliverEstoppelRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).deliverEstoppel(requestParameters.estoppelId, requestParameters.estoppelDeliverRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiEstoppelPdfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public estoppelPdf(requestParameters: BillingHandlersApiEstoppelPdfRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).estoppelPdf(requestParameters.estoppelId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGenerateRunsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateRuns(requestParameters: BillingHandlersApiGenerateRunsRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).generateRuns(requestParameters.scheduleId, requestParameters.generateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetCompanyPolicyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompanyPolicy(requestParameters: BillingHandlersApiGetCompanyPolicyRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getCompanyPolicy(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetCouponRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCoupon(requestParameters: BillingHandlersApiGetCouponRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getCoupon(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetHoaPolicyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoaPolicy(requestParameters: BillingHandlersApiGetHoaPolicyRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getHoaPolicy(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetHomeBalanceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHomeBalance(requestParameters: BillingHandlersApiGetHomeBalanceRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getHomeBalance(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetHomeLedgerRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHomeLedger(requestParameters: BillingHandlersApiGetHomeLedgerRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getHomeLedger(requestParameters.homeId, requestParameters.page, requestParameters.perPage, requestParameters.from, requestParameters.to, requestParameters.entryType, requestParameters.sourceType, requestParameters.order, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetRunRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getRun(requestParameters: BillingHandlersApiGetRunRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getRun(requestParameters.runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiGetStatementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getStatement(requestParameters: BillingHandlersApiGetStatementRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).getStatement(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiIssueEstoppelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public issueEstoppel(requestParameters: BillingHandlersApiIssueEstoppelRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).issueEstoppel(requestParameters.homeId, requestParameters.estoppelRequestCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListCaseEventsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listCaseEvents(requestParameters: BillingHandlersApiListCaseEventsRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listCaseEvents(requestParameters.caseId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListCasesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listCases(requestParameters: BillingHandlersApiListCasesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listCases(requestParameters.hoaId, requestParameters.includeClosed, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListCompanyNoticeRulesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listCompanyNoticeRules(requestParameters: BillingHandlersApiListCompanyNoticeRulesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listCompanyNoticeRules(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListEstoppelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listEstoppel(requestParameters: BillingHandlersApiListEstoppelRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listEstoppel(requestParameters.homeId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListHoaNoticeRulesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaNoticeRules(requestParameters: BillingHandlersApiListHoaNoticeRulesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listHoaNoticeRules(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListHoaPaymentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHoaPayments(requestParameters: BillingHandlersApiListHoaPaymentsRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listHoaPayments(requestParameters.hoaId, requestParameters.status, requestParameters.method, requestParameters.from, requestParameters.to, requestParameters.batchId, requestParameters.homeId, requestParameters.unappliedOnly, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListHomePaymentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHomePayments(requestParameters: BillingHandlersApiListHomePaymentsRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listHomePayments(requestParameters.homeId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListOverridesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOverrides(requestParameters: BillingHandlersApiListOverridesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listOverrides(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListRunsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listRuns(requestParameters: BillingHandlersApiListRunsRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listRuns(requestParameters.hoaId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListSchedulesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSchedules(requestParameters: BillingHandlersApiListSchedulesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listSchedules(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListTenureMembersRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listTenureMembers(requestParameters: BillingHandlersApiListTenureMembersRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listTenureMembers(requestParameters.tenureId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListTenuresRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listTenures(requestParameters: BillingHandlersApiListTenuresRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listTenures(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiListUnmatchedPaymentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listUnmatchedPayments(requestParameters: BillingHandlersApiListUnmatchedPaymentsRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).listUnmatchedPayments(requestParameters.hoaId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiMatchPaymentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public matchPayment(requestParameters: BillingHandlersApiMatchPaymentRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).matchPayment(requestParameters.paymentId, requestParameters.paymentMatchRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiOpenCaseRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public openCase(requestParameters: BillingHandlersApiOpenCaseRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).openCase(requestParameters.hoaId, requestParameters.caseOpenRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiOpenTenureRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public openTenure(requestParameters: BillingHandlersApiOpenTenureRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).openTenure(requestParameters.homeId, requestParameters.openTenureRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiPostChargeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postCharge(requestParameters: BillingHandlersApiPostChargeRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).postCharge(requestParameters.homeId, requestParameters.chargeCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiPostCreditRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postCredit(requestParameters: BillingHandlersApiPostCreditRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).postCredit(requestParameters.homeId, requestParameters.creditCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiPostRunRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postRun(requestParameters: BillingHandlersApiPostRunRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).postRun(requestParameters.runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiRecordPaymentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public recordPayment(requestParameters: BillingHandlersApiRecordPaymentRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).recordPayment(requestParameters.hoaId, requestParameters.paymentCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiRefundPaymentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public refundPayment(requestParameters: BillingHandlersApiRefundPaymentRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).refundPayment(requestParameters.paymentId, requestParameters.paymentRefundRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiReturnPaymentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public returnPayment(requestParameters: BillingHandlersApiReturnPaymentRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).returnPayment(requestParameters.paymentId, requestParameters.paymentReturnRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiReverseEntryRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reverseEntry(requestParameters: BillingHandlersApiReverseEntryRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).reverseEntry(requestParameters.entryId, requestParameters.reverseRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiReverseRunRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reverseRun(requestParameters: BillingHandlersApiReverseRunRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).reverseRun(requestParameters.runId, requestParameters.reverseRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiReviewQueueRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reviewQueue(requestParameters: BillingHandlersApiReviewQueueRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).reviewQueue(requestParameters.hoaId, requestParameters.condition, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiRunAgingRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public runAging(requestParameters: BillingHandlersApiRunAgingRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).runAging(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiRunInterestRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public runInterest(requestParameters: BillingHandlersApiRunInterestRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).runInterest(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiRunLateFeesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public runLateFees(requestParameters: BillingHandlersApiRunLateFeesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).runLateFees(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiRunNoticesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public runNotices(requestParameters: BillingHandlersApiRunNoticesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).runNotices(requestParameters.hoaId, requestParameters.runAsOfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiSetTenureHoldRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setTenureHold(requestParameters: BillingHandlersApiSetTenureHoldRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).setTenureHold(requestParameters.tenureId, requestParameters.holdRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiStatementPdfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public statementPdf(requestParameters: BillingHandlersApiStatementPdfRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).statementPdf(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiUpdateCompanyPolicyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateCompanyPolicy(requestParameters: BillingHandlersApiUpdateCompanyPolicyRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).updateCompanyPolicy(requestParameters.companyId, requestParameters.billingPolicyUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiUpdateHoaPolicyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateHoaPolicy(requestParameters: BillingHandlersApiUpdateHoaPolicyRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).updateHoaPolicy(requestParameters.hoaId, requestParameters.billingPolicyUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiUpdateNoticeRuleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateNoticeRule(requestParameters: BillingHandlersApiUpdateNoticeRuleRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).updateNoticeRule(requestParameters.ruleId, requestParameters.noticeRuleUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiUpdateOverrideRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateOverride(requestParameters: BillingHandlersApiUpdateOverrideRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).updateOverride(requestParameters.overrideId, requestParameters.overrideUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiUpdateScheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateSchedule(requestParameters: BillingHandlersApiUpdateScheduleRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).updateSchedule(requestParameters.scheduleId, requestParameters.scheduleUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingHandlersApiVerifyBalancesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verifyBalances(requestParameters: BillingHandlersApiVerifyBalancesRequest, options?: RawAxiosRequestConfig) {
        return BillingHandlersApiFp(this.configuration).verifyBalances(requestParameters.hoaId, requestParameters.repair, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * CommentHandlersApi - axios parameter creator
 */
export const CommentHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {CommentCreate} commentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createComment: async (commentCreate: CommentCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentCreate' is not null or undefined
            assertParamExists('createComment', 'commentCreate', commentCreate)
            const localVarPath = `/api/v1/comments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(commentCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteComment: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteComment', 'id', id)
            const localVarPath = `/api/v1/comments/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {string} attachmentId Attachment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCommentAttachment: async (id: string, attachmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteCommentAttachment', 'id', id)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('deleteCommentAttachment', 'attachmentId', attachmentId)
            const localVarPath = `/api/v1/comments/{id}/attachments/{attachment_id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"attachment_id"}}`, encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentById: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getCommentById', 'id', id)
            const localVarPath = `/api/v1/comments/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentsByReservation: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('getCommentsByReservation', 'reservationId', reservationId)
            const localVarPath = `/api/v1/comments/reservation/{reservation_id}`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentsByViolation: async (violationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('getCommentsByViolation', 'violationId', violationId)
            const localVarPath = `/api/v1/comments/violation/{violation_id}`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} workOrderId Work order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentsByWorkOrder: async (workOrderId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workOrderId' is not null or undefined
            assertParamExists('getCommentsByWorkOrder', 'workOrderId', workOrderId)
            const localVarPath = `/api/v1/comments/work-order/{work_order_id}`
                .replace(`{${"work_order_id"}}`, encodeURIComponent(String(workOrderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {CommentUpdate} commentUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateComment: async (id: string, commentUpdate: CommentUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateComment', 'id', id)
            // verify required parameter 'commentUpdate' is not null or undefined
            assertParamExists('updateComment', 'commentUpdate', commentUpdate)
            const localVarPath = `/api/v1/comments/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(commentUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadCommentAttachment: async (id: string, file: File, description?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('uploadCommentAttachment', 'id', id)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadCommentAttachment', 'file', file)
            const localVarPath = `/api/v1/comments/{id}/attachments`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CommentHandlersApi - functional programming interface
 */
export const CommentHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CommentHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {CommentCreate} commentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createComment(commentCreate: CommentCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createComment(commentCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.createComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteComment(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteComment(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.deleteComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {string} attachmentId Attachment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCommentAttachment(id: string, attachmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCommentAttachment(id, attachmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.deleteCommentAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCommentById(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCommentById(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.getCommentById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCommentsByReservation(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCommentsByReservation(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.getCommentsByReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCommentsByViolation(violationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCommentsByViolation(violationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.getCommentsByViolation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} workOrderId Work order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCommentsByWorkOrder(workOrderId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCommentsByWorkOrder(workOrderId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.getCommentsByWorkOrder']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {CommentUpdate} commentUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateComment(id: string, commentUpdate: CommentUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateComment(id, commentUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.updateComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Comment ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadCommentAttachment(id: string, file: File, description?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadCommentAttachment(id, file, description, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CommentHandlersApi.uploadCommentAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * CommentHandlersApi - factory interface
 */
export const CommentHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CommentHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {CommentHandlersApiCreateCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createComment(requestParameters: CommentHandlersApiCreateCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentReturn> {
            return localVarFp.createComment(requestParameters.commentCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiDeleteCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteComment(requestParameters: CommentHandlersApiDeleteCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteComment(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiDeleteCommentAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCommentAttachment(requestParameters: CommentHandlersApiDeleteCommentAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteCommentAttachment(requestParameters.id, requestParameters.attachmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiGetCommentByIdRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentById(requestParameters: CommentHandlersApiGetCommentByIdRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentReturn> {
            return localVarFp.getCommentById(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiGetCommentsByReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentsByReservation(requestParameters: CommentHandlersApiGetCommentsByReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentList> {
            return localVarFp.getCommentsByReservation(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiGetCommentsByViolationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentsByViolation(requestParameters: CommentHandlersApiGetCommentsByViolationRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentList> {
            return localVarFp.getCommentsByViolation(requestParameters.violationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiGetCommentsByWorkOrderRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCommentsByWorkOrder(requestParameters: CommentHandlersApiGetCommentsByWorkOrderRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentList> {
            return localVarFp.getCommentsByWorkOrder(requestParameters.workOrderId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiUpdateCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateComment(requestParameters: CommentHandlersApiUpdateCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentReturn> {
            return localVarFp.updateComment(requestParameters.id, requestParameters.commentUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CommentHandlersApiUploadCommentAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadCommentAttachment(requestParameters: CommentHandlersApiUploadCommentAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentReturn> {
            return localVarFp.uploadCommentAttachment(requestParameters.id, requestParameters.file, requestParameters.description, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createComment operation in CommentHandlersApi.
 */
export interface CommentHandlersApiCreateCommentRequest {
    readonly commentCreate: CommentCreate
}

/**
 * Request parameters for deleteComment operation in CommentHandlersApi.
 */
export interface CommentHandlersApiDeleteCommentRequest {
    /**
     * Comment ID
     */
    readonly id: string
}

/**
 * Request parameters for deleteCommentAttachment operation in CommentHandlersApi.
 */
export interface CommentHandlersApiDeleteCommentAttachmentRequest {
    /**
     * Comment ID
     */
    readonly id: string

    /**
     * Attachment ID
     */
    readonly attachmentId: string
}

/**
 * Request parameters for getCommentById operation in CommentHandlersApi.
 */
export interface CommentHandlersApiGetCommentByIdRequest {
    /**
     * Comment ID
     */
    readonly id: string
}

/**
 * Request parameters for getCommentsByReservation operation in CommentHandlersApi.
 */
export interface CommentHandlersApiGetCommentsByReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for getCommentsByViolation operation in CommentHandlersApi.
 */
export interface CommentHandlersApiGetCommentsByViolationRequest {
    /**
     * Violation ID
     */
    readonly violationId: string
}

/**
 * Request parameters for getCommentsByWorkOrder operation in CommentHandlersApi.
 */
export interface CommentHandlersApiGetCommentsByWorkOrderRequest {
    /**
     * Work order ID
     */
    readonly workOrderId: string
}

/**
 * Request parameters for updateComment operation in CommentHandlersApi.
 */
export interface CommentHandlersApiUpdateCommentRequest {
    /**
     * Comment ID
     */
    readonly id: string

    readonly commentUpdate: CommentUpdate
}

/**
 * Request parameters for uploadCommentAttachment operation in CommentHandlersApi.
 */
export interface CommentHandlersApiUploadCommentAttachmentRequest {
    /**
     * Comment ID
     */
    readonly id: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    readonly description?: string | null
}

/**
 * CommentHandlersApi - object-oriented interface
 */
export class CommentHandlersApi extends BaseAPI {
    /**
     * 
     * @param {CommentHandlersApiCreateCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createComment(requestParameters: CommentHandlersApiCreateCommentRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).createComment(requestParameters.commentCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiDeleteCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteComment(requestParameters: CommentHandlersApiDeleteCommentRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).deleteComment(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiDeleteCommentAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteCommentAttachment(requestParameters: CommentHandlersApiDeleteCommentAttachmentRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).deleteCommentAttachment(requestParameters.id, requestParameters.attachmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiGetCommentByIdRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCommentById(requestParameters: CommentHandlersApiGetCommentByIdRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).getCommentById(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiGetCommentsByReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCommentsByReservation(requestParameters: CommentHandlersApiGetCommentsByReservationRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).getCommentsByReservation(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiGetCommentsByViolationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCommentsByViolation(requestParameters: CommentHandlersApiGetCommentsByViolationRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).getCommentsByViolation(requestParameters.violationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiGetCommentsByWorkOrderRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCommentsByWorkOrder(requestParameters: CommentHandlersApiGetCommentsByWorkOrderRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).getCommentsByWorkOrder(requestParameters.workOrderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiUpdateCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateComment(requestParameters: CommentHandlersApiUpdateCommentRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).updateComment(requestParameters.id, requestParameters.commentUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CommentHandlersApiUploadCommentAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadCommentAttachment(requestParameters: CommentHandlersApiUploadCommentAttachmentRequest, options?: RawAxiosRequestConfig) {
        return CommentHandlersApiFp(this.configuration).uploadCommentAttachment(requestParameters.id, requestParameters.file, requestParameters.description, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * CompanyHandlersApi - axios parameter creator
 */
export const CompanyHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {CompanyCreate} companyCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCompany: async (companyCreate: CompanyCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyCreate' is not null or undefined
            assertParamExists('createCompany', 'companyCreate', companyCreate)
            const localVarPath = `/api/v1/company/create`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(companyCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateCompany: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deactivateCompany', 'id', id)
            const localVarPath = `/api/v1/company/{id}/deactivate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllCompanies: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/company`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompany: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getCompany', 'id', id)
            const localVarPath = `/api/v1/company/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Company ID
         * @param {CompanyUpdate} companyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCompany: async (id: string, companyUpdate: CompanyUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateCompany', 'id', id)
            // verify required parameter 'companyUpdate' is not null or undefined
            assertParamExists('updateCompany', 'companyUpdate', companyUpdate)
            const localVarPath = `/api/v1/company/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(companyUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CompanyHandlersApi - functional programming interface
 */
export const CompanyHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CompanyHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {CompanyCreate} companyCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createCompany(companyCreate: CompanyCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createCompany(companyCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CompanyHandlersApi.createCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deactivateCompany(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deactivateCompany(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CompanyHandlersApi.deactivateCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllCompanies(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CompanyReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllCompanies(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CompanyHandlersApi.getAllCompanies']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCompany(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCompany(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CompanyHandlersApi.getCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Company ID
         * @param {CompanyUpdate} companyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCompany(id: string, companyUpdate: CompanyUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCompany(id, companyUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CompanyHandlersApi.updateCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * CompanyHandlersApi - factory interface
 */
export const CompanyHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CompanyHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {CompanyHandlersApiCreateCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCompany(requestParameters: CompanyHandlersApiCreateCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<CompanyReturn> {
            return localVarFp.createCompany(requestParameters.companyCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CompanyHandlersApiDeactivateCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateCompany(requestParameters: CompanyHandlersApiDeactivateCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<CompanyReturn> {
            return localVarFp.deactivateCompany(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllCompanies(options?: RawAxiosRequestConfig): AxiosPromise<Array<CompanyReturn>> {
            return localVarFp.getAllCompanies(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CompanyHandlersApiGetCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCompany(requestParameters: CompanyHandlersApiGetCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<CompanyReturn> {
            return localVarFp.getCompany(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {CompanyHandlersApiUpdateCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCompany(requestParameters: CompanyHandlersApiUpdateCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<CompanyReturn> {
            return localVarFp.updateCompany(requestParameters.id, requestParameters.companyUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createCompany operation in CompanyHandlersApi.
 */
export interface CompanyHandlersApiCreateCompanyRequest {
    readonly companyCreate: CompanyCreate
}

/**
 * Request parameters for deactivateCompany operation in CompanyHandlersApi.
 */
export interface CompanyHandlersApiDeactivateCompanyRequest {
    /**
     * Company ID
     */
    readonly id: string
}

/**
 * Request parameters for getCompany operation in CompanyHandlersApi.
 */
export interface CompanyHandlersApiGetCompanyRequest {
    /**
     * Company ID
     */
    readonly id: string
}

/**
 * Request parameters for updateCompany operation in CompanyHandlersApi.
 */
export interface CompanyHandlersApiUpdateCompanyRequest {
    /**
     * Company ID
     */
    readonly id: string

    readonly companyUpdate: CompanyUpdate
}

/**
 * CompanyHandlersApi - object-oriented interface
 */
export class CompanyHandlersApi extends BaseAPI {
    /**
     * 
     * @param {CompanyHandlersApiCreateCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createCompany(requestParameters: CompanyHandlersApiCreateCompanyRequest, options?: RawAxiosRequestConfig) {
        return CompanyHandlersApiFp(this.configuration).createCompany(requestParameters.companyCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CompanyHandlersApiDeactivateCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deactivateCompany(requestParameters: CompanyHandlersApiDeactivateCompanyRequest, options?: RawAxiosRequestConfig) {
        return CompanyHandlersApiFp(this.configuration).deactivateCompany(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllCompanies(options?: RawAxiosRequestConfig) {
        return CompanyHandlersApiFp(this.configuration).getAllCompanies(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CompanyHandlersApiGetCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCompany(requestParameters: CompanyHandlersApiGetCompanyRequest, options?: RawAxiosRequestConfig) {
        return CompanyHandlersApiFp(this.configuration).getCompany(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {CompanyHandlersApiUpdateCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateCompany(requestParameters: CompanyHandlersApiUpdateCompanyRequest, options?: RawAxiosRequestConfig) {
        return CompanyHandlersApiFp(this.configuration).updateCompany(requestParameters.id, requestParameters.companyUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FileHandlersApi - axios parameter creator
 */
export const FileHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadFile: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('downloadFile', 'id', id)
            const localVarPath = `/api/v1/files/{id}/download`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/octet-stream';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFileMetadata: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getFileMetadata', 'id', id)
            const localVarPath = `/api/v1/files/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        hardDeleteFile: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('hardDeleteFile', 'id', id)
            const localVarPath = `/api/v1/files/{id}/permanent`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {number} [page] Page number (default: 1)
         * @param {number} [perPage] Items per page (default: 20)
         * @param {string} [documentType] Filter by document type
         * @param {boolean} [includeDeleted] Include soft-deleted files
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listFilesByHoa: async (hoaId: string, page?: number, perPage?: number, documentType?: string, includeDeleted?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listFilesByHoa', 'hoaId', hoaId)
            const localVarPath = `/api/v1/files/hoa/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            if (documentType !== undefined) {
                localVarQueryParameter['document_type'] = documentType;
            }

            if (includeDeleted !== undefined) {
                localVarQueryParameter['include_deleted'] = includeDeleted;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        restoreFile: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('restoreFile', 'id', id)
            const localVarPath = `/api/v1/files/{id}/restore`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        serveFileInline: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('serveFileInline', 'id', id)
            const localVarPath = `/api/v1/files/{id}/inline`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        softDeleteFile: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('softDeleteFile', 'id', id)
            const localVarPath = `/api/v1/files/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id File ID
         * @param {FileUpdate} fileUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFile: async (id: string, fileUpdate: FileUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateFile', 'id', id)
            // verify required parameter 'fileUpdate' is not null or undefined
            assertParamExists('updateFile', 'fileUpdate', fileUpdate)
            const localVarPath = `/api/v1/files/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(fileUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID to upload to
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {string | null} [documentType] One of &#x60;DocumentType&#x60;; defaults to &#x60;other&#x60; when absent.
         * @param {boolean | null} [isPublic] Stored, but read by no authorization decision anywhere — see the File rows in docs/AUTHORIZATION_MATRIX.csv.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadFile: async (hoaId: string, file: File, description?: string | null, documentType?: string | null, isPublic?: boolean | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('uploadFile', 'hoaId', hoaId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadFile', 'file', file)
            const localVarPath = `/api/v1/files/hoa/{hoa_id}/upload`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (documentType !== undefined) { 
                localVarFormParams.append('document_type', documentType as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }

            if (isPublic !== undefined) { 
                localVarFormParams.append('is_public', String(isPublic) as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FileHandlersApi - functional programming interface
 */
export const FileHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FileHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadFile(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadFile(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.downloadFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFileMetadata(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFileMetadata(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.getFileMetadata']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async hardDeleteFile(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.hardDeleteFile(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.hardDeleteFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {number} [page] Page number (default: 1)
         * @param {number} [perPage] Items per page (default: 20)
         * @param {string} [documentType] Filter by document type
         * @param {boolean} [includeDeleted] Include soft-deleted files
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listFilesByHoa(hoaId: string, page?: number, perPage?: number, documentType?: string, includeDeleted?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FileList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listFilesByHoa(hoaId, page, perPage, documentType, includeDeleted, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.listFilesByHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async restoreFile(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.restoreFile(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.restoreFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async serveFileInline(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.serveFileInline(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.serveFileInline']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id File ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async softDeleteFile(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.softDeleteFile(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.softDeleteFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id File ID
         * @param {FileUpdate} fileUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateFile(id: string, fileUpdate: FileUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateFile(id, fileUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.updateFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID to upload to
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {string | null} [documentType] One of &#x60;DocumentType&#x60;; defaults to &#x60;other&#x60; when absent.
         * @param {boolean | null} [isPublic] Stored, but read by no authorization decision anywhere — see the File rows in docs/AUTHORIZATION_MATRIX.csv.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadFile(hoaId: string, file: File, description?: string | null, documentType?: string | null, isPublic?: boolean | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadFile(hoaId, file, description, documentType, isPublic, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FileHandlersApi.uploadFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FileHandlersApi - factory interface
 */
export const FileHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FileHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {FileHandlersApiDownloadFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadFile(requestParameters: FileHandlersApiDownloadFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.downloadFile(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiGetFileMetadataRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFileMetadata(requestParameters: FileHandlersApiGetFileMetadataRequest, options?: RawAxiosRequestConfig): AxiosPromise<FileReturn> {
            return localVarFp.getFileMetadata(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiHardDeleteFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        hardDeleteFile(requestParameters: FileHandlersApiHardDeleteFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.hardDeleteFile(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiListFilesByHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listFilesByHoa(requestParameters: FileHandlersApiListFilesByHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<FileList> {
            return localVarFp.listFilesByHoa(requestParameters.hoaId, requestParameters.page, requestParameters.perPage, requestParameters.documentType, requestParameters.includeDeleted, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiRestoreFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        restoreFile(requestParameters: FileHandlersApiRestoreFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<FileReturn> {
            return localVarFp.restoreFile(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiServeFileInlineRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        serveFileInline(requestParameters: FileHandlersApiServeFileInlineRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.serveFileInline(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiSoftDeleteFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        softDeleteFile(requestParameters: FileHandlersApiSoftDeleteFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<FileReturn> {
            return localVarFp.softDeleteFile(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiUpdateFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFile(requestParameters: FileHandlersApiUpdateFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<FileReturn> {
            return localVarFp.updateFile(requestParameters.id, requestParameters.fileUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {FileHandlersApiUploadFileRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadFile(requestParameters: FileHandlersApiUploadFileRequest, options?: RawAxiosRequestConfig): AxiosPromise<FileReturn> {
            return localVarFp.uploadFile(requestParameters.hoaId, requestParameters.file, requestParameters.description, requestParameters.documentType, requestParameters.isPublic, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for downloadFile operation in FileHandlersApi.
 */
export interface FileHandlersApiDownloadFileRequest {
    /**
     * File ID
     */
    readonly id: string
}

/**
 * Request parameters for getFileMetadata operation in FileHandlersApi.
 */
export interface FileHandlersApiGetFileMetadataRequest {
    /**
     * File ID
     */
    readonly id: string
}

/**
 * Request parameters for hardDeleteFile operation in FileHandlersApi.
 */
export interface FileHandlersApiHardDeleteFileRequest {
    /**
     * File ID
     */
    readonly id: string
}

/**
 * Request parameters for listFilesByHoa operation in FileHandlersApi.
 */
export interface FileHandlersApiListFilesByHoaRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string

    /**
     * Page number (default: 1)
     */
    readonly page?: number

    /**
     * Items per page (default: 20)
     */
    readonly perPage?: number

    /**
     * Filter by document type
     */
    readonly documentType?: string

    /**
     * Include soft-deleted files
     */
    readonly includeDeleted?: boolean
}

/**
 * Request parameters for restoreFile operation in FileHandlersApi.
 */
export interface FileHandlersApiRestoreFileRequest {
    /**
     * File ID
     */
    readonly id: string
}

/**
 * Request parameters for serveFileInline operation in FileHandlersApi.
 */
export interface FileHandlersApiServeFileInlineRequest {
    /**
     * File ID
     */
    readonly id: string
}

/**
 * Request parameters for softDeleteFile operation in FileHandlersApi.
 */
export interface FileHandlersApiSoftDeleteFileRequest {
    /**
     * File ID
     */
    readonly id: string
}

/**
 * Request parameters for updateFile operation in FileHandlersApi.
 */
export interface FileHandlersApiUpdateFileRequest {
    /**
     * File ID
     */
    readonly id: string

    readonly fileUpdate: FileUpdate
}

/**
 * Request parameters for uploadFile operation in FileHandlersApi.
 */
export interface FileHandlersApiUploadFileRequest {
    /**
     * HOA ID to upload to
     */
    readonly hoaId: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    readonly description?: string | null

    /**
     * One of &#x60;DocumentType&#x60;; defaults to &#x60;other&#x60; when absent.
     */
    readonly documentType?: string | null

    /**
     * Stored, but read by no authorization decision anywhere — see the File rows in docs/AUTHORIZATION_MATRIX.csv.
     */
    readonly isPublic?: boolean | null
}

/**
 * FileHandlersApi - object-oriented interface
 */
export class FileHandlersApi extends BaseAPI {
    /**
     * 
     * @param {FileHandlersApiDownloadFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadFile(requestParameters: FileHandlersApiDownloadFileRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).downloadFile(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiGetFileMetadataRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFileMetadata(requestParameters: FileHandlersApiGetFileMetadataRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).getFileMetadata(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiHardDeleteFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public hardDeleteFile(requestParameters: FileHandlersApiHardDeleteFileRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).hardDeleteFile(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiListFilesByHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listFilesByHoa(requestParameters: FileHandlersApiListFilesByHoaRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).listFilesByHoa(requestParameters.hoaId, requestParameters.page, requestParameters.perPage, requestParameters.documentType, requestParameters.includeDeleted, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiRestoreFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public restoreFile(requestParameters: FileHandlersApiRestoreFileRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).restoreFile(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiServeFileInlineRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public serveFileInline(requestParameters: FileHandlersApiServeFileInlineRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).serveFileInline(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiSoftDeleteFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public softDeleteFile(requestParameters: FileHandlersApiSoftDeleteFileRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).softDeleteFile(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiUpdateFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateFile(requestParameters: FileHandlersApiUpdateFileRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).updateFile(requestParameters.id, requestParameters.fileUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {FileHandlersApiUploadFileRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadFile(requestParameters: FileHandlersApiUploadFileRequest, options?: RawAxiosRequestConfig) {
        return FileHandlersApiFp(this.configuration).uploadFile(requestParameters.hoaId, requestParameters.file, requestParameters.description, requestParameters.documentType, requestParameters.isPublic, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * HoaHandlersApi - axios parameter creator
 */
export const HoaHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {HoaCreate} hoaCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createHoa: async (hoaCreate: HoaCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaCreate' is not null or undefined
            assertParamExists('createHoa', 'hoaCreate', hoaCreate)
            const localVarPath = `/api/v1/hoa/create`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(hoaCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateHoa: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deactivateHoa', 'id', id)
            const localVarPath = `/api/v1/hoa/{id}/deactivate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteHoa: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteHoa', 'id', id)
            const localVarPath = `/api/v1/hoa/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} companyId Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveHoasByCompany: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getActiveHoasByCompany', 'companyId', companyId)
            const localVarPath = `/api/v1/hoa/company/{company_id}/active`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllActiveHoas: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/hoa/active`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllHoas: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/hoa`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoa: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHoa', 'id', id)
            const localVarPath = `/api/v1/hoa/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} companyId Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoasByCompany: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getHoasByCompany', 'companyId', companyId)
            const localVarPath = `/api/v1/hoa/company/{company_id}`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {BoardImpersonationUpdate} boardImpersonationUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setBoardImpersonation: async (id: string, boardImpersonationUpdate: BoardImpersonationUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('setBoardImpersonation', 'id', id)
            // verify required parameter 'boardImpersonationUpdate' is not null or undefined
            assertParamExists('setBoardImpersonation', 'boardImpersonationUpdate', boardImpersonationUpdate)
            const localVarPath = `/api/v1/hoa/{id}/board-impersonation`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(boardImpersonationUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {HoaTimezoneUpdate} hoaTimezoneUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setHoaTimezone: async (id: string, hoaTimezoneUpdate: HoaTimezoneUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('setHoaTimezone', 'id', id)
            // verify required parameter 'hoaTimezoneUpdate' is not null or undefined
            assertParamExists('setHoaTimezone', 'hoaTimezoneUpdate', hoaTimezoneUpdate)
            const localVarPath = `/api/v1/hoa/{id}/timezone`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(hoaTimezoneUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {HoaUpdate} hoaUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateHoa: async (id: string, hoaUpdate: HoaUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateHoa', 'id', id)
            // verify required parameter 'hoaUpdate' is not null or undefined
            assertParamExists('updateHoa', 'hoaUpdate', hoaUpdate)
            const localVarPath = `/api/v1/hoa/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(hoaUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * HoaHandlersApi - functional programming interface
 */
export const HoaHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = HoaHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {HoaCreate} hoaCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createHoa(hoaCreate: HoaCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createHoa(hoaCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.createHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deactivateHoa(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deactivateHoa(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.deactivateHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteHoa(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteHoa(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.deleteHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} companyId Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getActiveHoasByCompany(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HoaReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getActiveHoasByCompany(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.getActiveHoasByCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllActiveHoas(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HoaReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllActiveHoas(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.getAllActiveHoas']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllHoas(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HoaReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllHoas(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.getAllHoas']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoa(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoa(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.getHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} companyId Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHoasByCompany(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HoaReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHoasByCompany(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.getHoasByCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {BoardImpersonationUpdate} boardImpersonationUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setBoardImpersonation(id: string, boardImpersonationUpdate: BoardImpersonationUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setBoardImpersonation(id, boardImpersonationUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.setBoardImpersonation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {HoaTimezoneUpdate} hoaTimezoneUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setHoaTimezone(id: string, hoaTimezoneUpdate: HoaTimezoneUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setHoaTimezone(id, hoaTimezoneUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.setHoaTimezone']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id HOA ID
         * @param {HoaUpdate} hoaUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateHoa(id: string, hoaUpdate: HoaUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HoaReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateHoa(id, hoaUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HoaHandlersApi.updateHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * HoaHandlersApi - factory interface
 */
export const HoaHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = HoaHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {HoaHandlersApiCreateHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createHoa(requestParameters: HoaHandlersApiCreateHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaReturn> {
            return localVarFp.createHoa(requestParameters.hoaCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HoaHandlersApiDeactivateHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateHoa(requestParameters: HoaHandlersApiDeactivateHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaReturn> {
            return localVarFp.deactivateHoa(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HoaHandlersApiDeleteHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteHoa(requestParameters: HoaHandlersApiDeleteHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteHoa(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {HoaHandlersApiGetActiveHoasByCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getActiveHoasByCompany(requestParameters: HoaHandlersApiGetActiveHoasByCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<HoaReturn>> {
            return localVarFp.getActiveHoasByCompany(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllActiveHoas(options?: RawAxiosRequestConfig): AxiosPromise<Array<HoaReturn>> {
            return localVarFp.getAllActiveHoas(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllHoas(options?: RawAxiosRequestConfig): AxiosPromise<Array<HoaReturn>> {
            return localVarFp.getAllHoas(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HoaHandlersApiGetHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoa(requestParameters: HoaHandlersApiGetHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaReturn> {
            return localVarFp.getHoa(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {HoaHandlersApiGetHoasByCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHoasByCompany(requestParameters: HoaHandlersApiGetHoasByCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<HoaReturn>> {
            return localVarFp.getHoasByCompany(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HoaHandlersApiSetBoardImpersonationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setBoardImpersonation(requestParameters: HoaHandlersApiSetBoardImpersonationRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaReturn> {
            return localVarFp.setBoardImpersonation(requestParameters.id, requestParameters.boardImpersonationUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HoaHandlersApiSetHoaTimezoneRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setHoaTimezone(requestParameters: HoaHandlersApiSetHoaTimezoneRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaReturn> {
            return localVarFp.setHoaTimezone(requestParameters.id, requestParameters.hoaTimezoneUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HoaHandlersApiUpdateHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateHoa(requestParameters: HoaHandlersApiUpdateHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<HoaReturn> {
            return localVarFp.updateHoa(requestParameters.id, requestParameters.hoaUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createHoa operation in HoaHandlersApi.
 */
export interface HoaHandlersApiCreateHoaRequest {
    readonly hoaCreate: HoaCreate
}

/**
 * Request parameters for deactivateHoa operation in HoaHandlersApi.
 */
export interface HoaHandlersApiDeactivateHoaRequest {
    /**
     * HOA ID
     */
    readonly id: string
}

/**
 * Request parameters for deleteHoa operation in HoaHandlersApi.
 */
export interface HoaHandlersApiDeleteHoaRequest {
    /**
     * HOA ID
     */
    readonly id: string
}

/**
 * Request parameters for getActiveHoasByCompany operation in HoaHandlersApi.
 */
export interface HoaHandlersApiGetActiveHoasByCompanyRequest {
    /**
     * Company ID
     */
    readonly companyId: string
}

/**
 * Request parameters for getHoa operation in HoaHandlersApi.
 */
export interface HoaHandlersApiGetHoaRequest {
    /**
     * HOA ID
     */
    readonly id: string
}

/**
 * Request parameters for getHoasByCompany operation in HoaHandlersApi.
 */
export interface HoaHandlersApiGetHoasByCompanyRequest {
    /**
     * Company ID
     */
    readonly companyId: string
}

/**
 * Request parameters for setBoardImpersonation operation in HoaHandlersApi.
 */
export interface HoaHandlersApiSetBoardImpersonationRequest {
    /**
     * HOA ID
     */
    readonly id: string

    readonly boardImpersonationUpdate: BoardImpersonationUpdate
}

/**
 * Request parameters for setHoaTimezone operation in HoaHandlersApi.
 */
export interface HoaHandlersApiSetHoaTimezoneRequest {
    /**
     * HOA ID
     */
    readonly id: string

    readonly hoaTimezoneUpdate: HoaTimezoneUpdate
}

/**
 * Request parameters for updateHoa operation in HoaHandlersApi.
 */
export interface HoaHandlersApiUpdateHoaRequest {
    /**
     * HOA ID
     */
    readonly id: string

    readonly hoaUpdate: HoaUpdate
}

/**
 * HoaHandlersApi - object-oriented interface
 */
export class HoaHandlersApi extends BaseAPI {
    /**
     * 
     * @param {HoaHandlersApiCreateHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createHoa(requestParameters: HoaHandlersApiCreateHoaRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).createHoa(requestParameters.hoaCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HoaHandlersApiDeactivateHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deactivateHoa(requestParameters: HoaHandlersApiDeactivateHoaRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).deactivateHoa(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HoaHandlersApiDeleteHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteHoa(requestParameters: HoaHandlersApiDeleteHoaRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).deleteHoa(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary ✅ CHANGED: Path parameter from i32 to String
     * @param {HoaHandlersApiGetActiveHoasByCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getActiveHoasByCompany(requestParameters: HoaHandlersApiGetActiveHoasByCompanyRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).getActiveHoasByCompany(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllActiveHoas(options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).getAllActiveHoas(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllHoas(options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).getAllHoas(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HoaHandlersApiGetHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoa(requestParameters: HoaHandlersApiGetHoaRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).getHoa(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary ✅ CHANGED: Path parameter from i32 to String
     * @param {HoaHandlersApiGetHoasByCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHoasByCompany(requestParameters: HoaHandlersApiGetHoasByCompanyRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).getHoasByCompany(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HoaHandlersApiSetBoardImpersonationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setBoardImpersonation(requestParameters: HoaHandlersApiSetBoardImpersonationRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).setBoardImpersonation(requestParameters.id, requestParameters.boardImpersonationUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HoaHandlersApiSetHoaTimezoneRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setHoaTimezone(requestParameters: HoaHandlersApiSetHoaTimezoneRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).setHoaTimezone(requestParameters.id, requestParameters.hoaTimezoneUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HoaHandlersApiUpdateHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateHoa(requestParameters: HoaHandlersApiUpdateHoaRequest, options?: RawAxiosRequestConfig) {
        return HoaHandlersApiFp(this.configuration).updateHoa(requestParameters.id, requestParameters.hoaUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * HomeHandlersApi - axios parameter creator
 */
export const HomeHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {HomeCreate} homeCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createHome: async (homeCreate: HomeCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeCreate' is not null or undefined
            assertParamExists('createHome', 'homeCreate', homeCreate)
            const localVarPath = `/api/v1/homes/create`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(homeCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteHome: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteHome', 'id', id)
            const localVarPath = `/api/v1/homes/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllHomes: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/homes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHome: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getHome', 'id', id)
            const localVarPath = `/api/v1/homes/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomesByHoa: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getHomesByHoa', 'hoaId', hoaId)
            const localVarPath = `/api/v1/homes/hoa/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Authorized with `can_view_member`: seeing which homes a member belongs to is part of seeing the member. Results are deliberately **not** narrowed to the caller\'s HOAs — a member mapped to homes in several HOAs shows all of them, so a manager sees the member\'s full footprint.
         * @summary Every home a member is mapped to.
         * @param {string} memberId Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomesByMember: async (memberId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberId' is not null or undefined
            assertParamExists('getHomesByMember', 'memberId', memberId)
            const localVarPath = `/api/v1/homes/member/{member_id}`
                .replace(`{${"member_id"}}`, encodeURIComponent(String(memberId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Home ID
         * @param {HomeUpdate} homeUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateHome: async (id: string, homeUpdate: HomeUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateHome', 'id', id)
            // verify required parameter 'homeUpdate' is not null or undefined
            assertParamExists('updateHome', 'homeUpdate', homeUpdate)
            const localVarPath = `/api/v1/homes/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(homeUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * HomeHandlersApi - functional programming interface
 */
export const HomeHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = HomeHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {HomeCreate} homeCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createHome(homeCreate: HomeCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HomeReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createHome(homeCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.createHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteHome(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteHome(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.deleteHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllHomes(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HomeReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllHomes(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.getAllHomes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHome(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HomeReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHome(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.getHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHomesByHoa(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HomeReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHomesByHoa(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.getHomesByHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Authorized with `can_view_member`: seeing which homes a member belongs to is part of seeing the member. Results are deliberately **not** narrowed to the caller\'s HOAs — a member mapped to homes in several HOAs shows all of them, so a manager sees the member\'s full footprint.
         * @summary Every home a member is mapped to.
         * @param {string} memberId Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHomesByMember(memberId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<HomeReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHomesByMember(memberId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.getHomesByMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Home ID
         * @param {HomeUpdate} homeUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateHome(id: string, homeUpdate: HomeUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HomeReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateHome(id, homeUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HomeHandlersApi.updateHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * HomeHandlersApi - factory interface
 */
export const HomeHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = HomeHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {HomeHandlersApiCreateHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createHome(requestParameters: HomeHandlersApiCreateHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<HomeReturn> {
            return localVarFp.createHome(requestParameters.homeCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HomeHandlersApiDeleteHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteHome(requestParameters: HomeHandlersApiDeleteHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteHome(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllHomes(options?: RawAxiosRequestConfig): AxiosPromise<Array<HomeReturn>> {
            return localVarFp.getAllHomes(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HomeHandlersApiGetHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHome(requestParameters: HomeHandlersApiGetHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<HomeReturn> {
            return localVarFp.getHome(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HomeHandlersApiGetHomesByHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomesByHoa(requestParameters: HomeHandlersApiGetHomesByHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<HomeReturn>> {
            return localVarFp.getHomesByHoa(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * Authorized with `can_view_member`: seeing which homes a member belongs to is part of seeing the member. Results are deliberately **not** narrowed to the caller\'s HOAs — a member mapped to homes in several HOAs shows all of them, so a manager sees the member\'s full footprint.
         * @summary Every home a member is mapped to.
         * @param {HomeHandlersApiGetHomesByMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHomesByMember(requestParameters: HomeHandlersApiGetHomesByMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<HomeReturn>> {
            return localVarFp.getHomesByMember(requestParameters.memberId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {HomeHandlersApiUpdateHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateHome(requestParameters: HomeHandlersApiUpdateHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<HomeReturn> {
            return localVarFp.updateHome(requestParameters.id, requestParameters.homeUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createHome operation in HomeHandlersApi.
 */
export interface HomeHandlersApiCreateHomeRequest {
    readonly homeCreate: HomeCreate
}

/**
 * Request parameters for deleteHome operation in HomeHandlersApi.
 */
export interface HomeHandlersApiDeleteHomeRequest {
    /**
     * Home ID
     */
    readonly id: string
}

/**
 * Request parameters for getHome operation in HomeHandlersApi.
 */
export interface HomeHandlersApiGetHomeRequest {
    /**
     * Home ID
     */
    readonly id: string
}

/**
 * Request parameters for getHomesByHoa operation in HomeHandlersApi.
 */
export interface HomeHandlersApiGetHomesByHoaRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string
}

/**
 * Request parameters for getHomesByMember operation in HomeHandlersApi.
 */
export interface HomeHandlersApiGetHomesByMemberRequest {
    /**
     * Member ID
     */
    readonly memberId: string
}

/**
 * Request parameters for updateHome operation in HomeHandlersApi.
 */
export interface HomeHandlersApiUpdateHomeRequest {
    /**
     * Home ID
     */
    readonly id: string

    readonly homeUpdate: HomeUpdate
}

/**
 * HomeHandlersApi - object-oriented interface
 */
export class HomeHandlersApi extends BaseAPI {
    /**
     * 
     * @param {HomeHandlersApiCreateHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createHome(requestParameters: HomeHandlersApiCreateHomeRequest, options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).createHome(requestParameters.homeCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HomeHandlersApiDeleteHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteHome(requestParameters: HomeHandlersApiDeleteHomeRequest, options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).deleteHome(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllHomes(options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).getAllHomes(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HomeHandlersApiGetHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHome(requestParameters: HomeHandlersApiGetHomeRequest, options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).getHome(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HomeHandlersApiGetHomesByHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHomesByHoa(requestParameters: HomeHandlersApiGetHomesByHoaRequest, options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).getHomesByHoa(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Authorized with `can_view_member`: seeing which homes a member belongs to is part of seeing the member. Results are deliberately **not** narrowed to the caller\'s HOAs — a member mapped to homes in several HOAs shows all of them, so a manager sees the member\'s full footprint.
     * @summary Every home a member is mapped to.
     * @param {HomeHandlersApiGetHomesByMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHomesByMember(requestParameters: HomeHandlersApiGetHomesByMemberRequest, options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).getHomesByMember(requestParameters.memberId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {HomeHandlersApiUpdateHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateHome(requestParameters: HomeHandlersApiUpdateHomeRequest, options?: RawAxiosRequestConfig) {
        return HomeHandlersApiFp(this.configuration).updateHome(requestParameters.id, requestParameters.homeUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * JobsApi - axios parameter creator
 */
export const JobsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary One run and every association it touched. **SuperAdmin only.**
         * @param {string} runId Job run id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getJobRun: async (runId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'runId' is not null or undefined
            assertParamExists('getJobRun', 'runId', runId)
            const localVarPath = `/api/v1/jobs/runs/{run_id}`
                .replace(`{${"run_id"}}`, encodeURIComponent(String(runId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * **Owner/Admin of that association\'s company (or SuperAdmin)** — the same bar as submitting its payer file, because this is where you find out why a file did not go out.
         * @summary One association\'s scheduled-job history, newest first.
         * @param {string} hoaId HOA id
         * @param {number} [limit] Newest first; default 25, max 200
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        hoaHistory: async (hoaId: string, limit?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('hoaHistory', 'hoaId', hoaId)
            const localVarPath = `/api/v1/jobs/hoas/{hoa_id}/history`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Recent job runs, newest first. **SuperAdmin only** — a run spans every company on the platform.
         * @param {string | null} [job] Restrict to one job, e.g. &#x60;payer-file-upload&#x60;.
         * @param {number | null} [limit] Newest first; defaults to 25, capped at 200.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listJobRuns: async (job?: string | null, limit?: number | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/jobs/runs`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (job !== undefined) {
                localVarQueryParameter['job'] = job;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns **202** as soon as the run is claimed and does the work in the background. A sweep makes bank calls and retries; holding an HTTP request open for that is the same mistake as a cron that curls an endpoint, which is why this platform does not have one.  The claim is synchronous, so a collision with a scheduled firing is a **409** rather than a surprise later.
         * @summary Run a job now. **SuperAdmin only.**
         * @param {string} job e.g. payer-file-upload
         * @param {ManualRunRequest} manualRunRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        triggerRun: async (job: string, manualRunRequest: ManualRunRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'job' is not null or undefined
            assertParamExists('triggerRun', 'job', job)
            // verify required parameter 'manualRunRequest' is not null or undefined
            assertParamExists('triggerRun', 'manualRunRequest', manualRunRequest)
            const localVarPath = `/api/v1/jobs/{job}/run`
                .replace(`{${"job"}}`, encodeURIComponent(String(job)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(manualRunRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * JobsApi - functional programming interface
 */
export const JobsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = JobsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary One run and every association it touched. **SuperAdmin only.**
         * @param {string} runId Job run id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getJobRun(runId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<JobRunDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getJobRun(runId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JobsApi.getJobRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * **Owner/Admin of that association\'s company (or SuperAdmin)** — the same bar as submitting its payer file, because this is where you find out why a file did not go out.
         * @summary One association\'s scheduled-job history, newest first.
         * @param {string} hoaId HOA id
         * @param {number} [limit] Newest first; default 25, max 200
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async hoaHistory(hoaId: string, limit?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<JobRunTargetList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.hoaHistory(hoaId, limit, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JobsApi.hoaHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Recent job runs, newest first. **SuperAdmin only** — a run spans every company on the platform.
         * @param {string | null} [job] Restrict to one job, e.g. &#x60;payer-file-upload&#x60;.
         * @param {number | null} [limit] Newest first; defaults to 25, capped at 200.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listJobRuns(job?: string | null, limit?: number | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<JobRunList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listJobRuns(job, limit, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JobsApi.listJobRuns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns **202** as soon as the run is claimed and does the work in the background. A sweep makes bank calls and retries; holding an HTTP request open for that is the same mistake as a cron that curls an endpoint, which is why this platform does not have one.  The claim is synchronous, so a collision with a scheduled firing is a **409** rather than a surprise later.
         * @summary Run a job now. **SuperAdmin only.**
         * @param {string} job e.g. payer-file-upload
         * @param {ManualRunRequest} manualRunRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async triggerRun(job: string, manualRunRequest: ManualRunRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ManualRunAccepted>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.triggerRun(job, manualRunRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['JobsApi.triggerRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * JobsApi - factory interface
 */
export const JobsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = JobsApiFp(configuration)
    return {
        /**
         * 
         * @summary One run and every association it touched. **SuperAdmin only.**
         * @param {JobsApiGetJobRunRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getJobRun(requestParameters: JobsApiGetJobRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<JobRunDetail> {
            return localVarFp.getJobRun(requestParameters.runId, options).then((request) => request(axios, basePath));
        },
        /**
         * **Owner/Admin of that association\'s company (or SuperAdmin)** — the same bar as submitting its payer file, because this is where you find out why a file did not go out.
         * @summary One association\'s scheduled-job history, newest first.
         * @param {JobsApiHoaHistoryRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        hoaHistory(requestParameters: JobsApiHoaHistoryRequest, options?: RawAxiosRequestConfig): AxiosPromise<JobRunTargetList> {
            return localVarFp.hoaHistory(requestParameters.hoaId, requestParameters.limit, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Recent job runs, newest first. **SuperAdmin only** — a run spans every company on the platform.
         * @param {JobsApiListJobRunsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listJobRuns(requestParameters: JobsApiListJobRunsRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<JobRunList> {
            return localVarFp.listJobRuns(requestParameters.job, requestParameters.limit, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns **202** as soon as the run is claimed and does the work in the background. A sweep makes bank calls and retries; holding an HTTP request open for that is the same mistake as a cron that curls an endpoint, which is why this platform does not have one.  The claim is synchronous, so a collision with a scheduled firing is a **409** rather than a surprise later.
         * @summary Run a job now. **SuperAdmin only.**
         * @param {JobsApiTriggerRunRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        triggerRun(requestParameters: JobsApiTriggerRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<ManualRunAccepted> {
            return localVarFp.triggerRun(requestParameters.job, requestParameters.manualRunRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for getJobRun operation in JobsApi.
 */
export interface JobsApiGetJobRunRequest {
    /**
     * Job run id
     */
    readonly runId: string
}

/**
 * Request parameters for hoaHistory operation in JobsApi.
 */
export interface JobsApiHoaHistoryRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Newest first; default 25, max 200
     */
    readonly limit?: number
}

/**
 * Request parameters for listJobRuns operation in JobsApi.
 */
export interface JobsApiListJobRunsRequest {
    /**
     * Restrict to one job, e.g. &#x60;payer-file-upload&#x60;.
     */
    readonly job?: string | null

    /**
     * Newest first; defaults to 25, capped at 200.
     */
    readonly limit?: number | null
}

/**
 * Request parameters for triggerRun operation in JobsApi.
 */
export interface JobsApiTriggerRunRequest {
    /**
     * e.g. payer-file-upload
     */
    readonly job: string

    readonly manualRunRequest: ManualRunRequest
}

/**
 * JobsApi - object-oriented interface
 */
export class JobsApi extends BaseAPI {
    /**
     * 
     * @summary One run and every association it touched. **SuperAdmin only.**
     * @param {JobsApiGetJobRunRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getJobRun(requestParameters: JobsApiGetJobRunRequest, options?: RawAxiosRequestConfig) {
        return JobsApiFp(this.configuration).getJobRun(requestParameters.runId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * **Owner/Admin of that association\'s company (or SuperAdmin)** — the same bar as submitting its payer file, because this is where you find out why a file did not go out.
     * @summary One association\'s scheduled-job history, newest first.
     * @param {JobsApiHoaHistoryRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public hoaHistory(requestParameters: JobsApiHoaHistoryRequest, options?: RawAxiosRequestConfig) {
        return JobsApiFp(this.configuration).hoaHistory(requestParameters.hoaId, requestParameters.limit, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Recent job runs, newest first. **SuperAdmin only** — a run spans every company on the platform.
     * @param {JobsApiListJobRunsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listJobRuns(requestParameters: JobsApiListJobRunsRequest = {}, options?: RawAxiosRequestConfig) {
        return JobsApiFp(this.configuration).listJobRuns(requestParameters.job, requestParameters.limit, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns **202** as soon as the run is claimed and does the work in the background. A sweep makes bank calls and retries; holding an HTTP request open for that is the same mistake as a cron that curls an endpoint, which is why this platform does not have one.  The claim is synchronous, so a collision with a scheduled firing is a **409** rather than a surprise later.
     * @summary Run a job now. **SuperAdmin only.**
     * @param {JobsApiTriggerRunRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public triggerRun(requestParameters: JobsApiTriggerRunRequest, options?: RawAxiosRequestConfig) {
        return JobsApiFp(this.configuration).triggerRun(requestParameters.job, requestParameters.manualRunRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * LedgerHandlersApi - axios parameter creator
 */
export const LedgerHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Point a reconciliation at the institution\'s own statement, or clear the pointer. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {StatementAttach} statementAttach 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        attachReconciliationStatement: async (hoaId: string, reconciliationId: string, statementAttach: StatementAttach, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('attachReconciliationStatement', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('attachReconciliationStatement', 'reconciliationId', reconciliationId)
            // verify required parameter 'statementAttach' is not null or undefined
            assertParamExists('attachReconciliationStatement', 'statementAttach', statementAttach)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}/statement`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(statementAttach, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeReconciliation: async (hoaId: string, reconciliationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('completeReconciliation', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('completeReconciliation', 'reconciliationId', reconciliationId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}/complete`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {GlAccountCreate} glAccountCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAccount: async (hoaId: string, glAccountCreate: GlAccountCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('createAccount', 'hoaId', hoaId)
            // verify required parameter 'glAccountCreate' is not null or undefined
            assertParamExists('createAccount', 'glAccountCreate', glAccountCreate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/accounts`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(glAccountCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {FundCreate} fundCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createFund: async (hoaId: string, fundCreate: FundCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('createFund', 'hoaId', hoaId)
            // verify required parameter 'fundCreate' is not null or undefined
            assertParamExists('createFund', 'fundCreate', fundCreate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/funds`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(fundCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {PeriodGenerateRequest} periodGenerateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generatePeriods: async (hoaId: string, periodGenerateRequest: PeriodGenerateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('generatePeriods', 'hoaId', hoaId)
            // verify required parameter 'periodGenerateRequest' is not null or undefined
            assertParamExists('generatePeriods', 'periodGenerateRequest', periodGenerateRequest)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/periods`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(periodGenerateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} asOf Required. The date the sheet is struck at
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBalanceSheet: async (hoaId: string, asOf: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getBalanceSheet', 'hoaId', hoaId)
            // verify required parameter 'asOf' is not null or undefined
            assertParamExists('getBalanceSheet', 'asOf', asOf)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/balance-sheet`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (asOf !== undefined) {
                localVarQueryParameter['as_of'] = asOf;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} fiscalYear The year the fiscal year starts in
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBudget: async (hoaId: string, fiscalYear: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getBudget', 'hoaId', hoaId)
            // verify required parameter 'fiscalYear' is not null or undefined
            assertParamExists('getBudget', 'fiscalYear', fiscalYear)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/fiscal-years/{fiscal_year}/budget`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"fiscal_year"}}`, encodeURIComponent(String(fiscalYear)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary The period\'s financial reports as one PDF.
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFinancialReports: async (hoaId: string, periodId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getFinancialReports', 'hoaId', hoaId)
            // verify required parameter 'periodId' is not null or undefined
            assertParamExists('getFinancialReports', 'periodId', periodId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/periods/{period_id}/financial-reports`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"period_id"}}`, encodeURIComponent(String(periodId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getIncomeStatement: async (hoaId: string, periodId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getIncomeStatement', 'hoaId', hoaId)
            // verify required parameter 'periodId' is not null or undefined
            assertParamExists('getIncomeStatement', 'periodId', periodId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/periods/{period_id}/income-statement`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"period_id"}}`, encodeURIComponent(String(periodId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOpeningBalances: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getOpeningBalances', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/opening-balances`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReconciliation: async (hoaId: string, reconciliationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getReconciliation', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('getReconciliation', 'reconciliationId', reconciliationId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSettings: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getSettings', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/settings`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary A closing balance proposed for a statement, with its derivation. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} glAccountId The cash account being reconciled
         * @param {string} statementDate Statement closing date (YYYY-MM-DD)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSuggestedBalance: async (hoaId: string, glAccountId: string, statementDate: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getSuggestedBalance', 'hoaId', hoaId)
            // verify required parameter 'glAccountId' is not null or undefined
            assertParamExists('getSuggestedBalance', 'glAccountId', glAccountId)
            // verify required parameter 'statementDate' is not null or undefined
            assertParamExists('getSuggestedBalance', 'statementDate', statementDate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/suggested-balance`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (glAccountId !== undefined) {
                localVarQueryParameter['gl_account_id'] = glAccountId;
            }

            if (statementDate !== undefined) {
                localVarQueryParameter['statement_date'] = statementDate;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTieOut: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getTieOut', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/tie-out`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTrialBalance: async (hoaId: string, periodId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getTrialBalance', 'hoaId', hoaId)
            // verify required parameter 'periodId' is not null or undefined
            assertParamExists('getTrialBalance', 'periodId', periodId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/periods/{period_id}/trial-balance`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"period_id"}}`, encodeURIComponent(String(periodId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAccounts: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listAccounts', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/accounts`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listFunds: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listFunds', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/funds`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPeriods: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listPeriods', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/periods`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} [from] Inclusive earliest statement date (YYYY-MM-DD)
         * @param {string} [to] Inclusive latest statement date (YYYY-MM-DD)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReconciliations: async (hoaId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listReconciliations', 'hoaId', hoaId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary The institution\'s own statements near this reconciliation\'s statement date, to attach one from. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listStatementCandidates: async (hoaId: string, reconciliationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listStatementCandidates', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('listStatementCandidates', 'reconciliationId', reconciliationId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}/statement-candidates`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {ReconciliationMatch} reconciliationMatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        matchReconciliationItem: async (hoaId: string, reconciliationId: string, reconciliationMatch: ReconciliationMatch, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('matchReconciliationItem', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('matchReconciliationItem', 'reconciliationId', reconciliationId)
            // verify required parameter 'reconciliationMatch' is not null or undefined
            assertParamExists('matchReconciliationItem', 'reconciliationMatch', reconciliationMatch)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}/matches`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reconciliationMatch, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {ReconciliationOpen} reconciliationOpen 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        openReconciliation: async (hoaId: string, reconciliationOpen: ReconciliationOpen, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('openReconciliation', 'hoaId', hoaId)
            // verify required parameter 'reconciliationOpen' is not null or undefined
            assertParamExists('openReconciliation', 'reconciliationOpen', reconciliationOpen)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reconciliationOpen, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Post an unexplained bank item the institution originated, and clear it against the transaction that caused it. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {BankItemPost} bankItemPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postBankItem: async (hoaId: string, reconciliationId: string, bankItemPost: BankItemPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('postBankItem', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('postBankItem', 'reconciliationId', reconciliationId)
            // verify required parameter 'bankItemPost' is not null or undefined
            assertParamExists('postBankItem', 'bankItemPost', bankItemPost)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}/bank-items`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bankItemPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {OpeningBalancesPost} openingBalancesPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postOpeningBalances: async (hoaId: string, openingBalancesPost: OpeningBalancesPost, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('postOpeningBalances', 'hoaId', hoaId)
            // verify required parameter 'openingBalancesPost' is not null or undefined
            assertParamExists('postOpeningBalances', 'openingBalancesPost', openingBalancesPost)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/opening-balances`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(openingBalancesPost, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RevaluationRequest} revaluationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postRevaluation: async (hoaId: string, revaluationRequest: RevaluationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('postRevaluation', 'hoaId', hoaId)
            // verify required parameter 'revaluationRequest' is not null or undefined
            assertParamExists('postRevaluation', 'revaluationRequest', revaluationRequest)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/revaluations`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(revaluationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} fiscalYear The year the fiscal year starts in
         * @param {BudgetYearSet} budgetYearSet 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setBudget: async (hoaId: string, fiscalYear: number, budgetYearSet: BudgetYearSet, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('setBudget', 'hoaId', hoaId)
            // verify required parameter 'fiscalYear' is not null or undefined
            assertParamExists('setBudget', 'fiscalYear', fiscalYear)
            // verify required parameter 'budgetYearSet' is not null or undefined
            assertParamExists('setBudget', 'budgetYearSet', budgetYearSet)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/fiscal-years/{fiscal_year}/budget`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"fiscal_year"}}`, encodeURIComponent(String(fiscalYear)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(budgetYearSet, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {PeriodStatusUpdate} periodStatusUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setPeriodStatus: async (hoaId: string, periodId: string, periodStatusUpdate: PeriodStatusUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('setPeriodStatus', 'hoaId', hoaId)
            // verify required parameter 'periodId' is not null or undefined
            assertParamExists('setPeriodStatus', 'periodId', periodId)
            // verify required parameter 'periodStatusUpdate' is not null or undefined
            assertParamExists('setPeriodStatus', 'periodStatusUpdate', periodStatusUpdate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/periods/{period_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"period_id"}}`, encodeURIComponent(String(periodId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(periodStatusUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {string} itemId Match id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unmatchReconciliationItem: async (hoaId: string, reconciliationId: string, itemId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('unmatchReconciliationItem', 'hoaId', hoaId)
            // verify required parameter 'reconciliationId' is not null or undefined
            assertParamExists('unmatchReconciliationItem', 'reconciliationId', reconciliationId)
            // verify required parameter 'itemId' is not null or undefined
            assertParamExists('unmatchReconciliationItem', 'itemId', itemId)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/reconciliations/{reconciliation_id}/matches/{item_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"reconciliation_id"}}`, encodeURIComponent(String(reconciliationId)))
                .replace(`{${"item_id"}}`, encodeURIComponent(String(itemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} accountId GL account id
         * @param {GlAccountUpdate} glAccountUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAccount: async (hoaId: string, accountId: string, glAccountUpdate: GlAccountUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('updateAccount', 'hoaId', hoaId)
            // verify required parameter 'accountId' is not null or undefined
            assertParamExists('updateAccount', 'accountId', accountId)
            // verify required parameter 'glAccountUpdate' is not null or undefined
            assertParamExists('updateAccount', 'glAccountUpdate', glAccountUpdate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/accounts/{account_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"account_id"}}`, encodeURIComponent(String(accountId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(glAccountUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} fundId Fund id
         * @param {FundUpdate} fundUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFund: async (hoaId: string, fundId: string, fundUpdate: FundUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('updateFund', 'hoaId', hoaId)
            // verify required parameter 'fundId' is not null or undefined
            assertParamExists('updateFund', 'fundId', fundId)
            // verify required parameter 'fundUpdate' is not null or undefined
            assertParamExists('updateFund', 'fundUpdate', fundUpdate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/funds/{fund_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)))
                .replace(`{${"fund_id"}}`, encodeURIComponent(String(fundId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(fundUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {GlSettingsUpdate} glSettingsUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSettings: async (hoaId: string, glSettingsUpdate: GlSettingsUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('updateSettings', 'hoaId', hoaId)
            // verify required parameter 'glSettingsUpdate' is not null or undefined
            assertParamExists('updateSettings', 'glSettingsUpdate', glSettingsUpdate)
            const localVarPath = `/api/v1/ledger/hoas/{hoa_id}/settings`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(glSettingsUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * LedgerHandlersApi - functional programming interface
 */
export const LedgerHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = LedgerHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Point a reconciliation at the institution\'s own statement, or clear the pointer. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {StatementAttach} statementAttach 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async attachReconciliationStatement(hoaId: string, reconciliationId: string, statementAttach: StatementAttach, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.attachReconciliationStatement(hoaId, reconciliationId, statementAttach, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.attachReconciliationStatement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async completeReconciliation(hoaId: string, reconciliationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReconciliationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.completeReconciliation(hoaId, reconciliationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.completeReconciliation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {GlAccountCreate} glAccountCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAccount(hoaId: string, glAccountCreate: GlAccountCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GlAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAccount(hoaId, glAccountCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.createAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {FundCreate} fundCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createFund(hoaId: string, fundCreate: FundCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FundReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createFund(hoaId, fundCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.createFund']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {PeriodGenerateRequest} periodGenerateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generatePeriods(hoaId: string, periodGenerateRequest: PeriodGenerateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AccountingPeriodList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generatePeriods(hoaId, periodGenerateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.generatePeriods']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} asOf Required. The date the sheet is struck at
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBalanceSheet(hoaId: string, asOf: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BalanceSheet>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBalanceSheet(hoaId, asOf, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getBalanceSheet']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} fiscalYear The year the fiscal year starts in
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBudget(hoaId: string, fiscalYear: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BudgetYearReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBudget(hoaId, fiscalYear, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getBudget']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary The period\'s financial reports as one PDF.
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFinancialReports(hoaId: string, periodId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFinancialReports(hoaId, periodId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getFinancialReports']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getIncomeStatement(hoaId: string, periodId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IncomeStatement>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getIncomeStatement(hoaId, periodId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getIncomeStatement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOpeningBalances(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OpeningBalancesRequired>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOpeningBalances(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getOpeningBalances']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReconciliation(hoaId: string, reconciliationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReconciliationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReconciliation(hoaId, reconciliationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getReconciliation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSettings(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GlSettingsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSettings(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary A closing balance proposed for a statement, with its derivation. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} glAccountId The cash account being reconciled
         * @param {string} statementDate Statement closing date (YYYY-MM-DD)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSuggestedBalance(hoaId: string, glAccountId: string, statementDate: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SuggestedBalance>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSuggestedBalance(hoaId, glAccountId, statementDate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getSuggestedBalance']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTieOut(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubledgerTieOut>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTieOut(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getTieOut']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTrialBalance(hoaId: string, periodId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TrialBalance>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTrialBalance(hoaId, periodId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.getTrialBalance']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAccounts(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GlAccountList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAccounts(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.listAccounts']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listFunds(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FundList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listFunds(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.listFunds']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listPeriods(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AccountingPeriodList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listPeriods(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.listPeriods']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} [from] Inclusive earliest statement date (YYYY-MM-DD)
         * @param {string} [to] Inclusive latest statement date (YYYY-MM-DD)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReconciliations(hoaId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReconciliationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReconciliations(hoaId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.listReconciliations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary The institution\'s own statements near this reconciliation\'s statement date, to attach one from. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listStatementCandidates(hoaId: string, reconciliationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<StatementList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listStatementCandidates(hoaId, reconciliationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.listStatementCandidates']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {ReconciliationMatch} reconciliationMatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async matchReconciliationItem(hoaId: string, reconciliationId: string, reconciliationMatch: ReconciliationMatch, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReconciliationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.matchReconciliationItem(hoaId, reconciliationId, reconciliationMatch, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.matchReconciliationItem']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {ReconciliationOpen} reconciliationOpen 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async openReconciliation(hoaId: string, reconciliationOpen: ReconciliationOpen, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReconciliationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.openReconciliation(hoaId, reconciliationOpen, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.openReconciliation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Post an unexplained bank item the institution originated, and clear it against the transaction that caused it. **Manager+.**
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {BankItemPost} bankItemPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postBankItem(hoaId: string, reconciliationId: string, bankItemPost: BankItemPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<JournalEntryReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postBankItem(hoaId, reconciliationId, bankItemPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.postBankItem']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {OpeningBalancesPost} openingBalancesPost 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postOpeningBalances(hoaId: string, openingBalancesPost: OpeningBalancesPost, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<JournalEntryReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postOpeningBalances(hoaId, openingBalancesPost, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.postOpeningBalances']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {RevaluationRequest} revaluationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async postRevaluation(hoaId: string, revaluationRequest: RevaluationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RevaluationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.postRevaluation(hoaId, revaluationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.postRevaluation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {number} fiscalYear The year the fiscal year starts in
         * @param {BudgetYearSet} budgetYearSet 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setBudget(hoaId: string, fiscalYear: number, budgetYearSet: BudgetYearSet, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BudgetYearReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setBudget(hoaId, fiscalYear, budgetYearSet, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.setBudget']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} periodId Accounting period id
         * @param {PeriodStatusUpdate} periodStatusUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setPeriodStatus(hoaId: string, periodId: string, periodStatusUpdate: PeriodStatusUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AccountingPeriodReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setPeriodStatus(hoaId, periodId, periodStatusUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.setPeriodStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} reconciliationId Reconciliation id
         * @param {string} itemId Match id
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unmatchReconciliationItem(hoaId: string, reconciliationId: string, itemId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReconciliationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unmatchReconciliationItem(hoaId, reconciliationId, itemId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.unmatchReconciliationItem']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} accountId GL account id
         * @param {GlAccountUpdate} glAccountUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAccount(hoaId: string, accountId: string, glAccountUpdate: GlAccountUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GlAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAccount(hoaId, accountId, glAccountUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.updateAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {string} fundId Fund id
         * @param {FundUpdate} fundUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateFund(hoaId: string, fundId: string, fundUpdate: FundUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FundReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateFund(hoaId, fundId, fundUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.updateFund']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA id
         * @param {GlSettingsUpdate} glSettingsUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateSettings(hoaId: string, glSettingsUpdate: GlSettingsUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GlSettingsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateSettings(hoaId, glSettingsUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LedgerHandlersApi.updateSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * LedgerHandlersApi - factory interface
 */
export const LedgerHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = LedgerHandlersApiFp(configuration)
    return {
        /**
         * 
         * @summary Point a reconciliation at the institution\'s own statement, or clear the pointer. **Manager+.**
         * @param {LedgerHandlersApiAttachReconciliationStatementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        attachReconciliationStatement(requestParameters: LedgerHandlersApiAttachReconciliationStatementRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.attachReconciliationStatement(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.statementAttach, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiCompleteReconciliationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeReconciliation(requestParameters: LedgerHandlersApiCompleteReconciliationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReconciliationReport> {
            return localVarFp.completeReconciliation(requestParameters.hoaId, requestParameters.reconciliationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiCreateAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAccount(requestParameters: LedgerHandlersApiCreateAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<GlAccountReturn> {
            return localVarFp.createAccount(requestParameters.hoaId, requestParameters.glAccountCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiCreateFundRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createFund(requestParameters: LedgerHandlersApiCreateFundRequest, options?: RawAxiosRequestConfig): AxiosPromise<FundReturn> {
            return localVarFp.createFund(requestParameters.hoaId, requestParameters.fundCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGeneratePeriodsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generatePeriods(requestParameters: LedgerHandlersApiGeneratePeriodsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AccountingPeriodList> {
            return localVarFp.generatePeriods(requestParameters.hoaId, requestParameters.periodGenerateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetBalanceSheetRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBalanceSheet(requestParameters: LedgerHandlersApiGetBalanceSheetRequest, options?: RawAxiosRequestConfig): AxiosPromise<BalanceSheet> {
            return localVarFp.getBalanceSheet(requestParameters.hoaId, requestParameters.asOf, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetBudgetRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBudget(requestParameters: LedgerHandlersApiGetBudgetRequest, options?: RawAxiosRequestConfig): AxiosPromise<BudgetYearReturn> {
            return localVarFp.getBudget(requestParameters.hoaId, requestParameters.fiscalYear, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary The period\'s financial reports as one PDF.
         * @param {LedgerHandlersApiGetFinancialReportsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFinancialReports(requestParameters: LedgerHandlersApiGetFinancialReportsRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.getFinancialReports(requestParameters.hoaId, requestParameters.periodId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetIncomeStatementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getIncomeStatement(requestParameters: LedgerHandlersApiGetIncomeStatementRequest, options?: RawAxiosRequestConfig): AxiosPromise<IncomeStatement> {
            return localVarFp.getIncomeStatement(requestParameters.hoaId, requestParameters.periodId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetOpeningBalancesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOpeningBalances(requestParameters: LedgerHandlersApiGetOpeningBalancesRequest, options?: RawAxiosRequestConfig): AxiosPromise<OpeningBalancesRequired> {
            return localVarFp.getOpeningBalances(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetReconciliationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReconciliation(requestParameters: LedgerHandlersApiGetReconciliationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReconciliationReport> {
            return localVarFp.getReconciliation(requestParameters.hoaId, requestParameters.reconciliationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetSettingsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSettings(requestParameters: LedgerHandlersApiGetSettingsRequest, options?: RawAxiosRequestConfig): AxiosPromise<GlSettingsReturn> {
            return localVarFp.getSettings(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary A closing balance proposed for a statement, with its derivation. **Manager+.**
         * @param {LedgerHandlersApiGetSuggestedBalanceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSuggestedBalance(requestParameters: LedgerHandlersApiGetSuggestedBalanceRequest, options?: RawAxiosRequestConfig): AxiosPromise<SuggestedBalance> {
            return localVarFp.getSuggestedBalance(requestParameters.hoaId, requestParameters.glAccountId, requestParameters.statementDate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetTieOutRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTieOut(requestParameters: LedgerHandlersApiGetTieOutRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubledgerTieOut> {
            return localVarFp.getTieOut(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiGetTrialBalanceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTrialBalance(requestParameters: LedgerHandlersApiGetTrialBalanceRequest, options?: RawAxiosRequestConfig): AxiosPromise<TrialBalance> {
            return localVarFp.getTrialBalance(requestParameters.hoaId, requestParameters.periodId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiListAccountsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAccounts(requestParameters: LedgerHandlersApiListAccountsRequest, options?: RawAxiosRequestConfig): AxiosPromise<GlAccountList> {
            return localVarFp.listAccounts(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiListFundsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listFunds(requestParameters: LedgerHandlersApiListFundsRequest, options?: RawAxiosRequestConfig): AxiosPromise<FundList> {
            return localVarFp.listFunds(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiListPeriodsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPeriods(requestParameters: LedgerHandlersApiListPeriodsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AccountingPeriodList> {
            return localVarFp.listPeriods(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiListReconciliationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReconciliations(requestParameters: LedgerHandlersApiListReconciliationsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReconciliationList> {
            return localVarFp.listReconciliations(requestParameters.hoaId, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary The institution\'s own statements near this reconciliation\'s statement date, to attach one from. **Manager+.**
         * @param {LedgerHandlersApiListStatementCandidatesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listStatementCandidates(requestParameters: LedgerHandlersApiListStatementCandidatesRequest, options?: RawAxiosRequestConfig): AxiosPromise<StatementList> {
            return localVarFp.listStatementCandidates(requestParameters.hoaId, requestParameters.reconciliationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiMatchReconciliationItemRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        matchReconciliationItem(requestParameters: LedgerHandlersApiMatchReconciliationItemRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReconciliationReport> {
            return localVarFp.matchReconciliationItem(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.reconciliationMatch, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiOpenReconciliationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        openReconciliation(requestParameters: LedgerHandlersApiOpenReconciliationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReconciliationReport> {
            return localVarFp.openReconciliation(requestParameters.hoaId, requestParameters.reconciliationOpen, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Post an unexplained bank item the institution originated, and clear it against the transaction that caused it. **Manager+.**
         * @param {LedgerHandlersApiPostBankItemRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postBankItem(requestParameters: LedgerHandlersApiPostBankItemRequest, options?: RawAxiosRequestConfig): AxiosPromise<JournalEntryReturn> {
            return localVarFp.postBankItem(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.bankItemPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiPostOpeningBalancesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postOpeningBalances(requestParameters: LedgerHandlersApiPostOpeningBalancesRequest, options?: RawAxiosRequestConfig): AxiosPromise<JournalEntryReturn> {
            return localVarFp.postOpeningBalances(requestParameters.hoaId, requestParameters.openingBalancesPost, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiPostRevaluationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        postRevaluation(requestParameters: LedgerHandlersApiPostRevaluationRequest, options?: RawAxiosRequestConfig): AxiosPromise<RevaluationReturn> {
            return localVarFp.postRevaluation(requestParameters.hoaId, requestParameters.revaluationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiSetBudgetRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setBudget(requestParameters: LedgerHandlersApiSetBudgetRequest, options?: RawAxiosRequestConfig): AxiosPromise<BudgetYearReturn> {
            return localVarFp.setBudget(requestParameters.hoaId, requestParameters.fiscalYear, requestParameters.budgetYearSet, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiSetPeriodStatusRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setPeriodStatus(requestParameters: LedgerHandlersApiSetPeriodStatusRequest, options?: RawAxiosRequestConfig): AxiosPromise<AccountingPeriodReturn> {
            return localVarFp.setPeriodStatus(requestParameters.hoaId, requestParameters.periodId, requestParameters.periodStatusUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiUnmatchReconciliationItemRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unmatchReconciliationItem(requestParameters: LedgerHandlersApiUnmatchReconciliationItemRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReconciliationReport> {
            return localVarFp.unmatchReconciliationItem(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.itemId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiUpdateAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAccount(requestParameters: LedgerHandlersApiUpdateAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<GlAccountReturn> {
            return localVarFp.updateAccount(requestParameters.hoaId, requestParameters.accountId, requestParameters.glAccountUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiUpdateFundRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFund(requestParameters: LedgerHandlersApiUpdateFundRequest, options?: RawAxiosRequestConfig): AxiosPromise<FundReturn> {
            return localVarFp.updateFund(requestParameters.hoaId, requestParameters.fundId, requestParameters.fundUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {LedgerHandlersApiUpdateSettingsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSettings(requestParameters: LedgerHandlersApiUpdateSettingsRequest, options?: RawAxiosRequestConfig): AxiosPromise<GlSettingsReturn> {
            return localVarFp.updateSettings(requestParameters.hoaId, requestParameters.glSettingsUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for attachReconciliationStatement operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiAttachReconciliationStatementRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string

    readonly statementAttach: StatementAttach
}

/**
 * Request parameters for completeReconciliation operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiCompleteReconciliationRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string
}

/**
 * Request parameters for createAccount operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiCreateAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly glAccountCreate: GlAccountCreate
}

/**
 * Request parameters for createFund operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiCreateFundRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly fundCreate: FundCreate
}

/**
 * Request parameters for generatePeriods operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGeneratePeriodsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly periodGenerateRequest: PeriodGenerateRequest
}

/**
 * Request parameters for getBalanceSheet operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetBalanceSheetRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Required. The date the sheet is struck at
     */
    readonly asOf: string
}

/**
 * Request parameters for getBudget operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetBudgetRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * The year the fiscal year starts in
     */
    readonly fiscalYear: number
}

/**
 * Request parameters for getFinancialReports operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetFinancialReportsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Accounting period id
     */
    readonly periodId: string
}

/**
 * Request parameters for getIncomeStatement operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetIncomeStatementRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Accounting period id
     */
    readonly periodId: string
}

/**
 * Request parameters for getOpeningBalances operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetOpeningBalancesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for getReconciliation operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetReconciliationRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string
}

/**
 * Request parameters for getSettings operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetSettingsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for getSuggestedBalance operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetSuggestedBalanceRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * The cash account being reconciled
     */
    readonly glAccountId: string

    /**
     * Statement closing date (YYYY-MM-DD)
     */
    readonly statementDate: string
}

/**
 * Request parameters for getTieOut operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetTieOutRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for getTrialBalance operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiGetTrialBalanceRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Accounting period id
     */
    readonly periodId: string
}

/**
 * Request parameters for listAccounts operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiListAccountsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listFunds operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiListFundsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listPeriods operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiListPeriodsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string
}

/**
 * Request parameters for listReconciliations operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiListReconciliationsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Inclusive earliest statement date (YYYY-MM-DD)
     */
    readonly from?: string

    /**
     * Inclusive latest statement date (YYYY-MM-DD)
     */
    readonly to?: string
}

/**
 * Request parameters for listStatementCandidates operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiListStatementCandidatesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string
}

/**
 * Request parameters for matchReconciliationItem operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiMatchReconciliationItemRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string

    readonly reconciliationMatch: ReconciliationMatch
}

/**
 * Request parameters for openReconciliation operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiOpenReconciliationRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly reconciliationOpen: ReconciliationOpen
}

/**
 * Request parameters for postBankItem operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiPostBankItemRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string

    readonly bankItemPost: BankItemPost
}

/**
 * Request parameters for postOpeningBalances operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiPostOpeningBalancesRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly openingBalancesPost: OpeningBalancesPost
}

/**
 * Request parameters for postRevaluation operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiPostRevaluationRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly revaluationRequest: RevaluationRequest
}

/**
 * Request parameters for setBudget operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiSetBudgetRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * The year the fiscal year starts in
     */
    readonly fiscalYear: number

    readonly budgetYearSet: BudgetYearSet
}

/**
 * Request parameters for setPeriodStatus operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiSetPeriodStatusRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Accounting period id
     */
    readonly periodId: string

    readonly periodStatusUpdate: PeriodStatusUpdate
}

/**
 * Request parameters for unmatchReconciliationItem operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiUnmatchReconciliationItemRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Reconciliation id
     */
    readonly reconciliationId: string

    /**
     * Match id
     */
    readonly itemId: string
}

/**
 * Request parameters for updateAccount operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiUpdateAccountRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * GL account id
     */
    readonly accountId: string

    readonly glAccountUpdate: GlAccountUpdate
}

/**
 * Request parameters for updateFund operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiUpdateFundRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    /**
     * Fund id
     */
    readonly fundId: string

    readonly fundUpdate: FundUpdate
}

/**
 * Request parameters for updateSettings operation in LedgerHandlersApi.
 */
export interface LedgerHandlersApiUpdateSettingsRequest {
    /**
     * HOA id
     */
    readonly hoaId: string

    readonly glSettingsUpdate: GlSettingsUpdate
}

/**
 * LedgerHandlersApi - object-oriented interface
 */
export class LedgerHandlersApi extends BaseAPI {
    /**
     * 
     * @summary Point a reconciliation at the institution\'s own statement, or clear the pointer. **Manager+.**
     * @param {LedgerHandlersApiAttachReconciliationStatementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public attachReconciliationStatement(requestParameters: LedgerHandlersApiAttachReconciliationStatementRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).attachReconciliationStatement(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.statementAttach, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiCompleteReconciliationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public completeReconciliation(requestParameters: LedgerHandlersApiCompleteReconciliationRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).completeReconciliation(requestParameters.hoaId, requestParameters.reconciliationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiCreateAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAccount(requestParameters: LedgerHandlersApiCreateAccountRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).createAccount(requestParameters.hoaId, requestParameters.glAccountCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiCreateFundRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createFund(requestParameters: LedgerHandlersApiCreateFundRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).createFund(requestParameters.hoaId, requestParameters.fundCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGeneratePeriodsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generatePeriods(requestParameters: LedgerHandlersApiGeneratePeriodsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).generatePeriods(requestParameters.hoaId, requestParameters.periodGenerateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetBalanceSheetRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBalanceSheet(requestParameters: LedgerHandlersApiGetBalanceSheetRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getBalanceSheet(requestParameters.hoaId, requestParameters.asOf, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetBudgetRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBudget(requestParameters: LedgerHandlersApiGetBudgetRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getBudget(requestParameters.hoaId, requestParameters.fiscalYear, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary The period\'s financial reports as one PDF.
     * @param {LedgerHandlersApiGetFinancialReportsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFinancialReports(requestParameters: LedgerHandlersApiGetFinancialReportsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getFinancialReports(requestParameters.hoaId, requestParameters.periodId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetIncomeStatementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getIncomeStatement(requestParameters: LedgerHandlersApiGetIncomeStatementRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getIncomeStatement(requestParameters.hoaId, requestParameters.periodId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetOpeningBalancesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOpeningBalances(requestParameters: LedgerHandlersApiGetOpeningBalancesRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getOpeningBalances(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetReconciliationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReconciliation(requestParameters: LedgerHandlersApiGetReconciliationRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getReconciliation(requestParameters.hoaId, requestParameters.reconciliationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetSettingsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSettings(requestParameters: LedgerHandlersApiGetSettingsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getSettings(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary A closing balance proposed for a statement, with its derivation. **Manager+.**
     * @param {LedgerHandlersApiGetSuggestedBalanceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSuggestedBalance(requestParameters: LedgerHandlersApiGetSuggestedBalanceRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getSuggestedBalance(requestParameters.hoaId, requestParameters.glAccountId, requestParameters.statementDate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetTieOutRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTieOut(requestParameters: LedgerHandlersApiGetTieOutRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getTieOut(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiGetTrialBalanceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTrialBalance(requestParameters: LedgerHandlersApiGetTrialBalanceRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).getTrialBalance(requestParameters.hoaId, requestParameters.periodId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiListAccountsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAccounts(requestParameters: LedgerHandlersApiListAccountsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).listAccounts(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiListFundsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listFunds(requestParameters: LedgerHandlersApiListFundsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).listFunds(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiListPeriodsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listPeriods(requestParameters: LedgerHandlersApiListPeriodsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).listPeriods(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiListReconciliationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReconciliations(requestParameters: LedgerHandlersApiListReconciliationsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).listReconciliations(requestParameters.hoaId, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary The institution\'s own statements near this reconciliation\'s statement date, to attach one from. **Manager+.**
     * @param {LedgerHandlersApiListStatementCandidatesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listStatementCandidates(requestParameters: LedgerHandlersApiListStatementCandidatesRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).listStatementCandidates(requestParameters.hoaId, requestParameters.reconciliationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiMatchReconciliationItemRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public matchReconciliationItem(requestParameters: LedgerHandlersApiMatchReconciliationItemRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).matchReconciliationItem(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.reconciliationMatch, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiOpenReconciliationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public openReconciliation(requestParameters: LedgerHandlersApiOpenReconciliationRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).openReconciliation(requestParameters.hoaId, requestParameters.reconciliationOpen, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Post an unexplained bank item the institution originated, and clear it against the transaction that caused it. **Manager+.**
     * @param {LedgerHandlersApiPostBankItemRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postBankItem(requestParameters: LedgerHandlersApiPostBankItemRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).postBankItem(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.bankItemPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiPostOpeningBalancesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postOpeningBalances(requestParameters: LedgerHandlersApiPostOpeningBalancesRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).postOpeningBalances(requestParameters.hoaId, requestParameters.openingBalancesPost, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiPostRevaluationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public postRevaluation(requestParameters: LedgerHandlersApiPostRevaluationRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).postRevaluation(requestParameters.hoaId, requestParameters.revaluationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiSetBudgetRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setBudget(requestParameters: LedgerHandlersApiSetBudgetRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).setBudget(requestParameters.hoaId, requestParameters.fiscalYear, requestParameters.budgetYearSet, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiSetPeriodStatusRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setPeriodStatus(requestParameters: LedgerHandlersApiSetPeriodStatusRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).setPeriodStatus(requestParameters.hoaId, requestParameters.periodId, requestParameters.periodStatusUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiUnmatchReconciliationItemRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unmatchReconciliationItem(requestParameters: LedgerHandlersApiUnmatchReconciliationItemRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).unmatchReconciliationItem(requestParameters.hoaId, requestParameters.reconciliationId, requestParameters.itemId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiUpdateAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAccount(requestParameters: LedgerHandlersApiUpdateAccountRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).updateAccount(requestParameters.hoaId, requestParameters.accountId, requestParameters.glAccountUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiUpdateFundRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateFund(requestParameters: LedgerHandlersApiUpdateFundRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).updateFund(requestParameters.hoaId, requestParameters.fundId, requestParameters.fundUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {LedgerHandlersApiUpdateSettingsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateSettings(requestParameters: LedgerHandlersApiUpdateSettingsRequest, options?: RawAxiosRequestConfig) {
        return LedgerHandlersApiFp(this.configuration).updateSettings(requestParameters.hoaId, requestParameters.glSettingsUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MarketingApi - axios parameter creator
 */
export const MarketingApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {ContactFormRequest} contactFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitContactForm: async (contactFormRequest: ContactFormRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contactFormRequest' is not null or undefined
            assertParamExists('submitContactForm', 'contactFormRequest', contactFormRequest)
            const localVarPath = `/api/v1/marketing/contact`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(contactFormRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MarketingApi - functional programming interface
 */
export const MarketingApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MarketingApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {ContactFormRequest} contactFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async submitContactForm(contactFormRequest: ContactFormRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContactFormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.submitContactForm(contactFormRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MarketingApi.submitContactForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MarketingApi - factory interface
 */
export const MarketingApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MarketingApiFp(configuration)
    return {
        /**
         * 
         * @param {MarketingApiSubmitContactFormRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitContactForm(requestParameters: MarketingApiSubmitContactFormRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContactFormResponse> {
            return localVarFp.submitContactForm(requestParameters.contactFormRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for submitContactForm operation in MarketingApi.
 */
export interface MarketingApiSubmitContactFormRequest {
    readonly contactFormRequest: ContactFormRequest
}

/**
 * MarketingApi - object-oriented interface
 */
export class MarketingApi extends BaseAPI {
    /**
     * 
     * @param {MarketingApiSubmitContactFormRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public submitContactForm(requestParameters: MarketingApiSubmitContactFormRequest, options?: RawAxiosRequestConfig) {
        return MarketingApiFp(this.configuration).submitContactForm(requestParameters.contactFormRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberAuthApi - axios parameter creator
 */
export const MemberAuthApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Sign out, ending every session for the calling member.  Bumps `token_version`, which `authenticate_member_access_token` compares against the row on every request, so existing access tokens stop working immediately rather than at expiry.
         * @summary Refresh member access token
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberLogout: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member-portal/auth/logout`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Generates a new access token using a valid refresh token.
         * @param {MemberRefreshRequest} memberRefreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberRefreshToken: async (memberRefreshRequest: MemberRefreshRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberRefreshRequest' is not null or undefined
            assertParamExists('memberRefreshToken', 'memberRefreshRequest', memberRefreshRequest)
            const localVarPath = `/api/v1/member-portal/auth/refresh`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberRefreshRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {MemberVerifyRequest} memberVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberVerifyToken: async (memberVerifyRequest: MemberVerifyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberVerifyRequest' is not null or undefined
            assertParamExists('memberVerifyToken', 'memberVerifyRequest', memberVerifyRequest)
            const localVarPath = `/api/v1/member-portal/auth/verify`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberVerifyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Exchanges the AuthKit authorization code for the member\'s verified address and issues our own tokens.  **A verified identity matching no member row is a 401, never a signup.** Members are created by a manager and activated by an invitation; that gate is the whole basis of the realm.
         * @summary Member sign-in with WorkOS
         * @param {MemberWorkosLogin} memberWorkosLogin 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberWorkosLogin: async (memberWorkosLogin: MemberWorkosLogin, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberWorkosLogin' is not null or undefined
            assertParamExists('memberWorkosLogin', 'memberWorkosLogin', memberWorkosLogin)
            const localVarPath = `/api/v1/member-portal/auth/workos-login`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberWorkosLogin, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberAuthApi - functional programming interface
 */
export const MemberAuthApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberAuthApiAxiosParamCreator(configuration)
    return {
        /**
         * Sign out, ending every session for the calling member.  Bumps `token_version`, which `authenticate_member_access_token` compares against the row on every request, so existing access tokens stop working immediately rather than at expiry.
         * @summary Refresh member access token
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async memberLogout(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberLogoutResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.memberLogout(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberAuthApi.memberLogout']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Generates a new access token using a valid refresh token.
         * @param {MemberRefreshRequest} memberRefreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async memberRefreshToken(memberRefreshRequest: MemberRefreshRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberTokenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.memberRefreshToken(memberRefreshRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberAuthApi.memberRefreshToken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {MemberVerifyRequest} memberVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async memberVerifyToken(memberVerifyRequest: MemberVerifyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberVerifyResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.memberVerifyToken(memberVerifyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberAuthApi.memberVerifyToken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Exchanges the AuthKit authorization code for the member\'s verified address and issues our own tokens.  **A verified identity matching no member row is a 401, never a signup.** Members are created by a manager and activated by an invitation; that gate is the whole basis of the realm.
         * @summary Member sign-in with WorkOS
         * @param {MemberWorkosLogin} memberWorkosLogin 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async memberWorkosLogin(memberWorkosLogin: MemberWorkosLogin, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberTokenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.memberWorkosLogin(memberWorkosLogin, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberAuthApi.memberWorkosLogin']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberAuthApi - factory interface
 */
export const MemberAuthApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberAuthApiFp(configuration)
    return {
        /**
         * Sign out, ending every session for the calling member.  Bumps `token_version`, which `authenticate_member_access_token` compares against the row on every request, so existing access tokens stop working immediately rather than at expiry.
         * @summary Refresh member access token
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberLogout(options?: RawAxiosRequestConfig): AxiosPromise<MemberLogoutResponse> {
            return localVarFp.memberLogout(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Generates a new access token using a valid refresh token.
         * @param {MemberAuthApiMemberRefreshTokenRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberRefreshToken(requestParameters: MemberAuthApiMemberRefreshTokenRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberTokenResponse> {
            return localVarFp.memberRefreshToken(requestParameters.memberRefreshRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberAuthApiMemberVerifyTokenRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberVerifyToken(requestParameters: MemberAuthApiMemberVerifyTokenRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberVerifyResponse> {
            return localVarFp.memberVerifyToken(requestParameters.memberVerifyRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Exchanges the AuthKit authorization code for the member\'s verified address and issues our own tokens.  **A verified identity matching no member row is a 401, never a signup.** Members are created by a manager and activated by an invitation; that gate is the whole basis of the realm.
         * @summary Member sign-in with WorkOS
         * @param {MemberAuthApiMemberWorkosLoginRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberWorkosLogin(requestParameters: MemberAuthApiMemberWorkosLoginRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberTokenResponse> {
            return localVarFp.memberWorkosLogin(requestParameters.memberWorkosLogin, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for memberRefreshToken operation in MemberAuthApi.
 */
export interface MemberAuthApiMemberRefreshTokenRequest {
    readonly memberRefreshRequest: MemberRefreshRequest
}

/**
 * Request parameters for memberVerifyToken operation in MemberAuthApi.
 */
export interface MemberAuthApiMemberVerifyTokenRequest {
    readonly memberVerifyRequest: MemberVerifyRequest
}

/**
 * Request parameters for memberWorkosLogin operation in MemberAuthApi.
 */
export interface MemberAuthApiMemberWorkosLoginRequest {
    readonly memberWorkosLogin: MemberWorkosLogin
}

/**
 * MemberAuthApi - object-oriented interface
 */
export class MemberAuthApi extends BaseAPI {
    /**
     * Sign out, ending every session for the calling member.  Bumps `token_version`, which `authenticate_member_access_token` compares against the row on every request, so existing access tokens stop working immediately rather than at expiry.
     * @summary Refresh member access token
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public memberLogout(options?: RawAxiosRequestConfig) {
        return MemberAuthApiFp(this.configuration).memberLogout(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Generates a new access token using a valid refresh token.
     * @param {MemberAuthApiMemberRefreshTokenRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public memberRefreshToken(requestParameters: MemberAuthApiMemberRefreshTokenRequest, options?: RawAxiosRequestConfig) {
        return MemberAuthApiFp(this.configuration).memberRefreshToken(requestParameters.memberRefreshRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberAuthApiMemberVerifyTokenRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public memberVerifyToken(requestParameters: MemberAuthApiMemberVerifyTokenRequest, options?: RawAxiosRequestConfig) {
        return MemberAuthApiFp(this.configuration).memberVerifyToken(requestParameters.memberVerifyRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Exchanges the AuthKit authorization code for the member\'s verified address and issues our own tokens.  **A verified identity matching no member row is a 401, never a signup.** Members are created by a manager and activated by an invitation; that gate is the whole basis of the realm.
     * @summary Member sign-in with WorkOS
     * @param {MemberAuthApiMemberWorkosLoginRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public memberWorkosLogin(requestParameters: MemberAuthApiMemberWorkosLoginRequest, options?: RawAxiosRequestConfig) {
        return MemberAuthApiFp(this.configuration).memberWorkosLogin(requestParameters.memberWorkosLogin, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberHandlersApi - axios parameter creator
 */
export const MemberHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary ✅ CHANGED: Path parameter home_id from i32 to String
         * @param {string} memberId Member ID
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addMemberToHome: async (memberId: string, homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberId' is not null or undefined
            assertParamExists('addMemberToHome', 'memberId', memberId)
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('addMemberToHome', 'homeId', homeId)
            const localVarPath = `/api/v1/member/{member_id}/home/{home_id}`
                .replace(`{${"member_id"}}`, encodeURIComponent(String(memberId)))
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {MemberCreate} memberCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMember: async (memberCreate: MemberCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberCreate' is not null or undefined
            assertParamExists('createMember', 'memberCreate', memberCreate)
            const localVarPath = `/api/v1/member/create`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateMember: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deactivateMember', 'id', id)
            const localVarPath = `/api/v1/member/{id}/deactivate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMember: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteMember', 'id', id)
            const localVarPath = `/api/v1/member/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllMembers: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMember: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getMember', 'id', id)
            const localVarPath = `/api/v1/member/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} companyId Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMembersByCompany: async (companyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('getMembersByCompany', 'companyId', companyId)
            const localVarPath = `/api/v1/member/company/{company_id}`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMembersByHoa: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getMembersByHoa', 'hoaId', hoaId)
            const localVarPath = `/api/v1/member/hoa/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMembersByHome: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMembersByHome', 'homeId', homeId)
            const localVarPath = `/api/v1/member/home/{home_id}`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        impersonateMember: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('impersonateMember', 'id', id)
            const localVarPath = `/api/v1/member/{id}/impersonate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reactivateMember: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('reactivateMember', 'id', id)
            const localVarPath = `/api/v1/member/{id}/reactivate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter home_id from i32 to String
         * @param {string} memberId Member ID
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeMemberFromHome: async (memberId: string, homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberId' is not null or undefined
            assertParamExists('removeMemberFromHome', 'memberId', memberId)
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('removeMemberFromHome', 'homeId', homeId)
            const localVarPath = `/api/v1/member/{member_id}/home/{home_id}`
                .replace(`{${"member_id"}}`, encodeURIComponent(String(memberId)))
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendSetupEmail: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('resendSetupEmail', 'id', id)
            const localVarPath = `/api/v1/member/{id}/resend-setup`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {MemberUpdate} memberUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMember: async (id: string, memberUpdate: MemberUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateMember', 'id', id)
            // verify required parameter 'memberUpdate' is not null or undefined
            assertParamExists('updateMember', 'memberUpdate', memberUpdate)
            const localVarPath = `/api/v1/member/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberHandlersApi - functional programming interface
 */
export const MemberHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary ✅ CHANGED: Path parameter home_id from i32 to String
         * @param {string} memberId Member ID
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addMemberToHome(memberId: string, homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addMemberToHome(memberId, homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.addMemberToHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {MemberCreate} memberCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMember(memberCreate: MemberCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberCreatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMember(memberCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.createMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deactivateMember(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deactivateMember(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.deactivateMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteMember(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteMember(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.deleteMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllMembers(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<MemberReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllMembers(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.getAllMembers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMember(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMember(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.getMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} companyId Company ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMembersByCompany(companyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<MemberReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMembersByCompany(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.getMembersByCompany']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMembersByHoa(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<MemberReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMembersByHoa(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.getMembersByHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMembersByHome(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<MemberReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMembersByHome(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.getMembersByHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async impersonateMember(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberImpersonationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.impersonateMember(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.impersonateMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reactivateMember(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reactivateMember(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.reactivateMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter home_id from i32 to String
         * @param {string} memberId Member ID
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async removeMemberFromHome(memberId: string, homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.removeMemberFromHome(memberId, homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.removeMemberFromHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resendSetupEmail(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberInviteResendResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resendSetupEmail(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.resendSetupEmail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Member ID
         * @param {MemberUpdate} memberUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMember(id: string, memberUpdate: MemberUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MemberReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMember(id, memberUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberHandlersApi.updateMember']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberHandlersApi - factory interface
 */
export const MemberHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberHandlersApiFp(configuration)
    return {
        /**
         * 
         * @summary ✅ CHANGED: Path parameter home_id from i32 to String
         * @param {MemberHandlersApiAddMemberToHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addMemberToHome(requestParameters: MemberHandlersApiAddMemberToHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.addMemberToHome(requestParameters.memberId, requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiCreateMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMember(requestParameters: MemberHandlersApiCreateMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberCreatedResponse> {
            return localVarFp.createMember(requestParameters.memberCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiDeactivateMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateMember(requestParameters: MemberHandlersApiDeactivateMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberReturn> {
            return localVarFp.deactivateMember(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiDeleteMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMember(requestParameters: MemberHandlersApiDeleteMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteMember(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllMembers(options?: RawAxiosRequestConfig): AxiosPromise<Array<MemberReturn>> {
            return localVarFp.getAllMembers(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiGetMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMember(requestParameters: MemberHandlersApiGetMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberReturn> {
            return localVarFp.getMember(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {MemberHandlersApiGetMembersByCompanyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMembersByCompany(requestParameters: MemberHandlersApiGetMembersByCompanyRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<MemberReturn>> {
            return localVarFp.getMembersByCompany(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {MemberHandlersApiGetMembersByHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMembersByHoa(requestParameters: MemberHandlersApiGetMembersByHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<MemberReturn>> {
            return localVarFp.getMembersByHoa(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter from i32 to String
         * @param {MemberHandlersApiGetMembersByHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMembersByHome(requestParameters: MemberHandlersApiGetMembersByHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<MemberReturn>> {
            return localVarFp.getMembersByHome(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiImpersonateMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        impersonateMember(requestParameters: MemberHandlersApiImpersonateMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberImpersonationResponse> {
            return localVarFp.impersonateMember(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiReactivateMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reactivateMember(requestParameters: MemberHandlersApiReactivateMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberReturn> {
            return localVarFp.reactivateMember(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary ✅ CHANGED: Path parameter home_id from i32 to String
         * @param {MemberHandlersApiRemoveMemberFromHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeMemberFromHome(requestParameters: MemberHandlersApiRemoveMemberFromHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.removeMemberFromHome(requestParameters.memberId, requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiResendSetupEmailRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendSetupEmail(requestParameters: MemberHandlersApiResendSetupEmailRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberInviteResendResponse> {
            return localVarFp.resendSetupEmail(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberHandlersApiUpdateMemberRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMember(requestParameters: MemberHandlersApiUpdateMemberRequest, options?: RawAxiosRequestConfig): AxiosPromise<MemberReturn> {
            return localVarFp.updateMember(requestParameters.id, requestParameters.memberUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for addMemberToHome operation in MemberHandlersApi.
 */
export interface MemberHandlersApiAddMemberToHomeRequest {
    /**
     * Member ID
     */
    readonly memberId: string

    /**
     * Home ID
     */
    readonly homeId: string
}

/**
 * Request parameters for createMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiCreateMemberRequest {
    readonly memberCreate: MemberCreate
}

/**
 * Request parameters for deactivateMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiDeactivateMemberRequest {
    /**
     * Member ID
     */
    readonly id: string
}

/**
 * Request parameters for deleteMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiDeleteMemberRequest {
    /**
     * Member ID
     */
    readonly id: string
}

/**
 * Request parameters for getMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiGetMemberRequest {
    /**
     * Member ID
     */
    readonly id: string
}

/**
 * Request parameters for getMembersByCompany operation in MemberHandlersApi.
 */
export interface MemberHandlersApiGetMembersByCompanyRequest {
    /**
     * Company ID
     */
    readonly companyId: string
}

/**
 * Request parameters for getMembersByHoa operation in MemberHandlersApi.
 */
export interface MemberHandlersApiGetMembersByHoaRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string
}

/**
 * Request parameters for getMembersByHome operation in MemberHandlersApi.
 */
export interface MemberHandlersApiGetMembersByHomeRequest {
    /**
     * Home ID
     */
    readonly homeId: string
}

/**
 * Request parameters for impersonateMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiImpersonateMemberRequest {
    /**
     * Member ID
     */
    readonly id: string
}

/**
 * Request parameters for reactivateMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiReactivateMemberRequest {
    /**
     * Member ID
     */
    readonly id: string
}

/**
 * Request parameters for removeMemberFromHome operation in MemberHandlersApi.
 */
export interface MemberHandlersApiRemoveMemberFromHomeRequest {
    /**
     * Member ID
     */
    readonly memberId: string

    /**
     * Home ID
     */
    readonly homeId: string
}

/**
 * Request parameters for resendSetupEmail operation in MemberHandlersApi.
 */
export interface MemberHandlersApiResendSetupEmailRequest {
    /**
     * Member ID
     */
    readonly id: string
}

/**
 * Request parameters for updateMember operation in MemberHandlersApi.
 */
export interface MemberHandlersApiUpdateMemberRequest {
    /**
     * Member ID
     */
    readonly id: string

    readonly memberUpdate: MemberUpdate
}

/**
 * MemberHandlersApi - object-oriented interface
 */
export class MemberHandlersApi extends BaseAPI {
    /**
     * 
     * @summary ✅ CHANGED: Path parameter home_id from i32 to String
     * @param {MemberHandlersApiAddMemberToHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addMemberToHome(requestParameters: MemberHandlersApiAddMemberToHomeRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).addMemberToHome(requestParameters.memberId, requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiCreateMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMember(requestParameters: MemberHandlersApiCreateMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).createMember(requestParameters.memberCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiDeactivateMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deactivateMember(requestParameters: MemberHandlersApiDeactivateMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).deactivateMember(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiDeleteMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteMember(requestParameters: MemberHandlersApiDeleteMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).deleteMember(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllMembers(options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).getAllMembers(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiGetMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMember(requestParameters: MemberHandlersApiGetMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).getMember(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary ✅ CHANGED: Path parameter from i32 to String
     * @param {MemberHandlersApiGetMembersByCompanyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMembersByCompany(requestParameters: MemberHandlersApiGetMembersByCompanyRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).getMembersByCompany(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary ✅ CHANGED: Path parameter from i32 to String
     * @param {MemberHandlersApiGetMembersByHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMembersByHoa(requestParameters: MemberHandlersApiGetMembersByHoaRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).getMembersByHoa(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary ✅ CHANGED: Path parameter from i32 to String
     * @param {MemberHandlersApiGetMembersByHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMembersByHome(requestParameters: MemberHandlersApiGetMembersByHomeRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).getMembersByHome(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiImpersonateMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public impersonateMember(requestParameters: MemberHandlersApiImpersonateMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).impersonateMember(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiReactivateMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reactivateMember(requestParameters: MemberHandlersApiReactivateMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).reactivateMember(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary ✅ CHANGED: Path parameter home_id from i32 to String
     * @param {MemberHandlersApiRemoveMemberFromHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public removeMemberFromHome(requestParameters: MemberHandlersApiRemoveMemberFromHomeRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).removeMemberFromHome(requestParameters.memberId, requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiResendSetupEmailRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resendSetupEmail(requestParameters: MemberHandlersApiResendSetupEmailRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).resendSetupEmail(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberHandlersApiUpdateMemberRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMember(requestParameters: MemberHandlersApiUpdateMemberRequest, options?: RawAxiosRequestConfig) {
        return MemberHandlersApiFp(this.configuration).updateMember(requestParameters.id, requestParameters.memberUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalBillingAchHandlersApi - axios parameter creator
 */
export const MemberPortalBillingAchHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary The member\'s own ACH payments for this home, newest first.
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAchPayments: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listMyAchPayments', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/ach-payments`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Whether this home can be paid by bank account, what is on file, and the authorisation wording to show.
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        myAchAvailability: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('myAchAvailability', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/ach`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Remove the member\'s bank account. Payments already submitted are not affected.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeMyBankAccount: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member-portal/billing/bank-account`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Save the member\'s bank account for paying the association, accepting the debit authorisation. Replaces any previous account. The number is never returned.
         * @param {string} homeId One of the member\&#39;s homes; its address goes on the authorisation record
         * @param {HomeownerBankAccountUpsert} homeownerBankAccountUpsert 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setMyBankAccount: async (homeId: string, homeownerBankAccountUpsert: HomeownerBankAccountUpsert, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('setMyBankAccount', 'homeId', homeId)
            // verify required parameter 'homeownerBankAccountUpsert' is not null or undefined
            assertParamExists('setMyBankAccount', 'homeownerBankAccountUpsert', homeownerBankAccountUpsert)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/bank-account`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(homeownerBankAccountUpsert, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Pay the association from the account on file. The bank validates and holds the payment; the association settles it, usually the same day. **Moves nothing until then.**
         * @param {string} homeId One of the member\&#39;s homes
         * @param {AchPaymentCreate} achPaymentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitMyAchPayment: async (homeId: string, achPaymentCreate: AchPaymentCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('submitMyAchPayment', 'homeId', homeId)
            // verify required parameter 'achPaymentCreate' is not null or undefined
            assertParamExists('submitMyAchPayment', 'achPaymentCreate', achPaymentCreate)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/ach-payments`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(achPaymentCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalBillingAchHandlersApi - functional programming interface
 */
export const MemberPortalBillingAchHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalBillingAchHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary The member\'s own ACH payments for this home, newest first.
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyAchPayments(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MyAchPaymentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyAchPayments(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingAchHandlersApi.listMyAchPayments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Whether this home can be paid by bank account, what is on file, and the authorisation wording to show.
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async myAchAvailability(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AchAvailabilityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.myAchAvailability(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingAchHandlersApi.myAchAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Remove the member\'s bank account. Payments already submitted are not affected.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async removeMyBankAccount(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.removeMyBankAccount(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingAchHandlersApi.removeMyBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Save the member\'s bank account for paying the association, accepting the debit authorisation. Replaces any previous account. The number is never returned.
         * @param {string} homeId One of the member\&#39;s homes; its address goes on the authorisation record
         * @param {HomeownerBankAccountUpsert} homeownerBankAccountUpsert 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setMyBankAccount(homeId: string, homeownerBankAccountUpsert: HomeownerBankAccountUpsert, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CounterpartyBankAccountReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setMyBankAccount(homeId, homeownerBankAccountUpsert, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingAchHandlersApi.setMyBankAccount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Pay the association from the account on file. The bank validates and holds the payment; the association settles it, usually the same day. **Moves nothing until then.**
         * @param {string} homeId One of the member\&#39;s homes
         * @param {AchPaymentCreate} achPaymentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async submitMyAchPayment(homeId: string, achPaymentCreate: AchPaymentCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MyAchPaymentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.submitMyAchPayment(homeId, achPaymentCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingAchHandlersApi.submitMyAchPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalBillingAchHandlersApi - factory interface
 */
export const MemberPortalBillingAchHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalBillingAchHandlersApiFp(configuration)
    return {
        /**
         * 
         * @summary The member\'s own ACH payments for this home, newest first.
         * @param {MemberPortalBillingAchHandlersApiListMyAchPaymentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAchPayments(requestParameters: MemberPortalBillingAchHandlersApiListMyAchPaymentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<MyAchPaymentList> {
            return localVarFp.listMyAchPayments(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Whether this home can be paid by bank account, what is on file, and the authorisation wording to show.
         * @param {MemberPortalBillingAchHandlersApiMyAchAvailabilityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        myAchAvailability(requestParameters: MemberPortalBillingAchHandlersApiMyAchAvailabilityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AchAvailabilityReturn> {
            return localVarFp.myAchAvailability(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Remove the member\'s bank account. Payments already submitted are not affected.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeMyBankAccount(options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.removeMyBankAccount(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Save the member\'s bank account for paying the association, accepting the debit authorisation. Replaces any previous account. The number is never returned.
         * @param {MemberPortalBillingAchHandlersApiSetMyBankAccountRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setMyBankAccount(requestParameters: MemberPortalBillingAchHandlersApiSetMyBankAccountRequest, options?: RawAxiosRequestConfig): AxiosPromise<CounterpartyBankAccountReturn> {
            return localVarFp.setMyBankAccount(requestParameters.homeId, requestParameters.homeownerBankAccountUpsert, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Pay the association from the account on file. The bank validates and holds the payment; the association settles it, usually the same day. **Moves nothing until then.**
         * @param {MemberPortalBillingAchHandlersApiSubmitMyAchPaymentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitMyAchPayment(requestParameters: MemberPortalBillingAchHandlersApiSubmitMyAchPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<MyAchPaymentReturn> {
            return localVarFp.submitMyAchPayment(requestParameters.homeId, requestParameters.achPaymentCreate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for listMyAchPayments operation in MemberPortalBillingAchHandlersApi.
 */
export interface MemberPortalBillingAchHandlersApiListMyAchPaymentsRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for myAchAvailability operation in MemberPortalBillingAchHandlersApi.
 */
export interface MemberPortalBillingAchHandlersApiMyAchAvailabilityRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for setMyBankAccount operation in MemberPortalBillingAchHandlersApi.
 */
export interface MemberPortalBillingAchHandlersApiSetMyBankAccountRequest {
    /**
     * One of the member\&#39;s homes; its address goes on the authorisation record
     */
    readonly homeId: string

    readonly homeownerBankAccountUpsert: HomeownerBankAccountUpsert
}

/**
 * Request parameters for submitMyAchPayment operation in MemberPortalBillingAchHandlersApi.
 */
export interface MemberPortalBillingAchHandlersApiSubmitMyAchPaymentRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    readonly achPaymentCreate: AchPaymentCreate
}

/**
 * MemberPortalBillingAchHandlersApi - object-oriented interface
 */
export class MemberPortalBillingAchHandlersApi extends BaseAPI {
    /**
     * 
     * @summary The member\'s own ACH payments for this home, newest first.
     * @param {MemberPortalBillingAchHandlersApiListMyAchPaymentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyAchPayments(requestParameters: MemberPortalBillingAchHandlersApiListMyAchPaymentsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingAchHandlersApiFp(this.configuration).listMyAchPayments(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Whether this home can be paid by bank account, what is on file, and the authorisation wording to show.
     * @param {MemberPortalBillingAchHandlersApiMyAchAvailabilityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public myAchAvailability(requestParameters: MemberPortalBillingAchHandlersApiMyAchAvailabilityRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingAchHandlersApiFp(this.configuration).myAchAvailability(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Remove the member\'s bank account. Payments already submitted are not affected.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public removeMyBankAccount(options?: RawAxiosRequestConfig) {
        return MemberPortalBillingAchHandlersApiFp(this.configuration).removeMyBankAccount(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Save the member\'s bank account for paying the association, accepting the debit authorisation. Replaces any previous account. The number is never returned.
     * @param {MemberPortalBillingAchHandlersApiSetMyBankAccountRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setMyBankAccount(requestParameters: MemberPortalBillingAchHandlersApiSetMyBankAccountRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingAchHandlersApiFp(this.configuration).setMyBankAccount(requestParameters.homeId, requestParameters.homeownerBankAccountUpsert, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Pay the association from the account on file. The bank validates and holds the payment; the association settles it, usually the same day. **Moves nothing until then.**
     * @param {MemberPortalBillingAchHandlersApiSubmitMyAchPaymentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public submitMyAchPayment(requestParameters: MemberPortalBillingAchHandlersApiSubmitMyAchPaymentRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingAchHandlersApiFp(this.configuration).submitMyAchPayment(requestParameters.homeId, requestParameters.achPaymentCreate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalBillingHandlersApi - axios parameter creator
 */
export const MemberPortalBillingHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyBalance: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyBalance', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/balance`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyCoupon: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyCoupon', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/coupon`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {string} [from] Entries on or after this effective date
         * @param {string} [to] Entries on or before this effective date
         * @param {string} [entryType] Comma-separated entry types; omit for all
         * @param {string} [sourceType] assessment_run | payment | violation | manual | aging
         * @param {SortOrder} [order] asc (oldest first, the default) or desc
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyLedger: async (homeId: string, page?: number, perPage?: number, from?: string, to?: string, entryType?: string, sourceType?: string, order?: SortOrder, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyLedger', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/ledger`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (entryType !== undefined) {
                localVarQueryParameter['entry_type'] = entryType;
            }

            if (sourceType !== undefined) {
                localVarQueryParameter['source_type'] = sourceType;
            }

            if (order !== undefined) {
                localVarQueryParameter['order'] = order;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} [from] Period start
         * @param {string} [to] Period end
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyStatement: async (homeId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyStatement', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/statement`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyHomes: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member-portal/billing/homes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyPayments: async (homeId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listMyPayments', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/payments`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        myCouponPdf: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('myCouponPdf', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/coupon/pdf`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} [from] Period start (default: a month back)
         * @param {string} [to] Period end (default: today)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        myStatementPdf: async (homeId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('myStatementPdf', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/billing/homes/{home_id}/statement/pdf`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            localVarHeaderParameter['Accept'] = 'application/pdf';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalBillingHandlersApi - functional programming interface
 */
export const MemberPortalBillingHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalBillingHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyBalance(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BalanceReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyBalance(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.getMyBalance']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyCoupon(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CouponReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyCoupon(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.getMyCoupon']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {string} [from] Entries on or after this effective date
         * @param {string} [to] Entries on or before this effective date
         * @param {string} [entryType] Comma-separated entry types; omit for all
         * @param {string} [sourceType] assessment_run | payment | violation | manual | aging
         * @param {SortOrder} [order] asc (oldest first, the default) or desc
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyLedger(homeId: string, page?: number, perPage?: number, from?: string, to?: string, entryType?: string, sourceType?: string, order?: SortOrder, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LedgerPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyLedger(homeId, page, perPage, from, to, entryType, sourceType, order, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.getMyLedger']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} [from] Period start
         * @param {string} [to] Period end
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyStatement(homeId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingStatementReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyStatement(homeId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.getMyStatement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyHomes(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MyHomesReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyHomes(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.listMyHomes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {number} [page] 1-based page
         * @param {number} [perPage] Max 200, default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyPayments(homeId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MyPaymentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyPayments(homeId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.listMyPayments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async myCouponPdf(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.myCouponPdf(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.myCouponPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} [from] Period start (default: a month back)
         * @param {string} [to] Period end (default: today)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async myStatementPdf(homeId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.myStatementPdf(homeId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalBillingHandlersApi.myStatementPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalBillingHandlersApi - factory interface
 */
export const MemberPortalBillingHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalBillingHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {MemberPortalBillingHandlersApiGetMyBalanceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyBalance(requestParameters: MemberPortalBillingHandlersApiGetMyBalanceRequest, options?: RawAxiosRequestConfig): AxiosPromise<BalanceReturn> {
            return localVarFp.getMyBalance(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalBillingHandlersApiGetMyCouponRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyCoupon(requestParameters: MemberPortalBillingHandlersApiGetMyCouponRequest, options?: RawAxiosRequestConfig): AxiosPromise<CouponReturn> {
            return localVarFp.getMyCoupon(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalBillingHandlersApiGetMyLedgerRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyLedger(requestParameters: MemberPortalBillingHandlersApiGetMyLedgerRequest, options?: RawAxiosRequestConfig): AxiosPromise<LedgerPage> {
            return localVarFp.getMyLedger(requestParameters.homeId, requestParameters.page, requestParameters.perPage, requestParameters.from, requestParameters.to, requestParameters.entryType, requestParameters.sourceType, requestParameters.order, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalBillingHandlersApiGetMyStatementRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyStatement(requestParameters: MemberPortalBillingHandlersApiGetMyStatementRequest, options?: RawAxiosRequestConfig): AxiosPromise<BillingStatementReturn> {
            return localVarFp.getMyStatement(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyHomes(options?: RawAxiosRequestConfig): AxiosPromise<MyHomesReturn> {
            return localVarFp.listMyHomes(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalBillingHandlersApiListMyPaymentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyPayments(requestParameters: MemberPortalBillingHandlersApiListMyPaymentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<MyPaymentList> {
            return localVarFp.listMyPayments(requestParameters.homeId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalBillingHandlersApiMyCouponPdfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        myCouponPdf(requestParameters: MemberPortalBillingHandlersApiMyCouponPdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.myCouponPdf(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalBillingHandlersApiMyStatementPdfRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        myStatementPdf(requestParameters: MemberPortalBillingHandlersApiMyStatementPdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.myStatementPdf(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for getMyBalance operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiGetMyBalanceRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for getMyCoupon operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiGetMyCouponRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for getMyLedger operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiGetMyLedgerRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number

    /**
     * Entries on or after this effective date
     */
    readonly from?: string

    /**
     * Entries on or before this effective date
     */
    readonly to?: string

    /**
     * Comma-separated entry types; omit for all
     */
    readonly entryType?: string

    /**
     * assessment_run | payment | violation | manual | aging
     */
    readonly sourceType?: string

    /**
     * asc (oldest first, the default) or desc
     */
    readonly order?: SortOrder
}

/**
 * Request parameters for getMyStatement operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiGetMyStatementRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Period start
     */
    readonly from?: string

    /**
     * Period end
     */
    readonly to?: string
}

/**
 * Request parameters for listMyPayments operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiListMyPaymentsRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * 1-based page
     */
    readonly page?: number

    /**
     * Max 200, default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for myCouponPdf operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiMyCouponPdfRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for myStatementPdf operation in MemberPortalBillingHandlersApi.
 */
export interface MemberPortalBillingHandlersApiMyStatementPdfRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Period start (default: a month back)
     */
    readonly from?: string

    /**
     * Period end (default: today)
     */
    readonly to?: string
}

/**
 * MemberPortalBillingHandlersApi - object-oriented interface
 */
export class MemberPortalBillingHandlersApi extends BaseAPI {
    /**
     * 
     * @param {MemberPortalBillingHandlersApiGetMyBalanceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyBalance(requestParameters: MemberPortalBillingHandlersApiGetMyBalanceRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).getMyBalance(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalBillingHandlersApiGetMyCouponRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyCoupon(requestParameters: MemberPortalBillingHandlersApiGetMyCouponRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).getMyCoupon(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalBillingHandlersApiGetMyLedgerRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyLedger(requestParameters: MemberPortalBillingHandlersApiGetMyLedgerRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).getMyLedger(requestParameters.homeId, requestParameters.page, requestParameters.perPage, requestParameters.from, requestParameters.to, requestParameters.entryType, requestParameters.sourceType, requestParameters.order, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalBillingHandlersApiGetMyStatementRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyStatement(requestParameters: MemberPortalBillingHandlersApiGetMyStatementRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).getMyStatement(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyHomes(options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).listMyHomes(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalBillingHandlersApiListMyPaymentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyPayments(requestParameters: MemberPortalBillingHandlersApiListMyPaymentsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).listMyPayments(requestParameters.homeId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalBillingHandlersApiMyCouponPdfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public myCouponPdf(requestParameters: MemberPortalBillingHandlersApiMyCouponPdfRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).myCouponPdf(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalBillingHandlersApiMyStatementPdfRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public myStatementPdf(requestParameters: MemberPortalBillingHandlersApiMyStatementPdfRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalBillingHandlersApiFp(this.configuration).myStatementPdf(requestParameters.homeId, requestParameters.from, requestParameters.to, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalCommentHandlersApi - axios parameter creator
 */
export const MemberPortalCommentHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {MemberCommentCreate} memberCommentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyViolationComment: async (memberCommentCreate: MemberCommentCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberCommentCreate' is not null or undefined
            assertParamExists('createMyViolationComment', 'memberCommentCreate', memberCommentCreate)
            const localVarPath = `/api/v1/member-portal/comments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberCommentCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} commentId One of the member\&#39;s own comments
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMyViolationComment: async (commentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('deleteMyViolationComment', 'commentId', commentId)
            const localVarPath = `/api/v1/member-portal/comments/{comment_id}`
                .replace(`{${"comment_id"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} violationId A violation on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyViolationComments: async (violationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('listMyViolationComments', 'violationId', violationId)
            const localVarPath = `/api/v1/member-portal/comments/violation/{violation_id}`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} commentId One of the member\&#39;s own comments
         * @param {MemberCommentUpdate} memberCommentUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyViolationComment: async (commentId: string, memberCommentUpdate: MemberCommentUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('updateMyViolationComment', 'commentId', commentId)
            // verify required parameter 'memberCommentUpdate' is not null or undefined
            assertParamExists('updateMyViolationComment', 'memberCommentUpdate', memberCommentUpdate)
            const localVarPath = `/api/v1/member-portal/comments/{comment_id}`
                .replace(`{${"comment_id"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberCommentUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalCommentHandlersApi - functional programming interface
 */
export const MemberPortalCommentHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalCommentHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {MemberCommentCreate} memberCommentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMyViolationComment(memberCommentCreate: MemberCommentCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMyViolationComment(memberCommentCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalCommentHandlersApi.createMyViolationComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} commentId One of the member\&#39;s own comments
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteMyViolationComment(commentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteMyViolationComment(commentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalCommentHandlersApi.deleteMyViolationComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} violationId A violation on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyViolationComments(violationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyViolationComments(violationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalCommentHandlersApi.listMyViolationComments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} commentId One of the member\&#39;s own comments
         * @param {MemberCommentUpdate} memberCommentUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMyViolationComment(commentId: string, memberCommentUpdate: MemberCommentUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMyViolationComment(commentId, memberCommentUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalCommentHandlersApi.updateMyViolationComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalCommentHandlersApi - factory interface
 */
export const MemberPortalCommentHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalCommentHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {MemberPortalCommentHandlersApiCreateMyViolationCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyViolationComment(requestParameters: MemberPortalCommentHandlersApiCreateMyViolationCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentReturn> {
            return localVarFp.createMyViolationComment(requestParameters.memberCommentCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalCommentHandlersApiDeleteMyViolationCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMyViolationComment(requestParameters: MemberPortalCommentHandlersApiDeleteMyViolationCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteMyViolationComment(requestParameters.commentId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalCommentHandlersApiListMyViolationCommentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyViolationComments(requestParameters: MemberPortalCommentHandlersApiListMyViolationCommentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentList> {
            return localVarFp.listMyViolationComments(requestParameters.violationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalCommentHandlersApiUpdateMyViolationCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyViolationComment(requestParameters: MemberPortalCommentHandlersApiUpdateMyViolationCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentReturn> {
            return localVarFp.updateMyViolationComment(requestParameters.commentId, requestParameters.memberCommentUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createMyViolationComment operation in MemberPortalCommentHandlersApi.
 */
export interface MemberPortalCommentHandlersApiCreateMyViolationCommentRequest {
    readonly memberCommentCreate: MemberCommentCreate
}

/**
 * Request parameters for deleteMyViolationComment operation in MemberPortalCommentHandlersApi.
 */
export interface MemberPortalCommentHandlersApiDeleteMyViolationCommentRequest {
    /**
     * One of the member\&#39;s own comments
     */
    readonly commentId: string
}

/**
 * Request parameters for listMyViolationComments operation in MemberPortalCommentHandlersApi.
 */
export interface MemberPortalCommentHandlersApiListMyViolationCommentsRequest {
    /**
     * A violation on one of the member\&#39;s homes
     */
    readonly violationId: string
}

/**
 * Request parameters for updateMyViolationComment operation in MemberPortalCommentHandlersApi.
 */
export interface MemberPortalCommentHandlersApiUpdateMyViolationCommentRequest {
    /**
     * One of the member\&#39;s own comments
     */
    readonly commentId: string

    readonly memberCommentUpdate: MemberCommentUpdate
}

/**
 * MemberPortalCommentHandlersApi - object-oriented interface
 */
export class MemberPortalCommentHandlersApi extends BaseAPI {
    /**
     * 
     * @param {MemberPortalCommentHandlersApiCreateMyViolationCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMyViolationComment(requestParameters: MemberPortalCommentHandlersApiCreateMyViolationCommentRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalCommentHandlersApiFp(this.configuration).createMyViolationComment(requestParameters.memberCommentCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalCommentHandlersApiDeleteMyViolationCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteMyViolationComment(requestParameters: MemberPortalCommentHandlersApiDeleteMyViolationCommentRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalCommentHandlersApiFp(this.configuration).deleteMyViolationComment(requestParameters.commentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalCommentHandlersApiListMyViolationCommentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyViolationComments(requestParameters: MemberPortalCommentHandlersApiListMyViolationCommentsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalCommentHandlersApiFp(this.configuration).listMyViolationComments(requestParameters.violationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalCommentHandlersApiUpdateMyViolationCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMyViolationComment(requestParameters: MemberPortalCommentHandlersApiUpdateMyViolationCommentRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalCommentHandlersApiFp(this.configuration).updateMyViolationComment(requestParameters.commentId, requestParameters.memberCommentUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalFileHandlersApi - axios parameter creator
 */
export const MemberPortalFileHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} fileId A file attached to something the member can see
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        serveMyAttachmentInline: async (fileId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'fileId' is not null or undefined
            assertParamExists('serveMyAttachmentInline', 'fileId', fileId)
            const localVarPath = `/api/v1/member-portal/files/{file_id}/inline`
                .replace(`{${"file_id"}}`, encodeURIComponent(String(fileId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalFileHandlersApi - functional programming interface
 */
export const MemberPortalFileHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalFileHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} fileId A file attached to something the member can see
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async serveMyAttachmentInline(fileId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.serveMyAttachmentInline(fileId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalFileHandlersApi.serveMyAttachmentInline']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalFileHandlersApi - factory interface
 */
export const MemberPortalFileHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalFileHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {MemberPortalFileHandlersApiServeMyAttachmentInlineRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        serveMyAttachmentInline(requestParameters: MemberPortalFileHandlersApiServeMyAttachmentInlineRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.serveMyAttachmentInline(requestParameters.fileId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for serveMyAttachmentInline operation in MemberPortalFileHandlersApi.
 */
export interface MemberPortalFileHandlersApiServeMyAttachmentInlineRequest {
    /**
     * A file attached to something the member can see
     */
    readonly fileId: string
}

/**
 * MemberPortalFileHandlersApi - object-oriented interface
 */
export class MemberPortalFileHandlersApi extends BaseAPI {
    /**
     * 
     * @param {MemberPortalFileHandlersApiServeMyAttachmentInlineRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public serveMyAttachmentInline(requestParameters: MemberPortalFileHandlersApiServeMyAttachmentInlineRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalFileHandlersApiFp(this.configuration).serveMyAttachmentInline(requestParameters.fileId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalNotificationHandlersApi - axios parameter creator
 */
export const MemberPortalNotificationHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {BulkReadStatusUpdate} bulkReadStatusUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkMarkMyNotifications: async (bulkReadStatusUpdate: BulkReadStatusUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bulkReadStatusUpdate' is not null or undefined
            assertParamExists('bulkMarkMyNotifications', 'bulkReadStatusUpdate', bulkReadStatusUpdate)
            const localVarPath = `/api/v1/member-portal/notifications/bulk-read`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkReadStatusUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId One of the member\&#39;s own notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyNotification: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('getMyNotification', 'notificationId', notificationId)
            const localVarPath = `/api/v1/member-portal/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {number | null} [page] 
         * @param {number | null} [perPage] 
         * @param {boolean | null} [unreadOnly] 
         * @param {string | null} [notificationType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyNotifications: async (page?: number | null, perPage?: number | null, unreadOnly?: boolean | null, notificationType?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member-portal/notifications`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            if (unreadOnly !== undefined) {
                localVarQueryParameter['unread_only'] = unreadOnly;
            }

            if (notificationType !== undefined) {
                localVarQueryParameter['notification_type'] = notificationType;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId One of the member\&#39;s own notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markMyNotificationRead: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('markMyNotificationRead', 'notificationId', notificationId)
            const localVarPath = `/api/v1/member-portal/notifications/{notification_id}/read`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId One of the member\&#39;s own notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markMyNotificationUnread: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('markMyNotificationUnread', 'notificationId', notificationId)
            const localVarPath = `/api/v1/member-portal/notifications/{notification_id}/unread`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalNotificationHandlersApi - functional programming interface
 */
export const MemberPortalNotificationHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalNotificationHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {BulkReadStatusUpdate} bulkReadStatusUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkMarkMyNotifications(bulkReadStatusUpdate: BulkReadStatusUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BulkReadStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkMarkMyNotifications(bulkReadStatusUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalNotificationHandlersApi.bulkMarkMyNotifications']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId One of the member\&#39;s own notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyNotification(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyNotification(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalNotificationHandlersApi.getMyNotification']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {number | null} [page] 
         * @param {number | null} [perPage] 
         * @param {boolean | null} [unreadOnly] 
         * @param {string | null} [notificationType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyNotifications(page?: number | null, perPage?: number | null, unreadOnly?: boolean | null, notificationType?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyNotifications(page, perPage, unreadOnly, notificationType, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalNotificationHandlersApi.listMyNotifications']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId One of the member\&#39;s own notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markMyNotificationRead(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markMyNotificationRead(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalNotificationHandlersApi.markMyNotificationRead']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId One of the member\&#39;s own notifications
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markMyNotificationUnread(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markMyNotificationUnread(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalNotificationHandlersApi.markMyNotificationUnread']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalNotificationHandlersApi - factory interface
 */
export const MemberPortalNotificationHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalNotificationHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {MemberPortalNotificationHandlersApiBulkMarkMyNotificationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkMarkMyNotifications(requestParameters: MemberPortalNotificationHandlersApiBulkMarkMyNotificationsRequest, options?: RawAxiosRequestConfig): AxiosPromise<BulkReadStatusResponse> {
            return localVarFp.bulkMarkMyNotifications(requestParameters.bulkReadStatusUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalNotificationHandlersApiGetMyNotificationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyNotification(requestParameters: MemberPortalNotificationHandlersApiGetMyNotificationRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.getMyNotification(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalNotificationHandlersApiListMyNotificationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyNotifications(requestParameters: MemberPortalNotificationHandlersApiListMyNotificationsRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<NotificationList> {
            return localVarFp.listMyNotifications(requestParameters.page, requestParameters.perPage, requestParameters.unreadOnly, requestParameters.notificationType, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalNotificationHandlersApiMarkMyNotificationReadRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markMyNotificationRead(requestParameters: MemberPortalNotificationHandlersApiMarkMyNotificationReadRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.markMyNotificationRead(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalNotificationHandlersApiMarkMyNotificationUnreadRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markMyNotificationUnread(requestParameters: MemberPortalNotificationHandlersApiMarkMyNotificationUnreadRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.markMyNotificationUnread(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for bulkMarkMyNotifications operation in MemberPortalNotificationHandlersApi.
 */
export interface MemberPortalNotificationHandlersApiBulkMarkMyNotificationsRequest {
    readonly bulkReadStatusUpdate: BulkReadStatusUpdate
}

/**
 * Request parameters for getMyNotification operation in MemberPortalNotificationHandlersApi.
 */
export interface MemberPortalNotificationHandlersApiGetMyNotificationRequest {
    /**
     * One of the member\&#39;s own notifications
     */
    readonly notificationId: string
}

/**
 * Request parameters for listMyNotifications operation in MemberPortalNotificationHandlersApi.
 */
export interface MemberPortalNotificationHandlersApiListMyNotificationsRequest {
    readonly page?: number | null

    readonly perPage?: number | null

    readonly unreadOnly?: boolean | null

    readonly notificationType?: string | null
}

/**
 * Request parameters for markMyNotificationRead operation in MemberPortalNotificationHandlersApi.
 */
export interface MemberPortalNotificationHandlersApiMarkMyNotificationReadRequest {
    /**
     * One of the member\&#39;s own notifications
     */
    readonly notificationId: string
}

/**
 * Request parameters for markMyNotificationUnread operation in MemberPortalNotificationHandlersApi.
 */
export interface MemberPortalNotificationHandlersApiMarkMyNotificationUnreadRequest {
    /**
     * One of the member\&#39;s own notifications
     */
    readonly notificationId: string
}

/**
 * MemberPortalNotificationHandlersApi - object-oriented interface
 */
export class MemberPortalNotificationHandlersApi extends BaseAPI {
    /**
     * 
     * @param {MemberPortalNotificationHandlersApiBulkMarkMyNotificationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public bulkMarkMyNotifications(requestParameters: MemberPortalNotificationHandlersApiBulkMarkMyNotificationsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalNotificationHandlersApiFp(this.configuration).bulkMarkMyNotifications(requestParameters.bulkReadStatusUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalNotificationHandlersApiGetMyNotificationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyNotification(requestParameters: MemberPortalNotificationHandlersApiGetMyNotificationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalNotificationHandlersApiFp(this.configuration).getMyNotification(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalNotificationHandlersApiListMyNotificationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyNotifications(requestParameters: MemberPortalNotificationHandlersApiListMyNotificationsRequest = {}, options?: RawAxiosRequestConfig) {
        return MemberPortalNotificationHandlersApiFp(this.configuration).listMyNotifications(requestParameters.page, requestParameters.perPage, requestParameters.unreadOnly, requestParameters.notificationType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalNotificationHandlersApiMarkMyNotificationReadRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markMyNotificationRead(requestParameters: MemberPortalNotificationHandlersApiMarkMyNotificationReadRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalNotificationHandlersApiFp(this.configuration).markMyNotificationRead(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalNotificationHandlersApiMarkMyNotificationUnreadRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markMyNotificationUnread(requestParameters: MemberPortalNotificationHandlersApiMarkMyNotificationUnreadRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalNotificationHandlersApiFp(this.configuration).markMyNotificationUnread(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalProfileHandlersApi - axios parameter creator
 */
export const MemberPortalProfileHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyProfile: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member-portal/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalProfileHandlersApi - functional programming interface
 */
export const MemberPortalProfileHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalProfileHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyProfile(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MyProfileReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyProfile(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalProfileHandlersApi.getMyProfile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalProfileHandlersApi - factory interface
 */
export const MemberPortalProfileHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalProfileHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyProfile(options?: RawAxiosRequestConfig): AxiosPromise<MyProfileReturn> {
            return localVarFp.getMyProfile(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * MemberPortalProfileHandlersApi - object-oriented interface
 */
export class MemberPortalProfileHandlersApi extends BaseAPI {
    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyProfile(options?: RawAxiosRequestConfig) {
        return MemberPortalProfileHandlersApiFp(this.configuration).getMyProfile(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalReservationHandlersApi - axios parameter creator
 */
export const MemberPortalReservationHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} entryId A waitlist entry of one of the member\&#39;s homes
         * @param {MemberWaitlistAccept} memberWaitlistAccept 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptMyWaitlistOffer: async (entryId: string, memberWaitlistAccept: MemberWaitlistAccept, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'entryId' is not null or undefined
            assertParamExists('acceptMyWaitlistOffer', 'entryId', entryId)
            // verify required parameter 'memberWaitlistAccept' is not null or undefined
            assertParamExists('acceptMyWaitlistOffer', 'memberWaitlistAccept', memberWaitlistAccept)
            const localVarPath = `/api/v1/member-portal/waitlist/{entry_id}/accept`
                .replace(`{${"entry_id"}}`, encodeURIComponent(String(entryId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberWaitlistAccept, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationCancel} memberReservationCancel 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelMyReservation: async (reservationId: string, memberReservationCancel: MemberReservationCancel, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('cancelMyReservation', 'reservationId', reservationId)
            // verify required parameter 'memberReservationCancel' is not null or undefined
            assertParamExists('cancelMyReservation', 'memberReservationCancel', memberReservationCancel)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/cancel`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberReservationCancel, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} groupId Group ID
         * @param {MemberGroupReservationCreate} memberGroupReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyGroupReservation: async (homeId: string, groupId: string, memberGroupReservationCreate: MemberGroupReservationCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('createMyGroupReservation', 'homeId', homeId)
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('createMyGroupReservation', 'groupId', groupId)
            // verify required parameter 'memberGroupReservationCreate' is not null or undefined
            assertParamExists('createMyGroupReservation', 'memberGroupReservationCreate', memberGroupReservationCreate)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenity-groups/{group_id}/reservations`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)))
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberGroupReservationCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {MemberReservationCreate} memberReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyReservation: async (homeId: string, memberReservationCreate: MemberReservationCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('createMyReservation', 'homeId', homeId)
            // verify required parameter 'memberReservationCreate' is not null or undefined
            assertParamExists('createMyReservation', 'memberReservationCreate', memberReservationCreate)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/reservations`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberReservationCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationCommentCreate} memberReservationCommentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyReservationComment: async (reservationId: string, memberReservationCommentCreate: MemberReservationCommentCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('createMyReservationComment', 'reservationId', reservationId)
            // verify required parameter 'memberReservationCommentCreate' is not null or undefined
            assertParamExists('createMyReservationComment', 'memberReservationCommentCreate', memberReservationCommentCreate)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/comments`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberReservationCommentCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} entryId A waitlist entry of one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        declineMyWaitlistOffer: async (entryId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'entryId' is not null or undefined
            assertParamExists('declineMyWaitlistOffer', 'entryId', entryId)
            const localVarPath = `/api/v1/member-portal/waitlist/{entry_id}/decline`
                .replace(`{${"entry_id"}}`, encodeURIComponent(String(entryId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAmenity: async (homeId: string, amenityId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyAmenity', 'homeId', homeId)
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('getMyAmenity', 'amenityId', amenityId)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenities/{amenity_id}`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)))
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} amenityId Amenity ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {string} [to] Overnight amenities: the last night to report
         * @param {string} [excludeReservationId] This home\&#39;s own booking being moved: left out, so its slot shows free. Ignored for any other booking.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAmenityAvailability: async (homeId: string, amenityId: string, date: string, to?: string, excludeReservationId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyAmenityAvailability', 'homeId', homeId)
            // verify required parameter 'amenityId' is not null or undefined
            assertParamExists('getMyAmenityAvailability', 'amenityId', amenityId)
            // verify required parameter 'date' is not null or undefined
            assertParamExists('getMyAmenityAvailability', 'date', date)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenities/{amenity_id}/availability`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)))
                .replace(`{${"amenity_id"}}`, encodeURIComponent(String(amenityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (date !== undefined) {
                localVarQueryParameter['date'] = date;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (excludeReservationId !== undefined) {
                localVarQueryParameter['exclude_reservation_id'] = excludeReservationId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} groupId Group ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAmenityGroupAvailability: async (homeId: string, groupId: string, date: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getMyAmenityGroupAvailability', 'homeId', homeId)
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('getMyAmenityGroupAvailability', 'groupId', groupId)
            // verify required parameter 'date' is not null or undefined
            assertParamExists('getMyAmenityGroupAvailability', 'date', date)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenity-groups/{group_id}/availability`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)))
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (date !== undefined) {
                localVarQueryParameter['date'] = date;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyReservation: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('getMyReservation', 'reservationId', reservationId)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyReservationCalendar: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('getMyReservationCalendar', 'reservationId', reservationId)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/calendar.ics`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'text/calendar';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {MemberWaitlistJoin} memberWaitlistJoin 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        joinMyWaitlist: async (homeId: string, memberWaitlistJoin: MemberWaitlistJoin, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('joinMyWaitlist', 'homeId', homeId)
            // verify required parameter 'memberWaitlistJoin' is not null or undefined
            assertParamExists('joinMyWaitlist', 'memberWaitlistJoin', memberWaitlistJoin)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/waitlist`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberWaitlistJoin, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAmenities: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listMyAmenities', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenities`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAmenityGroups: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listMyAmenityGroups', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenity-groups`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {number} [page] 1-based
         * @param {number} [perPage] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyReservationComments: async (reservationId: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('listMyReservationComments', 'reservationId', reservationId)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/comments`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyReservationDocuments: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('listMyReservationDocuments', 'reservationId', reservationId)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/documents`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} [scope] upcoming (default) or past
         * @param {number} [page] 1-based
         * @param {number} [perPage] Default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyReservations: async (homeId: string, scope?: string, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listMyReservations', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/reservations`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (scope !== undefined) {
                localVarQueryParameter['scope'] = scope;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyWaitlist: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('listMyWaitlist', 'homeId', homeId)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/waitlist`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} groupId Group ID
         * @param {MemberGroupReservationCreate} memberGroupReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewMyGroupReservation: async (homeId: string, groupId: string, memberGroupReservationCreate: MemberGroupReservationCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('previewMyGroupReservation', 'homeId', homeId)
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('previewMyGroupReservation', 'groupId', groupId)
            // verify required parameter 'memberGroupReservationCreate' is not null or undefined
            assertParamExists('previewMyGroupReservation', 'memberGroupReservationCreate', memberGroupReservationCreate)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/amenity-groups/{group_id}/reservations/preview`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)))
                .replace(`{${"group_id"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberGroupReservationCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {MemberReservationCreate} memberReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewMyReservation: async (homeId: string, memberReservationCreate: MemberReservationCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('previewMyReservation', 'homeId', homeId)
            // verify required parameter 'memberReservationCreate' is not null or undefined
            assertParamExists('previewMyReservation', 'memberReservationCreate', memberReservationCreate)
            const localVarPath = `/api/v1/member-portal/homes/{home_id}/reservations/preview`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberReservationCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationReschedule} memberReservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewMyReservationReschedule: async (reservationId: string, memberReservationReschedule: MemberReservationReschedule, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('previewMyReservationReschedule', 'reservationId', reservationId)
            // verify required parameter 'memberReservationReschedule' is not null or undefined
            assertParamExists('previewMyReservationReschedule', 'memberReservationReschedule', memberReservationReschedule)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/reschedule/preview`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberReservationReschedule, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationReschedule} memberReservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rescheduleMyReservation: async (reservationId: string, memberReservationReschedule: MemberReservationReschedule, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('rescheduleMyReservation', 'reservationId', reservationId)
            // verify required parameter 'memberReservationReschedule' is not null or undefined
            assertParamExists('rescheduleMyReservation', 'memberReservationReschedule', memberReservationReschedule)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/reschedule`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberReservationReschedule, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {string} requirementId The requirement this document meets
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string} [expiresOn] The document\&#39;s expiry, YYYY-MM-DD; required when the requirement asks
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadMyReservationDocument: async (reservationId: string, requirementId: string, file: File, expiresOn?: string, description?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('uploadMyReservationDocument', 'reservationId', reservationId)
            // verify required parameter 'requirementId' is not null or undefined
            assertParamExists('uploadMyReservationDocument', 'requirementId', requirementId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadMyReservationDocument', 'file', file)
            const localVarPath = `/api/v1/member-portal/reservations/{reservation_id}/documents`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            if (requirementId !== undefined) {
                localVarQueryParameter['requirement_id'] = requirementId;
            }

            if (expiresOn !== undefined) {
                localVarQueryParameter['expires_on'] = expiresOn;
            }


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} entryId A waitlist entry of one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        withdrawMyWaitlistEntry: async (entryId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'entryId' is not null or undefined
            assertParamExists('withdrawMyWaitlistEntry', 'entryId', entryId)
            const localVarPath = `/api/v1/member-portal/waitlist/{entry_id}/withdraw`
                .replace(`{${"entry_id"}}`, encodeURIComponent(String(entryId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalReservationHandlersApi - functional programming interface
 */
export const MemberPortalReservationHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalReservationHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} entryId A waitlist entry of one of the member\&#39;s homes
         * @param {MemberWaitlistAccept} memberWaitlistAccept 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptMyWaitlistOffer(entryId: string, memberWaitlistAccept: MemberWaitlistAccept, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptMyWaitlistOffer(entryId, memberWaitlistAccept, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.acceptMyWaitlistOffer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationCancel} memberReservationCancel 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelMyReservation(reservationId: string, memberReservationCancel: MemberReservationCancel, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelMyReservation(reservationId, memberReservationCancel, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.cancelMyReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} groupId Group ID
         * @param {MemberGroupReservationCreate} memberGroupReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMyGroupReservation(homeId: string, groupId: string, memberGroupReservationCreate: MemberGroupReservationCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMyGroupReservation(homeId, groupId, memberGroupReservationCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.createMyGroupReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {MemberReservationCreate} memberReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMyReservation(homeId: string, memberReservationCreate: MemberReservationCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMyReservation(homeId, memberReservationCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.createMyReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationCommentCreate} memberReservationCommentCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMyReservationComment(reservationId: string, memberReservationCommentCreate: MemberReservationCommentCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMyReservationComment(reservationId, memberReservationCommentCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.createMyReservationComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} entryId A waitlist entry of one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async declineMyWaitlistOffer(entryId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationWaitlistReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.declineMyWaitlistOffer(entryId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.declineMyWaitlistOffer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} amenityId Amenity ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyAmenity(homeId: string, amenityId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyAmenity(homeId, amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.getMyAmenity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} amenityId Amenity ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {string} [to] Overnight amenities: the last night to report
         * @param {string} [excludeReservationId] This home\&#39;s own booking being moved: left out, so its slot shows free. Ignored for any other booking.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyAmenityAvailability(homeId: string, amenityId: string, date: string, to?: string, excludeReservationId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityAvailabilityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyAmenityAvailability(homeId, amenityId, date, to, excludeReservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.getMyAmenityAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} groupId Group ID
         * @param {string} date Local date, YYYY-MM-DD
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyAmenityGroupAvailability(homeId: string, groupId: string, date: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupAvailabilityReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyAmenityGroupAvailability(homeId, groupId, date, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.getMyAmenityGroupAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyReservation(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyReservation(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.getMyReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyReservationCalendar(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<string>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyReservationCalendar(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.getMyReservationCalendar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {MemberWaitlistJoin} memberWaitlistJoin 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async joinMyWaitlist(homeId: string, memberWaitlistJoin: MemberWaitlistJoin, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationWaitlistReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.joinMyWaitlist(homeId, memberWaitlistJoin, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.joinMyWaitlist']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyAmenities(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyAmenities(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.listMyAmenities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyAmenityGroups(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenityGroupList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyAmenityGroups(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.listMyAmenityGroups']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {number} [page] 1-based
         * @param {number} [perPage] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyReservationComments(reservationId: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyReservationComments(reservationId, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.listMyReservationComments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyReservationDocuments(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationDocumentsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyReservationDocuments(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.listMyReservationDocuments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} [scope] upcoming (default) or past
         * @param {number} [page] 1-based
         * @param {number} [perPage] Default 50
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyReservations(homeId: string, scope?: string, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyReservations(homeId, scope, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.listMyReservations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyWaitlist(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationWaitlistList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyWaitlist(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.listMyWaitlist']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {string} groupId Group ID
         * @param {MemberGroupReservationCreate} memberGroupReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewMyGroupReservation(homeId: string, groupId: string, memberGroupReservationCreate: MemberGroupReservationCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationGroupPreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewMyGroupReservation(homeId, groupId, memberGroupReservationCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.previewMyGroupReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId One of the member\&#39;s homes
         * @param {MemberReservationCreate} memberReservationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewMyReservation(homeId: string, memberReservationCreate: MemberReservationCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationPreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewMyReservation(homeId, memberReservationCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.previewMyReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationReschedule} memberReservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewMyReservationReschedule(reservationId: string, memberReservationReschedule: MemberReservationReschedule, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReschedulePreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewMyReservationReschedule(reservationId, memberReservationReschedule, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.previewMyReservationReschedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {MemberReservationReschedule} memberReservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async rescheduleMyReservation(reservationId: string, memberReservationReschedule: MemberReservationReschedule, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.rescheduleMyReservation(reservationId, memberReservationReschedule, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.rescheduleMyReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId A booking on one of the member\&#39;s homes
         * @param {string} requirementId The requirement this document meets
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string} [expiresOn] The document\&#39;s expiry, YYYY-MM-DD; required when the requirement asks
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadMyReservationDocument(reservationId: string, requirementId: string, file: File, expiresOn?: string, description?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationDocumentsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadMyReservationDocument(reservationId, requirementId, file, expiresOn, description, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.uploadMyReservationDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} entryId A waitlist entry of one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async withdrawMyWaitlistEntry(entryId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationWaitlistReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.withdrawMyWaitlistEntry(entryId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalReservationHandlersApi.withdrawMyWaitlistEntry']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalReservationHandlersApi - factory interface
 */
export const MemberPortalReservationHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalReservationHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {MemberPortalReservationHandlersApiAcceptMyWaitlistOfferRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptMyWaitlistOffer(requestParameters: MemberPortalReservationHandlersApiAcceptMyWaitlistOfferRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.acceptMyWaitlistOffer(requestParameters.entryId, requestParameters.memberWaitlistAccept, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiCancelMyReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelMyReservation(requestParameters: MemberPortalReservationHandlersApiCancelMyReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.cancelMyReservation(requestParameters.reservationId, requestParameters.memberReservationCancel, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiCreateMyGroupReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyGroupReservation(requestParameters: MemberPortalReservationHandlersApiCreateMyGroupReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.createMyGroupReservation(requestParameters.homeId, requestParameters.groupId, requestParameters.memberGroupReservationCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiCreateMyReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyReservation(requestParameters: MemberPortalReservationHandlersApiCreateMyReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.createMyReservation(requestParameters.homeId, requestParameters.memberReservationCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiCreateMyReservationCommentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyReservationComment(requestParameters: MemberPortalReservationHandlersApiCreateMyReservationCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentReturn> {
            return localVarFp.createMyReservationComment(requestParameters.reservationId, requestParameters.memberReservationCommentCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiDeclineMyWaitlistOfferRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        declineMyWaitlistOffer(requestParameters: MemberPortalReservationHandlersApiDeclineMyWaitlistOfferRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationWaitlistReturn> {
            return localVarFp.declineMyWaitlistOffer(requestParameters.entryId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiGetMyAmenityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAmenity(requestParameters: MemberPortalReservationHandlersApiGetMyAmenityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityReturn> {
            return localVarFp.getMyAmenity(requestParameters.homeId, requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiGetMyAmenityAvailabilityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAmenityAvailability(requestParameters: MemberPortalReservationHandlersApiGetMyAmenityAvailabilityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityAvailabilityReturn> {
            return localVarFp.getMyAmenityAvailability(requestParameters.homeId, requestParameters.amenityId, requestParameters.date, requestParameters.to, requestParameters.excludeReservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiGetMyAmenityGroupAvailabilityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAmenityGroupAvailability(requestParameters: MemberPortalReservationHandlersApiGetMyAmenityGroupAvailabilityRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupAvailabilityReturn> {
            return localVarFp.getMyAmenityGroupAvailability(requestParameters.homeId, requestParameters.groupId, requestParameters.date, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiGetMyReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyReservation(requestParameters: MemberPortalReservationHandlersApiGetMyReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.getMyReservation(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiGetMyReservationCalendarRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyReservationCalendar(requestParameters: MemberPortalReservationHandlersApiGetMyReservationCalendarRequest, options?: RawAxiosRequestConfig): AxiosPromise<string> {
            return localVarFp.getMyReservationCalendar(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiJoinMyWaitlistRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        joinMyWaitlist(requestParameters: MemberPortalReservationHandlersApiJoinMyWaitlistRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationWaitlistReturn> {
            return localVarFp.joinMyWaitlist(requestParameters.homeId, requestParameters.memberWaitlistJoin, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiListMyAmenitiesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAmenities(requestParameters: MemberPortalReservationHandlersApiListMyAmenitiesRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityList> {
            return localVarFp.listMyAmenities(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiListMyAmenityGroupsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAmenityGroups(requestParameters: MemberPortalReservationHandlersApiListMyAmenityGroupsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenityGroupList> {
            return localVarFp.listMyAmenityGroups(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiListMyReservationCommentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyReservationComments(requestParameters: MemberPortalReservationHandlersApiListMyReservationCommentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<CommentList> {
            return localVarFp.listMyReservationComments(requestParameters.reservationId, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiListMyReservationDocumentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyReservationDocuments(requestParameters: MemberPortalReservationHandlersApiListMyReservationDocumentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationDocumentsReturn> {
            return localVarFp.listMyReservationDocuments(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiListMyReservationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyReservations(requestParameters: MemberPortalReservationHandlersApiListMyReservationsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationList> {
            return localVarFp.listMyReservations(requestParameters.homeId, requestParameters.scope, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiListMyWaitlistRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyWaitlist(requestParameters: MemberPortalReservationHandlersApiListMyWaitlistRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationWaitlistList> {
            return localVarFp.listMyWaitlist(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiPreviewMyGroupReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewMyGroupReservation(requestParameters: MemberPortalReservationHandlersApiPreviewMyGroupReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationGroupPreviewReturn> {
            return localVarFp.previewMyGroupReservation(requestParameters.homeId, requestParameters.groupId, requestParameters.memberGroupReservationCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiPreviewMyReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewMyReservation(requestParameters: MemberPortalReservationHandlersApiPreviewMyReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationPreviewReturn> {
            return localVarFp.previewMyReservation(requestParameters.homeId, requestParameters.memberReservationCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiPreviewMyReservationRescheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewMyReservationReschedule(requestParameters: MemberPortalReservationHandlersApiPreviewMyReservationRescheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReschedulePreviewReturn> {
            return localVarFp.previewMyReservationReschedule(requestParameters.reservationId, requestParameters.memberReservationReschedule, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiRescheduleMyReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rescheduleMyReservation(requestParameters: MemberPortalReservationHandlersApiRescheduleMyReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.rescheduleMyReservation(requestParameters.reservationId, requestParameters.memberReservationReschedule, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiUploadMyReservationDocumentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadMyReservationDocument(requestParameters: MemberPortalReservationHandlersApiUploadMyReservationDocumentRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationDocumentsReturn> {
            return localVarFp.uploadMyReservationDocument(requestParameters.reservationId, requestParameters.requirementId, requestParameters.file, requestParameters.expiresOn, requestParameters.description, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {MemberPortalReservationHandlersApiWithdrawMyWaitlistEntryRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        withdrawMyWaitlistEntry(requestParameters: MemberPortalReservationHandlersApiWithdrawMyWaitlistEntryRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationWaitlistReturn> {
            return localVarFp.withdrawMyWaitlistEntry(requestParameters.entryId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for acceptMyWaitlistOffer operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiAcceptMyWaitlistOfferRequest {
    /**
     * A waitlist entry of one of the member\&#39;s homes
     */
    readonly entryId: string

    readonly memberWaitlistAccept: MemberWaitlistAccept
}

/**
 * Request parameters for cancelMyReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiCancelMyReservationRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string

    readonly memberReservationCancel: MemberReservationCancel
}

/**
 * Request parameters for createMyGroupReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiCreateMyGroupReservationRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Group ID
     */
    readonly groupId: string

    readonly memberGroupReservationCreate: MemberGroupReservationCreate
}

/**
 * Request parameters for createMyReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiCreateMyReservationRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    readonly memberReservationCreate: MemberReservationCreate
}

/**
 * Request parameters for createMyReservationComment operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiCreateMyReservationCommentRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string

    readonly memberReservationCommentCreate: MemberReservationCommentCreate
}

/**
 * Request parameters for declineMyWaitlistOffer operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiDeclineMyWaitlistOfferRequest {
    /**
     * A waitlist entry of one of the member\&#39;s homes
     */
    readonly entryId: string
}

/**
 * Request parameters for getMyAmenity operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiGetMyAmenityRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Amenity ID
     */
    readonly amenityId: string
}

/**
 * Request parameters for getMyAmenityAvailability operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiGetMyAmenityAvailabilityRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Amenity ID
     */
    readonly amenityId: string

    /**
     * Local date, YYYY-MM-DD
     */
    readonly date: string

    /**
     * Overnight amenities: the last night to report
     */
    readonly to?: string

    /**
     * This home\&#39;s own booking being moved: left out, so its slot shows free. Ignored for any other booking.
     */
    readonly excludeReservationId?: string
}

/**
 * Request parameters for getMyAmenityGroupAvailability operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiGetMyAmenityGroupAvailabilityRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Group ID
     */
    readonly groupId: string

    /**
     * Local date, YYYY-MM-DD
     */
    readonly date: string
}

/**
 * Request parameters for getMyReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiGetMyReservationRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string
}

/**
 * Request parameters for getMyReservationCalendar operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiGetMyReservationCalendarRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string
}

/**
 * Request parameters for joinMyWaitlist operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiJoinMyWaitlistRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    readonly memberWaitlistJoin: MemberWaitlistJoin
}

/**
 * Request parameters for listMyAmenities operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiListMyAmenitiesRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for listMyAmenityGroups operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiListMyAmenityGroupsRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for listMyReservationComments operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiListMyReservationCommentsRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string

    /**
     * 1-based
     */
    readonly page?: number

    /**
     * Page size
     */
    readonly perPage?: number
}

/**
 * Request parameters for listMyReservationDocuments operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiListMyReservationDocumentsRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string
}

/**
 * Request parameters for listMyReservations operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiListMyReservationsRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * upcoming (default) or past
     */
    readonly scope?: string

    /**
     * 1-based
     */
    readonly page?: number

    /**
     * Default 50
     */
    readonly perPage?: number
}

/**
 * Request parameters for listMyWaitlist operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiListMyWaitlistRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string
}

/**
 * Request parameters for previewMyGroupReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiPreviewMyGroupReservationRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    /**
     * Group ID
     */
    readonly groupId: string

    readonly memberGroupReservationCreate: MemberGroupReservationCreate
}

/**
 * Request parameters for previewMyReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiPreviewMyReservationRequest {
    /**
     * One of the member\&#39;s homes
     */
    readonly homeId: string

    readonly memberReservationCreate: MemberReservationCreate
}

/**
 * Request parameters for previewMyReservationReschedule operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiPreviewMyReservationRescheduleRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string

    readonly memberReservationReschedule: MemberReservationReschedule
}

/**
 * Request parameters for rescheduleMyReservation operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiRescheduleMyReservationRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string

    readonly memberReservationReschedule: MemberReservationReschedule
}

/**
 * Request parameters for uploadMyReservationDocument operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiUploadMyReservationDocumentRequest {
    /**
     * A booking on one of the member\&#39;s homes
     */
    readonly reservationId: string

    /**
     * The requirement this document meets
     */
    readonly requirementId: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    /**
     * The document\&#39;s expiry, YYYY-MM-DD; required when the requirement asks
     */
    readonly expiresOn?: string

    readonly description?: string | null
}

/**
 * Request parameters for withdrawMyWaitlistEntry operation in MemberPortalReservationHandlersApi.
 */
export interface MemberPortalReservationHandlersApiWithdrawMyWaitlistEntryRequest {
    /**
     * A waitlist entry of one of the member\&#39;s homes
     */
    readonly entryId: string
}

/**
 * MemberPortalReservationHandlersApi - object-oriented interface
 */
export class MemberPortalReservationHandlersApi extends BaseAPI {
    /**
     * 
     * @param {MemberPortalReservationHandlersApiAcceptMyWaitlistOfferRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public acceptMyWaitlistOffer(requestParameters: MemberPortalReservationHandlersApiAcceptMyWaitlistOfferRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).acceptMyWaitlistOffer(requestParameters.entryId, requestParameters.memberWaitlistAccept, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiCancelMyReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelMyReservation(requestParameters: MemberPortalReservationHandlersApiCancelMyReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).cancelMyReservation(requestParameters.reservationId, requestParameters.memberReservationCancel, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiCreateMyGroupReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMyGroupReservation(requestParameters: MemberPortalReservationHandlersApiCreateMyGroupReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).createMyGroupReservation(requestParameters.homeId, requestParameters.groupId, requestParameters.memberGroupReservationCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiCreateMyReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMyReservation(requestParameters: MemberPortalReservationHandlersApiCreateMyReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).createMyReservation(requestParameters.homeId, requestParameters.memberReservationCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiCreateMyReservationCommentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMyReservationComment(requestParameters: MemberPortalReservationHandlersApiCreateMyReservationCommentRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).createMyReservationComment(requestParameters.reservationId, requestParameters.memberReservationCommentCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiDeclineMyWaitlistOfferRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public declineMyWaitlistOffer(requestParameters: MemberPortalReservationHandlersApiDeclineMyWaitlistOfferRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).declineMyWaitlistOffer(requestParameters.entryId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiGetMyAmenityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyAmenity(requestParameters: MemberPortalReservationHandlersApiGetMyAmenityRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).getMyAmenity(requestParameters.homeId, requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiGetMyAmenityAvailabilityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyAmenityAvailability(requestParameters: MemberPortalReservationHandlersApiGetMyAmenityAvailabilityRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).getMyAmenityAvailability(requestParameters.homeId, requestParameters.amenityId, requestParameters.date, requestParameters.to, requestParameters.excludeReservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiGetMyAmenityGroupAvailabilityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyAmenityGroupAvailability(requestParameters: MemberPortalReservationHandlersApiGetMyAmenityGroupAvailabilityRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).getMyAmenityGroupAvailability(requestParameters.homeId, requestParameters.groupId, requestParameters.date, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiGetMyReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyReservation(requestParameters: MemberPortalReservationHandlersApiGetMyReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).getMyReservation(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiGetMyReservationCalendarRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyReservationCalendar(requestParameters: MemberPortalReservationHandlersApiGetMyReservationCalendarRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).getMyReservationCalendar(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiJoinMyWaitlistRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public joinMyWaitlist(requestParameters: MemberPortalReservationHandlersApiJoinMyWaitlistRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).joinMyWaitlist(requestParameters.homeId, requestParameters.memberWaitlistJoin, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiListMyAmenitiesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyAmenities(requestParameters: MemberPortalReservationHandlersApiListMyAmenitiesRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).listMyAmenities(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiListMyAmenityGroupsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyAmenityGroups(requestParameters: MemberPortalReservationHandlersApiListMyAmenityGroupsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).listMyAmenityGroups(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiListMyReservationCommentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyReservationComments(requestParameters: MemberPortalReservationHandlersApiListMyReservationCommentsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).listMyReservationComments(requestParameters.reservationId, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiListMyReservationDocumentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyReservationDocuments(requestParameters: MemberPortalReservationHandlersApiListMyReservationDocumentsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).listMyReservationDocuments(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiListMyReservationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyReservations(requestParameters: MemberPortalReservationHandlersApiListMyReservationsRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).listMyReservations(requestParameters.homeId, requestParameters.scope, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiListMyWaitlistRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyWaitlist(requestParameters: MemberPortalReservationHandlersApiListMyWaitlistRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).listMyWaitlist(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiPreviewMyGroupReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewMyGroupReservation(requestParameters: MemberPortalReservationHandlersApiPreviewMyGroupReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).previewMyGroupReservation(requestParameters.homeId, requestParameters.groupId, requestParameters.memberGroupReservationCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiPreviewMyReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewMyReservation(requestParameters: MemberPortalReservationHandlersApiPreviewMyReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).previewMyReservation(requestParameters.homeId, requestParameters.memberReservationCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiPreviewMyReservationRescheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewMyReservationReschedule(requestParameters: MemberPortalReservationHandlersApiPreviewMyReservationRescheduleRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).previewMyReservationReschedule(requestParameters.reservationId, requestParameters.memberReservationReschedule, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiRescheduleMyReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public rescheduleMyReservation(requestParameters: MemberPortalReservationHandlersApiRescheduleMyReservationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).rescheduleMyReservation(requestParameters.reservationId, requestParameters.memberReservationReschedule, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiUploadMyReservationDocumentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadMyReservationDocument(requestParameters: MemberPortalReservationHandlersApiUploadMyReservationDocumentRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).uploadMyReservationDocument(requestParameters.reservationId, requestParameters.requirementId, requestParameters.file, requestParameters.expiresOn, requestParameters.description, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {MemberPortalReservationHandlersApiWithdrawMyWaitlistEntryRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public withdrawMyWaitlistEntry(requestParameters: MemberPortalReservationHandlersApiWithdrawMyWaitlistEntryRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalReservationHandlersApiFp(this.configuration).withdrawMyWaitlistEntry(requestParameters.entryId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MemberPortalViolationHandlersApi - axios parameter creator
 */
export const MemberPortalViolationHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} violationId A violation on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyViolation: async (violationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('getMyViolation', 'violationId', violationId)
            const localVarPath = `/api/v1/member-portal/violations/{violation_id}`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyViolations: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/member-portal/violations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MemberPortalViolationHandlersApi - functional programming interface
 */
export const MemberPortalViolationHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MemberPortalViolationHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} violationId A violation on one of the member\&#39;s homes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyViolation(violationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyViolation(violationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalViolationHandlersApi.getMyViolation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyViolations(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyViolations(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MemberPortalViolationHandlersApi.listMyViolations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MemberPortalViolationHandlersApi - factory interface
 */
export const MemberPortalViolationHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MemberPortalViolationHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {MemberPortalViolationHandlersApiGetMyViolationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyViolation(requestParameters: MemberPortalViolationHandlersApiGetMyViolationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationReturn> {
            return localVarFp.getMyViolation(requestParameters.violationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyViolations(options?: RawAxiosRequestConfig): AxiosPromise<ViolationList> {
            return localVarFp.listMyViolations(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for getMyViolation operation in MemberPortalViolationHandlersApi.
 */
export interface MemberPortalViolationHandlersApiGetMyViolationRequest {
    /**
     * A violation on one of the member\&#39;s homes
     */
    readonly violationId: string
}

/**
 * MemberPortalViolationHandlersApi - object-oriented interface
 */
export class MemberPortalViolationHandlersApi extends BaseAPI {
    /**
     * 
     * @param {MemberPortalViolationHandlersApiGetMyViolationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyViolation(requestParameters: MemberPortalViolationHandlersApiGetMyViolationRequest, options?: RawAxiosRequestConfig) {
        return MemberPortalViolationHandlersApiFp(this.configuration).getMyViolation(requestParameters.violationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyViolations(options?: RawAxiosRequestConfig) {
        return MemberPortalViolationHandlersApiFp(this.configuration).listMyViolations(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * NotificationHandlersApi - axios parameter creator
 */
export const NotificationHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {BulkReadStatusUpdate} bulkReadStatusUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkUpdateReadStatus: async (bulkReadStatusUpdate: BulkReadStatusUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bulkReadStatusUpdate' is not null or undefined
            assertParamExists('bulkUpdateReadStatus', 'bulkReadStatusUpdate', bulkReadStatusUpdate)
            const localVarPath = `/api/v1/notifications/bulk-read`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkReadStatusUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteNotification: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('deleteNotification', 'notificationId', notificationId)
            const localVarPath = `/api/v1/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getNotificationById: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('getNotificationById', 'notificationId', notificationId)
            const localVarPath = `/api/v1/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {number} [page] Page number (1-based)
         * @param {number} [perPage] Items per page
         * @param {boolean} [unreadOnly] Filter to unread only
         * @param {string} [hoaId] Filter by HOA ID
         * @param {string} [notificationType] Filter by notification type
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getNotifications: async (page?: number, perPage?: number, unreadOnly?: boolean, hoaId?: string, notificationType?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/notifications`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            if (unreadOnly !== undefined) {
                localVarQueryParameter['unread_only'] = unreadOnly;
            }

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            if (notificationType !== undefined) {
                localVarQueryParameter['notification_type'] = notificationType;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markAsRead: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('markAsRead', 'notificationId', notificationId)
            const localVarPath = `/api/v1/notifications/{notification_id}/read`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markAsUnread: async (notificationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('markAsUnread', 'notificationId', notificationId)
            const localVarPath = `/api/v1/notifications/{notification_id}/unread`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {BulkMemberMessageRequest} bulkMemberMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendBulkMemberMessages: async (bulkMemberMessageRequest: BulkMemberMessageRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bulkMemberMessageRequest' is not null or undefined
            assertParamExists('sendBulkMemberMessages', 'bulkMemberMessageRequest', bulkMemberMessageRequest)
            const localVarPath = `/api/v1/notifications/bulk-message`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkMemberMessageRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {HoaMessageRequest} hoaMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendHoaMessage: async (hoaMessageRequest: HoaMessageRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaMessageRequest' is not null or undefined
            assertParamExists('sendHoaMessage', 'hoaMessageRequest', hoaMessageRequest)
            const localVarPath = `/api/v1/notifications/hoa-message`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(hoaMessageRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {MemberMessageRequest} memberMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendMemberMessage: async (memberMessageRequest: MemberMessageRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'memberMessageRequest' is not null or undefined
            assertParamExists('sendMemberMessage', 'memberMessageRequest', memberMessageRequest)
            const localVarPath = `/api/v1/notifications/member-message`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(memberMessageRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * NotificationHandlersApi - functional programming interface
 */
export const NotificationHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = NotificationHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {BulkReadStatusUpdate} bulkReadStatusUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkUpdateReadStatus(bulkReadStatusUpdate: BulkReadStatusUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BulkReadStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkUpdateReadStatus(bulkReadStatusUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.bulkUpdateReadStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteNotification(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteNotification(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.deleteNotification']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getNotificationById(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getNotificationById(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.getNotificationById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {number} [page] Page number (1-based)
         * @param {number} [perPage] Items per page
         * @param {boolean} [unreadOnly] Filter to unread only
         * @param {string} [hoaId] Filter by HOA ID
         * @param {string} [notificationType] Filter by notification type
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getNotifications(page?: number, perPage?: number, unreadOnly?: boolean, hoaId?: string, notificationType?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getNotifications(page, perPage, unreadOnly, hoaId, notificationType, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.getNotifications']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markAsRead(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markAsRead(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.markAsRead']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} notificationId Notification ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markAsUnread(notificationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markAsUnread(notificationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.markAsUnread']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {BulkMemberMessageRequest} bulkMemberMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendBulkMemberMessages(bulkMemberMessageRequest: BulkMemberMessageRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BulkMessageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendBulkMemberMessages(bulkMemberMessageRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.sendBulkMemberMessages']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {HoaMessageRequest} hoaMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendHoaMessage(hoaMessageRequest: HoaMessageRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BulkMessageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendHoaMessage(hoaMessageRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.sendHoaMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {MemberMessageRequest} memberMessageRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendMemberMessage(memberMessageRequest: MemberMessageRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendMemberMessage(memberMessageRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NotificationHandlersApi.sendMemberMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * NotificationHandlersApi - factory interface
 */
export const NotificationHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = NotificationHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {NotificationHandlersApiBulkUpdateReadStatusRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkUpdateReadStatus(requestParameters: NotificationHandlersApiBulkUpdateReadStatusRequest, options?: RawAxiosRequestConfig): AxiosPromise<BulkReadStatusResponse> {
            return localVarFp.bulkUpdateReadStatus(requestParameters.bulkReadStatusUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiDeleteNotificationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteNotification(requestParameters: NotificationHandlersApiDeleteNotificationRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteNotification(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiGetNotificationByIdRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getNotificationById(requestParameters: NotificationHandlersApiGetNotificationByIdRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.getNotificationById(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiGetNotificationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getNotifications(requestParameters: NotificationHandlersApiGetNotificationsRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<NotificationList> {
            return localVarFp.getNotifications(requestParameters.page, requestParameters.perPage, requestParameters.unreadOnly, requestParameters.hoaId, requestParameters.notificationType, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiMarkAsReadRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markAsRead(requestParameters: NotificationHandlersApiMarkAsReadRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.markAsRead(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiMarkAsUnreadRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markAsUnread(requestParameters: NotificationHandlersApiMarkAsUnreadRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.markAsUnread(requestParameters.notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiSendBulkMemberMessagesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendBulkMemberMessages(requestParameters: NotificationHandlersApiSendBulkMemberMessagesRequest, options?: RawAxiosRequestConfig): AxiosPromise<BulkMessageResponse> {
            return localVarFp.sendBulkMemberMessages(requestParameters.bulkMemberMessageRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiSendHoaMessageRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendHoaMessage(requestParameters: NotificationHandlersApiSendHoaMessageRequest, options?: RawAxiosRequestConfig): AxiosPromise<BulkMessageResponse> {
            return localVarFp.sendHoaMessage(requestParameters.hoaMessageRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {NotificationHandlersApiSendMemberMessageRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendMemberMessage(requestParameters: NotificationHandlersApiSendMemberMessageRequest, options?: RawAxiosRequestConfig): AxiosPromise<NotificationResponse> {
            return localVarFp.sendMemberMessage(requestParameters.memberMessageRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for bulkUpdateReadStatus operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiBulkUpdateReadStatusRequest {
    readonly bulkReadStatusUpdate: BulkReadStatusUpdate
}

/**
 * Request parameters for deleteNotification operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiDeleteNotificationRequest {
    /**
     * Notification ID
     */
    readonly notificationId: string
}

/**
 * Request parameters for getNotificationById operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiGetNotificationByIdRequest {
    /**
     * Notification ID
     */
    readonly notificationId: string
}

/**
 * Request parameters for getNotifications operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiGetNotificationsRequest {
    /**
     * Page number (1-based)
     */
    readonly page?: number

    /**
     * Items per page
     */
    readonly perPage?: number

    /**
     * Filter to unread only
     */
    readonly unreadOnly?: boolean

    /**
     * Filter by HOA ID
     */
    readonly hoaId?: string

    /**
     * Filter by notification type
     */
    readonly notificationType?: string
}

/**
 * Request parameters for markAsRead operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiMarkAsReadRequest {
    /**
     * Notification ID
     */
    readonly notificationId: string
}

/**
 * Request parameters for markAsUnread operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiMarkAsUnreadRequest {
    /**
     * Notification ID
     */
    readonly notificationId: string
}

/**
 * Request parameters for sendBulkMemberMessages operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiSendBulkMemberMessagesRequest {
    readonly bulkMemberMessageRequest: BulkMemberMessageRequest
}

/**
 * Request parameters for sendHoaMessage operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiSendHoaMessageRequest {
    readonly hoaMessageRequest: HoaMessageRequest
}

/**
 * Request parameters for sendMemberMessage operation in NotificationHandlersApi.
 */
export interface NotificationHandlersApiSendMemberMessageRequest {
    readonly memberMessageRequest: MemberMessageRequest
}

/**
 * NotificationHandlersApi - object-oriented interface
 */
export class NotificationHandlersApi extends BaseAPI {
    /**
     * 
     * @param {NotificationHandlersApiBulkUpdateReadStatusRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public bulkUpdateReadStatus(requestParameters: NotificationHandlersApiBulkUpdateReadStatusRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).bulkUpdateReadStatus(requestParameters.bulkReadStatusUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiDeleteNotificationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteNotification(requestParameters: NotificationHandlersApiDeleteNotificationRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).deleteNotification(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiGetNotificationByIdRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getNotificationById(requestParameters: NotificationHandlersApiGetNotificationByIdRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).getNotificationById(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiGetNotificationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getNotifications(requestParameters: NotificationHandlersApiGetNotificationsRequest = {}, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).getNotifications(requestParameters.page, requestParameters.perPage, requestParameters.unreadOnly, requestParameters.hoaId, requestParameters.notificationType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiMarkAsReadRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markAsRead(requestParameters: NotificationHandlersApiMarkAsReadRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).markAsRead(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiMarkAsUnreadRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markAsUnread(requestParameters: NotificationHandlersApiMarkAsUnreadRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).markAsUnread(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiSendBulkMemberMessagesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendBulkMemberMessages(requestParameters: NotificationHandlersApiSendBulkMemberMessagesRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).sendBulkMemberMessages(requestParameters.bulkMemberMessageRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiSendHoaMessageRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendHoaMessage(requestParameters: NotificationHandlersApiSendHoaMessageRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).sendHoaMessage(requestParameters.hoaMessageRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NotificationHandlersApiSendMemberMessageRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendMemberMessage(requestParameters: NotificationHandlersApiSendMemberMessageRequest, options?: RawAxiosRequestConfig) {
        return NotificationHandlersApiFp(this.configuration).sendMemberMessage(requestParameters.memberMessageRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ReservationHandlersApi - axios parameter creator
 */
export const ReservationHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationApprove} reservationApprove 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        approveReservation: async (reservationId: string, reservationApprove: ReservationApprove, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('approveReservation', 'reservationId', reservationId)
            // verify required parameter 'reservationApprove' is not null or undefined
            assertParamExists('approveReservation', 'reservationApprove', reservationApprove)
            const localVarPath = `/api/v1/reservations/{reservation_id}/approve`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationApprove, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationCancel} reservationCancel 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelReservation: async (reservationId: string, reservationCancel: ReservationCancel, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('cancelReservation', 'reservationId', reservationId)
            // verify required parameter 'reservationCancel' is not null or undefined
            assertParamExists('cancelReservation', 'reservationCancel', reservationCancel)
            const localVarPath = `/api/v1/reservations/{reservation_id}/cancel`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationCancel, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} seriesId Series ID
         * @param {ReservationSeriesCancel} reservationSeriesCancel 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelReservationSeries: async (seriesId: string, reservationSeriesCancel: ReservationSeriesCancel, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'seriesId' is not null or undefined
            assertParamExists('cancelReservationSeries', 'seriesId', seriesId)
            // verify required parameter 'reservationSeriesCancel' is not null or undefined
            assertParamExists('cancelReservationSeries', 'reservationSeriesCancel', reservationSeriesCancel)
            const localVarPath = `/api/v1/reservations/series/{series_id}/cancel`
                .replace(`{${"series_id"}}`, encodeURIComponent(String(seriesId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationSeriesCancel, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {ReservationStaffCreate} reservationStaffCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReservation: async (reservationStaffCreate: ReservationStaffCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationStaffCreate' is not null or undefined
            assertParamExists('createReservation', 'reservationStaffCreate', reservationStaffCreate)
            const localVarPath = `/api/v1/reservations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationStaffCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {ReservationSeriesCreate} reservationSeriesCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReservationSeries: async (reservationSeriesCreate: ReservationSeriesCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationSeriesCreate' is not null or undefined
            assertParamExists('createReservationSeries', 'reservationSeriesCreate', reservationSeriesCreate)
            const localVarPath = `/api/v1/reservations/series`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationSeriesCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationDeny} reservationDeny 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        denyReservation: async (reservationId: string, reservationDeny: ReservationDeny, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('denyReservation', 'reservationId', reservationId)
            // verify required parameter 'reservationDeny' is not null or undefined
            assertParamExists('denyReservation', 'reservationDeny', reservationDeny)
            const localVarPath = `/api/v1/reservations/{reservation_id}/deny`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationDeny, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservation: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('getReservation', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationCalendar: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('getReservationCalendar', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}/calendar.ics`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'text/calendar';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationPolicy: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getReservationPolicy', 'hoaId', hoaId)
            const localVarPath = `/api/v1/reservations/policy/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} seriesId Series ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationSeries: async (seriesId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'seriesId' is not null or undefined
            assertParamExists('getReservationSeries', 'seriesId', seriesId)
            const localVarPath = `/api/v1/reservations/series/{series_id}`
                .replace(`{${"series_id"}}`, encodeURIComponent(String(seriesId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {string} from Local date, inclusive
         * @param {string} to Local date, inclusive; at most 366 days after from
         * @param {string} [amenityId] One amenity
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationUtilization: async (hoaId: string, from: string, to: string, amenityId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getReservationUtilization', 'hoaId', hoaId)
            // verify required parameter 'from' is not null or undefined
            assertParamExists('getReservationUtilization', 'from', from)
            // verify required parameter 'to' is not null or undefined
            assertParamExists('getReservationUtilization', 'to', to)
            const localVarPath = `/api/v1/reservations/reports/utilization`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (amenityId !== undefined) {
                localVarQueryParameter['amenity_id'] = amenityId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservationEvents: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('listReservationEvents', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}/events`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} [hoaId] One association; omitted means every association the caller can see
         * @param {string} [amenityId] One amenity
         * @param {string} [homeId] One home
         * @param {string} [status] Comma-separated: pending,approved,denied,cancelled,expired,completed
         * @param {string} [from] Local date, inclusive
         * @param {string} [to] Local date, inclusive
         * @param {boolean} [newestFirst] Latest start first (default: soonest first)
         * @param {number} [page] 1-based
         * @param {number} [perPage] Default 50, max 200
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservations: async (hoaId?: string, amenityId?: string, homeId?: string, status?: string, from?: string, to?: string, newestFirst?: boolean, page?: number, perPage?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/reservations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            if (amenityId !== undefined) {
                localVarQueryParameter['amenity_id'] = amenityId;
            }

            if (homeId !== undefined) {
                localVarQueryParameter['home_id'] = homeId;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (newestFirst !== undefined) {
                localVarQueryParameter['newest_first'] = newestFirst;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (perPage !== undefined) {
                localVarQueryParameter['per_page'] = perPage;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationNoShow} reservationNoShow 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markReservationNoShow: async (reservationId: string, reservationNoShow: ReservationNoShow, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('markReservationNoShow', 'reservationId', reservationId)
            // verify required parameter 'reservationNoShow' is not null or undefined
            assertParamExists('markReservationNoShow', 'reservationNoShow', reservationNoShow)
            const localVarPath = `/api/v1/reservations/{reservation_id}/no-show`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationNoShow, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationPatch} reservationPatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchReservation: async (reservationId: string, reservationPatch: ReservationPatch, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('patchReservation', 'reservationId', reservationId)
            // verify required parameter 'reservationPatch' is not null or undefined
            assertParamExists('patchReservation', 'reservationPatch', reservationPatch)
            const localVarPath = `/api/v1/reservations/{reservation_id}`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationPatch, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {ReservationStaffCreate} reservationStaffCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReservation: async (reservationStaffCreate: ReservationStaffCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationStaffCreate' is not null or undefined
            assertParamExists('previewReservation', 'reservationStaffCreate', reservationStaffCreate)
            const localVarPath = `/api/v1/reservations/preview`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationStaffCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationReschedule} reservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReservationReschedule: async (reservationId: string, reservationReschedule: ReservationReschedule, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('previewReservationReschedule', 'reservationId', reservationId)
            // verify required parameter 'reservationReschedule' is not null or undefined
            assertParamExists('previewReservationReschedule', 'reservationReschedule', reservationReschedule)
            const localVarPath = `/api/v1/reservations/{reservation_id}/reschedule/preview`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationReschedule, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} seriesId Series ID
         * @param {ReservationSeriesCancelPreview} reservationSeriesCancelPreview 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReservationSeriesCancel: async (seriesId: string, reservationSeriesCancelPreview: ReservationSeriesCancelPreview, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'seriesId' is not null or undefined
            assertParamExists('previewReservationSeriesCancel', 'seriesId', seriesId)
            // verify required parameter 'reservationSeriesCancelPreview' is not null or undefined
            assertParamExists('previewReservationSeriesCancel', 'reservationSeriesCancelPreview', reservationSeriesCancelPreview)
            const localVarPath = `/api/v1/reservations/series/{series_id}/cancel/preview`
                .replace(`{${"series_id"}}`, encodeURIComponent(String(seriesId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationSeriesCancelPreview, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationReschedule} reservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rescheduleReservation: async (reservationId: string, reservationReschedule: ReservationReschedule, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('rescheduleReservation', 'reservationId', reservationId)
            // verify required parameter 'reservationReschedule' is not null or undefined
            assertParamExists('rescheduleReservation', 'reservationReschedule', reservationReschedule)
            const localVarPath = `/api/v1/reservations/{reservation_id}/reschedule`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationReschedule, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {ReservationPolicyUpdate} reservationPolicyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateReservationPolicy: async (hoaId: string, reservationPolicyUpdate: ReservationPolicyUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('updateReservationPolicy', 'hoaId', hoaId)
            // verify required parameter 'reservationPolicyUpdate' is not null or undefined
            assertParamExists('updateReservationPolicy', 'reservationPolicyUpdate', reservationPolicyUpdate)
            const localVarPath = `/api/v1/reservations/policy/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationPolicyUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ReservationHandlersApi - functional programming interface
 */
export const ReservationHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ReservationHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationApprove} reservationApprove 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async approveReservation(reservationId: string, reservationApprove: ReservationApprove, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.approveReservation(reservationId, reservationApprove, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.approveReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationCancel} reservationCancel 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelReservation(reservationId: string, reservationCancel: ReservationCancel, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelReservation(reservationId, reservationCancel, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.cancelReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} seriesId Series ID
         * @param {ReservationSeriesCancel} reservationSeriesCancel 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelReservationSeries(seriesId: string, reservationSeriesCancel: ReservationSeriesCancel, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationSeriesDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelReservationSeries(seriesId, reservationSeriesCancel, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.cancelReservationSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {ReservationStaffCreate} reservationStaffCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createReservation(reservationStaffCreate: ReservationStaffCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createReservation(reservationStaffCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.createReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {ReservationSeriesCreate} reservationSeriesCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createReservationSeries(reservationSeriesCreate: ReservationSeriesCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationSeriesResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createReservationSeries(reservationSeriesCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.createReservationSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationDeny} reservationDeny 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async denyReservation(reservationId: string, reservationDeny: ReservationDeny, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.denyReservation(reservationId, reservationDeny, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.denyReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReservation(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReservation(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.getReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReservationCalendar(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<string>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReservationCalendar(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.getReservationCalendar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReservationPolicy(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationPolicyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReservationPolicy(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.getReservationPolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} seriesId Series ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReservationSeries(seriesId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationSeriesDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReservationSeries(seriesId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.getReservationSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {string} from Local date, inclusive
         * @param {string} to Local date, inclusive; at most 366 days after from
         * @param {string} [amenityId] One amenity
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReservationUtilization(hoaId: string, from: string, to: string, amenityId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationUtilizationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReservationUtilization(hoaId, from, to, amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.getReservationUtilization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReservationEvents(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationEventList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReservationEvents(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.listReservationEvents']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} [hoaId] One association; omitted means every association the caller can see
         * @param {string} [amenityId] One amenity
         * @param {string} [homeId] One home
         * @param {string} [status] Comma-separated: pending,approved,denied,cancelled,expired,completed
         * @param {string} [from] Local date, inclusive
         * @param {string} [to] Local date, inclusive
         * @param {boolean} [newestFirst] Latest start first (default: soonest first)
         * @param {number} [page] 1-based
         * @param {number} [perPage] Default 50, max 200
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReservations(hoaId?: string, amenityId?: string, homeId?: string, status?: string, from?: string, to?: string, newestFirst?: boolean, page?: number, perPage?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReservations(hoaId, amenityId, homeId, status, from, to, newestFirst, page, perPage, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.listReservations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationNoShow} reservationNoShow 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markReservationNoShow(reservationId: string, reservationNoShow: ReservationNoShow, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markReservationNoShow(reservationId, reservationNoShow, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.markReservationNoShow']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationPatch} reservationPatch 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async patchReservation(reservationId: string, reservationPatch: ReservationPatch, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.patchReservation(reservationId, reservationPatch, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.patchReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {ReservationStaffCreate} reservationStaffCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewReservation(reservationStaffCreate: ReservationStaffCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationPreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewReservation(reservationStaffCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.previewReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationReschedule} reservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewReservationReschedule(reservationId: string, reservationReschedule: ReservationReschedule, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReschedulePreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewReservationReschedule(reservationId, reservationReschedule, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.previewReservationReschedule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} seriesId Series ID
         * @param {ReservationSeriesCancelPreview} reservationSeriesCancelPreview 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewReservationSeriesCancel(seriesId: string, reservationSeriesCancelPreview: ReservationSeriesCancelPreview, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationSeriesCancelPreviewReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewReservationSeriesCancel(seriesId, reservationSeriesCancelPreview, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.previewReservationSeriesCancel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationReschedule} reservationReschedule 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async rescheduleReservation(reservationId: string, reservationReschedule: ReservationReschedule, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.rescheduleReservation(reservationId, reservationReschedule, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.rescheduleReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {ReservationPolicyUpdate} reservationPolicyUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateReservationPolicy(hoaId: string, reservationPolicyUpdate: ReservationPolicyUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationPolicyReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateReservationPolicy(hoaId, reservationPolicyUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationHandlersApi.updateReservationPolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ReservationHandlersApi - factory interface
 */
export const ReservationHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ReservationHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {ReservationHandlersApiApproveReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        approveReservation(requestParameters: ReservationHandlersApiApproveReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.approveReservation(requestParameters.reservationId, requestParameters.reservationApprove, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiCancelReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelReservation(requestParameters: ReservationHandlersApiCancelReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.cancelReservation(requestParameters.reservationId, requestParameters.reservationCancel, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiCancelReservationSeriesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelReservationSeries(requestParameters: ReservationHandlersApiCancelReservationSeriesRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationSeriesDetail> {
            return localVarFp.cancelReservationSeries(requestParameters.seriesId, requestParameters.reservationSeriesCancel, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiCreateReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReservation(requestParameters: ReservationHandlersApiCreateReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.createReservation(requestParameters.reservationStaffCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiCreateReservationSeriesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReservationSeries(requestParameters: ReservationHandlersApiCreateReservationSeriesRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationSeriesResult> {
            return localVarFp.createReservationSeries(requestParameters.reservationSeriesCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiDenyReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        denyReservation(requestParameters: ReservationHandlersApiDenyReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.denyReservation(requestParameters.reservationId, requestParameters.reservationDeny, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiGetReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservation(requestParameters: ReservationHandlersApiGetReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.getReservation(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiGetReservationCalendarRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationCalendar(requestParameters: ReservationHandlersApiGetReservationCalendarRequest, options?: RawAxiosRequestConfig): AxiosPromise<string> {
            return localVarFp.getReservationCalendar(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiGetReservationPolicyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationPolicy(requestParameters: ReservationHandlersApiGetReservationPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationPolicyReturn> {
            return localVarFp.getReservationPolicy(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiGetReservationSeriesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationSeries(requestParameters: ReservationHandlersApiGetReservationSeriesRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationSeriesDetail> {
            return localVarFp.getReservationSeries(requestParameters.seriesId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiGetReservationUtilizationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationUtilization(requestParameters: ReservationHandlersApiGetReservationUtilizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationUtilizationReport> {
            return localVarFp.getReservationUtilization(requestParameters.hoaId, requestParameters.from, requestParameters.to, requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiListReservationEventsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservationEvents(requestParameters: ReservationHandlersApiListReservationEventsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationEventList> {
            return localVarFp.listReservationEvents(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiListReservationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservations(requestParameters: ReservationHandlersApiListReservationsRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<ReservationList> {
            return localVarFp.listReservations(requestParameters.hoaId, requestParameters.amenityId, requestParameters.homeId, requestParameters.status, requestParameters.from, requestParameters.to, requestParameters.newestFirst, requestParameters.page, requestParameters.perPage, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiMarkReservationNoShowRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markReservationNoShow(requestParameters: ReservationHandlersApiMarkReservationNoShowRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.markReservationNoShow(requestParameters.reservationId, requestParameters.reservationNoShow, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiPatchReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        patchReservation(requestParameters: ReservationHandlersApiPatchReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.patchReservation(requestParameters.reservationId, requestParameters.reservationPatch, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiPreviewReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReservation(requestParameters: ReservationHandlersApiPreviewReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationPreviewReturn> {
            return localVarFp.previewReservation(requestParameters.reservationStaffCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiPreviewReservationRescheduleRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReservationReschedule(requestParameters: ReservationHandlersApiPreviewReservationRescheduleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReschedulePreviewReturn> {
            return localVarFp.previewReservationReschedule(requestParameters.reservationId, requestParameters.reservationReschedule, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiPreviewReservationSeriesCancelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReservationSeriesCancel(requestParameters: ReservationHandlersApiPreviewReservationSeriesCancelRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationSeriesCancelPreviewReturn> {
            return localVarFp.previewReservationSeriesCancel(requestParameters.seriesId, requestParameters.reservationSeriesCancelPreview, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiRescheduleReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rescheduleReservation(requestParameters: ReservationHandlersApiRescheduleReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.rescheduleReservation(requestParameters.reservationId, requestParameters.reservationReschedule, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationHandlersApiUpdateReservationPolicyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateReservationPolicy(requestParameters: ReservationHandlersApiUpdateReservationPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationPolicyReturn> {
            return localVarFp.updateReservationPolicy(requestParameters.hoaId, requestParameters.reservationPolicyUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for approveReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiApproveReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationApprove: ReservationApprove
}

/**
 * Request parameters for cancelReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiCancelReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationCancel: ReservationCancel
}

/**
 * Request parameters for cancelReservationSeries operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiCancelReservationSeriesRequest {
    /**
     * Series ID
     */
    readonly seriesId: string

    readonly reservationSeriesCancel: ReservationSeriesCancel
}

/**
 * Request parameters for createReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiCreateReservationRequest {
    readonly reservationStaffCreate: ReservationStaffCreate
}

/**
 * Request parameters for createReservationSeries operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiCreateReservationSeriesRequest {
    readonly reservationSeriesCreate: ReservationSeriesCreate
}

/**
 * Request parameters for denyReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiDenyReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationDeny: ReservationDeny
}

/**
 * Request parameters for getReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiGetReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for getReservationCalendar operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiGetReservationCalendarRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for getReservationPolicy operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiGetReservationPolicyRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string
}

/**
 * Request parameters for getReservationSeries operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiGetReservationSeriesRequest {
    /**
     * Series ID
     */
    readonly seriesId: string
}

/**
 * Request parameters for getReservationUtilization operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiGetReservationUtilizationRequest {
    /**
     * The association
     */
    readonly hoaId: string

    /**
     * Local date, inclusive
     */
    readonly from: string

    /**
     * Local date, inclusive; at most 366 days after from
     */
    readonly to: string

    /**
     * One amenity
     */
    readonly amenityId?: string
}

/**
 * Request parameters for listReservationEvents operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiListReservationEventsRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for listReservations operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiListReservationsRequest {
    /**
     * One association; omitted means every association the caller can see
     */
    readonly hoaId?: string

    /**
     * One amenity
     */
    readonly amenityId?: string

    /**
     * One home
     */
    readonly homeId?: string

    /**
     * Comma-separated: pending,approved,denied,cancelled,expired,completed
     */
    readonly status?: string

    /**
     * Local date, inclusive
     */
    readonly from?: string

    /**
     * Local date, inclusive
     */
    readonly to?: string

    /**
     * Latest start first (default: soonest first)
     */
    readonly newestFirst?: boolean

    /**
     * 1-based
     */
    readonly page?: number

    /**
     * Default 50, max 200
     */
    readonly perPage?: number
}

/**
 * Request parameters for markReservationNoShow operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiMarkReservationNoShowRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationNoShow: ReservationNoShow
}

/**
 * Request parameters for patchReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiPatchReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationPatch: ReservationPatch
}

/**
 * Request parameters for previewReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiPreviewReservationRequest {
    readonly reservationStaffCreate: ReservationStaffCreate
}

/**
 * Request parameters for previewReservationReschedule operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiPreviewReservationRescheduleRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationReschedule: ReservationReschedule
}

/**
 * Request parameters for previewReservationSeriesCancel operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiPreviewReservationSeriesCancelRequest {
    /**
     * Series ID
     */
    readonly seriesId: string

    readonly reservationSeriesCancelPreview: ReservationSeriesCancelPreview
}

/**
 * Request parameters for rescheduleReservation operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiRescheduleReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationReschedule: ReservationReschedule
}

/**
 * Request parameters for updateReservationPolicy operation in ReservationHandlersApi.
 */
export interface ReservationHandlersApiUpdateReservationPolicyRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string

    readonly reservationPolicyUpdate: ReservationPolicyUpdate
}

/**
 * ReservationHandlersApi - object-oriented interface
 */
export class ReservationHandlersApi extends BaseAPI {
    /**
     * 
     * @param {ReservationHandlersApiApproveReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public approveReservation(requestParameters: ReservationHandlersApiApproveReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).approveReservation(requestParameters.reservationId, requestParameters.reservationApprove, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiCancelReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelReservation(requestParameters: ReservationHandlersApiCancelReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).cancelReservation(requestParameters.reservationId, requestParameters.reservationCancel, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiCancelReservationSeriesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelReservationSeries(requestParameters: ReservationHandlersApiCancelReservationSeriesRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).cancelReservationSeries(requestParameters.seriesId, requestParameters.reservationSeriesCancel, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiCreateReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createReservation(requestParameters: ReservationHandlersApiCreateReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).createReservation(requestParameters.reservationStaffCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiCreateReservationSeriesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createReservationSeries(requestParameters: ReservationHandlersApiCreateReservationSeriesRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).createReservationSeries(requestParameters.reservationSeriesCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiDenyReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public denyReservation(requestParameters: ReservationHandlersApiDenyReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).denyReservation(requestParameters.reservationId, requestParameters.reservationDeny, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiGetReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReservation(requestParameters: ReservationHandlersApiGetReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).getReservation(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiGetReservationCalendarRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReservationCalendar(requestParameters: ReservationHandlersApiGetReservationCalendarRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).getReservationCalendar(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiGetReservationPolicyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReservationPolicy(requestParameters: ReservationHandlersApiGetReservationPolicyRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).getReservationPolicy(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiGetReservationSeriesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReservationSeries(requestParameters: ReservationHandlersApiGetReservationSeriesRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).getReservationSeries(requestParameters.seriesId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiGetReservationUtilizationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReservationUtilization(requestParameters: ReservationHandlersApiGetReservationUtilizationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).getReservationUtilization(requestParameters.hoaId, requestParameters.from, requestParameters.to, requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiListReservationEventsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReservationEvents(requestParameters: ReservationHandlersApiListReservationEventsRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).listReservationEvents(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiListReservationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReservations(requestParameters: ReservationHandlersApiListReservationsRequest = {}, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).listReservations(requestParameters.hoaId, requestParameters.amenityId, requestParameters.homeId, requestParameters.status, requestParameters.from, requestParameters.to, requestParameters.newestFirst, requestParameters.page, requestParameters.perPage, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiMarkReservationNoShowRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markReservationNoShow(requestParameters: ReservationHandlersApiMarkReservationNoShowRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).markReservationNoShow(requestParameters.reservationId, requestParameters.reservationNoShow, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiPatchReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public patchReservation(requestParameters: ReservationHandlersApiPatchReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).patchReservation(requestParameters.reservationId, requestParameters.reservationPatch, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiPreviewReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewReservation(requestParameters: ReservationHandlersApiPreviewReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).previewReservation(requestParameters.reservationStaffCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiPreviewReservationRescheduleRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewReservationReschedule(requestParameters: ReservationHandlersApiPreviewReservationRescheduleRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).previewReservationReschedule(requestParameters.reservationId, requestParameters.reservationReschedule, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiPreviewReservationSeriesCancelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewReservationSeriesCancel(requestParameters: ReservationHandlersApiPreviewReservationSeriesCancelRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).previewReservationSeriesCancel(requestParameters.seriesId, requestParameters.reservationSeriesCancelPreview, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiRescheduleReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public rescheduleReservation(requestParameters: ReservationHandlersApiRescheduleReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).rescheduleReservation(requestParameters.reservationId, requestParameters.reservationReschedule, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationHandlersApiUpdateReservationPolicyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateReservationPolicy(requestParameters: ReservationHandlersApiUpdateReservationPolicyRequest, options?: RawAxiosRequestConfig) {
        return ReservationHandlersApiFp(this.configuration).updateReservationPolicy(requestParameters.hoaId, requestParameters.reservationPolicyUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ReservationOperationsApi - axios parameter creator
 */
export const ReservationOperationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkInReservation: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('checkInReservation', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}/check-in`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkOutReservation: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('checkOutReservation', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}/check-out`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {AmenitySuspensionCreate} amenitySuspensionCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenitySuspension: async (amenitySuspensionCreate: AmenitySuspensionCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'amenitySuspensionCreate' is not null or undefined
            assertParamExists('createAmenitySuspension', 'amenitySuspensionCreate', amenitySuspensionCreate)
            const localVarPath = `/api/v1/reservations/suspensions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenitySuspensionCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationInspectionCreate} reservationInspectionCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReservationInspection: async (reservationId: string, reservationInspectionCreate: ReservationInspectionCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('createReservationInspection', 'reservationId', reservationId)
            // verify required parameter 'reservationInspectionCreate' is not null or undefined
            assertParamExists('createReservationInspection', 'reservationInspectionCreate', reservationInspectionCreate)
            const localVarPath = `/api/v1/reservations/{reservation_id}/inspections`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationInspectionCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {string} from Local date, inclusive
         * @param {string} to Local date, inclusive; at most 366 days after from
         * @param {string} [amenityId] One amenity
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationRevenue: async (hoaId: string, from: string, to: string, amenityId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getReservationRevenue', 'hoaId', hoaId)
            // verify required parameter 'from' is not null or undefined
            assertParamExists('getReservationRevenue', 'from', from)
            // verify required parameter 'to' is not null or undefined
            assertParamExists('getReservationRevenue', 'to', to)
            const localVarPath = `/api/v1/reservations/reports/revenue`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            if (from !== undefined) {
                localVarQueryParameter['from'] = from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = to;
            }

            if (amenityId !== undefined) {
                localVarQueryParameter['amenity_id'] = amenityId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} suspensionId Suspension ID
         * @param {AmenitySuspensionLift} amenitySuspensionLift 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        liftAmenitySuspension: async (suspensionId: string, amenitySuspensionLift: AmenitySuspensionLift, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suspensionId' is not null or undefined
            assertParamExists('liftAmenitySuspension', 'suspensionId', suspensionId)
            // verify required parameter 'amenitySuspensionLift' is not null or undefined
            assertParamExists('liftAmenitySuspension', 'amenitySuspensionLift', amenitySuspensionLift)
            const localVarPath = `/api/v1/reservations/suspensions/{suspension_id}/lift`
                .replace(`{${"suspension_id"}}`, encodeURIComponent(String(suspensionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(amenitySuspensionLift, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {string} [homeId] One home
         * @param {boolean} [active] Only suspensions in force
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenitySuspensions: async (hoaId: string, homeId?: string, active?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('listAmenitySuspensions', 'hoaId', hoaId)
            const localVarPath = `/api/v1/reservations/suspensions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            if (homeId !== undefined) {
                localVarQueryParameter['home_id'] = homeId;
            }

            if (active !== undefined) {
                localVarQueryParameter['active'] = active;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservationDocuments: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('listReservationDocuments', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}/documents`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservationInspections: async (reservationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('listReservationInspections', 'reservationId', reservationId)
            const localVarPath = `/api/v1/reservations/{reservation_id}/inspections`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationReturnMoney} reservationReturnMoney 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        returnReservationMoney: async (reservationId: string, reservationReturnMoney: ReservationReturnMoney, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('returnReservationMoney', 'reservationId', reservationId)
            // verify required parameter 'reservationReturnMoney' is not null or undefined
            assertParamExists('returnReservationMoney', 'reservationReturnMoney', reservationReturnMoney)
            const localVarPath = `/api/v1/reservations/{reservation_id}/return`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationReturnMoney, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {string} documentId Document ID
         * @param {ReservationDocumentReview} reservationDocumentReview 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reviewReservationDocument: async (reservationId: string, documentId: string, reservationDocumentReview: ReservationDocumentReview, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('reviewReservationDocument', 'reservationId', reservationId)
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('reviewReservationDocument', 'documentId', documentId)
            // verify required parameter 'reservationDocumentReview' is not null or undefined
            assertParamExists('reviewReservationDocument', 'reservationDocumentReview', reservationDocumentReview)
            const localVarPath = `/api/v1/reservations/{reservation_id}/documents/{document_id}/review`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)))
                .replace(`{${"document_id"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationDocumentReview, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationDepositSettle} reservationDepositSettle 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        settleReservationDeposit: async (reservationId: string, reservationDepositSettle: ReservationDepositSettle, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('settleReservationDeposit', 'reservationId', reservationId)
            // verify required parameter 'reservationDepositSettle' is not null or undefined
            assertParamExists('settleReservationDeposit', 'reservationDepositSettle', reservationDepositSettle)
            const localVarPath = `/api/v1/reservations/{reservation_id}/deposit/settle`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reservationDepositSettle, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {string} requirementId The requirement this document meets
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string} [expiresOn] The document\&#39;s expiry, YYYY-MM-DD; required when the requirement asks
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadReservationDocument: async (reservationId: string, requirementId: string, file: File, expiresOn?: string, description?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('uploadReservationDocument', 'reservationId', reservationId)
            // verify required parameter 'requirementId' is not null or undefined
            assertParamExists('uploadReservationDocument', 'requirementId', requirementId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadReservationDocument', 'file', file)
            const localVarPath = `/api/v1/reservations/{reservation_id}/documents`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            if (requirementId !== undefined) {
                localVarQueryParameter['requirement_id'] = requirementId;
            }

            if (expiresOn !== undefined) {
                localVarQueryParameter['expires_on'] = expiresOn;
            }


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {string} inspectionId Inspection ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadReservationInspectionPhoto: async (reservationId: string, inspectionId: string, file: File, description?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'reservationId' is not null or undefined
            assertParamExists('uploadReservationInspectionPhoto', 'reservationId', reservationId)
            // verify required parameter 'inspectionId' is not null or undefined
            assertParamExists('uploadReservationInspectionPhoto', 'inspectionId', inspectionId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadReservationInspectionPhoto', 'file', file)
            const localVarPath = `/api/v1/reservations/{reservation_id}/inspections/{inspection_id}/photos`
                .replace(`{${"reservation_id"}}`, encodeURIComponent(String(reservationId)))
                .replace(`{${"inspection_id"}}`, encodeURIComponent(String(inspectionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} entryId Waitlist entry ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        withdrawWaitlistEntry: async (entryId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'entryId' is not null or undefined
            assertParamExists('withdrawWaitlistEntry', 'entryId', entryId)
            const localVarPath = `/api/v1/reservations/waitlist/{entry_id}/withdraw`
                .replace(`{${"entry_id"}}`, encodeURIComponent(String(entryId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ReservationOperationsApi - functional programming interface
 */
export const ReservationOperationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ReservationOperationsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkInReservation(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkInReservation(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.checkInReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkOutReservation(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkOutReservation(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.checkOutReservation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {AmenitySuspensionCreate} amenitySuspensionCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAmenitySuspension(amenitySuspensionCreate: AmenitySuspensionCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenitySuspensionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAmenitySuspension(amenitySuspensionCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.createAmenitySuspension']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationInspectionCreate} reservationInspectionCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createReservationInspection(reservationId: string, reservationInspectionCreate: ReservationInspectionCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationInspectionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createReservationInspection(reservationId, reservationInspectionCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.createReservationInspection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {string} from Local date, inclusive
         * @param {string} to Local date, inclusive; at most 366 days after from
         * @param {string} [amenityId] One amenity
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReservationRevenue(hoaId: string, from: string, to: string, amenityId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationRevenueReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReservationRevenue(hoaId, from, to, amenityId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.getReservationRevenue']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} suspensionId Suspension ID
         * @param {AmenitySuspensionLift} amenitySuspensionLift 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async liftAmenitySuspension(suspensionId: string, amenitySuspensionLift: AmenitySuspensionLift, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenitySuspensionReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.liftAmenitySuspension(suspensionId, amenitySuspensionLift, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.liftAmenitySuspension']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId The association
         * @param {string} [homeId] One home
         * @param {boolean} [active] Only suspensions in force
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAmenitySuspensions(hoaId: string, homeId?: string, active?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AmenitySuspensionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAmenitySuspensions(hoaId, homeId, active, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.listAmenitySuspensions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReservationDocuments(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationDocumentsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReservationDocuments(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.listReservationDocuments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReservationInspections(reservationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationInspectionList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReservationInspections(reservationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.listReservationInspections']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationReturnMoney} reservationReturnMoney 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async returnReservationMoney(reservationId: string, reservationReturnMoney: ReservationReturnMoney, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.returnReservationMoney(reservationId, reservationReturnMoney, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.returnReservationMoney']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {string} documentId Document ID
         * @param {ReservationDocumentReview} reservationDocumentReview 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reviewReservationDocument(reservationId: string, documentId: string, reservationDocumentReview: ReservationDocumentReview, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationDocumentsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reviewReservationDocument(reservationId, documentId, reservationDocumentReview, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.reviewReservationDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {ReservationDepositSettle} reservationDepositSettle 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async settleReservationDeposit(reservationId: string, reservationDepositSettle: ReservationDepositSettle, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.settleReservationDeposit(reservationId, reservationDepositSettle, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.settleReservationDeposit']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {string} requirementId The requirement this document meets
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string} [expiresOn] The document\&#39;s expiry, YYYY-MM-DD; required when the requirement asks
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadReservationDocument(reservationId: string, requirementId: string, file: File, expiresOn?: string, description?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationDocumentsReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadReservationDocument(reservationId, requirementId, file, expiresOn, description, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.uploadReservationDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} reservationId Reservation ID
         * @param {string} inspectionId Inspection ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadReservationInspectionPhoto(reservationId: string, inspectionId: string, file: File, description?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadReservationInspectionPhoto(reservationId, inspectionId, file, description, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.uploadReservationInspectionPhoto']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} entryId Waitlist entry ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async withdrawWaitlistEntry(entryId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReservationWaitlistReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.withdrawWaitlistEntry(entryId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReservationOperationsApi.withdrawWaitlistEntry']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ReservationOperationsApi - factory interface
 */
export const ReservationOperationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ReservationOperationsApiFp(configuration)
    return {
        /**
         * 
         * @param {ReservationOperationsApiCheckInReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkInReservation(requestParameters: ReservationOperationsApiCheckInReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.checkInReservation(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiCheckOutReservationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkOutReservation(requestParameters: ReservationOperationsApiCheckOutReservationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.checkOutReservation(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiCreateAmenitySuspensionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAmenitySuspension(requestParameters: ReservationOperationsApiCreateAmenitySuspensionRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenitySuspensionReturn> {
            return localVarFp.createAmenitySuspension(requestParameters.amenitySuspensionCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiCreateReservationInspectionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReservationInspection(requestParameters: ReservationOperationsApiCreateReservationInspectionRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationInspectionReturn> {
            return localVarFp.createReservationInspection(requestParameters.reservationId, requestParameters.reservationInspectionCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiGetReservationRevenueRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReservationRevenue(requestParameters: ReservationOperationsApiGetReservationRevenueRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationRevenueReport> {
            return localVarFp.getReservationRevenue(requestParameters.hoaId, requestParameters.from, requestParameters.to, requestParameters.amenityId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiLiftAmenitySuspensionRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        liftAmenitySuspension(requestParameters: ReservationOperationsApiLiftAmenitySuspensionRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenitySuspensionReturn> {
            return localVarFp.liftAmenitySuspension(requestParameters.suspensionId, requestParameters.amenitySuspensionLift, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiListAmenitySuspensionsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAmenitySuspensions(requestParameters: ReservationOperationsApiListAmenitySuspensionsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AmenitySuspensionList> {
            return localVarFp.listAmenitySuspensions(requestParameters.hoaId, requestParameters.homeId, requestParameters.active, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiListReservationDocumentsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservationDocuments(requestParameters: ReservationOperationsApiListReservationDocumentsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationDocumentsReturn> {
            return localVarFp.listReservationDocuments(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiListReservationInspectionsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReservationInspections(requestParameters: ReservationOperationsApiListReservationInspectionsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationInspectionList> {
            return localVarFp.listReservationInspections(requestParameters.reservationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiReturnReservationMoneyRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        returnReservationMoney(requestParameters: ReservationOperationsApiReturnReservationMoneyRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.returnReservationMoney(requestParameters.reservationId, requestParameters.reservationReturnMoney, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiReviewReservationDocumentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reviewReservationDocument(requestParameters: ReservationOperationsApiReviewReservationDocumentRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationDocumentsReturn> {
            return localVarFp.reviewReservationDocument(requestParameters.reservationId, requestParameters.documentId, requestParameters.reservationDocumentReview, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiSettleReservationDepositRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        settleReservationDeposit(requestParameters: ReservationOperationsApiSettleReservationDepositRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationReturn> {
            return localVarFp.settleReservationDeposit(requestParameters.reservationId, requestParameters.reservationDepositSettle, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiUploadReservationDocumentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadReservationDocument(requestParameters: ReservationOperationsApiUploadReservationDocumentRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationDocumentsReturn> {
            return localVarFp.uploadReservationDocument(requestParameters.reservationId, requestParameters.requirementId, requestParameters.file, requestParameters.expiresOn, requestParameters.description, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiUploadReservationInspectionPhotoRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadReservationInspectionPhoto(requestParameters: ReservationOperationsApiUploadReservationInspectionPhotoRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentReturn> {
            return localVarFp.uploadReservationInspectionPhoto(requestParameters.reservationId, requestParameters.inspectionId, requestParameters.file, requestParameters.description, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ReservationOperationsApiWithdrawWaitlistEntryRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        withdrawWaitlistEntry(requestParameters: ReservationOperationsApiWithdrawWaitlistEntryRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReservationWaitlistReturn> {
            return localVarFp.withdrawWaitlistEntry(requestParameters.entryId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for checkInReservation operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiCheckInReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for checkOutReservation operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiCheckOutReservationRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for createAmenitySuspension operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiCreateAmenitySuspensionRequest {
    readonly amenitySuspensionCreate: AmenitySuspensionCreate
}

/**
 * Request parameters for createReservationInspection operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiCreateReservationInspectionRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationInspectionCreate: ReservationInspectionCreate
}

/**
 * Request parameters for getReservationRevenue operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiGetReservationRevenueRequest {
    /**
     * The association
     */
    readonly hoaId: string

    /**
     * Local date, inclusive
     */
    readonly from: string

    /**
     * Local date, inclusive; at most 366 days after from
     */
    readonly to: string

    /**
     * One amenity
     */
    readonly amenityId?: string
}

/**
 * Request parameters for liftAmenitySuspension operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiLiftAmenitySuspensionRequest {
    /**
     * Suspension ID
     */
    readonly suspensionId: string

    readonly amenitySuspensionLift: AmenitySuspensionLift
}

/**
 * Request parameters for listAmenitySuspensions operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiListAmenitySuspensionsRequest {
    /**
     * The association
     */
    readonly hoaId: string

    /**
     * One home
     */
    readonly homeId?: string

    /**
     * Only suspensions in force
     */
    readonly active?: boolean
}

/**
 * Request parameters for listReservationDocuments operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiListReservationDocumentsRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for listReservationInspections operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiListReservationInspectionsRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string
}

/**
 * Request parameters for returnReservationMoney operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiReturnReservationMoneyRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationReturnMoney: ReservationReturnMoney
}

/**
 * Request parameters for reviewReservationDocument operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiReviewReservationDocumentRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    /**
     * Document ID
     */
    readonly documentId: string

    readonly reservationDocumentReview: ReservationDocumentReview
}

/**
 * Request parameters for settleReservationDeposit operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiSettleReservationDepositRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    readonly reservationDepositSettle: ReservationDepositSettle
}

/**
 * Request parameters for uploadReservationDocument operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiUploadReservationDocumentRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    /**
     * The requirement this document meets
     */
    readonly requirementId: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    /**
     * The document\&#39;s expiry, YYYY-MM-DD; required when the requirement asks
     */
    readonly expiresOn?: string

    readonly description?: string | null
}

/**
 * Request parameters for uploadReservationInspectionPhoto operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiUploadReservationInspectionPhotoRequest {
    /**
     * Reservation ID
     */
    readonly reservationId: string

    /**
     * Inspection ID
     */
    readonly inspectionId: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    readonly description?: string | null
}

/**
 * Request parameters for withdrawWaitlistEntry operation in ReservationOperationsApi.
 */
export interface ReservationOperationsApiWithdrawWaitlistEntryRequest {
    /**
     * Waitlist entry ID
     */
    readonly entryId: string
}

/**
 * ReservationOperationsApi - object-oriented interface
 */
export class ReservationOperationsApi extends BaseAPI {
    /**
     * 
     * @param {ReservationOperationsApiCheckInReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public checkInReservation(requestParameters: ReservationOperationsApiCheckInReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).checkInReservation(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiCheckOutReservationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public checkOutReservation(requestParameters: ReservationOperationsApiCheckOutReservationRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).checkOutReservation(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiCreateAmenitySuspensionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAmenitySuspension(requestParameters: ReservationOperationsApiCreateAmenitySuspensionRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).createAmenitySuspension(requestParameters.amenitySuspensionCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiCreateReservationInspectionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createReservationInspection(requestParameters: ReservationOperationsApiCreateReservationInspectionRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).createReservationInspection(requestParameters.reservationId, requestParameters.reservationInspectionCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiGetReservationRevenueRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReservationRevenue(requestParameters: ReservationOperationsApiGetReservationRevenueRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).getReservationRevenue(requestParameters.hoaId, requestParameters.from, requestParameters.to, requestParameters.amenityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiLiftAmenitySuspensionRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public liftAmenitySuspension(requestParameters: ReservationOperationsApiLiftAmenitySuspensionRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).liftAmenitySuspension(requestParameters.suspensionId, requestParameters.amenitySuspensionLift, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiListAmenitySuspensionsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAmenitySuspensions(requestParameters: ReservationOperationsApiListAmenitySuspensionsRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).listAmenitySuspensions(requestParameters.hoaId, requestParameters.homeId, requestParameters.active, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiListReservationDocumentsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReservationDocuments(requestParameters: ReservationOperationsApiListReservationDocumentsRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).listReservationDocuments(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiListReservationInspectionsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReservationInspections(requestParameters: ReservationOperationsApiListReservationInspectionsRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).listReservationInspections(requestParameters.reservationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiReturnReservationMoneyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public returnReservationMoney(requestParameters: ReservationOperationsApiReturnReservationMoneyRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).returnReservationMoney(requestParameters.reservationId, requestParameters.reservationReturnMoney, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiReviewReservationDocumentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reviewReservationDocument(requestParameters: ReservationOperationsApiReviewReservationDocumentRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).reviewReservationDocument(requestParameters.reservationId, requestParameters.documentId, requestParameters.reservationDocumentReview, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiSettleReservationDepositRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public settleReservationDeposit(requestParameters: ReservationOperationsApiSettleReservationDepositRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).settleReservationDeposit(requestParameters.reservationId, requestParameters.reservationDepositSettle, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiUploadReservationDocumentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadReservationDocument(requestParameters: ReservationOperationsApiUploadReservationDocumentRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).uploadReservationDocument(requestParameters.reservationId, requestParameters.requirementId, requestParameters.file, requestParameters.expiresOn, requestParameters.description, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiUploadReservationInspectionPhotoRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadReservationInspectionPhoto(requestParameters: ReservationOperationsApiUploadReservationInspectionPhotoRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).uploadReservationInspectionPhoto(requestParameters.reservationId, requestParameters.inspectionId, requestParameters.file, requestParameters.description, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ReservationOperationsApiWithdrawWaitlistEntryRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public withdrawWaitlistEntry(requestParameters: ReservationOperationsApiWithdrawWaitlistEntryRequest, options?: RawAxiosRequestConfig) {
        return ReservationOperationsApiFp(this.configuration).withdrawWaitlistEntry(requestParameters.entryId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * RoadmapApi - axios parameter creator
 */
export const RoadmapApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * SuperAdmin-only. Everyone else gets 403, whatever their role.
         * @summary The roadmap snapshot: every GitHub issue, with its lane, status and date.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRoadmap: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/roadmap`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * RoadmapApi - functional programming interface
 */
export const RoadmapApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = RoadmapApiAxiosParamCreator(configuration)
    return {
        /**
         * SuperAdmin-only. Everyone else gets 403, whatever their role.
         * @summary The roadmap snapshot: every GitHub issue, with its lane, status and date.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getRoadmap(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Roadmap>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getRoadmap(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['RoadmapApi.getRoadmap']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * RoadmapApi - factory interface
 */
export const RoadmapApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = RoadmapApiFp(configuration)
    return {
        /**
         * SuperAdmin-only. Everyone else gets 403, whatever their role.
         * @summary The roadmap snapshot: every GitHub issue, with its lane, status and date.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getRoadmap(options?: RawAxiosRequestConfig): AxiosPromise<Roadmap> {
            return localVarFp.getRoadmap(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * RoadmapApi - object-oriented interface
 */
export class RoadmapApi extends BaseAPI {
    /**
     * SuperAdmin-only. Everyone else gets 403, whatever their role.
     * @summary The roadmap snapshot: every GitHub issue, with its lane, status and date.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getRoadmap(options?: RawAxiosRequestConfig) {
        return RoadmapApiFp(this.configuration).getRoadmap(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SecurityApi - axios parameter creator
 */
export const SecurityApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * **Unauthenticated by necessity.** The browser posts this on its own, with no token and from any page the policy covers — including the sign-in page, where there is no session yet. So it is rate limited by address, capped in body size, and writes only to the log.  The endpoint exists so that the Report-Only phase means something. Without somewhere for reports to go, \"roll out in Report-Only first\" amounts to one person clicking through staging with DevTools open, which finds the paths they thought to try and no others. What we actually need to know before enforcing — does some screen nobody remembers load a font from somewhere — only real traffic answers.  Always answers 204, including for a body that does not parse. The browser has nowhere to put an error and will not retry; a 400 here would only be noise in someone\'s console.
         * @summary Receive one CSP violation report.
         * @param {CspReport} cspReport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cspReport: async (cspReport: CspReport, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'cspReport' is not null or undefined
            assertParamExists('cspReport', 'cspReport', cspReport)
            const localVarPath = `/api/v1/security/csp-report`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(cspReport, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SecurityApi - functional programming interface
 */
export const SecurityApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SecurityApiAxiosParamCreator(configuration)
    return {
        /**
         * **Unauthenticated by necessity.** The browser posts this on its own, with no token and from any page the policy covers — including the sign-in page, where there is no session yet. So it is rate limited by address, capped in body size, and writes only to the log.  The endpoint exists so that the Report-Only phase means something. Without somewhere for reports to go, \"roll out in Report-Only first\" amounts to one person clicking through staging with DevTools open, which finds the paths they thought to try and no others. What we actually need to know before enforcing — does some screen nobody remembers load a font from somewhere — only real traffic answers.  Always answers 204, including for a body that does not parse. The browser has nowhere to put an error and will not retry; a 400 here would only be noise in someone\'s console.
         * @summary Receive one CSP violation report.
         * @param {CspReport} cspReport 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cspReport(cspReport: CspReport, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cspReport(cspReport, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.cspReport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SecurityApi - factory interface
 */
export const SecurityApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SecurityApiFp(configuration)
    return {
        /**
         * **Unauthenticated by necessity.** The browser posts this on its own, with no token and from any page the policy covers — including the sign-in page, where there is no session yet. So it is rate limited by address, capped in body size, and writes only to the log.  The endpoint exists so that the Report-Only phase means something. Without somewhere for reports to go, \"roll out in Report-Only first\" amounts to one person clicking through staging with DevTools open, which finds the paths they thought to try and no others. What we actually need to know before enforcing — does some screen nobody remembers load a font from somewhere — only real traffic answers.  Always answers 204, including for a body that does not parse. The browser has nowhere to put an error and will not retry; a 400 here would only be noise in someone\'s console.
         * @summary Receive one CSP violation report.
         * @param {SecurityApiCspReportRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cspReport(requestParameters: SecurityApiCspReportRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.cspReport(requestParameters.cspReport, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for cspReport operation in SecurityApi.
 */
export interface SecurityApiCspReportRequest {
    readonly cspReport: CspReport
}

/**
 * SecurityApi - object-oriented interface
 */
export class SecurityApi extends BaseAPI {
    /**
     * **Unauthenticated by necessity.** The browser posts this on its own, with no token and from any page the policy covers — including the sign-in page, where there is no session yet. So it is rate limited by address, capped in body size, and writes only to the log.  The endpoint exists so that the Report-Only phase means something. Without somewhere for reports to go, \"roll out in Report-Only first\" amounts to one person clicking through staging with DevTools open, which finds the paths they thought to try and no others. What we actually need to know before enforcing — does some screen nobody remembers load a font from somewhere — only real traffic answers.  Always answers 204, including for a body that does not parse. The browser has nowhere to put an error and will not retry; a 400 here would only be noise in someone\'s console.
     * @summary Receive one CSP violation report.
     * @param {SecurityApiCspReportRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cspReport(requestParameters: SecurityApiCspReportRequest, options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).cspReport(requestParameters.cspReport, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * UserHandlersApi - axios parameter creator
 */
export const UserHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * **The lost-device path.** MFA is enforced at WorkOS, so a user whose phone is gone cannot get past the challenge and cannot reach the self-service screen behind it. Somebody with authority has to clear the factor for them, and that is this.  **SuperAdmin only, and deliberately narrower than `revoke_tokens`.** Clearing a second factor lowers the bar to an account, so unlike token revocation — which only ever *ends* access — this one is not delegated to company Owners or Admins. It is also exactly the call an attacker who had compromised an Owner account would want to make.  **Irreversible**, in WorkOS\'s words: the factors are deleted, not disabled, and the user re-enrols from scratch at their next sign-in.
         * @summary Clear a user\'s enrolled MFA factors so they can enrol again.
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        clearMfaFactors: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('clearMfaFactors', 'id', id)
            const localVarPath = `/api/v1/user/{id}/mfa-factors`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The gap this closes: since sign-in moved to WorkOS, `update_user` refuses to write `users.email` for a row with a `workos_user_id`, because writing it directly desynchronises the two and locks the person out. The self-service flow is the supported route — and it is unreachable for the one case that needs fixing most, an address typed in wrongly, because the wrong address is the one they would have to sign in with.  **Delegated to Owners and Admins, unlike MFA reset.** The two were weighed separately rather than shipped as one permission. Clearing a second factor *lowers* the bar to an account and is what an attacker holding a compromised Owner session would reach for, so it stays SuperAdmin-only. Correcting an address does not lower any bar: the new address lands at WorkOS **unverified**, so it still has to be proved by whoever receives mail there before it can sign in.  Two checks, not one. `can_update_user` keeps Owners and Admins inside their own company; `can_modify_by_privilege` stops them repointing an account that outranks theirs — without which an Admin could move an Owner\'s address and wait for the account to come to them.
         * @summary Correct the email address on an account WorkOS owns.
         * @param {string} id User ID
         * @param {UserEmailCorrection} userEmailCorrection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        correctUserEmail: async (id: string, userEmailCorrection: UserEmailCorrection, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('correctUserEmail', 'id', id)
            // verify required parameter 'userEmailCorrection' is not null or undefined
            assertParamExists('correctUserEmail', 'userEmailCorrection', userEmailCorrection)
            const localVarPath = `/api/v1/user/{id}/email`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userEmailCorrection, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {UserCreate} userCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createUser: async (userCreate: UserCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userCreate' is not null or undefined
            assertParamExists('createUser', 'userCreate', userCreate)
            const localVarPath = `/api/v1/user/create`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateUser: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deactivateUser', 'id', id)
            const localVarPath = `/api/v1/user/{id}/deactivate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteUser: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteUser', 'id', id)
            const localVarPath = `/api/v1/user/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUser: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getUser', 'id', id)
            const localVarPath = `/api/v1/user/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The backing list for the user-management screen, which did not exist until that screen needed one.  **Scoped by the row, not by a query parameter.** An Owner or Admin sees their own company and nothing else, regardless of what they ask for — `company_id` is read from `auth_user`, which the extractor re-read from the database, rather than from the request. Only a SuperAdmin sees across companies, and only they may narrow by a company they name.
         * @summary List the users this caller is allowed to see.
         * @param {string} [companyId] SuperAdmin only: narrow to one company. Ignored for anyone else, who always sees their own.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUsers: async (companyId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/user`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (companyId !== undefined) {
                localVarQueryParameter['company_id'] = companyId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeTokens: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('revokeTokens', 'id', id)
            const localVarPath = `/api/v1/user/revoke-tokens/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id User ID
         * @param {UserUpdate} userUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUser: async (id: string, userUpdate: UserUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateUser', 'id', id)
            // verify required parameter 'userUpdate' is not null or undefined
            assertParamExists('updateUser', 'userUpdate', userUpdate)
            const localVarPath = `/api/v1/user/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UserHandlersApi - functional programming interface
 */
export const UserHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = UserHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * **The lost-device path.** MFA is enforced at WorkOS, so a user whose phone is gone cannot get past the challenge and cannot reach the self-service screen behind it. Somebody with authority has to clear the factor for them, and that is this.  **SuperAdmin only, and deliberately narrower than `revoke_tokens`.** Clearing a second factor lowers the bar to an account, so unlike token revocation — which only ever *ends* access — this one is not delegated to company Owners or Admins. It is also exactly the call an attacker who had compromised an Owner account would want to make.  **Irreversible**, in WorkOS\'s words: the factors are deleted, not disabled, and the user re-enrols from scratch at their next sign-in.
         * @summary Clear a user\'s enrolled MFA factors so they can enrol again.
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async clearMfaFactors(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.clearMfaFactors(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.clearMfaFactors']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The gap this closes: since sign-in moved to WorkOS, `update_user` refuses to write `users.email` for a row with a `workos_user_id`, because writing it directly desynchronises the two and locks the person out. The self-service flow is the supported route — and it is unreachable for the one case that needs fixing most, an address typed in wrongly, because the wrong address is the one they would have to sign in with.  **Delegated to Owners and Admins, unlike MFA reset.** The two were weighed separately rather than shipped as one permission. Clearing a second factor *lowers* the bar to an account and is what an attacker holding a compromised Owner session would reach for, so it stays SuperAdmin-only. Correcting an address does not lower any bar: the new address lands at WorkOS **unverified**, so it still has to be proved by whoever receives mail there before it can sign in.  Two checks, not one. `can_update_user` keeps Owners and Admins inside their own company; `can_modify_by_privilege` stops them repointing an account that outranks theirs — without which an Admin could move an Owner\'s address and wait for the account to come to them.
         * @summary Correct the email address on an account WorkOS owns.
         * @param {string} id User ID
         * @param {UserEmailCorrection} userEmailCorrection 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async correctUserEmail(id: string, userEmailCorrection: UserEmailCorrection, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.correctUserEmail(id, userEmailCorrection, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.correctUserEmail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {UserCreate} userCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createUser(userCreate: UserCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createUser(userCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.createUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deactivateUser(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deactivateUser(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.deactivateUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteUser(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteUser(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.deleteUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUser(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUser(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.getUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The backing list for the user-management screen, which did not exist until that screen needed one.  **Scoped by the row, not by a query parameter.** An Owner or Admin sees their own company and nothing else, regardless of what they ask for — `company_id` is read from `auth_user`, which the extractor re-read from the database, rather than from the request. Only a SuperAdmin sees across companies, and only they may narrow by a company they name.
         * @summary List the users this caller is allowed to see.
         * @param {string} [companyId] SuperAdmin only: narrow to one company. Ignored for anyone else, who always sees their own.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listUsers(companyId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<UserReturn>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listUsers(companyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.listUsers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeTokens(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeTokens(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.revokeTokens']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id User ID
         * @param {UserUpdate} userUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateUser(id: string, userUpdate: UserUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateUser(id, userUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserHandlersApi.updateUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * UserHandlersApi - factory interface
 */
export const UserHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = UserHandlersApiFp(configuration)
    return {
        /**
         * **The lost-device path.** MFA is enforced at WorkOS, so a user whose phone is gone cannot get past the challenge and cannot reach the self-service screen behind it. Somebody with authority has to clear the factor for them, and that is this.  **SuperAdmin only, and deliberately narrower than `revoke_tokens`.** Clearing a second factor lowers the bar to an account, so unlike token revocation — which only ever *ends* access — this one is not delegated to company Owners or Admins. It is also exactly the call an attacker who had compromised an Owner account would want to make.  **Irreversible**, in WorkOS\'s words: the factors are deleted, not disabled, and the user re-enrols from scratch at their next sign-in.
         * @summary Clear a user\'s enrolled MFA factors so they can enrol again.
         * @param {UserHandlersApiClearMfaFactorsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        clearMfaFactors(requestParameters: UserHandlersApiClearMfaFactorsRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.clearMfaFactors(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * The gap this closes: since sign-in moved to WorkOS, `update_user` refuses to write `users.email` for a row with a `workos_user_id`, because writing it directly desynchronises the two and locks the person out. The self-service flow is the supported route — and it is unreachable for the one case that needs fixing most, an address typed in wrongly, because the wrong address is the one they would have to sign in with.  **Delegated to Owners and Admins, unlike MFA reset.** The two were weighed separately rather than shipped as one permission. Clearing a second factor *lowers* the bar to an account and is what an attacker holding a compromised Owner session would reach for, so it stays SuperAdmin-only. Correcting an address does not lower any bar: the new address lands at WorkOS **unverified**, so it still has to be proved by whoever receives mail there before it can sign in.  Two checks, not one. `can_update_user` keeps Owners and Admins inside their own company; `can_modify_by_privilege` stops them repointing an account that outranks theirs — without which an Admin could move an Owner\'s address and wait for the account to come to them.
         * @summary Correct the email address on an account WorkOS owns.
         * @param {UserHandlersApiCorrectUserEmailRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        correctUserEmail(requestParameters: UserHandlersApiCorrectUserEmailRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserReturn> {
            return localVarFp.correctUserEmail(requestParameters.id, requestParameters.userEmailCorrection, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UserHandlersApiCreateUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createUser(requestParameters: UserHandlersApiCreateUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserReturn> {
            return localVarFp.createUser(requestParameters.userCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UserHandlersApiDeactivateUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deactivateUser(requestParameters: UserHandlersApiDeactivateUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserReturn> {
            return localVarFp.deactivateUser(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UserHandlersApiDeleteUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteUser(requestParameters: UserHandlersApiDeleteUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteUser(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UserHandlersApiGetUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUser(requestParameters: UserHandlersApiGetUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserReturn> {
            return localVarFp.getUser(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * The backing list for the user-management screen, which did not exist until that screen needed one.  **Scoped by the row, not by a query parameter.** An Owner or Admin sees their own company and nothing else, regardless of what they ask for — `company_id` is read from `auth_user`, which the extractor re-read from the database, rather than from the request. Only a SuperAdmin sees across companies, and only they may narrow by a company they name.
         * @summary List the users this caller is allowed to see.
         * @param {UserHandlersApiListUsersRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUsers(requestParameters: UserHandlersApiListUsersRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<Array<UserReturn>> {
            return localVarFp.listUsers(requestParameters.companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UserHandlersApiRevokeTokensRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeTokens(requestParameters: UserHandlersApiRevokeTokensRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.revokeTokens(requestParameters.id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UserHandlersApiUpdateUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUser(requestParameters: UserHandlersApiUpdateUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserReturn> {
            return localVarFp.updateUser(requestParameters.id, requestParameters.userUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for clearMfaFactors operation in UserHandlersApi.
 */
export interface UserHandlersApiClearMfaFactorsRequest {
    /**
     * User ID
     */
    readonly id: string
}

/**
 * Request parameters for correctUserEmail operation in UserHandlersApi.
 */
export interface UserHandlersApiCorrectUserEmailRequest {
    /**
     * User ID
     */
    readonly id: string

    readonly userEmailCorrection: UserEmailCorrection
}

/**
 * Request parameters for createUser operation in UserHandlersApi.
 */
export interface UserHandlersApiCreateUserRequest {
    readonly userCreate: UserCreate
}

/**
 * Request parameters for deactivateUser operation in UserHandlersApi.
 */
export interface UserHandlersApiDeactivateUserRequest {
    /**
     * User ID
     */
    readonly id: string
}

/**
 * Request parameters for deleteUser operation in UserHandlersApi.
 */
export interface UserHandlersApiDeleteUserRequest {
    /**
     * User ID
     */
    readonly id: string
}

/**
 * Request parameters for getUser operation in UserHandlersApi.
 */
export interface UserHandlersApiGetUserRequest {
    /**
     * User ID
     */
    readonly id: string
}

/**
 * Request parameters for listUsers operation in UserHandlersApi.
 */
export interface UserHandlersApiListUsersRequest {
    /**
     * SuperAdmin only: narrow to one company. Ignored for anyone else, who always sees their own.
     */
    readonly companyId?: string
}

/**
 * Request parameters for revokeTokens operation in UserHandlersApi.
 */
export interface UserHandlersApiRevokeTokensRequest {
    /**
     * User ID
     */
    readonly id: string
}

/**
 * Request parameters for updateUser operation in UserHandlersApi.
 */
export interface UserHandlersApiUpdateUserRequest {
    /**
     * User ID
     */
    readonly id: string

    readonly userUpdate: UserUpdate
}

/**
 * UserHandlersApi - object-oriented interface
 */
export class UserHandlersApi extends BaseAPI {
    /**
     * **The lost-device path.** MFA is enforced at WorkOS, so a user whose phone is gone cannot get past the challenge and cannot reach the self-service screen behind it. Somebody with authority has to clear the factor for them, and that is this.  **SuperAdmin only, and deliberately narrower than `revoke_tokens`.** Clearing a second factor lowers the bar to an account, so unlike token revocation — which only ever *ends* access — this one is not delegated to company Owners or Admins. It is also exactly the call an attacker who had compromised an Owner account would want to make.  **Irreversible**, in WorkOS\'s words: the factors are deleted, not disabled, and the user re-enrols from scratch at their next sign-in.
     * @summary Clear a user\'s enrolled MFA factors so they can enrol again.
     * @param {UserHandlersApiClearMfaFactorsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public clearMfaFactors(requestParameters: UserHandlersApiClearMfaFactorsRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).clearMfaFactors(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The gap this closes: since sign-in moved to WorkOS, `update_user` refuses to write `users.email` for a row with a `workos_user_id`, because writing it directly desynchronises the two and locks the person out. The self-service flow is the supported route — and it is unreachable for the one case that needs fixing most, an address typed in wrongly, because the wrong address is the one they would have to sign in with.  **Delegated to Owners and Admins, unlike MFA reset.** The two were weighed separately rather than shipped as one permission. Clearing a second factor *lowers* the bar to an account and is what an attacker holding a compromised Owner session would reach for, so it stays SuperAdmin-only. Correcting an address does not lower any bar: the new address lands at WorkOS **unverified**, so it still has to be proved by whoever receives mail there before it can sign in.  Two checks, not one. `can_update_user` keeps Owners and Admins inside their own company; `can_modify_by_privilege` stops them repointing an account that outranks theirs — without which an Admin could move an Owner\'s address and wait for the account to come to them.
     * @summary Correct the email address on an account WorkOS owns.
     * @param {UserHandlersApiCorrectUserEmailRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public correctUserEmail(requestParameters: UserHandlersApiCorrectUserEmailRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).correctUserEmail(requestParameters.id, requestParameters.userEmailCorrection, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UserHandlersApiCreateUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createUser(requestParameters: UserHandlersApiCreateUserRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).createUser(requestParameters.userCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UserHandlersApiDeactivateUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deactivateUser(requestParameters: UserHandlersApiDeactivateUserRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).deactivateUser(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UserHandlersApiDeleteUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteUser(requestParameters: UserHandlersApiDeleteUserRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).deleteUser(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UserHandlersApiGetUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUser(requestParameters: UserHandlersApiGetUserRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).getUser(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The backing list for the user-management screen, which did not exist until that screen needed one.  **Scoped by the row, not by a query parameter.** An Owner or Admin sees their own company and nothing else, regardless of what they ask for — `company_id` is read from `auth_user`, which the extractor re-read from the database, rather than from the request. Only a SuperAdmin sees across companies, and only they may narrow by a company they name.
     * @summary List the users this caller is allowed to see.
     * @param {UserHandlersApiListUsersRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listUsers(requestParameters: UserHandlersApiListUsersRequest = {}, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).listUsers(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UserHandlersApiRevokeTokensRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeTokens(requestParameters: UserHandlersApiRevokeTokensRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).revokeTokens(requestParameters.id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UserHandlersApiUpdateUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateUser(requestParameters: UserHandlersApiUpdateUserRequest, options?: RawAxiosRequestConfig) {
        return UserHandlersApiFp(this.configuration).updateUser(requestParameters.id, requestParameters.userUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * VendorHandlersApi - axios parameter creator
 */
export const VendorHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {VendorCreate} vendorCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createVendor: async (vendorCreate: VendorCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'vendorCreate' is not null or undefined
            assertParamExists('createVendor', 'vendorCreate', vendorCreate)
            const localVarPath = `/api/v1/vendors`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(vendorCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} vendorId Vendor ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteVendor: async (vendorId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'vendorId' is not null or undefined
            assertParamExists('deleteVendor', 'vendorId', vendorId)
            const localVarPath = `/api/v1/vendors/{vendor_id}`
                .replace(`{${"vendor_id"}}`, encodeURIComponent(String(vendorId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} vendorId Vendor ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorById: async (vendorId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'vendorId' is not null or undefined
            assertParamExists('getVendorById', 'vendorId', vendorId)
            const localVarPath = `/api/v1/vendors/{vendor_id}`
                .replace(`{${"vendor_id"}}`, encodeURIComponent(String(vendorId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorsByHoa: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getVendorsByHoa', 'hoaId', hoaId)
            const localVarPath = `/api/v1/vendors/hoa/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorsForUser: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/vendors`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} vendorId Vendor ID
         * @param {VendorUpdate} vendorUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateVendor: async (vendorId: string, vendorUpdate: VendorUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'vendorId' is not null or undefined
            assertParamExists('updateVendor', 'vendorId', vendorId)
            // verify required parameter 'vendorUpdate' is not null or undefined
            assertParamExists('updateVendor', 'vendorUpdate', vendorUpdate)
            const localVarPath = `/api/v1/vendors/{vendor_id}`
                .replace(`{${"vendor_id"}}`, encodeURIComponent(String(vendorId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(vendorUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * VendorHandlersApi - functional programming interface
 */
export const VendorHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = VendorHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {VendorCreate} vendorCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createVendor(vendorCreate: VendorCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VendorReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createVendor(vendorCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VendorHandlersApi.createVendor']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} vendorId Vendor ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteVendor(vendorId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteVendor(vendorId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VendorHandlersApi.deleteVendor']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} vendorId Vendor ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVendorById(vendorId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VendorReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVendorById(vendorId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VendorHandlersApi.getVendorById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVendorsByHoa(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VendorList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVendorsByHoa(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VendorHandlersApi.getVendorsByHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVendorsForUser(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VendorList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVendorsForUser(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VendorHandlersApi.getVendorsForUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} vendorId Vendor ID
         * @param {VendorUpdate} vendorUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateVendor(vendorId: string, vendorUpdate: VendorUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VendorReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateVendor(vendorId, vendorUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['VendorHandlersApi.updateVendor']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * VendorHandlersApi - factory interface
 */
export const VendorHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = VendorHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {VendorHandlersApiCreateVendorRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createVendor(requestParameters: VendorHandlersApiCreateVendorRequest, options?: RawAxiosRequestConfig): AxiosPromise<VendorReturn> {
            return localVarFp.createVendor(requestParameters.vendorCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {VendorHandlersApiDeleteVendorRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteVendor(requestParameters: VendorHandlersApiDeleteVendorRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteVendor(requestParameters.vendorId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {VendorHandlersApiGetVendorByIdRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorById(requestParameters: VendorHandlersApiGetVendorByIdRequest, options?: RawAxiosRequestConfig): AxiosPromise<VendorReturn> {
            return localVarFp.getVendorById(requestParameters.vendorId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {VendorHandlersApiGetVendorsByHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorsByHoa(requestParameters: VendorHandlersApiGetVendorsByHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<VendorList> {
            return localVarFp.getVendorsByHoa(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVendorsForUser(options?: RawAxiosRequestConfig): AxiosPromise<VendorList> {
            return localVarFp.getVendorsForUser(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {VendorHandlersApiUpdateVendorRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateVendor(requestParameters: VendorHandlersApiUpdateVendorRequest, options?: RawAxiosRequestConfig): AxiosPromise<VendorReturn> {
            return localVarFp.updateVendor(requestParameters.vendorId, requestParameters.vendorUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createVendor operation in VendorHandlersApi.
 */
export interface VendorHandlersApiCreateVendorRequest {
    readonly vendorCreate: VendorCreate
}

/**
 * Request parameters for deleteVendor operation in VendorHandlersApi.
 */
export interface VendorHandlersApiDeleteVendorRequest {
    /**
     * Vendor ID
     */
    readonly vendorId: string
}

/**
 * Request parameters for getVendorById operation in VendorHandlersApi.
 */
export interface VendorHandlersApiGetVendorByIdRequest {
    /**
     * Vendor ID
     */
    readonly vendorId: string
}

/**
 * Request parameters for getVendorsByHoa operation in VendorHandlersApi.
 */
export interface VendorHandlersApiGetVendorsByHoaRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string
}

/**
 * Request parameters for updateVendor operation in VendorHandlersApi.
 */
export interface VendorHandlersApiUpdateVendorRequest {
    /**
     * Vendor ID
     */
    readonly vendorId: string

    readonly vendorUpdate: VendorUpdate
}

/**
 * VendorHandlersApi - object-oriented interface
 */
export class VendorHandlersApi extends BaseAPI {
    /**
     * 
     * @param {VendorHandlersApiCreateVendorRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createVendor(requestParameters: VendorHandlersApiCreateVendorRequest, options?: RawAxiosRequestConfig) {
        return VendorHandlersApiFp(this.configuration).createVendor(requestParameters.vendorCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {VendorHandlersApiDeleteVendorRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteVendor(requestParameters: VendorHandlersApiDeleteVendorRequest, options?: RawAxiosRequestConfig) {
        return VendorHandlersApiFp(this.configuration).deleteVendor(requestParameters.vendorId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {VendorHandlersApiGetVendorByIdRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getVendorById(requestParameters: VendorHandlersApiGetVendorByIdRequest, options?: RawAxiosRequestConfig) {
        return VendorHandlersApiFp(this.configuration).getVendorById(requestParameters.vendorId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {VendorHandlersApiGetVendorsByHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getVendorsByHoa(requestParameters: VendorHandlersApiGetVendorsByHoaRequest, options?: RawAxiosRequestConfig) {
        return VendorHandlersApiFp(this.configuration).getVendorsByHoa(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getVendorsForUser(options?: RawAxiosRequestConfig) {
        return VendorHandlersApiFp(this.configuration).getVendorsForUser(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {VendorHandlersApiUpdateVendorRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateVendor(requestParameters: VendorHandlersApiUpdateVendorRequest, options?: RawAxiosRequestConfig) {
        return VendorHandlersApiFp(this.configuration).updateVendor(requestParameters.vendorId, requestParameters.vendorUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ViolationHandlersApi - axios parameter creator
 */
export const ViolationHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {ViolationCreate} violationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createViolation: async (violationCreate: ViolationCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationCreate' is not null or undefined
            assertParamExists('createViolation', 'violationCreate', violationCreate)
            const localVarPath = `/api/v1/violations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(violationCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete a violation
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteViolation: async (violationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('deleteViolation', 'violationId', violationId)
            const localVarPath = `/api/v1/violations/{violation_id}`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Violation ID
         * @param {string} attachmentId Attachment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteViolationAttachment: async (id: string, attachmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteViolationAttachment', 'id', id)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('deleteViolationAttachment', 'attachmentId', attachmentId)
            const localVarPath = `/api/v1/violations/{id}/attachments/{attachment_id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"attachment_id"}}`, encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllViolationsForUser: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/violations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getViolationById: async (violationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('getViolationById', 'violationId', violationId)
            const localVarPath = `/api/v1/violations/{violation_id}`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get violation statistics
         * @param {string} [homeId] Filter stats by home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getViolationStats: async (homeId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/violations/stats`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (homeId !== undefined) {
                localVarQueryParameter['home_id'] = homeId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getViolationsByHoa: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getViolationsByHoa', 'hoaId', hoaId)
            const localVarPath = `/api/v1/violations/hoa/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Writes a real payment applied to the fine\'s ledger entry, so the money reaches the home\'s balance, the payer file and aging like any other payment. Omit `amount` to settle whatever is still open.
         * @summary Record a fine payment
         * @param {string} violationId Violation ID
         * @param {FinePaymentRequest} finePaymentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        payFine: async (violationId: string, finePaymentRequest: FinePaymentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('payFine', 'violationId', violationId)
            // verify required parameter 'finePaymentRequest' is not null or undefined
            assertParamExists('payFine', 'finePaymentRequest', finePaymentRequest)
            const localVarPath = `/api/v1/violations/{violation_id}/pay-fine`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(finePaymentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Mark a violation as resolved
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveViolation: async (violationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('resolveViolation', 'violationId', violationId)
            const localVarPath = `/api/v1/violations/{violation_id}/resolve`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update a violation
         * @param {string} violationId Violation ID
         * @param {ViolationUpdate} violationUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateViolation: async (violationId: string, violationUpdate: ViolationUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'violationId' is not null or undefined
            assertParamExists('updateViolation', 'violationId', violationId)
            // verify required parameter 'violationUpdate' is not null or undefined
            assertParamExists('updateViolation', 'violationUpdate', violationUpdate)
            const localVarPath = `/api/v1/violations/{violation_id}`
                .replace(`{${"violation_id"}}`, encodeURIComponent(String(violationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(violationUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} id Violation ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadViolationAttachment: async (id: string, file: File, description?: string | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('uploadViolationAttachment', 'id', id)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadViolationAttachment', 'file', file)
            const localVarPath = `/api/v1/violations/{id}/attachments`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();


            if (description !== undefined) { 
                localVarFormParams.append('description', description as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ViolationHandlersApi - functional programming interface
 */
export const ViolationHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ViolationHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {ViolationCreate} violationCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createViolation(violationCreate: ViolationCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createViolation(violationCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.createViolation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete a violation
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteViolation(violationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteViolation(violationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.deleteViolation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Violation ID
         * @param {string} attachmentId Attachment ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteViolationAttachment(id: string, attachmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteViolationAttachment(id, attachmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.deleteViolationAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllViolationsForUser(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllViolationsForUser(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.getAllViolationsForUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getViolationById(violationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getViolationById(violationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.getViolationById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get violation statistics
         * @param {string} [homeId] Filter stats by home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getViolationStats(homeId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationStats>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getViolationStats(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.getViolationStats']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getViolationsByHoa(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getViolationsByHoa(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.getViolationsByHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Writes a real payment applied to the fine\'s ledger entry, so the money reaches the home\'s balance, the payer file and aging like any other payment. Omit `amount` to settle whatever is still open.
         * @summary Record a fine payment
         * @param {string} violationId Violation ID
         * @param {FinePaymentRequest} finePaymentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async payFine(violationId: string, finePaymentRequest: FinePaymentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.payFine(violationId, finePaymentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.payFine']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Mark a violation as resolved
         * @param {string} violationId Violation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveViolation(violationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveViolation(violationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.resolveViolation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Update a violation
         * @param {string} violationId Violation ID
         * @param {ViolationUpdate} violationUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateViolation(violationId: string, violationUpdate: ViolationUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ViolationReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateViolation(violationId, violationUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.updateViolation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} id Violation ID
         * @param {File} file The file itself. Streamed, not buffered through serde.
         * @param {string | null} [description] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadViolationAttachment(id: string, file: File, description?: string | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadViolationAttachment(id, file, description, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ViolationHandlersApi.uploadViolationAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ViolationHandlersApi - factory interface
 */
export const ViolationHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ViolationHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {ViolationHandlersApiCreateViolationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createViolation(requestParameters: ViolationHandlersApiCreateViolationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationReturn> {
            return localVarFp.createViolation(requestParameters.violationCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete a violation
         * @param {ViolationHandlersApiDeleteViolationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteViolation(requestParameters: ViolationHandlersApiDeleteViolationRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteViolation(requestParameters.violationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ViolationHandlersApiDeleteViolationAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteViolationAttachment(requestParameters: ViolationHandlersApiDeleteViolationAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteViolationAttachment(requestParameters.id, requestParameters.attachmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllViolationsForUser(options?: RawAxiosRequestConfig): AxiosPromise<ViolationList> {
            return localVarFp.getAllViolationsForUser(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ViolationHandlersApiGetViolationByIdRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getViolationById(requestParameters: ViolationHandlersApiGetViolationByIdRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationReturn> {
            return localVarFp.getViolationById(requestParameters.violationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get violation statistics
         * @param {ViolationHandlersApiGetViolationStatsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getViolationStats(requestParameters: ViolationHandlersApiGetViolationStatsRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<ViolationStats> {
            return localVarFp.getViolationStats(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ViolationHandlersApiGetViolationsByHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getViolationsByHoa(requestParameters: ViolationHandlersApiGetViolationsByHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationList> {
            return localVarFp.getViolationsByHoa(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * Writes a real payment applied to the fine\'s ledger entry, so the money reaches the home\'s balance, the payer file and aging like any other payment. Omit `amount` to settle whatever is still open.
         * @summary Record a fine payment
         * @param {ViolationHandlersApiPayFineRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        payFine(requestParameters: ViolationHandlersApiPayFineRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationReturn> {
            return localVarFp.payFine(requestParameters.violationId, requestParameters.finePaymentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Mark a violation as resolved
         * @param {ViolationHandlersApiResolveViolationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveViolation(requestParameters: ViolationHandlersApiResolveViolationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationReturn> {
            return localVarFp.resolveViolation(requestParameters.violationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update a violation
         * @param {ViolationHandlersApiUpdateViolationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateViolation(requestParameters: ViolationHandlersApiUpdateViolationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ViolationReturn> {
            return localVarFp.updateViolation(requestParameters.violationId, requestParameters.violationUpdate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {ViolationHandlersApiUploadViolationAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadViolationAttachment(requestParameters: ViolationHandlersApiUploadViolationAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentReturn> {
            return localVarFp.uploadViolationAttachment(requestParameters.id, requestParameters.file, requestParameters.description, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createViolation operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiCreateViolationRequest {
    readonly violationCreate: ViolationCreate
}

/**
 * Request parameters for deleteViolation operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiDeleteViolationRequest {
    /**
     * Violation ID
     */
    readonly violationId: string
}

/**
 * Request parameters for deleteViolationAttachment operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiDeleteViolationAttachmentRequest {
    /**
     * Violation ID
     */
    readonly id: string

    /**
     * Attachment ID
     */
    readonly attachmentId: string
}

/**
 * Request parameters for getViolationById operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiGetViolationByIdRequest {
    /**
     * Violation ID
     */
    readonly violationId: string
}

/**
 * Request parameters for getViolationStats operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiGetViolationStatsRequest {
    /**
     * Filter stats by home ID
     */
    readonly homeId?: string
}

/**
 * Request parameters for getViolationsByHoa operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiGetViolationsByHoaRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string
}

/**
 * Request parameters for payFine operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiPayFineRequest {
    /**
     * Violation ID
     */
    readonly violationId: string

    readonly finePaymentRequest: FinePaymentRequest
}

/**
 * Request parameters for resolveViolation operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiResolveViolationRequest {
    /**
     * Violation ID
     */
    readonly violationId: string
}

/**
 * Request parameters for updateViolation operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiUpdateViolationRequest {
    /**
     * Violation ID
     */
    readonly violationId: string

    readonly violationUpdate: ViolationUpdate
}

/**
 * Request parameters for uploadViolationAttachment operation in ViolationHandlersApi.
 */
export interface ViolationHandlersApiUploadViolationAttachmentRequest {
    /**
     * Violation ID
     */
    readonly id: string

    /**
     * The file itself. Streamed, not buffered through serde.
     */
    readonly file: File

    readonly description?: string | null
}

/**
 * ViolationHandlersApi - object-oriented interface
 */
export class ViolationHandlersApi extends BaseAPI {
    /**
     * 
     * @param {ViolationHandlersApiCreateViolationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createViolation(requestParameters: ViolationHandlersApiCreateViolationRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).createViolation(requestParameters.violationCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete a violation
     * @param {ViolationHandlersApiDeleteViolationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteViolation(requestParameters: ViolationHandlersApiDeleteViolationRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).deleteViolation(requestParameters.violationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ViolationHandlersApiDeleteViolationAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteViolationAttachment(requestParameters: ViolationHandlersApiDeleteViolationAttachmentRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).deleteViolationAttachment(requestParameters.id, requestParameters.attachmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllViolationsForUser(options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).getAllViolationsForUser(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ViolationHandlersApiGetViolationByIdRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getViolationById(requestParameters: ViolationHandlersApiGetViolationByIdRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).getViolationById(requestParameters.violationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get violation statistics
     * @param {ViolationHandlersApiGetViolationStatsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getViolationStats(requestParameters: ViolationHandlersApiGetViolationStatsRequest = {}, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).getViolationStats(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ViolationHandlersApiGetViolationsByHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getViolationsByHoa(requestParameters: ViolationHandlersApiGetViolationsByHoaRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).getViolationsByHoa(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Writes a real payment applied to the fine\'s ledger entry, so the money reaches the home\'s balance, the payer file and aging like any other payment. Omit `amount` to settle whatever is still open.
     * @summary Record a fine payment
     * @param {ViolationHandlersApiPayFineRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public payFine(requestParameters: ViolationHandlersApiPayFineRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).payFine(requestParameters.violationId, requestParameters.finePaymentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Mark a violation as resolved
     * @param {ViolationHandlersApiResolveViolationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveViolation(requestParameters: ViolationHandlersApiResolveViolationRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).resolveViolation(requestParameters.violationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Update a violation
     * @param {ViolationHandlersApiUpdateViolationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateViolation(requestParameters: ViolationHandlersApiUpdateViolationRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).updateViolation(requestParameters.violationId, requestParameters.violationUpdate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ViolationHandlersApiUploadViolationAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadViolationAttachment(requestParameters: ViolationHandlersApiUploadViolationAttachmentRequest, options?: RawAxiosRequestConfig) {
        return ViolationHandlersApiFp(this.configuration).uploadViolationAttachment(requestParameters.id, requestParameters.file, requestParameters.description, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * WebhooksHandlersApi - axios parameter creator
 */
export const WebhooksHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * A separate endpoint from the management one because it verifies against a **different signing secret** — WorkOS issues one per endpoint, and the two realms are separate projects. That choice has to be made by the route, before the body is read: the signature is the authentication, so nothing inside the payload can be trusted to say which secret should validate it.  Mounted outside `AuthMiddleware`, like its sibling. There is no bearer token on a webhook and never will be.
         * @summary Receive one webhook from the **member** WorkOS project.
         * @param {string} body Raw WorkOS event payload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberWorkosWebhook: async (body: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            assertParamExists('memberWorkosWebhook', 'body', body)
            const localVarPath = `/api/v1/webhooks/workos/member`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(body, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * **Unauthenticated by our scheme, and mounted outside `AuthMiddleware` on purpose.** A webhook carries no bearer token; its signature is the authentication, and nothing below the verification step runs until that passes.  ## Why the body is `Bytes` and not `Json`  The signature covers the exact bytes WorkOS sent. `web::Json` would deserialize and hand us a struct, and re-serializing that to check the MAC would change key order and whitespace — so it would never match. The raw body has to survive intact all the way to the verifier.  ## Why almost everything answers 200  WorkOS retries anything that is not a 2xx. That is right for \"we were down\" and wrong for \"we will never be able to act on this\" — an event about a user who does not exist here, or one we deliberately ignore, would be retried forever. So a *verified* request is acknowledged even when it changes nothing, and only a request we could not authenticate is refused.
         * @summary Receive a WorkOS event.
         * @param {string} body Raw WorkOS event payload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        workosWebhook: async (body: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            assertParamExists('workosWebhook', 'body', body)
            const localVarPath = `/api/v1/webhooks/workos`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(body, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * WebhooksHandlersApi - functional programming interface
 */
export const WebhooksHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = WebhooksHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * A separate endpoint from the management one because it verifies against a **different signing secret** — WorkOS issues one per endpoint, and the two realms are separate projects. That choice has to be made by the route, before the body is read: the signature is the authentication, so nothing inside the payload can be trusted to say which secret should validate it.  Mounted outside `AuthMiddleware`, like its sibling. There is no bearer token on a webhook and never will be.
         * @summary Receive one webhook from the **member** WorkOS project.
         * @param {string} body Raw WorkOS event payload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async memberWorkosWebhook(body: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.memberWorkosWebhook(body, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebhooksHandlersApi.memberWorkosWebhook']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * **Unauthenticated by our scheme, and mounted outside `AuthMiddleware` on purpose.** A webhook carries no bearer token; its signature is the authentication, and nothing below the verification step runs until that passes.  ## Why the body is `Bytes` and not `Json`  The signature covers the exact bytes WorkOS sent. `web::Json` would deserialize and hand us a struct, and re-serializing that to check the MAC would change key order and whitespace — so it would never match. The raw body has to survive intact all the way to the verifier.  ## Why almost everything answers 200  WorkOS retries anything that is not a 2xx. That is right for \"we were down\" and wrong for \"we will never be able to act on this\" — an event about a user who does not exist here, or one we deliberately ignore, would be retried forever. So a *verified* request is acknowledged even when it changes nothing, and only a request we could not authenticate is refused.
         * @summary Receive a WorkOS event.
         * @param {string} body Raw WorkOS event payload
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async workosWebhook(body: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.workosWebhook(body, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WebhooksHandlersApi.workosWebhook']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * WebhooksHandlersApi - factory interface
 */
export const WebhooksHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = WebhooksHandlersApiFp(configuration)
    return {
        /**
         * A separate endpoint from the management one because it verifies against a **different signing secret** — WorkOS issues one per endpoint, and the two realms are separate projects. That choice has to be made by the route, before the body is read: the signature is the authentication, so nothing inside the payload can be trusted to say which secret should validate it.  Mounted outside `AuthMiddleware`, like its sibling. There is no bearer token on a webhook and never will be.
         * @summary Receive one webhook from the **member** WorkOS project.
         * @param {WebhooksHandlersApiMemberWorkosWebhookRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        memberWorkosWebhook(requestParameters: WebhooksHandlersApiMemberWorkosWebhookRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.memberWorkosWebhook(requestParameters.body, options).then((request) => request(axios, basePath));
        },
        /**
         * **Unauthenticated by our scheme, and mounted outside `AuthMiddleware` on purpose.** A webhook carries no bearer token; its signature is the authentication, and nothing below the verification step runs until that passes.  ## Why the body is `Bytes` and not `Json`  The signature covers the exact bytes WorkOS sent. `web::Json` would deserialize and hand us a struct, and re-serializing that to check the MAC would change key order and whitespace — so it would never match. The raw body has to survive intact all the way to the verifier.  ## Why almost everything answers 200  WorkOS retries anything that is not a 2xx. That is right for \"we were down\" and wrong for \"we will never be able to act on this\" — an event about a user who does not exist here, or one we deliberately ignore, would be retried forever. So a *verified* request is acknowledged even when it changes nothing, and only a request we could not authenticate is refused.
         * @summary Receive a WorkOS event.
         * @param {WebhooksHandlersApiWorkosWebhookRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        workosWebhook(requestParameters: WebhooksHandlersApiWorkosWebhookRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.workosWebhook(requestParameters.body, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for memberWorkosWebhook operation in WebhooksHandlersApi.
 */
export interface WebhooksHandlersApiMemberWorkosWebhookRequest {
    /**
     * Raw WorkOS event payload
     */
    readonly body: string
}

/**
 * Request parameters for workosWebhook operation in WebhooksHandlersApi.
 */
export interface WebhooksHandlersApiWorkosWebhookRequest {
    /**
     * Raw WorkOS event payload
     */
    readonly body: string
}

/**
 * WebhooksHandlersApi - object-oriented interface
 */
export class WebhooksHandlersApi extends BaseAPI {
    /**
     * A separate endpoint from the management one because it verifies against a **different signing secret** — WorkOS issues one per endpoint, and the two realms are separate projects. That choice has to be made by the route, before the body is read: the signature is the authentication, so nothing inside the payload can be trusted to say which secret should validate it.  Mounted outside `AuthMiddleware`, like its sibling. There is no bearer token on a webhook and never will be.
     * @summary Receive one webhook from the **member** WorkOS project.
     * @param {WebhooksHandlersApiMemberWorkosWebhookRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public memberWorkosWebhook(requestParameters: WebhooksHandlersApiMemberWorkosWebhookRequest, options?: RawAxiosRequestConfig) {
        return WebhooksHandlersApiFp(this.configuration).memberWorkosWebhook(requestParameters.body, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * **Unauthenticated by our scheme, and mounted outside `AuthMiddleware` on purpose.** A webhook carries no bearer token; its signature is the authentication, and nothing below the verification step runs until that passes.  ## Why the body is `Bytes` and not `Json`  The signature covers the exact bytes WorkOS sent. `web::Json` would deserialize and hand us a struct, and re-serializing that to check the MAC would change key order and whitespace — so it would never match. The raw body has to survive intact all the way to the verifier.  ## Why almost everything answers 200  WorkOS retries anything that is not a 2xx. That is right for \"we were down\" and wrong for \"we will never be able to act on this\" — an event about a user who does not exist here, or one we deliberately ignore, would be retried forever. So a *verified* request is acknowledged even when it changes nothing, and only a request we could not authenticate is refused.
     * @summary Receive a WorkOS event.
     * @param {WebhooksHandlersApiWorkosWebhookRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public workosWebhook(requestParameters: WebhooksHandlersApiWorkosWebhookRequest, options?: RawAxiosRequestConfig) {
        return WebhooksHandlersApiFp(this.configuration).workosWebhook(requestParameters.body, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * WorkOrderHandlersApi - axios parameter creator
 */
export const WorkOrderHandlersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeWorkOrder: async (workOrderId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workOrderId' is not null or undefined
            assertParamExists('completeWorkOrder', 'workOrderId', workOrderId)
            const localVarPath = `/api/v1/work-orders/{work_order_id}/complete`
                .replace(`{${"work_order_id"}}`, encodeURIComponent(String(workOrderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {WorkOrderCreate} workOrderCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createWorkOrder: async (workOrderCreate: WorkOrderCreate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workOrderCreate' is not null or undefined
            assertParamExists('createWorkOrder', 'workOrderCreate', workOrderCreate)
            const localVarPath = `/api/v1/work-orders`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workOrderCreate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteWorkOrder: async (workOrderId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workOrderId' is not null or undefined
            assertParamExists('deleteWorkOrder', 'workOrderId', workOrderId)
            const localVarPath = `/api/v1/work-orders/{work_order_id}`
                .replace(`{${"work_order_id"}}`, encodeURIComponent(String(workOrderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;


            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllWorkOrdersForUser: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/work-orders`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrderById: async (workOrderId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workOrderId' is not null or undefined
            assertParamExists('getWorkOrderById', 'workOrderId', workOrderId)
            const localVarPath = `/api/v1/work-orders/{work_order_id}`
                .replace(`{${"work_order_id"}}`, encodeURIComponent(String(workOrderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} [hoaId] Filter stats by HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrderStats: async (hoaId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/work-orders/stats`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (hoaId !== undefined) {
                localVarQueryParameter['hoa_id'] = hoaId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrdersByHoa: async (hoaId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'hoaId' is not null or undefined
            assertParamExists('getWorkOrdersByHoa', 'hoaId', hoaId)
            const localVarPath = `/api/v1/work-orders/hoa/{hoa_id}`
                .replace(`{${"hoa_id"}}`, encodeURIComponent(String(hoaId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrdersByHome: async (homeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'homeId' is not null or undefined
            assertParamExists('getWorkOrdersByHome', 'homeId', homeId)
            const localVarPath = `/api/v1/work-orders/home/{home_id}`
                .replace(`{${"home_id"}}`, encodeURIComponent(String(homeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {WorkOrderUpdate} workOrderUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateWorkOrder: async (workOrderId: string, workOrderUpdate: WorkOrderUpdate, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workOrderId' is not null or undefined
            assertParamExists('updateWorkOrder', 'workOrderId', workOrderId)
            // verify required parameter 'workOrderUpdate' is not null or undefined
            assertParamExists('updateWorkOrder', 'workOrderUpdate', workOrderUpdate)
            const localVarPath = `/api/v1/work-orders/{work_order_id}`
                .replace(`{${"work_order_id"}}`, encodeURIComponent(String(workOrderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workOrderUpdate, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * WorkOrderHandlersApi - functional programming interface
 */
export const WorkOrderHandlersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = WorkOrderHandlersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async completeWorkOrder(workOrderId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.completeWorkOrder(workOrderId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.completeWorkOrder']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {WorkOrderCreate} workOrderCreate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createWorkOrder(workOrderCreate: WorkOrderCreate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createWorkOrder(workOrderCreate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.createWorkOrder']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteWorkOrder(workOrderId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteWorkOrder(workOrderId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.deleteWorkOrder']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAllWorkOrdersForUser(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAllWorkOrdersForUser(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.getAllWorkOrdersForUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getWorkOrderById(workOrderId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getWorkOrderById(workOrderId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.getWorkOrderById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} [hoaId] Filter stats by HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getWorkOrderStats(hoaId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderStats>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getWorkOrderStats(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.getWorkOrderStats']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} hoaId HOA ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getWorkOrdersByHoa(hoaId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getWorkOrdersByHoa(hoaId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.getWorkOrdersByHoa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} homeId Home ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getWorkOrdersByHome(homeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getWorkOrdersByHome(homeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.getWorkOrdersByHome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} workOrderId Work Order ID
         * @param {WorkOrderUpdate} workOrderUpdate 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateWorkOrder(workOrderId: string, workOrderUpdate: WorkOrderUpdate, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkOrderReturn>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateWorkOrder(workOrderId, workOrderUpdate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkOrderHandlersApi.updateWorkOrder']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * WorkOrderHandlersApi - factory interface
 */
export const WorkOrderHandlersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = WorkOrderHandlersApiFp(configuration)
    return {
        /**
         * 
         * @param {WorkOrderHandlersApiCompleteWorkOrderRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeWorkOrder(requestParameters: WorkOrderHandlersApiCompleteWorkOrderRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderReturn> {
            return localVarFp.completeWorkOrder(requestParameters.workOrderId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiCreateWorkOrderRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createWorkOrder(requestParameters: WorkOrderHandlersApiCreateWorkOrderRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderReturn> {
            return localVarFp.createWorkOrder(requestParameters.workOrderCreate, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiDeleteWorkOrderRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteWorkOrder(requestParameters: WorkOrderHandlersApiDeleteWorkOrderRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteWorkOrder(requestParameters.workOrderId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAllWorkOrdersForUser(options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderList> {
            return localVarFp.getAllWorkOrdersForUser(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiGetWorkOrderByIdRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrderById(requestParameters: WorkOrderHandlersApiGetWorkOrderByIdRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderReturn> {
            return localVarFp.getWorkOrderById(requestParameters.workOrderId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiGetWorkOrderStatsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrderStats(requestParameters: WorkOrderHandlersApiGetWorkOrderStatsRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderStats> {
            return localVarFp.getWorkOrderStats(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiGetWorkOrdersByHoaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrdersByHoa(requestParameters: WorkOrderHandlersApiGetWorkOrdersByHoaRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderList> {
            return localVarFp.getWorkOrdersByHoa(requestParameters.hoaId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiGetWorkOrdersByHomeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkOrdersByHome(requestParameters: WorkOrderHandlersApiGetWorkOrdersByHomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderList> {
            return localVarFp.getWorkOrdersByHome(requestParameters.homeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkOrderHandlersApiUpdateWorkOrderRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateWorkOrder(requestParameters: WorkOrderHandlersApiUpdateWorkOrderRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkOrderReturn> {
            return localVarFp.updateWorkOrder(requestParameters.workOrderId, requestParameters.workOrderUpdate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for completeWorkOrder operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiCompleteWorkOrderRequest {
    /**
     * Work Order ID
     */
    readonly workOrderId: string
}

/**
 * Request parameters for createWorkOrder operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiCreateWorkOrderRequest {
    readonly workOrderCreate: WorkOrderCreate
}

/**
 * Request parameters for deleteWorkOrder operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiDeleteWorkOrderRequest {
    /**
     * Work Order ID
     */
    readonly workOrderId: string
}

/**
 * Request parameters for getWorkOrderById operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiGetWorkOrderByIdRequest {
    /**
     * Work Order ID
     */
    readonly workOrderId: string
}

/**
 * Request parameters for getWorkOrderStats operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiGetWorkOrderStatsRequest {
    /**
     * Filter stats by HOA ID
     */
    readonly hoaId?: string
}

/**
 * Request parameters for getWorkOrdersByHoa operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiGetWorkOrdersByHoaRequest {
    /**
     * HOA ID
     */
    readonly hoaId: string
}

/**
 * Request parameters for getWorkOrdersByHome operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiGetWorkOrdersByHomeRequest {
    /**
     * Home ID
     */
    readonly homeId: string
}

/**
 * Request parameters for updateWorkOrder operation in WorkOrderHandlersApi.
 */
export interface WorkOrderHandlersApiUpdateWorkOrderRequest {
    /**
     * Work Order ID
     */
    readonly workOrderId: string

    readonly workOrderUpdate: WorkOrderUpdate
}

/**
 * WorkOrderHandlersApi - object-oriented interface
 */
export class WorkOrderHandlersApi extends BaseAPI {
    /**
     * 
     * @param {WorkOrderHandlersApiCompleteWorkOrderRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public completeWorkOrder(requestParameters: WorkOrderHandlersApiCompleteWorkOrderRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).completeWorkOrder(requestParameters.workOrderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiCreateWorkOrderRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createWorkOrder(requestParameters: WorkOrderHandlersApiCreateWorkOrderRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).createWorkOrder(requestParameters.workOrderCreate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiDeleteWorkOrderRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteWorkOrder(requestParameters: WorkOrderHandlersApiDeleteWorkOrderRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).deleteWorkOrder(requestParameters.workOrderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAllWorkOrdersForUser(options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).getAllWorkOrdersForUser(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiGetWorkOrderByIdRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getWorkOrderById(requestParameters: WorkOrderHandlersApiGetWorkOrderByIdRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).getWorkOrderById(requestParameters.workOrderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiGetWorkOrderStatsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getWorkOrderStats(requestParameters: WorkOrderHandlersApiGetWorkOrderStatsRequest = {}, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).getWorkOrderStats(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiGetWorkOrdersByHoaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getWorkOrdersByHoa(requestParameters: WorkOrderHandlersApiGetWorkOrdersByHoaRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).getWorkOrdersByHoa(requestParameters.hoaId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiGetWorkOrdersByHomeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getWorkOrdersByHome(requestParameters: WorkOrderHandlersApiGetWorkOrdersByHomeRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).getWorkOrdersByHome(requestParameters.homeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkOrderHandlersApiUpdateWorkOrderRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateWorkOrder(requestParameters: WorkOrderHandlersApiUpdateWorkOrderRequest, options?: RawAxiosRequestConfig) {
        return WorkOrderHandlersApiFp(this.configuration).updateWorkOrder(requestParameters.workOrderId, requestParameters.workOrderUpdate, options).then((request) => request(this.axios, this.basePath));
    }
}



